import React, { useState, useEffect, useCallback, createContext, useContext, useRef, Component } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './App.css';
import EverfitClientManagement from './EverfitClientManager';
import EverfitClientProfile from './EverfitClientProfile';
import { 
  isPushSupported, 
  isPushEnabled, 
  enablePushNotifications, 
  disablePushNotifications,
  sendTestPush,
  getNotificationPermission,
  isIOS,
  isStandalone,
  getIOSInstructions
} from './pushNotifications';
import { playNotificationSound, playSound, getSoundPresets, previewSound, getStoredPreferences, savePreferences, NOTIFICATION_TYPES } from './notificationSound';
import { wsService } from './websocketService';
import { ExerciseVideoModal } from './ExerciseVideoModal';
import { PushCheckPanel } from './PushCheckPanel';
import { NotificationPrefs } from './NotificationPrefs';
import { PlanAdjustmentsPanel } from './PlanAdjustmentsPanel';
import { ProgressReportCard, TrainerReportsTab } from './ProgressReportCard';
import { NotificationReenablePrompt } from './NotificationReenablePrompt';

// Error Boundary Component to catch rendering errors
class WorkoutErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Workout Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={`min-h-screen flex items-center justify-center ${this.props.isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className={`text-center p-8 rounded-xl max-w-md ${this.props.isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <svg className="w-16 h-16 mx-auto mb-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className={`text-xl font-bold mb-2 ${this.props.isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Something went wrong
            </h2>
            <p className={`mb-4 ${this.props.isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              There was an error loading this workout. Please try again or contact support.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                if (this.props.onClose) this.props.onClose();
              }}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Helper function to safely get sets count from exercise
// Handles both number (e.g., 3) and array formats (e.g., [{set_number: 1, ...}])
const getSetsCount = (sets) => {
  if (sets === null || sets === undefined) return 0;
  if (typeof sets === 'number') return sets;
  if (typeof sets === 'string') return parseInt(sets) || 0;
  if (Array.isArray(sets)) return sets.length;
  return 0;
};

// Helper function to safely get reps value
// Handles both number and string formats
const getRepsDisplay = (exercise) => {
  const reps = exercise?.reps || exercise?.rep_range;
  if (reps === null || reps === undefined) return '8-12';
  if (typeof reps === 'object') return '8-12'; // Handle unexpected object
  if (typeof reps === 'string') {
    // If it contains exercise names or complex data, extract just the numbers
    // Handle formats like "OHP: 8-10, Pull-t..." or "8-10, Exercise Name: 12-15"
    if (reps.includes(':') || (reps.includes(',') && /[a-zA-Z]{3,}/.test(reps))) {
      // Try to find just a rep range like "8-10" or "12"
      const match = reps.match(/^(\d+(?:-\d+)?)/);
      if (match) return match[1];
      // Or find the first number range anywhere
      const rangeMatch = reps.match(/(\d+(?:-\d+)?)/);
      return rangeMatch ? rangeMatch[1] : '8-12';
    }
  }
  return reps;
};

// Simple Weight Chart Component
const WeightChart = ({ weightData, weightGoal }) => {
  if (!weightData || weightData.length === 0) {
    return (
      <div className="text-center py-8 " style={{color: "#2d3748"}}>
        <p>No weight data to display</p>
        <p className="text-sm">Start logging your weight to see progress!</p>
      </div>
    );
  }

  // Sort data by date
  const sortedData = [...weightData].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  
  if (sortedData.length < 2 && !weightGoal) {
    return (
      <div className="text-center py-8 " style={{color: "#2d3748"}}>
        <p>Need at least 2 weight entries to show progress chart</p>
        {weightGoal && (
          <p className="text-sm text-blue-600 mt-2">Goal: {weightGoal.target_weight} lbs</p>
        )}
      </div>
    );
  }

  const weights = sortedData.map(d => d.weight);
  let minWeight = Math.min(...weights) - 5;
  let maxWeight = Math.max(...weights) + 5;
  
  // Include weight goal in the range calculation
  if (weightGoal) {
    minWeight = Math.min(minWeight, weightGoal.target_weight - 5);
    maxWeight = Math.max(maxWeight, weightGoal.target_weight + 5);
  }
  
  const weightRange = maxWeight - minWeight;
  
  const chartWidth = 800;
  const chartHeight = 300;
  const padding = 50;
  const plotWidth = chartWidth - (padding * 2);
  const plotHeight = chartHeight - (padding * 2);
  
  const points = sortedData.map((entry, index) => {
    const x = padding + (index / Math.max(sortedData.length - 1, 1)) * plotWidth;
    const y = padding + ((maxWeight - entry.weight) / weightRange) * plotHeight;
    return { x, y, weight: entry.weight, date: entry.date };
  });
  
  const pathData = points.map((point, index) => 
    `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  ).join(' ');

  // Calculate goal line Y position
  const goalY = weightGoal ? padding + ((maxWeight - weightGoal.target_weight) / weightRange) * plotHeight : null;

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-black">Weight Progress Chart</h3>
        {weightGoal && (
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-3 h-0.5 bg-red-500"></div>
            <span className="" style={{color: "#2d3748"}}>Goal: {weightGoal.target_weight} lbs</span>
          </div>
        )}
      </div>
      
      <div className="overflow-x-auto">
        <svg width={chartWidth} height={chartHeight} viewBox={`0 0 ${chartWidth} ${chartHeight}`}>
          {/* Grid lines */}
          {[0, 1, 2, 3, 4].map(i => {
            const y = padding + (i / 4) * plotHeight;
            const weight = maxWeight - (i / 4) * weightRange;
            return (
              <g key={`grid-${i}`}>
                <line 
                  x1={padding} 
                  y1={y} 
                  x2={padding + plotWidth} 
                  y2={y} 
                  stroke="#e5e7eb" 
                  strokeWidth="1"
                />
                <text 
                  x={padding - 10} 
                  y={y + 5} 
                  textAnchor="end" 
                  fontSize="12" 
                  fill="#6b7280"
                >
                  {weight.toFixed(0)} lbs
                </text>
              </g>
            );
          })}
          
          {/* Chart line */}
          <path 
            d={pathData} 
            fill="none" 
            stroke="#010eda" 
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          
          {/* Weight Goal Line */}
          {weightGoal && goalY && (
            <g>
              <line
                x1={padding}
                y1={goalY}
                x2={padding + plotWidth}
                y2={goalY}
                stroke="#ef4444"
                strokeWidth="2"
                strokeDasharray="5,5"
              />
              <text
                x={padding + plotWidth - 10}
                y={goalY - 5}
                textAnchor="end"
                fontSize="12"
                fill="#ef4444"
                fontWeight="500"
              >
                Goal: {weightGoal.target_weight} lbs
              </text>
            </g>
          )}
          
          {/* Data points */}
          {points.map((point, index) => (
            <g key={`point-${index}`}>
              <circle 
                cx={point.x} 
                cy={point.y} 
                r="6" 
                fill="#010eda"
                stroke="white"
                strokeWidth="2"
              />
              <text
                x={point.x}
                y={point.y - 15}
                textAnchor="middle"
                fontSize="10"
                fill="#374151"
                fontWeight="500"
              >
                {point.weight} lbs
              </text>
            </g>
          ))}
          
          {/* X-axis labels */}
          {points.map((point, index) => (
            <text
              key={`label-${index}`}
              x={point.x}
              y={chartHeight - 10}
              textAnchor="middle"
              fontSize="10"
              fill="#6b7280"
            >
              {new Date(point.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </text>
          ))}
        </svg>
      </div>
    </div>
  );
};

// Exercise Video Component
const ExerciseVideo = ({ video_url, external_video_url, exerciseName, backendUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  
  // Function to extract YouTube video ID
  const getYouTubeVideoId = (url) => {
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };
  
  // Function to extract Vimeo video ID
  const getVimeoVideoId = (url) => {
    const regex = /vimeo\.com\/(?:video\/)?(\d+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };
  
  if (!video_url && !external_video_url) return null;
  
  // Handle uploaded video
  if (video_url) {
    const fullVideoUrl = video_url.startsWith('http') ? video_url : `${backendUrl}${video_url}`;
    
    return (
      <div className="mt-3">
        {!showVideo ? (
          <div 
            className="relative bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors"
            onClick={() => setShowVideo(true)}
          >
            <div className="aspect-video flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </div>
                <p className="text-sm font-medium " style={{color: "#2d3748"}}>Watch {exerciseName}</p>
                <p className="text-xs " style={{color: "#2d3748"}}>Click to play video</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="relative">
            <video
              src={fullVideoUrl}
              controls
              className="w-full rounded-lg"
              style={{ maxHeight: '300px' }}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            <button
              onClick={() => setShowVideo(false)}
              className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-70"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  }
  
  // Handle external video (YouTube/Vimeo)
  if (external_video_url) {
    const youtubeId = getYouTubeVideoId(external_video_url);
    const vimeoId = getVimeoVideoId(external_video_url);
    
    if (youtubeId) {
      return (
        <div className="mt-3 max-w-md">
          {!showVideo ? (
            <div 
              className="relative bg-red-100 rounded-lg cursor-pointer hover:bg-red-200 transition-colors"
              onClick={() => setShowVideo(true)}
            >
              <div className="flex items-center justify-center py-3 px-4">
                <div className="text-center">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-1">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                  <p className="text-xs font-medium " style={{color: "#2d3748"}}>Watch Demo</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}`}
                title={exerciseName}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full rounded-lg"
                style={{ height: '200px' }}
              />
              <button
                onClick={() => setShowVideo(false)}
                className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-70"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
      );
    }
    
    if (vimeoId) {
      return (
        <div className="mt-3 max-w-md">
          {!showVideo ? (
            <div 
              className="relative bg-blue-100 rounded-lg cursor-pointer hover:bg-blue-200 transition-colors"
              onClick={() => setShowVideo(true)}
            >
              <div className="flex items-center justify-center py-3 px-4">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-1">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                  <p className="text-xs font-medium " style={{color: "#2d3748"}}>Watch Demo</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              <iframe
                src={`https://player.vimeo.com/video/${vimeoId}`}
                title={exerciseName}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                className="w-full rounded-lg"
                style={{ height: '200px' }}
              />
              <button
                onClick={() => setShowVideo(false)}
                className="absolute top-2 right-2 bg-black bg-opacity-50 text-white p-1 rounded-full hover:bg-opacity-70"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
      );
    }
    
    // Fallback for other external URLs
    return (
      <div className="mt-3">
        <a
          href={external_video_url}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-gray-100 rounded-lg p-4 hover:bg-gray-200 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gray-600 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium " style={{color: "#2d3748"}}>{exerciseName} - External Video</p>
              <p className="text-xs " style={{color: "#2d3748"}}>Click to open in new tab</p>
            </div>
          </div>
        </a>
      </div>
    );
  }
  
  return null;
};

// Everfit Overview Section
const EverfitOverviewSection = ({ client, clientData }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold" style={{color: "#1a1a1a"}}>Client Overview</h3>
        <p className="mt-1" style={{color: "#2d3748"}}>Summary of {client?.name || 'Client'}'s activity and progress</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-xl">
          <div className="text-2xl font-bold text-blue-600">{clientData?.goals?.length || 0}</div>
          <div className="text-sm text-gray-600">Active Goals</div>
        </div>
        <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-xl">
          <div className="text-2xl font-bold text-green-600">{clientData?.tasks?.filter(t => t.status === 'completed')?.length || 0}</div>
          <div className="text-sm text-gray-600">Completed Tasks</div>
        </div>
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-xl">
          <div className="text-2xl font-bold text-purple-600">{clientData?.habits?.length || 0}</div>
          <div className="text-sm text-gray-600">Habits Tracked</div>
        </div>
      </div>
      
      {/* Recent Activity */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h4 className="font-semibold text-lg text-gray-900 mb-4">Recent Activity</h4>
        <div className="space-y-3">
          {clientData?.tasks?.slice(0, 3).map((task, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-700">{task.title || task.name}</span>
              <span className={`px-2 py-1 rounded-full text-xs ${
                task.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                {task.status || 'pending'}
              </span>
            </div>
          ))}
          {(!clientData?.tasks || clientData.tasks.length === 0) && (
            <p className="text-gray-500 text-center py-4">No recent activity</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Everfit Habits Section
const EverfitHabitsSection = ({ client, habits, onCreateHabit, onRefresh, backendUrl }) => {
  const toggleHabitComplete = async (habitId, currentStatus) => {
    try {
      const response = await fetch(`${backendUrl}/api/habits/${habitId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ completed: !currentStatus })
      });
      if (response.ok && onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error updating habit:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl font-bold" style={{color: "#1a1a1a"}}>Habits</h3>
          <p className="mt-1" style={{color: "#2d3748"}}>Track {client?.name || 'Client'}'s daily habits</p>
        </div>
        <button
          onClick={onCreateHabit}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          + Add Habit
        </button>
      </div>
      
      <div className="space-y-3">
        {habits && habits.length > 0 ? (
          habits.map((habit, idx) => (
            <div key={habit.id || idx} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:border-blue-300 transition-colors">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => toggleHabitComplete(habit.id, habit.completed)}
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    habit.completed ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}
                >
                  {habit.completed && (
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
                <div>
                  <p className="font-medium text-gray-900">{habit.name || habit.title}</p>
                  {habit.frequency && <p className="text-sm text-gray-500">{habit.frequency}</p>}
                </div>
              </div>
              <div className="text-sm text-gray-500">
                {habit.streak ? `🔥 ${habit.streak} day streak` : ''}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <p className="text-gray-500">No habits created yet</p>
            <button
              onClick={onCreateHabit}
              className="mt-3 text-blue-600 hover:text-blue-700 font-medium"
            >
              Create your first habit
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Everfit Create Modal
const EverfitCreateModal = ({ type, client, onClose, onCreate }) => {
  const [formData, setFormData] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(type, formData);
  };

  const getFormFields = () => {
    switch (type) {
      case 'goal':
        return (
          <>
            <input
              type="text"
              placeholder="Goal title"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={formData.title || ''}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
            />
            <textarea
              placeholder="Description"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={formData.description || ''}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={3}
            />
            <input
              type="date"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={formData.target_date || ''}
              onChange={(e) => setFormData({...formData, target_date: e.target.value})}
            />
          </>
        );
      case 'task':
        return (
          <>
            <input
              type="text"
              placeholder="Task title"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={formData.title || ''}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
            />
            <textarea
              placeholder="Description"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={formData.description || ''}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={2}
            />
            <input
              type="date"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={formData.due_date || ''}
              onChange={(e) => setFormData({...formData, due_date: e.target.value})}
            />
          </>
        );
      case 'habit':
        return (
          <>
            <input
              type="text"
              placeholder="Habit name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={formData.name || ''}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
            <select
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={formData.frequency || 'daily'}
              onChange={(e) => setFormData({...formData, frequency: e.target.value})}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="3x-week">3x per week</option>
            </select>
          </>
        );
      default:
        return (
          <input
            type="text"
            placeholder="Name"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            value={formData.name || ''}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            required
          />
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
      <div className="bg-white rounded-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">
            Create New {type.charAt(0).toUpperCase() + type.slice(1)}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {getFormFields()}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Everfit Goals Section
const EverfitGoalsSection = ({ client, goals, onCreateGoal, onRefresh, backendUrl }) => {
  const [editingGoal, setEditingGoal] = useState(null);

  const updateGoal = async (goalId, updateData) => {
    try {
      const response = await fetch(`${backendUrl}/api/goals/${goalId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        onRefresh();
        setEditingGoal(null);
      }
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  const getGoalTypeIcon = (type) => {
    const icons = {
      weight_loss: '⚖️',
      muscle_gain: '💪',
      strength: '🏋️',
      endurance: '🏃',
      body_composition: '📊'
    };
    return icons[type] || '🎯';
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'paused': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateProgress = (goal) => {
    if (!goal.target_value || !goal.current_value) return 0;
    return Math.min(100, Math.max(0, (goal.current_value / goal.target_value) * 100));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold " style={{color: "#1a1a1a"}}>Client Goals</h3>
          <p className=" mt-1" style={{color: "#2d3748"}}>Track and manage {client.name}'s fitness goals</p>
        </div>
        <button
          onClick={onCreateGoal}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Add Goal</span>
        </button>
      </div>

      {/* Goals Grid */}
      {goals.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">🎯</span>
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">No Goals Set</h4>
          <p className=" mb-4" style={{color: "#2d3748"}}>Help {client.name} achieve success by setting clear, measurable goals</p>
          <button
            onClick={onCreateGoal}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create First Goal
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {goals.map(goal => (
            <div key={goal.id} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
              {/* Goal Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xl">{getGoalTypeIcon(goal.goal_type)}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{goal.title}</h4>
                    <p className="text-sm " style={{color: "#2d3748"}}>{goal.description}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(goal.status)}`}>
                  {goal.status}
                </span>
              </div>

              {/* Progress Section */}
              {goal.target_value && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium " style={{color: "#2d3748"}}>Progress</span>
                    <span className="text-sm " style={{color: "#2d3748"}}>
                      {goal.current_value || 0} / {goal.target_value} {goal.unit}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${calculateProgress(goal)}%` }}
                    ></div>
                  </div>
                  <div className="text-xs  mt-1" style={{color: "#2d3748"}}>
                    {Math.round(calculateProgress(goal))}% complete
                  </div>
                </div>
              )}

              {/* Target Date */}
              {goal.target_date && (
                <div className="mb-4">
                  <div className="flex items-center space-x-2 text-sm " style={{color: "#2d3748"}}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4h3a1 1 0 011 1v6a1 1 0 01-1 1h-3v4a1 1 0 01-1 1H9a1 1 0 01-1-1v-4H5a1 1 0 01-1-1V8a1 1 0 011-1h3z" />
                    </svg>
                    <span>Target: {new Date(goal.target_date).toLocaleDateString()}</span>
                  </div>
                </div>
              )}

              {/* Priority Badge */}
              <div className="flex items-center justify-between">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  goal.priority === 'high' ? 'bg-red-100 text-red-800' :
                  goal.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {goal.priority} priority
                </span>

                {/* Quick Update Button */}
                {goal.status === 'active' && (
                  <button
                    onClick={() => setEditingGoal(goal)}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Update Progress
                  </button>
                )}
              </div>

              {/* Milestones */}
              {goal.milestones && goal.milestones.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h5 className="text-sm font-medium  mb-2" style={{color: "#2d3748"}}>Milestones</h5>
                  <div className="space-y-2">
                    {goal.milestones.map((milestone, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${milestone.completed ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                        <span className={`text-sm ${milestone.completed ? 'text-green-700 line-through' : 'text-gray-700'}`}>
                          {milestone.title}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Quick Update Modal */}
      {editingGoal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Update Progress</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>
                    Current Value ({editingGoal.unit})
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    defaultValue={editingGoal.current_value}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    id="currentValue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>Status</label>
                  <select
                    defaultValue={editingGoal.status}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    id="status"
                  >
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="paused">Paused</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="p-6 bg-gray-50 flex items-center justify-end space-x-3">
              <button
                onClick={() => setEditingGoal(null)}
                className="px-4 py-2 bg-gray-300  rounded-lg hover:bg-gray-400" style={{color: "#2d3748"}}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const currentValue = document.getElementById('currentValue').value;
                  const status = document.getElementById('status').value;
                  updateGoal(editingGoal.id, { 
                    current_value: parseFloat(currentValue),
                    status: status
                  });
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Everfit Tasks Section
const EverfitTasksSection = ({ client, tasks, onCreateTask, onRefresh, backendUrl }) => {
  const [editingTask, setEditingTask] = useState(null);

  const updateTask = async (taskId, updateData) => {
    try {
      const response = await fetch(`${backendUrl}/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });
      
      if (response.ok) {
        onRefresh();
        setEditingTask(null);
      }
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/tasks/${taskId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold " style={{color: "#1a1a1a"}}>Client Tasks</h3>
          <p className=" mt-1" style={{color: "#2d3748"}}>Manage tasks and action items for {client.name}</p>
        </div>
        <button
          onClick={onCreateTask}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Add Task</span>
        </button>
      </div>

      {/* Tasks Grid */}
      {tasks.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">📋</span>
          </div>
          <h4 className="text-lg font-semibold text-gray-900 mb-2">No Tasks Created</h4>
          <p className=" mb-4" style={{color: "#2d3748"}}>Create tasks to help {client.name} stay on track</p>
          <button
            onClick={onCreateTask}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Create First Task
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {tasks.map(task => (
            <div key={task.id} className={`bg-white border-2 rounded-xl p-6 hover:shadow-md transition-shadow ${getStatusColor(task.status)}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <input
                      type="checkbox"
                      checked={task.status === 'completed'}
                      onChange={(e) => updateTask(task.id, { 
                        status: e.target.checked ? 'completed' : 'pending' 
                      })}
                      className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                    />
                    <h4 className={`font-semibold text-lg ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                      {task.title}
                    </h4>
                    <span className={`flex items-center text-sm ${getPriorityColor(task.priority)}`}>
                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3 6a3 3 0 013-3h10a1 1 0 01.8 1.6L14.25 8l2.55 3.4A1 1 0 0116 13H6a1 1 0 00-1 1v3a1 1 0 11-2 0V6z" clipRule="evenodd" />
                      </svg>
                      {task.priority}
                    </span>
                  </div>
                  
                  {task.description && (
                    <p className=" mb-3" style={{color: "#2d3748"}}>{task.description}</p>
                  )}

                  <div className="flex items-center space-x-4 text-sm " style={{color: "#2d3748"}}>
                    {task.due_date && (
                      <div className="flex items-center space-x-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                      </div>
                    )}
                    {task.category && (
                      <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                        {task.category}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setEditingTask(task)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => deleteTask(task.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Everfit Nutrition Section
const EverfitNutritionSection = ({ client, nutritionPlan, mealPlans, onCreatePlan, onRefresh, backendUrl }) => {
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold " style={{color: "#1a1a1a"}}>Nutrition Management</h3>
          <p className=" mt-1" style={{color: "#2d3748"}}>Manage nutrition plans and meal plans for {client.name}</p>
        </div>
        <button
          onClick={onCreatePlan}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Create Plan</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('macros')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'macros'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Macro Targets
          </button>
          <button
            onClick={() => setActiveTab('meals')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'meals'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Meal Plans
          </button>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Current Nutrition Plan */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h4 className="font-semibold text-lg text-gray-900 mb-4">Current Nutrition Plan</h4>
            {nutritionPlan ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="" style={{color: "#2d3748"}}>Calories</span>
                  <span className="font-semibold text-gray-900">{nutritionPlan.daily_calories} kcal</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="" style={{color: "#2d3748"}}>Protein</span>
                  <span className="font-semibold text-gray-900">{nutritionPlan.protein}g</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="" style={{color: "#2d3748"}}>Carbs</span>
                  <span className="font-semibold text-gray-900">{nutritionPlan.carbs}g</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="" style={{color: "#2d3748"}}>Fats</span>
                  <span className="font-semibold text-gray-900">{nutritionPlan.fats}g</span>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 " style={{color: "#2d3748"}}>
                <p>No nutrition plan set</p>
                <button
                  onClick={onCreatePlan}
                  className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Create Nutrition Plan
                </button>
              </div>
            )}
          </div>

          {/* Meal Plans Summary */}
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <h4 className="font-semibold text-lg text-gray-900 mb-4">Meal Plans</h4>
            {mealPlans && mealPlans.length > 0 ? (
              <div className="space-y-2">
                {mealPlans.map(plan => (
                  <div key={plan.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium " style={{color: "#1a1a1a"}}>{plan.name}</span>
                      <span className="text-sm " style={{color: "#2d3748"}}>{plan.meals?.length || 0} meals</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 " style={{color: "#2d3748"}}>
                <p>No meal plans created</p>
                <button
                  onClick={onCreatePlan}
                  className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Create Meal Plan
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Macros Tab */}
      {activeTab === 'macros' && (
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h4 className="font-semibold text-lg text-gray-900 mb-4">Macro Target Settings</h4>
          {nutritionPlan ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>Daily Calories</label>
                  <input
                    type="number"
                    value={nutritionPlan.daily_calories}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>Protein (g)</label>
                  <input
                    type="number"
                    value={nutritionPlan.protein}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>Carbs (g)</label>
                  <input
                    type="number"
                    value={nutritionPlan.carbs}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>Fats (g)</label>
                  <input
                    type="number"
                    value={nutritionPlan.fats}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    readOnly
                  />
                </div>
              </div>
              <button
                onClick={onCreatePlan}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Update Macros
              </button>
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <p className=" mb-4" style={{color: "#2d3748"}}>No macro targets set</p>
              <button
                onClick={onCreatePlan}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Set Macro Targets
              </button>
            </div>
          )}
        </div>
      )}

      {/* Meals Tab */}
      {activeTab === 'meals' && (
        <div className="space-y-4">
          {mealPlans && mealPlans.length > 0 ? (
            mealPlans.map(plan => (
              <div key={plan.id} className="bg-white border border-gray-200 rounded-xl p-6">
                <h4 className="font-semibold text-lg text-gray-900 mb-3">{plan.name}</h4>
                {plan.meals && plan.meals.length > 0 ? (
                  <div className="space-y-2">
                    {plan.meals.map((meal, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium " style={{color: "#1a1a1a"}}>{meal.name}</p>
                          <p className="text-sm " style={{color: "#2d3748"}}>{meal.calories} kcal</p>
                        </div>
                        <span className="text-xs " style={{color: "#2d3748"}}>{meal.time}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className=" text-sm" style={{color: "#2d3748"}}>No meals in this plan</p>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🍽️</span>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">No Meal Plans</h4>
              <p className=" mb-4" style={{color: "#2d3748"}}>Create meal plans to help {client.name} with nutrition</p>
              <button
                onClick={onCreatePlan}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create First Meal Plan
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Everfit Workouts Section (placeholder)
const EverfitWorkoutsSection = ({ client, backendUrl }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold " style={{color: "#1a1a1a"}}>Workout Management</h3>
        <p className=" mt-1" style={{color: "#2d3748"}}>View and manage {client.name}'s workout schedule</p>
      </div>
      <div className="text-center py-12 bg-gray-50 rounded-xl">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">💪</span>
        </div>
        <h4 className="text-lg font-semibold text-gray-900 mb-2">Workout Management</h4>
        <p className="" style={{color: "#2d3748"}}>Use the Training tab in the client profile for detailed workout management</p>
      </div>
    </div>
  );
};

// Everfit Progress Section (placeholder)
const EverfitProgressSection = ({ client, progressPhotos, performanceMetrics, backendUrl }) => {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-2xl font-bold " style={{color: "#1a1a1a"}}>Progress Tracking</h3>
        <p className=" mt-1" style={{color: "#2d3748"}}>Monitor {client.name}'s progress and achievements</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Progress Photos */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h4 className="font-semibold text-lg text-gray-900 mb-4">Progress Photos</h4>
          {progressPhotos && progressPhotos.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {progressPhotos.slice(0, 4).map(photo => (
                <div key={photo.id} className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                  <img src={photo.photo_url} alt="Progress" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 " style={{color: "#2d3748"}}>
              <p>No progress photos yet</p>
            </div>
          )}
        </div>

        {/* Performance Metrics */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h4 className="font-semibold text-lg text-gray-900 mb-4">Performance Metrics</h4>
          {performanceMetrics && performanceMetrics.length > 0 ? (
            <div className="space-y-3">
              {performanceMetrics.slice(0, 5).map(metric => (
                <div key={metric.id} className="flex items-center justify-between">
                  <span className="" style={{color: "#2d3748"}}>{metric.exercise_name}</span>
                  <span className="font-semibold text-gray-900">{metric.value} {metric.unit}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 " style={{color: "#2d3748"}}>
              <p>No performance metrics yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Calendar setup
const localizer = momentLocalizer(moment);

// Everfit-Style Calendar Assignment Interface
const EverfitCalendarAssignment = ({ client, workoutTemplates, workouts, onAssignWorkout, backendUrl }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAssignmentPanel, setShowAssignmentPanel] = useState(false);
  const [workoutEvents, setWorkoutEvents] = useState([]);
  const [draggedWorkout, setDraggedWorkout] = useState(null);

  const fetchClientWorkouts = useCallback(async () => {
    if (!client) return;
    try {
      const response = await fetch(`${backendUrl}/api/clients/${client.id}/assignments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const events = data.sessions
          .filter(session => session.scheduled_date) // Filter out sessions with null scheduled_date
          .map(session => {
          const startDate = new Date(session.scheduled_date);
          // Set to start of day and make it an all-day event
          const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
          const end = new Date(start);
          end.setDate(end.getDate() + 1); // All-day event
          
          return {
            id: session.id,
            title: session.name,
            start: start,
            end: end,
            resource: session,
            status: session.status,
            allDay: true
          };
        });
        setWorkoutEvents(events);
      }
    } catch (error) {
      console.error('Error fetching client workouts:', error);
    }
  }, [client, backendUrl]);

  useEffect(() => {
    if (client) {
      fetchClientWorkouts();
    }
  }, [client, fetchClientWorkouts]);

  const handleSelectSlot = ({ start, end }) => {
    setSelectedDate(start);
    setShowAssignmentPanel(true);
  };

  const handleEventDrop = async ({ event, start, end }) => {
    const updatedEvent = { ...event, start, end };
    
    // Update backend
    try {
      const response = await fetch(`${backendUrl}/api/workout-sessions/${event.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          scheduled_date: start.toISOString()
        })
      });
      
      if (response.ok) {
        // Update local state
        setWorkoutEvents(prev => prev.map(e => e.id === event.id ? updatedEvent : e));
      }
    } catch (error) {
      console.error('Error updating workout date:', error);
    }
  };

  const assignWorkoutToDate = async (workoutData, date) => {
    // Set to start of day for consistent date handling
    const assignmentDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0, 0);
    
    const assignmentData = {
      name: workoutData.name,
      exercises: workoutData.exercises,
      template_id: workoutData.id && workoutData.exercises ? null : workoutData.id,
      workout_id: workoutData.id && workoutData.exercises ? workoutData.id : null,
      scheduled_date: assignmentDate.toISOString(),
      notes: ''
    };

    try {
      const response = await fetch(`${backendUrl}/api/clients/${client.id}/assign-workout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(assignmentData)
      });
      
      if (response.ok) {
        fetchClientWorkouts(); // Refresh calendar
        return true;
      }
    } catch (error) {
      console.error('Error assigning workout:', error);
    }
    return false;
  };

  const eventStyleGetter = (event, start, end, isSelected) => {
    let backgroundColor = '#3174ad';
    
    switch(event.status) {
      case 'completed':
        backgroundColor = '#28a745';
        break;
      case 'in_progress':
        backgroundColor = '#007bff';
        break;
      case 'scheduled':
        backgroundColor = '#6f42c1';
        break;
      case 'cancelled':
        backgroundColor = '#dc3545';
        break;
      default:
        backgroundColor = '#6c757d';
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity: 0.8,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '12px'
      }
    };
  };

  return (
    <div className="flex h-[600px] bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Workout Library Sidebar */}
      <div className="w-80 border-r border-gray-200 p-4 overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Assign to {client?.name}
        </h3>
        
        {/* Templates Section */}
        <div className="mb-6">
          <h4 className="text-sm font-medium  mb-3 flex items-center" style={{color: "#2d3748"}}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 011-1h1a1 1 0 011 1v1m-4 0h4m-4 4v1m0-1h4m-4 1v1h4m-2-3h.01" />
            </svg>
            Workout Templates
          </h4>
          <div className="space-y-2">
            {workoutTemplates.map(template => (
              <div
                key={template.id}
                draggable
                onDragStart={(e) => {
                  setDraggedWorkout(template);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                className="p-3 bg-blue-50 border border-blue-200 rounded-lg cursor-move hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-blue-900">{template.name}</p>
                    <p className="text-xs text-blue-700">{template.exercises.length} exercises</p>
                    {template.estimated_duration && (
                      <p className="text-xs text-blue-600">{template.estimated_duration}min</p>
                    )}
                  </div>
                  <div className="text-blue-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 7v8m3-8v8m3-8v8" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Workouts Section */}
        <div>
          <h4 className="text-sm font-medium  mb-3 flex items-center" style={{color: "#2d3748"}}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
            Custom Workouts
          </h4>
          <div className="space-y-2">
            {workouts.map(workout => (
              <div
                key={workout.id}
                draggable
                onDragStart={(e) => {
                  setDraggedWorkout(workout);
                  e.dataTransfer.effectAllowed = 'move';
                }}
                className="p-3 bg-green-50 border border-green-200 rounded-lg cursor-move hover:bg-green-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-green-900">{workout.name}</p>
                    <p className="text-xs text-green-700">{workout.exercises.length} exercises</p>
                    <p className="text-xs text-green-600">Custom workout</p>
                  </div>
                  <div className="text-green-400">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 7v8m3-8v8m3-8v8" />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-6 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs " style={{color: "#2d3748"}}>
            <strong>Drag and drop</strong> workouts onto calendar dates to assign them, or <strong>click a date</strong> to open assignment panel.
          </p>
        </div>
      </div>

      {/* Calendar Area */}
      <div className="flex-1 p-4">
        <div 
          className="h-full"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (draggedWorkout) {
              // Get the date from the drop position (simplified - you'd need more complex logic for exact date)
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const y = e.clientY - rect.top;
              
              // For now, use selected date or today
              assignWorkoutToDate(draggedWorkout, selectedDate || new Date());
              setDraggedWorkout(null);
            }
          }}
        >
          <Calendar
            localizer={localizer}
            events={workoutEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={(event) => {
              // Handle event click
              console.log('Event clicked:', event);
            }}
            onEventDrop={handleEventDrop}
            selectable
            resizable
            eventPropGetter={eventStyleGetter}
            views={['month', 'week']}
            defaultView="month"
            showMultiDayTimes={false}
            components={{
              event: ({ event }) => (
                <div className="text-xs">
                  <div className="font-semibold truncate">{event.title}</div>
                  <div className="opacity-75">{event.resource?.exercises?.length || 0} exercises</div>
                </div>
              )
            }}
          />
        </div>
      </div>

      {/* Quick Assignment Panel */}
      {showAssignmentPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Assign Workout for {moment(selectedDate).format('MMMM Do, YYYY')}
              </h3>
            </div>
            
            <div className="p-6 space-y-4 max-h-96 overflow-y-auto">
              {/* Templates */}
              {workoutTemplates.length > 0 && (
                <div>
                  <h4 className="font-medium  mb-2" style={{color: "#1a1a1a"}}>Templates</h4>
                  <div className="space-y-2">
                    {workoutTemplates.map(template => (
                      <button
                        key={template.id}
                        onClick={() => {
                          assignWorkoutToDate(template, selectedDate);
                          setShowAssignmentPanel(false);
                        }}
                        className="w-full p-3 text-left border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      >
                        <div className="font-medium " style={{color: "#1a1a1a"}}>{template.name}</div>
                        <div className="text-sm " style={{color: "#2d3748"}}>{template.exercises.length} exercises • {template.estimated_duration || '45'}min</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Workouts */}
              {workouts.length > 0 && (
                <div>
                  <h4 className="font-medium  mb-2" style={{color: "#1a1a1a"}}>Custom Workouts</h4>
                  <div className="space-y-2">
                    {workouts.map(workout => (
                      <button
                        key={workout.id}
                        onClick={() => {
                          assignWorkoutToDate(workout, selectedDate);
                          setShowAssignmentPanel(false);
                        }}
                        className="w-full p-3 text-left border border-gray-200 rounded-lg hover:bg-green-50 hover:border-green-300 transition-colors"
                      >
                        <div className="font-medium " style={{color: "#1a1a1a"}}>{workout.name}</div>
                        <div className="text-sm " style={{color: "#2d3748"}}>{workout.exercises.length} exercises</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 bg-gray-50 flex items-center justify-end">
              <button
                onClick={() => setShowAssignmentPanel(false)}
                className="px-4 py-2 bg-gray-300  rounded-lg hover:bg-gray-400" style={{color: "#2d3748"}}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Client Calendar View (for clients to see their assigned workouts)
// Client Daily Workout View (for clients to see today's workout and cycle through the week)
const ClientDailyWorkoutView = ({ workoutSessions, onStartWorkout, backendUrl, isDarkMode, onRefresh, clientEquipment = '' }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showFullWorkout, setShowFullWorkout] = useState(false);
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [workoutToMove, setWorkoutToMove] = useState(null);
  const [moving, setMoving] = useState(false);
  const [showAllExercises, setShowAllExercises] = useState(false);

  // Get current week's workouts only
  const getCurrentWeekWorkouts = () => {
    const today = new Date();
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6); // End of week (Saturday)

    return workoutSessions.filter(session => {
      if (!session.scheduled_date) return false;
      // Filter out temporary dates (used during swap operations)
      if (session.scheduled_date.includes('2099')) return false;
      const sessionDate = new Date(session.scheduled_date);
      return sessionDate >= currentWeekStart && sessionDate <= currentWeekEnd;
    });
  };

  // Get workout for specific date
  const getWorkoutForDate = (date) => {
    const dateString = date.toISOString().split('T')[0];
    return workoutSessions.find(session => {
      // Skip sessions without scheduled_date or with temporary dates
      if (!session.scheduled_date || session.scheduled_date.includes('2099')) return false;
      return session.scheduled_date.split('T')[0] === dateString;
    });
  };

  // Navigate to previous/next day (within current week only)
  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction);
    
    // Check if new date is within current week
    const today = new Date();
    const currentWeekStart = new Date(today);
    currentWeekStart.setDate(today.getDate() - today.getDay());
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6);
    
    if (newDate >= currentWeekStart && newDate <= currentWeekEnd) {
      setCurrentDate(newDate);
      setShowAllExercises(false); // Reset expanded state when changing days
    }
  };

  const currentWorkout = getWorkoutForDate(currentDate);
  const isToday = currentDate.toDateString() === new Date().toDateString();
  const currentWeekWorkouts = getCurrentWeekWorkouts();

  // Days of the week for navigation
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();
  const currentWeekStart = new Date(today);
  currentWeekStart.setDate(today.getDate() - today.getDay());

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const day = new Date(currentWeekStart);
    day.setDate(currentWeekStart.getDate() + i);
    return day;
  });

  const handleStartWorkout = () => {
    if (currentWorkout) {
      console.log('[Workout] Starting workout:', currentWorkout);
      
      // Validate workout structure
      if (!currentWorkout.id) {
        console.error('[Workout] Missing workout ID');
        alert('This workout appears to be corrupted. Please refresh and try again.');
        return;
      }
      
      // Check if workout has exercises
      if (!currentWorkout.exercises || !Array.isArray(currentWorkout.exercises) || currentWorkout.exercises.length === 0) {
        console.error('[Workout] No exercises in workout:', currentWorkout);
        alert('This workout has no exercises. Please contact your trainer to add exercises.');
        return;
      }
      
      // Validate each exercise has required fields
      const validExercises = currentWorkout.exercises.filter(ex => ex && ex.name);
      if (validExercises.length === 0) {
        console.error('[Workout] No valid exercises found');
        alert('This workout has no valid exercises. Please contact your trainer.');
        return;
      }
      
      // Use validated exercises
      const safeWorkout = {
        ...currentWorkout,
        exercises: validExercises
      };
      
      console.log('[Workout] Setting selected workout with', validExercises.length, 'exercises');
      setSelectedWorkout(safeWorkout);
      setShowFullWorkout(true);
      if (onStartWorkout) {
        onStartWorkout(safeWorkout);
      }
    }
  };

  // Handle moving a workout to a different day
  const handleMoveWorkout = async (workout, newDate) => {
    if (!workout || !newDate) {
      console.log('[Move] Missing workout or newDate');
      return;
    }
    
    // Validate workout has an ID
    if (!workout.id) {
      console.error('[Move] Workout has no ID');
      alert('Cannot move this workout - missing workout ID');
      return;
    }
    
    setMoving(true);
    try {
      const newDateStr = newDate.toISOString().split('T')[0];
      const originalDateStr = workout.scheduled_date ? 
        (typeof workout.scheduled_date === 'string' ? workout.scheduled_date.split('T')[0] : workout.scheduled_date?.toISOString?.()?.split('T')[0])
        : null;
      
      const workoutName = workout.name || 'Workout';
      
      console.log('[Move] Moving from:', originalDateStr, 'to:', newDateStr);
      
      // Find if there's a workout on the target date that needs to be swapped
      const workoutOnTargetDate = workoutSessions.find(s => {
        if (!s.scheduled_date || !s.id) return false;
        const sessionDate = typeof s.scheduled_date === 'string' 
          ? s.scheduled_date.split('T')[0] 
          : s.scheduled_date?.toISOString()?.split('T')[0];
        return sessionDate === newDateStr && s.id !== workout.id;
      });
      
      console.log('[Swap] Workout on target date:', workoutOnTargetDate ? (workoutOnTargetDate.name || 'Unnamed workout') : 'none');
      console.log('[Swap] Original date:', originalDateStr, 'New date:', newDateStr);
      
      // Helper function to move a single workout
      const moveWorkout = async (w, targetDate) => {
        if (!w.id) {
          console.error('[Move] Cannot move workout without ID');
          return { ok: false };
        }
        
        // Try sessions first for client-created workouts, assignments for AI/template workouts
        const isAssignment = w.is_ai_generated === true || !!w.workout_template_id || !!w.workout_id;
        console.log('[Swap] Moving workout:', w.name || 'unnamed', 'isAssignment:', isAssignment, 'to:', targetDate);
        
        let endpoint = isAssignment
          ? `${backendUrl}/api/workout-assignments/${w.id}`
          : `${backendUrl}/api/workout-sessions/${w.id}`;
        
        try {
          let response = await fetch(endpoint, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ scheduled_date: targetDate })
          });
          
          // If first endpoint fails with 404, try alternate
          if (!response.ok && response.status === 404) {
            const alternateEndpoint = isAssignment
              ? `${backendUrl}/api/workout-sessions/${w.id}`
              : `${backendUrl}/api/workout-assignments/${w.id}`;
            
            console.log('[Swap] First endpoint failed, trying alternate:', alternateEndpoint);
            response = await fetch(alternateEndpoint, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ scheduled_date: targetDate })
            });
          }
          
          return response;
        } catch (err) {
          console.error('[Swap] Network error moving workout:', err);
          return { ok: false };
        }
      };
      
      // If there's a workout on the target date and we have an original date, swap both
      if (workoutOnTargetDate && originalDateStr) {
        const targetName = workoutOnTargetDate.name || 'Workout';
        console.log('[Swap] Swapping workouts...');
        console.log('[Swap] Selected workout:', workoutName, 'from', originalDateStr, '→', newDateStr);
        console.log('[Swap] Target workout:', targetName, 'from', newDateStr, '→', originalDateStr);
        
        // Use a temporary date to avoid potential conflicts
        const tempDate = '2099-12-31';
        let swapSuccess = false;
        
        try {
          // Step 1: Move target workout to temp date
          const tempResponse = await moveWorkout(workoutOnTargetDate, tempDate);
          if (!tempResponse.ok) {
            console.error('[Swap] Failed to move to temp date');
            throw new Error('Failed to move target to temp date');
          }
          
          // Step 2: Move selected workout to target date (newDateStr)
          const response1 = await moveWorkout(workout, newDateStr);
          console.log('[Swap] Move selected result:', response1.ok);
          if (!response1.ok) {
            // Rollback: move target back to its original date
            await moveWorkout(workoutOnTargetDate, newDateStr);
            throw new Error('Failed to move selected workout');
          }
          
          // Step 3: Move target workout to original date
          const response2 = await moveWorkout(workoutOnTargetDate, originalDateStr);
          console.log('[Swap] Move target result:', response2.ok);
          if (!response2.ok) {
            // Rollback: move selected back, then target back
            await moveWorkout(workout, originalDateStr);
            await moveWorkout(workoutOnTargetDate, newDateStr);
            throw new Error('Failed to move target workout');
          }
          
          swapSuccess = true;
        } catch (swapError) {
          console.error('[Swap] Swap failed:', swapError);
          // Ensure nothing is left on temp date - clean up
          try {
            await moveWorkout(workoutOnTargetDate, newDateStr);
          } catch (cleanupErr) {
            console.error('[Swap] Cleanup failed:', cleanupErr);
          }
        }
        
        if (swapSuccess) {
          setShowMoveModal(false);
          setWorkoutToMove(null);
          if (onRefresh) {
            await onRefresh();
          }
          const originalDateDisplay = new Date(originalDateStr).toLocaleDateString();
          alert(`Workouts swapped successfully!\n\n${workoutName} → ${newDate.toLocaleDateString()}\n${targetName} → ${originalDateDisplay}`);
        } else {
          alert('Failed to swap workouts. Please try again.');
        }
      } else if (workoutOnTargetDate && !originalDateStr) {
        // Workout being moved has no original date but target has a workout
        // Ask user if they want to replace the target workout's date
        const targetName = workoutOnTargetDate.name || 'Workout';
        const confirmSwap = window.confirm(
          `There's a workout on ${newDate.toLocaleDateString()}: "${targetName}"\n\n` +
          `Since "${workoutName}" doesn't have a current date, moving it will replace "${targetName}" on that date.\n\n` +
          `"${targetName}" will be moved to tomorrow.\n\n` +
          `Do you want to proceed?`
        );
        
        if (confirmSwap) {
          // Move target to tomorrow instead of a far future date
          const tomorrow = new Date(newDate);
          tomorrow.setDate(tomorrow.getDate() + 1);
          const tomorrowStr = tomorrow.toISOString().split('T')[0];
          
          const targetMoveResponse = await moveWorkout(workoutOnTargetDate, tomorrowStr);
          if (!targetMoveResponse.ok) {
            console.error('[Move] Failed to move target workout');
            alert('Failed to move the existing workout. Please try again.');
            return;
          }
          
          // Move selected to target date
          const response = await moveWorkout(workout, newDateStr);
          
          if (response.ok) {
            setShowMoveModal(false);
            setWorkoutToMove(null);
            if (onRefresh) {
              await onRefresh();
            }
            alert(`Workout moved successfully!\n${workoutName} → ${newDate.toLocaleDateString()}\n${targetName} → ${tomorrow.toLocaleDateString()}`);
          } else {
            // Rollback: move target back
            await moveWorkout(workoutOnTargetDate, newDateStr);
            alert('Failed to move workout. Please try again.');
          }
        }
      } else {
        // Just move the single workout (no workout on target date)
        const response = await moveWorkout(workout, newDateStr);
        
        if (response.ok) {
          setShowMoveModal(false);
          setWorkoutToMove(null);
          if (onRefresh) {
            await onRefresh();
          }
          alert('Workout moved successfully!');
        } else {
          console.error('[Move] Move failed');
          alert('Failed to move workout. Please try again.');
        }
      }
    } catch (error) {
      console.error('[Move] Error moving workout:', error);
      alert('Failed to move workout. Please try again.');
    } finally {
      setMoving(false);
    }
  };

  // Open move modal
  const openMoveModal = (workout) => {
    setWorkoutToMove(workout);
    setShowMoveModal(true);
  };

  if (showFullWorkout && selectedWorkout) {
    return (
      <WorkoutErrorBoundary isDarkMode={isDarkMode} onClose={() => setShowFullWorkout(false)}>
        <ClientFullWorkoutView 
          workout={selectedWorkout} 
          onClose={() => { setShowFullWorkout(false); if (onRefresh) onRefresh(); }}
          onWorkoutComplete={onRefresh}
          backendUrl={backendUrl}
          isDarkMode={isDarkMode}
          clientEquipment={clientEquipment}
        />
      </WorkoutErrorBoundary>
    );
  }

  return (
    <div className="my-workouts-area space-y-4 sm:space-y-6">
      <>
      {/* Week Navigation */}
      <div className={`rounded-xl shadow-sm border p-3 sm:p-6 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className={`text-lg sm:text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>My Workouts</h2>
          <div className="flex items-center gap-2">
            <span className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Current Week</span>
          </div>
        </div>

        {/* Week Days Grid with Workout Details - Responsive */}
        <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-4 sm:mb-6">
          {weekDays.map((day) => {
            const dayWorkout = getWorkoutForDate(day);
            const isSelected = day.toDateString() === currentDate.toDateString();
            const isCurrentDay = day.toDateString() === new Date().toDateString();
            const exerciseCount = dayWorkout?.exercises?.length || 0;
            const dayName = day.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
            const isCompleted = dayWorkout?.status === 'completed';
            
            // Extract workout name (remove "Day X - " prefix for cleaner display)
            const workoutName = dayWorkout?.name?.replace(/^Day \d+ - /, '') || '';
            
            return (
              <button
                key={day.toISOString()}
                onClick={() => {
                  setCurrentDate(day);
                  setShowAllExercises(false);
                }}
                className={`p-1.5 sm:p-3 rounded-lg sm:rounded-xl border-2 transition-all min-h-[70px] sm:min-h-[120px] flex flex-col relative ${
                  isCompleted
                    ? 'bg-green-900/40 text-white border-green-500 shadow-lg'
                    : isSelected
                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg'
                    : isCurrentDay
                    ? isDarkMode ? 'bg-blue-900 text-blue-200 border-blue-700' : 'bg-blue-50 text-blue-700 border-blue-300'
                    : dayWorkout
                    ? isDarkMode ? 'bg-gray-700 text-white border-gray-600 hover:border-blue-500' : 'bg-white text-gray-900 border-gray-200 hover:border-blue-300 hover:shadow-md'
                    : isDarkMode ? 'bg-gray-800 text-gray-500 border-gray-700' : 'bg-gray-50 text-gray-400 border-gray-100'
                }`}
              >
                {/* Completed Checkmark */}
                {isCompleted && (
                  <div className="absolute top-1 right-1 sm:top-2 sm:right-2 w-4 h-4 sm:w-5 sm:h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                <div className={`text-[10px] sm:text-xs font-semibold ${isCompleted ? 'text-green-400' : isSelected ? 'text-blue-200' : ''}`}>
                  {dayName}
                </div>
                <div className={`text-sm sm:text-xl font-bold ${isCompleted ? 'text-green-300' : isSelected ? '' : isCurrentDay ? 'text-blue-600' : ''}`}>
                  {day.getDate()}
                </div>
                
                {dayWorkout ? (
                  <div className="flex-1 flex flex-col justify-center mt-1 sm:mt-2">
                    <div className={`text-[8px] sm:text-xs font-medium leading-tight line-clamp-2 ${
                      isCompleted ? 'text-green-200' : isSelected ? 'text-white' : isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      <span className="hidden sm:inline">{workoutName || dayWorkout.name}</span>
                      <span className="sm:hidden">{workoutName?.substring(0, 1) || dayWorkout.name?.substring(0, 1) || 'W'}</span>
                    </div>
                    <div className={`text-[8px] sm:text-xs mt-0.5 sm:mt-1 ${isCompleted ? 'text-green-400' : isSelected ? 'text-blue-200' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span className="hidden sm:inline">{exerciseCount} exercises</span>
                      <span className="sm:hidden">{exerciseCount}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center">
                    <span className={`text-[8px] sm:text-xs ${isCompleted ? 'text-green-400' : isSelected ? 'text-blue-200' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Rest</span>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected Day Workout Details */}
        {currentWorkout ? (
          <div className={`rounded-xl p-3 sm:p-5 border ${
            currentWorkout.status === 'completed'
              ? 'bg-gradient-to-r from-green-900/30 to-green-800/30 border-green-500'
              : isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100'
          }`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <div>
                <p className={`text-xs sm:text-sm font-medium ${
                  currentWorkout.status === 'completed' ? 'text-green-400' : 'text-blue-600'
                }`}>
                  {currentWorkout.status === 'completed' 
                    ? '✓ Completed Workout' 
                    : isToday ? "Today's Workout" : daysOfWeek[currentDate.getDay()] + "'s Workout"
                  }
                </p>
                <h3 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{currentWorkout.name || 'Workout'}</h3>
                <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {currentWorkout.exercises?.length || 0} exercises
                  {currentWorkout.target_muscles?.length > 0 && ` • ${currentWorkout.target_muscles.join(', ')}`}
                </p>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                {currentWorkout.status !== 'completed' && (
                  <button
                    onClick={() => openMoveModal(currentWorkout)}
                    className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 sm:py-3 rounded-xl font-medium transition-colors text-sm sm:text-base ${
                      isDarkMode 
                        ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' 
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                    data-testid="move-workout-btn"
                  >
                    Move
                  </button>
                )}
                <button
                  onClick={handleStartWorkout}
                  className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold transition-colors shadow-md text-sm sm:text-base ${
                    currentWorkout.status === 'completed'
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {currentWorkout.status === 'completed' ? 'View Results' : currentWorkout.status === 'in_progress' ? 'Continue Workout →' : 'Start Workout →'}
                </button>
              </div>
            </div>
            
            {/* Exercise Preview */}
            <div className="grid grid-cols-2 gap-2 mt-3 sm:mt-4">
              {currentWorkout.exercises?.slice(0, showAllExercises ? undefined : 4).map((exercise, idx) => (
                <div key={idx} className={`rounded-lg p-2 sm:p-3 border ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-white border-gray-200'}`}>
                  <p className={`text-xs sm:text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{exercise.name}</p>
                  <p className={`text-[10px] sm:text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                    {getSetsCount(exercise.sets)} × {getRepsDisplay(exercise)}
                  </p>
                </div>
              ))}
            </div>
            {currentWorkout.exercises?.length > 4 && (
              <button
                onClick={() => setShowAllExercises(!showAllExercises)}
                className={`w-full mt-2 rounded-lg p-2 sm:p-3 flex items-center justify-center gap-2 transition-colors ${
                  isDarkMode 
                    ? 'bg-gray-600 hover:bg-gray-500 text-gray-300' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-500'
                }`}
              >
                <span className="text-xs sm:text-sm">
                  {showAllExercises ? 'Show less' : `+${currentWorkout.exercises.length - 4} more`}
                </span>
                <svg 
                  className={`w-4 h-4 transition-transform ${showAllExercises ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          <div className={`text-center py-6 sm:py-8 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <div className="text-3xl sm:text-4xl mb-2">😴</div>
            <h3 className={`text-base sm:text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Rest Day</h3>
            <p className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No workout scheduled for {daysOfWeek[currentDate.getDay()]}</p>
          </div>
        )}
      </div>

      {/* Move Workout Modal */}
      {showMoveModal && workoutToMove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl p-6 w-full max-w-md ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Move Workout
            </h3>
            <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Select a new day for "{workoutToMove.name || 'Workout'}"
            </p>
            
            {/* Week Days Selection */}
            <div className="grid grid-cols-7 gap-2 mb-6">
              {weekDays.map((day, dayIndex) => {
                const dayWorkout = getWorkoutForDate(day);
                const isCurrentWorkoutDay = workoutToMove && workoutToMove.scheduled_date && 
                  day.toDateString() === new Date(workoutToMove.scheduled_date).toDateString();
                const hasOtherWorkout = dayWorkout && !isCurrentWorkoutDay;
                
                return (
                  <button
                    key={day.toISOString()}
                    onClick={() => !isCurrentWorkoutDay && !moving && handleMoveWorkout(workoutToMove, day)}
                    disabled={isCurrentWorkoutDay || moving}
                    className={`p-2 rounded-lg text-center transition-all ${
                      isCurrentWorkoutDay
                        ? 'bg-blue-600 text-white cursor-not-allowed'
                        : hasOtherWorkout
                        ? isDarkMode 
                          ? 'bg-yellow-900/50 text-yellow-300 hover:bg-yellow-800/50 border border-yellow-700'
                          : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border border-yellow-300'
                        : isDarkMode
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } ${moving ? 'opacity-50' : ''}`}
                  >
                    <div className="text-xs font-medium">{daysOfWeek[dayIndex]}</div>
                    <div className="text-lg font-bold">{day.getDate()}</div>
                    {hasOtherWorkout && (
                      <div className="text-[8px] mt-1">Swap</div>
                    )}
                  </button>
                );
              })}
            </div>
            
            <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Tip: Click a day with another workout to swap them
            </p>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowMoveModal(false);
                  setWorkoutToMove(null);
                }}
                disabled={moving}
                className={`px-4 py-2 rounded-lg ${
                  isDarkMode 
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </>
    </div>
  );
};
// ClientFullWorkoutView - Shows the complete workout when client clicks "Start Workout"
const RestTimerBar = ({ seconds, total, onAdjust, onSkip, isDarkMode }) => {
  const pct = total ? Math.min(100, (seconds / total) * 100) : 0;
  const btn = `px-3 py-2 rounded-lg text-sm font-semibold ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`;
  return (
    <div data-testid="rest-timer-bar" className={`rounded-xl border shadow-2xl overflow-hidden ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
      <div className={`h-1 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
        <div className="h-1 bg-blue-500 transition-[width] duration-500 ease-linear" style={{ width: `${pct}%` }} />
      </div>
      <div className="px-3 py-2 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Rest</p>
          <p data-testid="rest-timer-remaining" className={`text-2xl font-bold tabular-nums ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {Math.floor(seconds / 60)}:{(seconds % 60).toString().padStart(2, '0')}
          </p>
        </div>
        <button data-testid="rest-timer-minus-btn" onClick={() => onAdjust(-15)} className={btn}>-15s</button>
        <button data-testid="rest-timer-plus-btn" onClick={() => onAdjust(15)} className={btn}>+15s</button>
        <button data-testid="rest-timer-skip-btn" onClick={onSkip} className="px-3 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700">Skip</button>
      </div>
    </div>
  );
};

const ClientFullWorkoutView = ({ workout, onClose, backendUrl, isDarkMode, onWorkoutComplete, clientEquipment = '' }) => {
  // All hooks must be declared at the top level, before any conditional returns
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [completedSets, setCompletedSets] = useState({});
  const [setWeights, setSetWeights] = useState({});
  const [setReps, setSetReps] = useState({});
  const [restTimer, setRestTimer] = useState(0);
  const [restTimerTotal, setRestTimerTotal] = useState(0); // Total rest time for progress calculation
  const [isResting, setIsResting] = useState(false);
  const [savingSet, setSavingSet] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [showPRModal, setShowPRModal] = useState(false);
  const [newPRs, setNewPRs] = useState([]);
  const [previousExerciseData, setPreviousExerciseData] = useState(null);
  const [exercisePR, setExercisePR] = useState(null);
  const [showPRCelebration, setShowPRCelebration] = useState(false);
  const [prCelebrationData, setPRCelebrationData] = useState(null);
  const [showProgressTracker, setShowProgressTracker] = useState(false);
  const [exerciseHistory, setExerciseHistory] = useState([]);
  
  // New state for video guide, substitutes, and superset
  const [videoExercise, setVideoExercise] = useState(null);
  const { user: authUser } = useContext(AuthContext) || {};
  const [showSubstituteModal, setShowSubstituteModal] = useState(false);
  const [substituteExercises, setSubstituteExercises] = useState([]);
  const [activeExercise, setActiveExercise] = useState(null); // For tracking which exercise in superset
  const [supersetPartner, setSupersetPartner] = useState(null); // The other exercise in the superset
  const [swapSide, setSwapSide] = useState(null); // 'A' | 'B' when swapping one half of a superset
  const [progressLoaded, setProgressLoaded] = useState(false);
  const progressStorageKey = `workout_progress_${workout.id}`;
  const workoutHeaders = () => {
    const token = localStorage.getItem('session_token');
    return { 'Content-Type': 'application/json', ...(token ? { 'X-Session-Token': token } : {}) };
  };

  // Load saved progress when workout is opened (for continuing a workout)
  useEffect(() => {
    const loadSavedProgress = async () => {
      let saved = null;
      try {
        const response = await fetch(`${backendUrl}/api/workout-sessions/${workout.id}/progress`, {
          credentials: 'include',
          headers: workoutHeaders()
        });
        if (response.ok) {
          const data = await response.json();
          if (data.progress && data.status === 'in_progress') saved = data.progress;
          if (data.status === 'completed') localStorage.removeItem(progressStorageKey);
        }
      } catch (error) {
        console.error('[Workout] Error loading progress:', error);
      }
      // On-device backup covers offline / failed saves - use whichever is newer
      try {
        const local = JSON.parse(localStorage.getItem(progressStorageKey) || 'null');
        if (local && (!saved || (local.saved_at || '') > (saved.saved_at || ''))) saved = local;
      } catch (e) { /* corrupt backup */ }
      if (saved) {
        console.log('[Workout] Restoring saved progress:', saved);
        if (saved.current_exercise_index !== undefined) setCurrentExerciseIndex(saved.current_exercise_index);
        if (saved.completed_sets) setCompletedSets(saved.completed_sets);
        if (saved.set_weights) setSetWeights(saved.set_weights);
        if (saved.set_reps) setSetReps(saved.set_reps);
      }
      setProgressLoaded(true);
    };
    
    loadSavedProgress();
  }, [workout.id, backendUrl]);

  const currentExercise = workout.exercises?.[currentExerciseIndex] || null;
  
  // Safety check - if currentExercise is null but workout has exercises, reset to first
  React.useEffect(() => {
    if (!currentExercise && workout.exercises?.length > 0) {
      setCurrentExerciseIndex(0);
    }
  }, [currentExercise, workout.exercises]);
  
  const restIntervalRef = React.useRef(null);
  const restEndRef = React.useRef(null);
  const [restingSetKey, setRestingSetKey] = useState(null); // Track which set is resting
  const saveTimeoutRef = React.useRef({});

  // Helper to detect and parse AI-generated supersets
  const parseAISuperset = (exerciseName, exerciseReps) => {
    if (!exerciseName) return null;
    
    // Check for various superset formats:
    // 1. "Superset: Exercise A + Exercise B"
    // 2. "Exercise A + Exercise B (Superset)"
    // 3. "Exercise A + Exercise B" (with + sign)
    // 4. "Superset X: Exercise A + Exercise B"
    // 5. "Exercise A SS Exercise B" (SS = Super Set)
    // 6. Reps field contains "ExerciseName: X-Y, ExerciseName: X-Y"
    
    const hasSuperset = exerciseName.toLowerCase().includes('superset');
    const hasPlusSign = exerciseName.includes('+');
    const hasSSIndicator = / SS /i.test(exerciseName); // " SS " with spaces
    
    // Format 1-5: Name-based superset detection
    if (hasSuperset || hasPlusSign || hasSSIndicator) {
      // Clean the name - remove superset prefix/suffix variations
      let cleanName = exerciseName
        .replace(/^Superset\s*\d*:\s*/i, '')  // "Superset:", "Superset 1:", "Superset 3:"
        .replace(/\s*\(Superset\)\s*$/i, '')
        .trim();
      
      // Determine the separator
      let separator = '+';
      if (hasSSIndicator) {
        separator = / SS /i;
      }
      
      // Split by separator to get the two exercises
      const parts = cleanName.split(separator).map(p => p.trim()).filter(p => p.length > 0);
      
      if (parts.length === 2) {
        const repNums = typeof exerciseReps === 'string'
          ? [...exerciseReps.matchAll(/:\s*(\d+(?:-\d+)?)/g)].map(m => m[1])
          : [];
        const hasPairReps = repNums.length === 2;
        return {
          exerciseA: parts[0],
          exerciseB: parts[1],
          isSuperset: true,
          repsA: hasPairReps ? repNums[0] : null,
          repsB: hasPairReps ? repNums[1] : null
        };
      }
    }
    
    // Format 6: Reps field contains "ExerciseName: X-Y, ExerciseName: X-Y"
    // e.g., "OHP: 8-10, Pull-ups: 10-12" or "Bench 8-10 / Row 8-10"
    if (exerciseReps && typeof exerciseReps === 'string') {
      // Check for "Name X-Y / Name X-Y" format (slash separator)
      const slashPattern = /^(.+?)\s*(\d+(?:-\d+)?)\s*\/\s*(.+?)\s*(\d+(?:-\d+)?)$/i;
      const slashMatch = exerciseReps.match(slashPattern);
      if (slashMatch) {
        return {
          exerciseA: slashMatch[1].trim(),
          exerciseB: slashMatch[3].trim(),
          isSuperset: true,
          repsA: slashMatch[2],
          repsB: slashMatch[4]
        };
      }
      
      // Check if reps contains pattern like "Name: reps, Name: reps"
      const repsPattern = /^([^:]+):\s*(\d+(?:-\d+)?),?\s*([^:]+):\s*(\d+(?:-\d+)?)$/i;
      const match = exerciseReps.match(repsPattern);
      
      if (match) {
        return {
          exerciseA: match[1].trim(),
          exerciseB: match[3].trim(),
          isSuperset: true,
          repsA: match[2],
          repsB: match[4]
        };
      }
      
      // Also check for comma-separated exercises in reps: "Exercise A, Exercise B" or contains multiple colons
      if (exerciseReps.includes(',') && exerciseReps.includes(':')) {
        const parts = exerciseReps.split(',').map(p => p.trim());
        if (parts.length >= 2) {
          // Extract exercise names and reps from each part
          const parseExercisePart = (part) => {
            const colonIndex = part.indexOf(':');
            if (colonIndex > 0) {
              return {
                name: part.substring(0, colonIndex).trim(),
                reps: part.substring(colonIndex + 1).trim()
              };
            }
            return { name: part, reps: '8-12' };
          };
          
          const partA = parseExercisePart(parts[0]);
          const partB = parseExercisePart(parts[1]);
          
          if (partA.name && partB.name) {
            return {
              exerciseA: partA.name,
              exerciseB: partB.name,
              isSuperset: true,
              repsA: partA.reps,
              repsB: partB.reps
            };
          }
        }
      }
    }
    
    return null;
  };

  // Get parsed superset info for current exercise
  const currentSuperset = parseAISuperset(currentExercise?.name, currentExercise?.reps);

  // Check if weight is heavy (near or above PR) for rest timer adjustment
  const isHeavySet = (weight) => {
    if (!exercisePR?.weight || !weight) return false;
    return parseFloat(weight) >= exercisePR.weight * 0.9; // Within 90% of PR
  };

  // Get recommended rest time based on weight
  const getRecommendedRest = (weight) => {
    if (!weight) return { seconds: 90, label: '1:30' };
    const w = parseFloat(weight);
    if (exercisePR?.weight && w >= exercisePR.weight) {
      return { seconds: 180, label: '3:00', reason: 'PR attempt' };
    }
    if (exercisePR?.weight && w >= exercisePR.weight * 0.9) {
      return { seconds: 150, label: '2:30', reason: 'Heavy set' };
    }
    return { seconds: 90, label: '1:30' };
  };

  // Check if current weight needs form check reminder (10%+ above previous best)
  const needsFormCheck = (weight) => {
    if (!previousExerciseData?.sets || !weight) return false;
    const maxPrevWeight = Math.max(...previousExerciseData.sets.map(s => s.weight || 0));
    return parseFloat(weight) > maxPrevWeight * 1.1;
  };

  // Check if completing this set would be a new PR
  const wouldBePR = (weight, reps) => {
    if (!exercisePR?.weight || !weight) return false;
    const w = parseFloat(weight);
    const r = parseInt(reps) || 0;
    // PR if higher weight, or same weight with more reps
    return w > exercisePR.weight || (w === exercisePR.weight && r > (exercisePR.reps || 0));
  };

  // Calculate suggested weight based on previous performance
  const getSuggestedWeight = (setIndex) => {
    if (currentExercise?.target_weight) return { weight: currentExercise.target_weight, reason: 'plan' };
    if (!previousExerciseData?.sets?.[setIndex] || !currentExercise) return null;
    const prevSet = previousExerciseData.sets[setIndex];
    const prevWeight = prevSet.weight || 0;
    const prevReps = prevSet.reps || 0;
    const targetReps = parseInt(currentExercise?.reps) || 10;
    
    // If they hit or exceeded target reps last time, suggest +5 lbs
    if (prevReps >= targetReps && prevWeight > 0) {
      return { weight: prevWeight + 5, reason: 'increase' };
    }
    // If they were close (within 2 reps), suggest same weight
    if (prevReps >= targetReps - 2 && prevWeight > 0) {
      return { weight: prevWeight, reason: 'maintain' };
    }
    // If they struggled, suggest same or slightly less
    return prevWeight > 5 ? { weight: prevWeight - 5, reason: 'decrease' } : { weight: prevWeight, reason: 'maintain' };
  };

  // Get progress indicator comparing current input to previous
  const getProgressIndicator = (setIndex, currentWeight) => {
    if (!previousExerciseData?.sets?.[setIndex] || !currentWeight) return null;
    const prevWeight = previousExerciseData.sets[setIndex].weight || 0;
    const diff = parseFloat(currentWeight) - prevWeight;
    
    if (diff > 0) return { direction: 'up', diff: `+${diff}` };
    if (diff < 0) return { direction: 'down', diff: `${diff}` };
    return { direction: 'same', diff: '=' };
  };

  // Fetch previous exercise data and PR when exercise changes
  React.useEffect(() => {
    const fetchPreviousData = async () => {
      if (!currentExercise?.name) return;
      
      try {
        // Fetch workout history with session token for PWA support
        const sessionToken = localStorage.getItem('session_token');
        const headers = {};
        if (sessionToken) {
          headers['X-Session-Token'] = sessionToken;
        }
        
        const historyResponse = await fetch(
          `${backendUrl}/api/workout-history/${encodeURIComponent(currentExercise.name)}`,
          { credentials: 'include', headers }
        );
        if (historyResponse.ok) {
          const data = await historyResponse.json();
          const previousWorkout = data.history?.find(h => h.sets && h.sets.length > 0);
          setPreviousExerciseData(previousWorkout || null);
          
          // Store full history for progressive overload tracker
          setExerciseHistory(data.history || []);
          
          // Calculate PR from all history
          let maxWeight = 0;
          let prSet = null;
          data.history?.forEach(workout => {
            workout.sets?.forEach(set => {
              if (set.weight > maxWeight) {
                maxWeight = set.weight;
                prSet = { weight: set.weight, reps: set.reps, date: workout.date };
              }
            });
          });
          setExercisePR(prSet);
        }
        
        // Check for superset partner
        if (currentExercise.superset_id) {
          const partner = workout.exercises.find(
            ex => ex.superset_id === currentExercise.superset_id && ex.name !== currentExercise.name
          );
          setSupersetPartner(partner || null);
        } else {
          setSupersetPartner(null);
        }
      } catch (error) {
        console.error('Error fetching previous exercise data:', error);
        setPreviousExerciseData(null);
        setExercisePR(null);
        setExerciseHistory([]);
      }
    };
    
    fetchPreviousData();
  }, [currentExerciseIndex, currentExercise?.name, currentExercise?.superset_id, backendUrl, workout.exercises]);

  // Fetch video URL for exercise
  const fetchVideoUrl = (exerciseName, attached = '') => {
    setVideoExercise({ name: exerciseName, attached: attached || '' });
  };

  // Fetch substitute exercises
  const fetchSubstitutes = async (exerciseName, side = null) => {
    setActiveExercise(exerciseName);
    setSwapSide(side);
    try {
      // Get session token for PWA support
      const sessionToken = localStorage.getItem('session_token');
      const headers = {};
      if (sessionToken) {
        headers['X-Session-Token'] = sessionToken;
      }
      
      // Include equipment access in the query
      const equipmentParam = clientEquipment ? `?equipment_access=${encodeURIComponent(clientEquipment)}` : '';
      const response = await fetch(
        `${backendUrl}/api/exercises/${encodeURIComponent(exerciseName)}/substitutes${equipmentParam}`,
        { credentials: 'include', headers }
      );
      if (response.ok) {
        const data = await response.json();
        const partner = side && currentSuperset ? (side === 'A' ? currentSuperset.exerciseB : currentSuperset.exerciseA) : null;
        setSubstituteExercises((data.substitutes || []).filter(s => !partner || s.name?.toLowerCase() !== partner.toLowerCase()));
        setShowSubstituteModal(true);
      } else {
        console.error('Failed to fetch substitutes:', response.status);
        setSubstituteExercises([]);
        setShowSubstituteModal(true);
      }
    } catch (error) {
      console.error('Error fetching substitutes:', error);
      setSubstituteExercises([]);
      setShowSubstituteModal(true);
    }
  };
  
  // Handle selecting a substitute exercise
  const handleSelectSubstitute = async (substituteName) => {
    if (!currentExercise || !substituteName) return;
    
    try {
      // Get session token for PWA support
      const sessionToken = localStorage.getItem('session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (sessionToken) {
        headers['X-Session-Token'] = sessionToken;
      }
      
      // For a superset half, rebuild the pair keeping the other half intact
      let newName = substituteName;
      let newReps = null;
      if (swapSide && currentSuperset) {
        const a = swapSide === 'A' ? substituteName : currentSuperset.exerciseA;
        const b = swapSide === 'B' ? substituteName : currentSuperset.exerciseB;
        newName = `Superset: ${a} + ${b}`;
        if (currentSuperset.repsA && currentSuperset.repsB) {
          newReps = `${a}: ${currentSuperset.repsA}, ${b}: ${currentSuperset.repsB}`;
        }
      }

      // Update the workout exercise on the backend
      const response = await fetch(`${backendUrl}/api/workout-sessions/${workout.id}/swap-exercise`, {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: JSON.stringify({
          exercise_index: currentExerciseIndex,
          new_exercise_name: newName,
          original_exercise_name: currentExercise.name,
          ...(newReps && { new_reps: newReps })
        })
      });
      
      if (response.ok) {
        // Update local workout state
        const updatedExercises = [...workout.exercises];
        updatedExercises[currentExerciseIndex] = {
          ...updatedExercises[currentExerciseIndex],
          name: newName,
          ...(newReps && { reps: newReps }),
          original_name: currentExercise.original_name || currentExercise.name
        };
        workout.exercises = updatedExercises;
        
        setShowSubstituteModal(false);
        alert(`Exercise swapped to ${substituteName}!`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Swap failed:', response.status, errorData);
        alert(`Failed to swap exercise: ${errorData.detail || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error swapping exercise:', error);
      alert('Failed to swap exercise. Please try again.');
    }
  };

  // Extract YouTube video ID for embedding
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  // Finish workout and check for PRs
  const handleFinishWorkout = async () => {
    if (restEndRef.current) skipRest();
    setFinishing(true);
    try {
      // Get session token for PWA support
      const sessionToken = localStorage.getItem('session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (sessionToken) {
        headers['X-Session-Token'] = sessionToken;
      }
      
      // Stop auto-saves so they can't flip the workout back to in-progress
      workoutFinishedRef.current = true;
      clearTimeout(autoSaveTimerRef.current);
      const response = await fetch(`${backendUrl}/api/workout-sessions/${workout.id}/complete`, {
        method: 'POST',
        headers: headers,
        credentials: 'include'
      });
      
      if (!response.ok) workoutFinishedRef.current = false;
      if (response.ok) {
        localStorage.removeItem(progressStorageKey);
        const data = await response.json();
        console.log('[Workout] Completed:', data);
        
        // Build completion message with streak info
        let completionMessage = '🎉 Great workout! Your progress has been saved.';
        if (data.streak) {
          if (data.streak.current_streak > 1) {
            completionMessage += `\n\n🔥 ${data.streak.current_streak} day streak! Keep it up!`;
          } else if (data.streak.current_streak === 1) {
            completionMessage += '\n\n🔥 Streak started! Come back tomorrow to keep it going!';
          }
          if (data.streak.is_new_record) {
            completionMessage += '\n\n🏆 New streak record!';
          }
        }
        
        if (data.new_prs && data.new_prs.length > 0) {
          // Show PR celebration modal
          setNewPRs(data.new_prs);
          setShowPRModal(true);
        } else {
          // Just close and refresh
          if (onWorkoutComplete) onWorkoutComplete();
          closeWorkout();
          alert(completionMessage);
        }
      } else {
        alert('Failed to complete workout. Please try again.');
      }
    } catch (error) {
      workoutFinishedRef.current = false;
      console.error('[Workout] Error completing workout:', error);
      alert('Failed to complete workout. Please try again.');
    } finally {
      setFinishing(false);
    }
  };

  // Close PR modal and finish
  const handleClosePRModal = () => {
    setShowPRModal(false);
    if (onWorkoutComplete) onWorkoutComplete();
    closeWorkout();
  };
  
  // Save workout progress when backing out (so user can continue later)
  const saveProgressAndClose = async (fromBackGesture = false) => {
    if (workoutFinishedRef.current) {
      closeWorkout(fromBackGesture === true);
      return;
    }
    try {
      // Get session token for PWA support
      const sessionToken = localStorage.getItem('session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (sessionToken) {
        headers['X-Session-Token'] = sessionToken;
      }
      
      const snap = progressSnapshot();
      if (hasProgress(snap)) {
        localStorage.setItem(progressStorageKey, JSON.stringify(snap));
        await fetch(`${backendUrl}/api/workout-sessions/${workout.id}/save-progress`, {
          method: 'POST',
          headers: headers,
          credentials: 'include',
          body: JSON.stringify(snap)
        });
      }
      console.log('[Workout] Progress saved');
    } catch (error) {
      console.error('[Workout] Error saving progress:', error);
    }
    closeWorkout(fromBackGesture === true);
  };

  // Save set data to backend - supports superset parts (A/B)
  const saveSetData = async (exerciseIndex, setIndex, weight, reps, completed, supersetPart = null) => {
    const key = supersetPart ? `${exerciseIndex}_${setIndex}_${supersetPart}` : `${exerciseIndex}_${setIndex}`;
    setSavingSet(key);
    
    try {
      // Get session token for PWA support
      const sessionToken = localStorage.getItem('session_token');
      const headers = { 'Content-Type': 'application/json' };
      if (sessionToken) {
        headers['X-Session-Token'] = sessionToken;
      }
      
      const response = await fetch(`${backendUrl}/api/workout-sessions/${workout.id}/log-set`, {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: JSON.stringify({
          exercise_index: exerciseIndex,
          set_index: setIndex,
          weight: weight || null,
          reps: reps || null,
          completed: completed,
          superset_part: supersetPart // 'A', 'B', or null for regular exercises
        })
      });
      
      if (response.ok) {
        console.log('[Workout] Set saved:', { exerciseIndex, setIndex, weight, reps, completed, supersetPart });
      } else {
        console.error('[Workout] Failed to save set');
      }
    } catch (error) {
      console.error('[Workout] Error saving set:', error);
    } finally {
      setSavingSet(null);
    }
  };

  // Refs to track latest state for debounced saves (avoid stale closure)
  const setWeightsRef = React.useRef(setWeights);
  const setRepsRef = React.useRef(setReps);
  const completedSetsRef = React.useRef(completedSets);
  
  // Keep refs updated with latest state
  React.useEffect(() => {
    setWeightsRef.current = setWeights;
  }, [setWeights]);
  
  React.useEffect(() => {
    setRepsRef.current = setReps;
  }, [setReps]);
  
  React.useEffect(() => {
    completedSetsRef.current = completedSets;
  }, [completedSets]);

  const exerciseIndexRef = React.useRef(currentExerciseIndex);
  const autoSaveTimerRef = React.useRef(null);
  const workoutFinishedRef = React.useRef(false);

  const progressSnapshot = () => ({
    current_exercise_index: exerciseIndexRef.current,
    completed_sets: completedSetsRef.current,
    set_weights: setWeightsRef.current,
    set_reps: setRepsRef.current,
    saved_at: new Date().toISOString()
  });

  const hasProgress = (snap) => snap.current_exercise_index > 0 || [snap.completed_sets, snap.set_weights, snap.set_reps]
    .some((m) => Object.values(m || {}).some((v) => v !== '' && v !== null && v !== undefined && v !== false));

  // keepalive lets the save finish even when the app is being closed
  const persistProgress = (keepalive = false) => {
    if (workoutFinishedRef.current) return;
    const snap = progressSnapshot();
    if (!hasProgress(snap)) return;
    localStorage.setItem(progressStorageKey, JSON.stringify(snap));
    fetch(`${backendUrl}/api/workout-sessions/${workout.id}/save-progress`, {
      method: 'POST', credentials: 'include', headers: workoutHeaders(), keepalive, body: JSON.stringify(snap)
    }).catch(() => {});
  };

  // Auto-save every change (on-device immediately, server after a short pause)
  React.useEffect(() => {
    exerciseIndexRef.current = currentExerciseIndex;
    if (!progressLoaded) return;
    const snap = progressSnapshot();
    if (!hasProgress(snap)) return;
    localStorage.setItem(progressStorageKey, JSON.stringify(snap));
    clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => persistProgress(), 1500);
  }, [currentExerciseIndex, completedSets, setWeights, setReps, progressLoaded]);

  // Save when the app is hidden, swiped away or closed
  React.useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') persistProgress(true); };
    const onPageHide = () => persistProgress(true);
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onPageHide);
      clearTimeout(autoSaveTimerRef.current);
      if (!closingRef.current) persistProgress(true);
    };
  }, []);

  // Phone back gesture returns to the workout list (and saves) instead of leaving the app.
  // A per-open token survives StrictMode remounts and ignores stale entries after a reload.
  const historyTokenRef = React.useRef(`${workout.id}-${Math.random().toString(36).slice(2)}`);
  const closingRef = React.useRef(false);
  React.useEffect(() => {
    const token = historyTokenRef.current;
    if (window.history.state?.workoutToken !== token) window.history.pushState({ workoutToken: token }, '');
    const onPopState = (e) => {
      if (e.state?.workoutToken === token || closingRef.current || workoutFinishedRef.current) return;
      saveProgressAndClose(true);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const closeWorkout = (fromBackGesture = false) => {
    if (closingRef.current) return;
    closingRef.current = true;
    if (!fromBackGesture && window.history.state?.workoutToken === historyTokenRef.current) window.history.back();
    onClose();
  };

  // Debounced save for weight/reps changes - supports superset parts
  // Uses refs to read latest state values when timeout fires
  const debouncedSave = (exerciseIndex, setIndex, supersetPart = null) => {
    const key = supersetPart ? `${exerciseIndex}_${setIndex}_${supersetPart}` : `${exerciseIndex}_${setIndex}`;
    
    // Clear existing timeout
    if (saveTimeoutRef.current[key]) {
      clearTimeout(saveTimeoutRef.current[key]);
    }
    
    // Set new timeout to save after 1 second of no changes
    saveTimeoutRef.current[key] = setTimeout(() => {
      // Read latest values from refs (not stale closure)
      const weight = setWeightsRef.current[key];
      const reps = setRepsRef.current[key];
      const completed = completedSetsRef.current[key] || false;
      
      // Skip save if set is already completed (to avoid overwriting immediate save)
      if (completed) {
        return;
      }
      
      if (weight || reps) {
        saveSetData(exerciseIndex, setIndex, weight, reps, completed, supersetPart);
      }
    }, 1000);
  };

  const handleCompleteSet = (exerciseIndex, setIndex, supersetPart = null) => {
    const key = supersetPart ? `${exerciseIndex}_${setIndex}_${supersetPart}` : `${exerciseIndex}_${setIndex}`;
    
    // Clear any pending debounced save for this key to prevent overwrite
    if (saveTimeoutRef.current[key]) {
      clearTimeout(saveTimeoutRef.current[key]);
      delete saveTimeoutRef.current[key];
    }
    
    const wasCompleted = completedSets[key];
    const newCompleted = !wasCompleted;
    
    setCompletedSets(prev => ({
      ...prev,
      [key]: newCompleted
    }));
    
    // Save immediately when completing a set
    const weight = setWeights[key];
    const reps = setReps[key];
    saveSetData(exerciseIndex, setIndex, weight, reps, newCompleted, supersetPart);
    
    // Check for new PR and trigger celebration (only for non-superset or track separately)
    if (!wasCompleted && weight && reps && !supersetPart) {
      if (wouldBePR(weight, reps)) {
        setPRCelebrationData({
          exercise: currentExercise.name,
          weight: parseFloat(weight),
          reps: parseInt(reps),
          previousPR: exercisePR
        });
        setShowPRCelebration(true);
        // Update local PR state
        setExercisePR({ weight: parseFloat(weight), reps: parseInt(reps), date: new Date().toISOString() });
        // Hide celebration after 3 seconds
        setTimeout(() => setShowPRCelebration(false), 3000);
      }
    }
    
    // Auto-start rest timer when marking a set as complete (not when unchecking)
    // For supersets: only start rest timer after BOTH A and B are completed
    if (!wasCompleted && newCompleted) {
      if (supersetPart) {
        // Check if partner exercise is also completed
        const partnerPart = supersetPart === 'A' ? 'B' : 'A';
        const partnerKey = `${exerciseIndex}_${setIndex}_${partnerPart}`;
        const partnerCompleted = completedSetsRef.current[partnerKey];
        
        // Only start rest timer when both A and B are done
        if (partnerCompleted) {
          const restRecommendation = getRecommendedRest(weight);
          // Use the superset set key (without A/B suffix) for display
          startRestTimer(restRecommendation.seconds, `${exerciseIndex}_${setIndex}_superset`);
        }
      } else {
        // Regular exercise - start rest timer immediately
        const restRecommendation = getRecommendedRest(weight);
        startRestTimer(restRecommendation.seconds, key);
      }
    }
  };
  
  const skipRest = () => {
    finishRest();
    syncRestPush(0);
  };
  
  const handleWeightChange = (exerciseIndex, setIndex, value, supersetPart = null) => {
    const key = supersetPart ? `${exerciseIndex}_${setIndex}_${supersetPart}` : `${exerciseIndex}_${setIndex}`;
    setSetWeights(prev => ({
      ...prev,
      [key]: value
    }));
    // Trigger debounced save
    debouncedSave(exerciseIndex, setIndex, supersetPart);
  };
  
  const handleRepsChange = (exerciseIndex, setIndex, value, supersetPart = null) => {
    const key = supersetPart ? `${exerciseIndex}_${setIndex}_${supersetPart}` : `${exerciseIndex}_${setIndex}`;
    setSetReps(prev => ({
      ...prev,
      [key]: value
    }));
    // Trigger debounced save
    debouncedSave(exerciseIndex, setIndex, supersetPart);
  };

  const handleNextExercise = () => {
    if (currentExerciseIndex < workout.exercises.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
    }
  };

  const handlePreviousExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(currentExerciseIndex - 1);
    }
  };

  // Server sends a 'rest over' push so clients are alerted even with the phone locked
  const syncRestPush = (seconds) => {
    const token = localStorage.getItem('session_token');
    const headers = { 'Content-Type': 'application/json', ...(token ? { 'X-Session-Token': token } : {}) };
    fetch(`${backendUrl}/api/push/rest-timer`, seconds > 0
      ? { method: 'POST', credentials: 'include', headers, body: JSON.stringify({ seconds }) }
      : { method: 'DELETE', credentials: 'include', headers }
    ).catch(() => {});
  };

  const finishRest = () => {
    clearInterval(restIntervalRef.current);
    restIntervalRef.current = null;
    restEndRef.current = null;
    setIsResting(false);
    setRestingSetKey(null);
    setRestTimerTotal(0);
    setRestTimer(0);
  };

  const tickRest = () => {
    if (!restEndRef.current) return;
    const remaining = Math.max(0, Math.ceil((restEndRef.current - Date.now()) / 1000));
    setRestTimer(remaining);
    if (remaining === 0) {
      finishRest();
      playSound('alert');
      setTimeout(() => playSound('alert'), 400);
      if (navigator.vibrate) navigator.vibrate([300, 150, 300]);
    }
  };

  // Timestamp-based so it stays accurate if the phone locks or the app is backgrounded
  const startRestTimer = (seconds, setKey = null) => {
    if (restIntervalRef.current) clearInterval(restIntervalRef.current);
    playSound('soft', 0.01); // unlock audio on iOS during the user tap
    restEndRef.current = Date.now() + seconds * 1000;
    setRestTimer(seconds);
    setRestTimerTotal(seconds);
    setIsResting(true);
    setRestingSetKey(setKey);
    restIntervalRef.current = setInterval(tickRest, 500);
    syncRestPush(seconds);
  };

  const adjustRest = (delta) => {
    if (!restEndRef.current) return;
    restEndRef.current = Math.max(Date.now() + 1000, restEndRef.current + delta * 1000);
    const remaining = Math.ceil((restEndRef.current - Date.now()) / 1000);
    setRestTimer(remaining);
    setRestTimerTotal(prev => Math.max(prev, remaining));
    syncRestPush(remaining);
  };

  React.useEffect(() => {
    document.addEventListener('visibilitychange', tickRest);
    return () => document.removeEventListener('visibilitychange', tickRest);
  }, []);
  
  // Cleanup interval on unmount
  React.useEffect(() => {
    return () => {
      if (restIntervalRef.current) {
        clearInterval(restIntervalRef.current);
      }
      if (restEndRef.current) syncRestPush(0);
    };
  }, []);

  return (
    // If workout has no exercises, show friendly message
    !workout?.exercises || workout.exercises.length === 0 ? (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="max-w-lg mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => closeWorkout()}
              className={`flex items-center space-x-1 ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back</span>
            </button>
          </div>
          <div className={`text-center py-12 px-6 rounded-xl ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <svg className="w-16 h-16 mx-auto mb-4 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              No Exercises Found
            </h2>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              This workout doesn't have any exercises yet. Please contact your trainer to add exercises to this workout.
            </p>
            <button
              onClick={() => closeWorkout()}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    ) : (
    <div className={`min-h-screen pb-20 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`border-b p-3 sm:p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={saveProgressAndClose}
            className={`flex items-center space-x-1 flex-shrink-0 ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm">Back</span>
          </button>
          <h1 className={`text-sm sm:text-lg font-semibold text-center flex-1 min-w-0 truncate px-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {workout.name}
          </h1>
          <div className={`text-xs sm:text-sm flex-shrink-0 whitespace-nowrap ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {currentExerciseIndex + 1} of {workout.exercises?.length || 0}
          </div>
        </div>
      </div>

      {/* Exercise Content - Full width on mobile */}
      <div className="px-2 sm:px-4 py-4 space-y-4">
        {!currentExercise && workout.exercises?.length > 0 && (
          <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <p>Loading exercise...</p>
          </div>
        )}
        {currentExercise && (
          <>
            {/* Exercise Header - Full Width Design */}
            <div className={`rounded-xl p-3 sm:p-4 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              {/* Top Row: Name + PR Badge */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className={`text-base sm:text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {currentExercise.name || 'Exercise'}
                    </h2>
                    {(currentExercise.superset_id || currentSuperset) && (
                      <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-purple-500/20 text-purple-400 rounded">
                        SUPERSET
                      </span>
                    )}
                  </div>
                  {/* Show superset exercises separately */}
                  {currentSuperset ? (
                    <div className={`mt-2 space-y-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="px-1.5 py-0.5 text-xs font-bold bg-purple-500 text-white rounded">A</span>
                        <span className="truncate">{currentSuperset.exerciseA}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="px-1.5 py-0.5 text-xs font-bold bg-blue-500 text-white rounded">B</span>
                        <span className="truncate">{currentSuperset.exerciseB}</span>
                      </div>
                      <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {getSetsCount(currentExercise.sets)} sets × {getRepsDisplay(currentExercise)} reps each
                      </div>
                    </div>
                  ) : (
                    <div className={`flex items-center gap-1.5 text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span>{getSetsCount(currentExercise.sets)} sets</span>
                      <span>•</span>
                      <span>{getRepsDisplay(currentExercise)} reps</span>
                      {currentExercise.weight && (
                        <>
                          <span>•</span>
                          <span>{currentExercise.weight} lbs</span>
                        </>
                      )}
                    </div>
                  )}
                </div>
                
                {/* PR Badge - More Compact */}
                {exercisePR && (
                  <button
                    onClick={() => setShowProgressTracker(true)}
                    className={`ml-2 px-2 py-1.5 rounded-lg flex-shrink-0 ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-700/50' : 'bg-yellow-50 border border-yellow-200'}`}
                  >
                    <div className="flex items-center gap-1">
                      <span className="text-sm">🏆</span>
                      <span className={`text-xs font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                        {exercisePR.weight}lbs
                      </span>
                    </div>
                  </button>
                )}
              </div>
              
              {[['', currentExercise?.progression_note], ['-a', currentExercise?.progression_note_a], ['-b', currentExercise?.progression_note_b]]
                .filter(([, note]) => note)
                .map(([suffix, note]) => (
                  <p key={suffix} data-testid={`exercise-progression-note${suffix}`} className={`mb-2 px-2.5 py-1.5 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700'}`}>
                    📈 {note}
                  </p>
                ))}
              
              {/* Action Row: Video, Subs, Progress - Inline Pills */}
              <div className="flex items-center gap-2 flex-wrap">
                {!currentSuperset && (
                <button
                  data-testid="exercise-video-btn"
                  onClick={() => fetchVideoUrl(currentExercise.name, currentExercise.external_video_url || currentExercise.video_url)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    isDarkMode 
                      ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' 
                      : 'bg-red-50 text-red-600 hover:bg-red-100'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                  </svg>
                  Video
                </button>
                )}
                {!currentSuperset && (
                <button
                  data-testid="exercise-swap-btn"
                  onClick={() => fetchSubstitutes(currentExercise.name)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                    isDarkMode 
                      ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30' 
                      : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                  Swap
                </button>
                )}
                {exerciseHistory.length > 0 && (
                  <button
                    onClick={() => setShowProgressTracker(true)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${
                      isDarkMode 
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span className="text-xs">📈</span>
                    History
                  </button>
                )}
              </div>
              
              {/* Exercise Notes - Collapsible/Subtle */}
              {currentExercise.notes && (
                <p className={`mt-3 pt-3 text-xs leading-relaxed border-t ${isDarkMode ? 'text-gray-500 border-gray-700' : 'text-gray-400 border-gray-100'}`}>
                  {currentExercise.notes}
                </p>
              )}
            </div>
            
            {/* Superset Partner Exercise (if exists) */}
            {supersetPartner && (
              <div className={`rounded-lg p-4 border-2 border-dashed ${isDarkMode ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-300'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-purple-500">⚡</span>
                  <span className={`text-sm font-semibold ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                    Superset with: {supersetPartner.name}
                  </span>
                </div>
                <div className={`flex items-center flex-wrap gap-2 text-sm mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <span>{supersetPartner.sets} sets</span>
                  <span>•</span>
                  <span>{supersetPartner.reps} reps</span>
                  {supersetPartner.weight && (
                    <>
                      <span>•</span>
                      <span>{supersetPartner.weight} lbs</span>
                    </>
                  )}
                </div>
                
                {/* Video Guide & Substitute for Superset Partner */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    onClick={() => fetchVideoUrl(supersetPartner.name)}
                    className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded transition-colors ${
                      isDarkMode 
                        ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' 
                        : 'bg-red-50 text-red-600 hover:bg-red-100'
                    }`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                    </svg>
                    Video
                  </button>
                  <button
                    onClick={() => fetchSubstitutes(supersetPartner.name)}
                    className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded transition-colors ${
                      isDarkMode 
                        ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50' 
                        : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    }`}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                    Subs
                  </button>
                </div>
                
                {/* Superset Partner Sets */}
                <div className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                  Log {supersetPartner.name} Sets:
                </div>
                <div className="space-y-2">
                  {Array.from({ length: parseInt(supersetPartner.sets) || 1 }, (_, index) => {
                    const partnerSetKey = `superset_${supersetPartner.name}_${index}`;
                    const partnerExerciseIndex = workout.exercises.findIndex(ex => ex.name === supersetPartner.name);
                    const fullSetKey = `${partnerExerciseIndex}_${index}`;
                    
                    return (
                      <div key={partnerSetKey} className="flex items-center gap-2">
                        <span className={`w-8 text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          Set {index + 1}
                        </span>
                        <input
                          type="number"
                          placeholder={supersetPartner.weight || "lbs"}
                          value={setWeights[fullSetKey] || ''}
                          onChange={(e) => setSetWeights(prev => ({ ...prev, [fullSetKey]: e.target.value }))}
                          className={`w-20 px-2 py-1.5 text-sm rounded border ${
                            isDarkMode 
                              ? 'bg-gray-700 border-gray-600 text-white' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        />
                        <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>lbs</span>
                        <input
                          type="number"
                          placeholder={supersetPartner.reps || "reps"}
                          value={setReps[fullSetKey] || ''}
                          onChange={(e) => setSetReps(prev => ({ ...prev, [fullSetKey]: e.target.value }))}
                          className={`w-16 px-2 py-1.5 text-sm rounded border ${
                            isDarkMode 
                              ? 'bg-gray-700 border-gray-600 text-white' 
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                        />
                        <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>reps</span>
                        <button
                          onClick={() => setCompletedSets(prev => ({ ...prev, [fullSetKey]: !prev[fullSetKey] }))}
                          className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${
                            completedSets[fullSetKey]
                              ? 'bg-green-500 text-white'
                              : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-400'
                          }`}
                        >
                          {completedSets[fullSetKey] ? '✓' : ''}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sets Tracking */}
            <div className={`rounded-lg p-4 sm:p-6 border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {currentSuperset ? 'Superset Sets' : 'Sets'}
                  </h3>
                  {currentSuperset && (
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                      ⚡ Complete each pair: A then B
                    </p>
                  )}
                  {previousExerciseData && !currentSuperset && (
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Previous: {previousExerciseData.date ? new Date(previousExerciseData.date).toLocaleDateString() : 'N/A'}
                    </p>
                  )}
                </div>
                {savingSet && (
                  <span className="text-xs text-blue-500 flex items-center">
                    <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </span>
                )}
              </div>
              
              
              {/* SUPERSET INPUTS - Full Width Layout with A/B entries */}
              {currentSuperset ? (
                <div className="space-y-3">
                  {Array.from({ length: parseInt(getSetsCount(currentExercise.sets)) || 1 }, (_, index) => {
                    const setKeyA = `${currentExerciseIndex}_${index}_A`;
                    const setKeyB = `${currentExerciseIndex}_${index}_B`;
                    const isACompleted = completedSets[setKeyA];
                    const isBCompleted = completedSets[setKeyB];
                    const bothCompleted = isACompleted && isBCompleted;
                    // Use individual reps from parsed superset if available, otherwise fall back to getRepsDisplay
                    const targetRepsA = currentSuperset.repsA || getRepsDisplay(currentExercise) || '8-12';
                    const targetRepsB = currentSuperset.repsB || getRepsDisplay(currentExercise) || '8-12';
                    
                    return (
                      <div key={index} className={`p-2.5 rounded-xl border-2 ${
                        bothCompleted 
                          ? (isDarkMode ? 'bg-green-900/20 border-green-600' : 'bg-green-50 border-green-400')
                          : (isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200 shadow-sm')
                      }`}>
                        {/* Set Number Header - Compact */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              bothCompleted ? 'bg-green-500' : 'bg-purple-500'
                            }`}>
                              <span className="text-white text-sm font-bold">{index + 1}</span>
                            </div>
                            <div>
                              <span className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                Set {index + 1}
                              </span>
                              <span className={`text-xs ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {targetRepsA} reps
                              </span>
                            </div>
                          </div>
                          {bothCompleted && (
                            <span className="text-green-500 text-sm font-bold">✓</span>
                          )}
                        </div>
                        
                        {/* Exercise A Card - Compact */}
                        <div className={`p-2 rounded-lg mb-2 border ${
                          isACompleted 
                            ? (isDarkMode ? 'bg-green-900/30 border-green-500' : 'bg-green-50 border-green-400')
                            : (isDarkMode ? 'bg-purple-900/20 border-purple-500' : 'bg-purple-50 border-purple-300')
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="px-2 py-1 text-xs font-bold bg-purple-500 text-white rounded flex-shrink-0">A</span>
                              <span className={`text-xs font-semibold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {currentSuperset.exerciseA}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                              <button
                                data-testid={`superset-video-${index}-A`}
                                onClick={() => fetchVideoUrl(currentSuperset.exerciseA)}
                                className={`px-2 py-1.5 rounded text-xs inline-flex items-center gap-1 ${isDarkMode ? 'bg-red-900/40 text-red-400 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                                aria-label="Video for Exercise A"
                              >
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                <span className="font-semibold">Video</span>
                              </button>
                              <button
                                data-testid={`superset-swap-${index}-A`}
                                onClick={() => fetchSubstitutes(currentSuperset.exerciseA, 'A')}
                                className={`px-2 py-1.5 rounded text-xs inline-flex items-center gap-1 ${isDarkMode ? 'bg-blue-900/40 text-blue-400 hover:bg-blue-900/60' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                                title="Swap Exercise A"
                                aria-label="Swap Exercise A"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                                <span className="font-semibold">Swap</span>
                              </button>
                              <button
                                data-testid={`superset-complete-set-${index}-A`}
                                onClick={() => handleCompleteSet(currentExerciseIndex, index, 'A')}
                                className={`w-7 h-7 min-h-0 flex-shrink-0 rounded-full border-2 flex items-center justify-center ${
                                  isACompleted ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-green-500'
                                }`}
                              >
                                {isACompleted && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                              </button>
                            </div>
                          </div>
                          
                          {/* Compact Weight and Reps Inputs */}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className={`text-xs font-medium block mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Weight</label>
                              <div className="flex">
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  data-testid={`superset-weight-${index}-A`}
                                  placeholder={currentExercise?.target_weight_a ? String(currentExercise.target_weight_a) : "0"}
                                  value={setWeights[setKeyA] || ''}
                                  onChange={(e) => handleWeightChange(currentExerciseIndex, index, e.target.value, 'A')}
                                  className={`w-full px-2 py-2 text-lg font-bold rounded-l-lg border-2 border-r-0 ${
                                    isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                                  } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                                />
                                <span className={`px-2 py-2 text-xs font-semibold border-2 border-l-0 rounded-r-lg flex items-center ${
                                  isDarkMode ? 'bg-gray-600 border-gray-500 text-gray-300' : 'bg-gray-100 border-gray-300 text-gray-600'
                                }`}>lbs</span>
                              </div>
                            </div>
                            <div>
                              <label className={`text-xs font-medium block mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Reps</label>
                              <input
                                type="number"
                                inputMode="numeric"
                                data-testid={`superset-reps-${index}-A`}
                                placeholder={targetRepsA}
                                value={setReps[setKeyA] || ''}
                                onChange={(e) => handleRepsChange(currentExerciseIndex, index, e.target.value, 'A')}
                                className={`w-full px-2 py-2 text-lg font-bold rounded-lg border-2 ${
                                  isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                                } focus:outline-none focus:ring-2 focus:ring-purple-500`}
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Arrow Transition - Compact */}
                        <div className={`text-center py-1 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                          <span className="text-xs font-medium">↓ B ↓</span>
                        </div>
                        
                        {/* Exercise B Card - Compact */}
                        <div className={`p-2 rounded-lg border ${
                          isBCompleted 
                            ? (isDarkMode ? 'bg-green-900/30 border-green-500' : 'bg-green-50 border-green-400')
                            : (isDarkMode ? 'bg-blue-900/20 border-blue-500' : 'bg-blue-50 border-blue-300')
                        }`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className="px-2 py-1 text-xs font-bold bg-blue-500 text-white rounded flex-shrink-0">B</span>
                              <span className={`text-xs font-semibold leading-tight ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {currentSuperset.exerciseB}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                              <button
                                data-testid={`superset-video-${index}-B`}
                                onClick={() => fetchVideoUrl(currentSuperset.exerciseB)}
                                className={`px-2 py-1.5 rounded text-xs inline-flex items-center gap-1 ${isDarkMode ? 'bg-red-900/40 text-red-400 hover:bg-red-900/60' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
                                aria-label="Video for Exercise B"
                              >
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                                <span className="font-semibold">Video</span>
                              </button>
                              <button
                                data-testid={`superset-swap-${index}-B`}
                                onClick={() => fetchSubstitutes(currentSuperset.exerciseB, 'B')}
                                className={`px-2 py-1.5 rounded text-xs inline-flex items-center gap-1 ${isDarkMode ? 'bg-blue-900/40 text-blue-400 hover:bg-blue-900/60' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
                                title="Swap Exercise B"
                                aria-label="Swap Exercise B"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                                <span className="font-semibold">Swap</span>
                              </button>
                              <button
                                data-testid={`superset-complete-set-${index}-B`}
                                onClick={() => handleCompleteSet(currentExerciseIndex, index, 'B')}
                                className={`w-7 h-7 min-h-0 flex-shrink-0 rounded-full border-2 flex items-center justify-center ${
                                  isBCompleted ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 hover:border-green-500'
                                }`}
                              >
                                {isBCompleted && <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                              </button>
                            </div>
                          </div>
                          
                          {/* Compact Weight and Reps Inputs */}
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className={`text-xs font-medium block mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Weight</label>
                              <div className="flex">
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  data-testid={`superset-weight-${index}-B`}
                                  placeholder={currentExercise?.target_weight_b ? String(currentExercise.target_weight_b) : "0"}
                                  value={setWeights[setKeyB] || ''}
                                  onChange={(e) => handleWeightChange(currentExerciseIndex, index, e.target.value, 'B')}
                                  className={`w-full px-2 py-2 text-lg font-bold rounded-l-lg border-2 border-r-0 ${
                                    isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                                  } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                />
                                <span className={`px-2 py-2 text-xs font-semibold border-2 border-l-0 rounded-r-lg flex items-center ${
                                  isDarkMode ? 'bg-gray-600 border-gray-500 text-gray-300' : 'bg-gray-100 border-gray-300 text-gray-600'
                                }`}>lbs</span>
                              </div>
                            </div>
                            <div>
                              <label className={`text-xs font-medium block mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Reps</label>
                              <input
                                type="number"
                                inputMode="numeric"
                                data-testid={`superset-reps-${index}-B`}
                                placeholder={targetRepsB}
                                value={setReps[setKeyB] || ''}
                                onChange={(e) => handleRepsChange(currentExerciseIndex, index, e.target.value, 'B')}
                                className={`w-full px-2 py-2 text-lg font-bold rounded-lg border-2 ${
                                  isDarkMode ? 'bg-gray-700 border-gray-500 text-white' : 'bg-white border-gray-300 text-gray-900'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Rest Timer after completing both - Compact */}
                        {bothCompleted && (
                          <>
                            {isResting && restingSetKey === `${currentExerciseIndex}_${index}_superset` ? (
                              <div data-testid={`superset-inline-rest-timer-${index}`} className={`mt-2 text-center p-2 rounded-lg ${isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                                <div className="relative w-14 h-14 mx-auto mb-1">
                                  <svg className="w-14 h-14 transform -rotate-90">
                                    <circle
                                      cx="28"
                                      cy="28"
                                      r="24"
                                      stroke={isDarkMode ? '#374151' : '#E5E7EB'}
                                      strokeWidth="4"
                                      fill="none"
                                    />
                                    <circle
                                      cx="28"
                                      cy="28"
                                      r="24"
                                      stroke="#3B82F6"
                                      strokeWidth="4"
                                      fill="none"
                                      strokeLinecap="round"
                                      strokeDasharray={`${2 * Math.PI * 24}`}
                                      strokeDashoffset={`${2 * Math.PI * 24 * (1 - restTimer / restTimerTotal)}`}
                                      className="transition-all duration-1000 ease-linear"
                                    />
                                  </svg>
                                  <div className="absolute inset-0 flex items-center justify-center">
                                    <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                      {Math.floor(restTimer / 60)}:{(restTimer % 60).toString().padStart(2, '0')}
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={skipRest}
                                  data-testid={`skip-rest-superset-${index}`}
                                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                    isDarkMode 
                                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                                  }`}
                                >
                                  Skip →
                                </button>
                              </div>
                            ) : (
                              <div className={`mt-2 text-center text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                ✓ Done! Rest before next set
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
              /* REGULAR EXERCISE INPUTS - Original code for non-superset exercises */
              <div className="space-y-4">
                {Array.from({ length: parseInt(getSetsCount(currentExercise.sets)) || 1 }, (_, index) => {
                  const setKey = `${currentExerciseIndex}_${index}`;
                  const defaultReps = getRepsDisplay(currentExercise) || '';
                  const defaultWeight = currentExercise.weight || '';
                  const isThisSetResting = isResting && restingSetKey === setKey;
                  const isSavingThisSet = savingSet === setKey;
                  
                  return (
                    <div key={index} className={`p-4 rounded-xl ${completedSets[setKey] ? (isDarkMode ? 'bg-green-900/30 border border-green-700' : 'bg-green-50 border border-green-200') : (isDarkMode ? 'bg-gray-700' : 'bg-gray-50')}`}>
                      {/* Set Header */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${completedSets[setKey] ? 'bg-green-500' : 'bg-blue-500'}`}>
                            <span className={`text-sm font-bold text-white`}>{index + 1}</span>
                          </div>
                          <div>
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              Set {index + 1}
                            </span>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Target: {getRepsDisplay(currentExercise)} reps
                            </div>
                          </div>
                        </div>
                        <button
                          data-testid={`complete-set-${index}`}
                          onClick={() => handleCompleteSet(currentExerciseIndex, index)}
                          className={`w-8 h-8 min-h-0 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-all ${
                            completedSets[setKey]
                              ? 'bg-green-500 border-green-500 text-white'
                              : isDarkMode 
                                ? 'border-gray-500 hover:border-green-500 text-transparent hover:text-green-500'
                                : 'border-gray-300 hover:border-green-500 text-transparent hover:text-green-500'
                          }`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                      </div>
                      
                      {/* Form Check Reminder for Heavy Lifts */}
                      {needsFormCheck(setWeights[setKey]) && (
                        <div className={`mt-2 p-2 rounded-lg flex items-center space-x-2 ${
                          isDarkMode ? 'bg-orange-900/30 border border-orange-700' : 'bg-orange-50 border border-orange-200'
                        }`}>
                          <span className="text-lg">⚠️</span>
                          <div>
                            <p className={`text-xs font-medium ${isDarkMode ? 'text-orange-300' : 'text-orange-800'}`}>
                              Form Check Reminder
                            </p>
                            <p className={`text-xs ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                              This weight is 10%+ above your previous best. Focus on proper form!
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Previous Workout Data & Suggestions */}
                      {previousExerciseData?.sets?.[index] && (
                        <div className={`mt-1 flex flex-wrap items-center gap-2 text-xs`}>
                          {/* Last workout data - tap to use */}
                          <button
                            onClick={() => handleWeightChange(currentExerciseIndex, index, previousExerciseData.sets[index].weight?.toString() || '0')}
                            className={`inline-flex items-center px-2 py-0.5 rounded ${
                              isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                            } transition-colors`}
                            title="Tap to use this weight"
                          >
                            <span className="italic">Last: {previousExerciseData.sets[index].weight || 0} lbs × {previousExerciseData.sets[index].reps || 0}</span>
                          </button>
                          
                          {/* Suggested weight */}
                          {(() => {
                            const suggestion = getSuggestedWeight(index);
                            if (!suggestion) return null;
                            return (
                              <button
                                onClick={() => handleWeightChange(currentExerciseIndex, index, suggestion.weight.toString())}
                                className={`inline-flex items-center px-2 py-0.5 rounded font-medium transition-colors ${
                                  suggestion.reason === 'increase' 
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                                    : suggestion.reason === 'decrease'
                                    ? 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                }`}
                                title={suggestion.reason === 'increase' ? 'You hit your reps! Try +5 lbs' : suggestion.reason === 'decrease' ? 'Consider dropping weight' : 'Maintain current weight'}
                              >
                                {suggestion.reason === 'increase' && '💪 '}
                                {suggestion.reason === 'plan' ? 'Target' : 'Try'}: {suggestion.weight} lbs
                                {suggestion.reason === 'increase' && ' (+5)'}
                              </button>
                            );
                          })()}
                        </div>
                      )}
                      
                      {/* Weight and Reps Input - Full Width on Mobile */}
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Weight</label>
                            {/* Progress Indicator */}
                            {(() => {
                              const progress = getProgressIndicator(index, setWeights[setKey]);
                              if (!progress) return null;
                              return (
                                <span className={`text-xs font-medium flex items-center ${
                                  progress.direction === 'up' ? 'text-green-500' :
                                  progress.direction === 'down' ? 'text-red-500' : 'text-gray-400'
                                }`}>
                                  {progress.direction === 'up' && <span className="mr-0.5">↑</span>}
                                  {progress.direction === 'down' && <span className="mr-0.5">↓</span>}
                                  {progress.direction === 'same' && <span className="mr-0.5">=</span>}
                                  {progress.diff} lbs
                                </span>
                              );
                            })()}
                          </div>
                          <div className="flex">
                            <input
                              type="number"
                              inputMode="decimal"
                              placeholder={defaultWeight || (currentExercise?.target_weight ? String(currentExercise.target_weight) : "0")}
                              value={setWeights[setKey] || ''}
                              onChange={(e) => handleWeightChange(currentExerciseIndex, index, e.target.value)}
                              className={`w-full px-3 py-3 text-lg font-medium rounded-l-lg border-t border-b border-l ${
                                isDarkMode 
                                  ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' 
                                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                            <span className={`px-3 py-3 text-sm font-medium border rounded-r-lg flex-shrink-0 ${
                              isDarkMode ? 'bg-gray-700 border-gray-500 text-gray-300' : 'bg-gray-100 border-gray-300 text-gray-600'
                            }`}>
                              lbs
                            </span>
                          </div>
                        </div>
                        <div>
                          <label className={`text-sm font-medium block mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Reps</label>
                          <input
                            type="number"
                            inputMode="numeric"
                            placeholder={defaultReps || "0"}
                            value={setReps[setKey] || ''}
                            onChange={(e) => handleRepsChange(currentExerciseIndex, index, e.target.value)}
                            className={`w-full px-3 py-3 text-lg font-medium rounded-lg border ${
                              isDarkMode 
                                ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' 
                                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>
                      </div>
                      
                      {/* Rest Timer Button */}
                      <div className="mt-3 flex justify-center">
                        {(() => {
                          const restRec = getRecommendedRest(setWeights[setKey]);
                          const isHeavy = restRec.seconds > 90;
                          return (
                            <button
                              onClick={() => startRestTimer(restRec.seconds, setKey)}
                              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                isHeavy 
                                  ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                                  : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                              }`}
                            >
                              Start Rest Timer ({restRec.label})
                              {isHeavy && ' 🔥'}
                            </button>
                          );
                        })()}
                      </div>
                      
                      {/* Inline Rest Timer - shows under the set that triggered it */}
                      {isThisSetResting && (
                        <div data-testid={`inline-rest-timer-${index}`} className={`mt-3 p-4 rounded-xl text-center ${isDarkMode ? 'bg-blue-900/50 border-2 border-blue-500' : 'bg-blue-50 border-2 border-blue-300'}`}>
                          {/* Circular Progress Timer */}
                          <div className="relative w-24 h-24 mx-auto mb-3">
                            <svg className="w-24 h-24 transform -rotate-90">
                              <circle
                                cx="48"
                                cy="48"
                                r="44"
                                stroke={isDarkMode ? '#374151' : '#E5E7EB'}
                                strokeWidth="8"
                                fill="none"
                              />
                              <circle
                                cx="48"
                                cy="48"
                                r="44"
                                stroke="#3B82F6"
                                strokeWidth="8"
                                fill="none"
                                strokeLinecap="round"
                                strokeDasharray={`${2 * Math.PI * 44}`}
                                strokeDashoffset={`${2 * Math.PI * 44 * (1 - restTimer / restTimerTotal)}`}
                                className="transition-all duration-1000 ease-linear"
                              />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {Math.floor(restTimer / 60)}:{(restTimer % 60).toString().padStart(2, '0')}
                              </span>
                            </div>
                          </div>
                          <div className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                            Rest Time
                          </div>
                          <button
                            onClick={skipRest}
                            data-testid={`skip-rest-${index}`}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              isDarkMode 
                                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                            }`}
                          >
                            Skip Rest →
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </div>

            {isResting && (
              <>
                <div className="h-24" aria-hidden="true" />
              </>
            )}

            {/* Global Rest Timer - only show if triggered without a specific set */}
            {isResting && !restingSetKey && (
              <div className={`border-2 rounded-xl p-6 text-center ${isDarkMode ? 'bg-blue-900/50 border-blue-500' : 'bg-blue-50 border-blue-300'}`}>
                {/* Circular Progress Timer */}
                <div className="relative w-28 h-28 mx-auto mb-4">
                  <svg className="w-28 h-28 transform -rotate-90">
                    <circle
                      cx="56"
                      cy="56"
                      r="50"
                      stroke={isDarkMode ? '#374151' : '#E5E7EB'}
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="56"
                      cy="56"
                      r="50"
                      stroke="#3B82F6"
                      strokeWidth="8"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 50}`}
                      strokeDashoffset={`${2 * Math.PI * 50 * (1 - restTimer / restTimerTotal)}`}
                      className="transition-all duration-1000 ease-linear"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {Math.floor(restTimer / 60)}:{(restTimer % 60).toString().padStart(2, '0')}
                    </span>
                  </div>
                </div>
                <div className={`text-base font-medium mb-3 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                  Rest Time
                </div>
                <button
                  onClick={skipRest}
                  data-testid="skip-rest-global"
                  className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  Skip Rest →
                </button>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={handlePreviousExercise}
                disabled={currentExerciseIndex === 0}
                className={`flex items-center space-x-2 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Previous</span>
              </button>

              <div className="text-center">
                <div className={`text-sm mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Progress</div>
                <div className={`w-32 rounded-full h-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${((currentExerciseIndex + 1) / workout.exercises.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              <button
                onClick={handleNextExercise}
                disabled={currentExerciseIndex === workout.exercises.length - 1}
                className={`flex items-center space-x-2 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
              >
                <span>Next</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Finish Workout Button */}
      <div className="fixed bottom-20 left-4 right-4 z-50 max-w-lg mx-auto space-y-2">
        {isResting && (
          <RestTimerBar seconds={restTimer} total={restTimerTotal} onAdjust={adjustRest} onSkip={skipRest} isDarkMode={isDarkMode} />
        )}
        <button
          onClick={handleFinishWorkout}
          disabled={finishing}
          data-testid="finish-workout-btn"
          className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center shadow-lg"
        >
          {finishing ? (
            <>
              <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Saving...
            </>
          ) : (
            'Finish Workout'
          )}
        </button>
      </div>

      {/* PR Celebration Modal */}
      {showPRModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl p-6 max-w-sm w-full text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="text-6xl mb-4">🏆</div>
            <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              New Personal Record{newPRs.length > 1 ? 's' : ''}!
            </h2>
            <p className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Congratulations! You crushed it! 💪
            </p>
            <div className="space-y-3 mb-6">
              {newPRs.map((pr, index) => (
                <div key={index} className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {pr.exercise_name}
                  </div>
                  <div className="text-yellow-500 font-bold text-lg">
                    {pr.weight} lbs × {pr.reps} reps
                  </div>
                  {pr.pr_type === 'weight' && pr.previous_weight && (
                    <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Previous: {pr.previous_weight} lbs (+{pr.weight - pr.previous_weight} lbs!)
                    </div>
                  )}
                  {pr.pr_type === 'first' && (
                    <div className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                      First time logging this exercise!
                    </div>
                  )}
                  {pr.pr_type === 'reps' && (
                    <div className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                      More reps at this weight!
                    </div>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={handleClosePRModal}
              className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Awesome! 🎉
            </button>
          </div>
        </div>
      )}

      {/* Live PR Celebration Animation */}
      {showPRCelebration && prCelebrationData && (
        <div className="fixed inset-0 pointer-events-none z-[60] flex items-center justify-center">
          {/* Confetti */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(50)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-bounce"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `-20px`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  animationDuration: `${1 + Math.random()}s`,
                  transform: `rotate(${Math.random() * 360}deg)`,
                }}
              >
                <div
                  className={`w-3 h-3 ${
                    ['bg-yellow-400', 'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500'][i % 6]
                  }`}
                  style={{
                    animation: `fall ${2 + Math.random()}s linear forwards`,
                  }}
                />
              </div>
            ))}
          </div>
          {/* PR Banner */}
          <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 text-white px-8 py-6 rounded-2xl shadow-2xl transform animate-pulse">
            <div className="text-center">
              <div className="text-4xl mb-2">🏆🎉🏆</div>
              <div className="text-2xl font-bold">NEW PR!</div>
              <div className="text-lg font-semibold mt-1">
                {prCelebrationData.exercise}
              </div>
              <div className="text-3xl font-bold mt-2">
                {prCelebrationData.weight} lbs × {prCelebrationData.reps}
              </div>
              {prCelebrationData.previousPR && (
                <div className="text-sm mt-1 opacity-90">
                  +{(prCelebrationData.weight - prCelebrationData.previousPR.weight).toFixed(0)} lbs from previous!
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Weekly Progressive Overload Tracker Modal */}
      {showProgressTracker && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                📈 {currentExercise?.name} Progress
              </h2>
              <button
                onClick={() => setShowProgressTracker(false)}
                className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* PR Summary */}
            {exercisePR && (
              <div className={`p-4 rounded-lg mb-4 ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className={`text-sm font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>Personal Best</div>
                    <div className={`text-2xl font-bold ${isDarkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
                      {exercisePR.weight} lbs × {exercisePR.reps}
                    </div>
                    {exercisePR.date && (
                      <div className={`text-xs ${isDarkMode ? 'text-yellow-500' : 'text-yellow-600'}`}>
                        Set on {new Date(exercisePR.date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  <span className="text-4xl">🏆</span>
                </div>
              </div>
            )}

            {/* Weekly Progress Chart */}
            <div className={`mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <h3 className="font-semibold mb-2">Weight Progression</h3>
              {exerciseHistory.length > 0 ? (
                <div className="space-y-2">
                  {exerciseHistory.slice(0, 8).map((workout, idx) => {
                    const maxWeight = Math.max(...(workout.sets?.map(s => s.weight || 0) || [0]));
                    const totalReps = workout.sets?.reduce((sum, s) => sum + (s.reps || 0), 0) || 0;
                    const prWeight = exercisePR?.weight || maxWeight;
                    const percentage = prWeight > 0 ? (maxWeight / prWeight) * 100 : 0;
                    
                    return (
                      <div key={idx} className="flex items-center space-x-2">
                        <div className={`w-20 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {workout.date ? new Date(workout.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                        </div>
                        <div className="flex-1">
                          <div className={`h-6 rounded-full overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                            <div
                              className={`h-full rounded-full transition-all ${
                                maxWeight >= (exercisePR?.weight || 0) ? 'bg-yellow-500' : 'bg-blue-500'
                              }`}
                              style={{ width: `${Math.min(percentage, 100)}%` }}
                            />
                          </div>
                        </div>
                        <div className={`w-24 text-right text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {maxWeight} lbs
                          {maxWeight >= (exercisePR?.weight || 0) && <span className="ml-1">🏆</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  No history yet. Complete workouts to see your progress!
                </p>
              )}
            </div>

            {/* Stats Summary */}
            {exerciseHistory.length > 0 && (
              <div className={`grid grid-cols-3 gap-3 pt-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="text-center">
                  <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {exerciseHistory.length}
                  </div>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Workouts</div>
                </div>
                <div className="text-center">
                  <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {(() => {
                      const firstWeight = exerciseHistory[exerciseHistory.length - 1]?.sets?.[0]?.weight || 0;
                      const lastWeight = exerciseHistory[0]?.sets?.[0]?.weight || 0;
                      const diff = lastWeight - firstWeight;
                      return diff >= 0 ? `+${diff}` : diff;
                    })()}
                  </div>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>lbs gained</div>
                </div>
                <div className="text-center">
                  <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {exerciseHistory.reduce((sum, w) => sum + (w.sets?.length || 0), 0)}
                  </div>
                  <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Sets</div>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowProgressTracker(false)}
              className="w-full mt-4 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Video Guide Modal */}
      {videoExercise && (
        <ExerciseVideoModal
          exerciseName={videoExercise.name}
          attachedUrl={videoExercise.attached}
          backendUrl={backendUrl}
          isDarkMode={isDarkMode}
          canReplace={authUser?.role === 'trainer'}
          onClose={() => setVideoExercise(null)}
        />
      )}

      {/* Substitute Exercises Modal */}
      {showSubstituteModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl max-w-md w-full max-h-[80vh] overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div>
                <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Substitute Exercises
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {swapSide ? `Superset ${swapSide}: ` : ''}Alternatives for {activeExercise}
                </p>
              </div>
              <button
                data-testid="substitute-modal-close-btn"
                onClick={() => setShowSubstituteModal(false)}
                className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {clientEquipment && (
                <div className={`mb-4 p-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700/50 text-gray-300' : 'bg-blue-50 text-blue-700'}`}>
                  🏋️ Showing exercises for: <strong>{clientEquipment || 'All Equipment'}</strong>
                </div>
              )}
              {substituteExercises.length > 0 ? (
                <div className="space-y-3">
                  {substituteExercises.map((sub, idx) => (
                    <div
                      key={idx}
                      data-testid={`substitute-option-${idx}`}
                      onClick={() => sub.available !== false && handleSelectSubstitute(sub.name)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        sub.available === false
                          ? isDarkMode 
                            ? 'bg-gray-800/50 border-gray-700 opacity-50 cursor-not-allowed' 
                            : 'bg-gray-100 border-gray-200 opacity-50 cursor-not-allowed'
                          : isDarkMode 
                            ? 'bg-gray-700/50 border-gray-600 hover:border-blue-500 hover:bg-gray-700' 
                            : 'bg-gray-50 border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {sub.name}
                            </div>
                            {sub.available === false && (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-red-900/50 text-red-400' : 'bg-red-100 text-red-600'}`}>
                                Needs Equipment
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {sub.equipment_label && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                sub.equipment === 'none' 
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400'
                                  : sub.equipment === 'minimal'
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400'
                                    : sub.equipment === 'home'
                                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-400'
                                      : 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-400'
                              }`}>
                                {sub.equipment_label}
                              </span>
                            )}
                            <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Tap to swap
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {(
                            <button
                              data-testid={`substitute-video-btn-${idx}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowSubstituteModal(false);
                                fetchVideoUrl(sub.name, sub.video_url);
                              }}
                              className={`flex items-center gap-1 px-2 py-1 text-xs rounded ${
                                isDarkMode 
                                  ? 'bg-red-900/40 text-red-400 hover:bg-red-900/60' 
                                  : 'bg-red-50 text-red-600 hover:bg-red-100'
                              }`}
                            >
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                              </svg>
                              Video
                            </button>
                          )}
                          {sub.available !== false && (
                            <svg className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M12 14h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p>No substitute exercises found for this exercise.</p>
                  <p className="text-sm mt-1">Ask your trainer for alternatives!</p>
                </div>
              )}
            </div>
            <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowSubstituteModal(false)}
                className="w-full bg-blue-600 text-white py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for confetti animation */}
      <style>{`
        @keyframes fall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
    )
  );
};

// Everfit-style Workout Components
const SetRow = ({ set, exerciseId, onUpdateSet, onDeleteSet, previousSet, showPrevious = true, isDarkMode = false, isAIGenerated = false, prescribedReps = null, prescribedRest = null }) => {
  const [isEditing, setIsEditing] = useState(false);
  
  const getSetTypeColor = (type) => {
    if (isDarkMode) {
      switch(type) {
        case 'warmup': return 'bg-yellow-900 text-yellow-200';
        case 'dropset': return 'bg-purple-900 text-purple-200';
        case 'failure': return 'bg-red-900 text-red-200';
        default: return 'bg-blue-900 text-blue-200';
      }
    }
    switch(type) {
      case 'warmup': return 'bg-yellow-100 text-yellow-800';
      case 'dropset': return 'bg-purple-100 text-purple-800';
      case 'failure': return 'bg-red-100 text-red-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getSetTypeLabel = (type) => {
    switch(type) {
      case 'warmup': return 'W';
      case 'dropset': return 'D';
      case 'failure': return 'F';
      default: return set.set_number;
    }
  };

  return (
    <div className={`flex items-center space-x-2 py-2 px-3 rounded-lg border ${
      set.completed 
        ? (isDarkMode ? 'bg-green-900 border-green-700' : 'bg-green-50 border-green-200')
        : (isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200')
    } hover:shadow-sm transition-all`}>
      {/* Set Number/Type */}
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${getSetTypeColor(set.set_type)}`}>
        {getSetTypeLabel(set.set_type)}
      </div>

      {/* Previous Performance */}
      {showPrevious && previousSet && (
        <div className={`text-xs min-w-[60px] ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {previousSet.weight && `${previousSet.weight}kg`}
          {previousSet.reps && ` × ${previousSet.reps}`}
        </div>
      )}

      {/* Weight Input - Always Editable */}
      <div className="flex-1 min-w-[80px]">
        <input
          type="number"
          step="0.5"
          value={set.weight || ''}
          onChange={(e) => onUpdateSet(set.id, 'weight', parseFloat(e.target.value) || null)}
          placeholder="kg"
          className={`w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-500 text-white placeholder-gray-400' 
              : 'bg-white border-gray-300 text-gray-900'
          }`}
        />
      </div>

      {/* Reps - Display only for AI workouts, editable otherwise */}
      <div className="flex-1 min-w-[60px]">
        {isAIGenerated ? (
          <div className={`w-full px-2 py-1 text-sm border rounded text-center font-medium ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-500 text-gray-200' 
              : 'bg-gray-100 border-gray-200 text-gray-700'
          }`}>
            {prescribedReps || set.reps || '-'} reps
          </div>
        ) : (
          <input
            type="number"
            value={set.reps || ''}
            onChange={(e) => onUpdateSet(set.id, 'reps', parseInt(e.target.value) || null)}
            placeholder="reps"
            className={`w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-500 text-white placeholder-gray-400' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        )}
      </div>

      {/* Distance Input (for cardio) */}
      {set.distance !== undefined && (
        <div className="flex-1 min-w-[60px]">
          <input
            type="number"
            step="0.1"
            value={set.distance || ''}
            onChange={(e) => onUpdateSet(set.id, 'distance', parseFloat(e.target.value) || null)}
            placeholder="km"
            className={`w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-500 text-white placeholder-gray-400' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>
      )}

      {/* Duration Input */}
      {set.duration !== undefined && (
        <div className="flex-1 min-w-[60px]">
          <input
            type="number"
            value={set.duration || ''}
            onChange={(e) => onUpdateSet(set.id, 'duration', parseInt(e.target.value) || null)}
            placeholder="sec"
            className={`w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-500 text-white placeholder-gray-400' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        </div>
      )}

      {/* Rest Timer - Display only for AI workouts */}
      <div className="min-w-[50px]">
        {isAIGenerated ? (
          <div className={`w-full px-1 py-1 text-xs border rounded text-center font-medium ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-500 text-gray-300' 
              : 'bg-gray-100 border-gray-200 text-gray-600'
          }`}>
            {prescribedRest || 60}s
          </div>
        ) : (
          <input
            type="number"
            value={set.rest_time || 60}
            onChange={(e) => onUpdateSet(set.id, 'rest_time', parseInt(e.target.value) || 60)}
            placeholder="60s"
            className={`w-full px-1 py-1 text-xs border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-500 text-white placeholder-gray-400' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          />
        )}
      </div>

      {/* Completed Checkbox */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={set.completed}
          onChange={(e) => onUpdateSet(set.id, 'completed', e.target.checked)}
          className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
        />
        
        {/* Delete Set - Hide for AI generated workouts */}
        {!isAIGenerated && (
          <button
            onClick={() => onDeleteSet(set.id)}
            className={`p-1 ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

const ExerciseCard = ({ exercise, onUpdateExercise, onDeleteExercise, previousData, showPrevious = true, backendUrl, isDarkMode = false }) => {
  const [showNotes, setShowNotes] = useState(false);
  const [showSubstitutions, setShowSubstitutions] = useState(false);
  const [showVideoGuide, setShowVideoGuide] = useState(false);
  
  // Generate YouTube search URL for exercise video
  const getYouTubeSearchUrl = (exerciseName) => {
    const query = encodeURIComponent(`${exerciseName} exercise form tutorial`);
    return `https://www.youtube.com/results?search_query=${query}`;
  };
  
  // Get embedded video URL (using YouTube embed for common exercises)
  const getExerciseVideoEmbed = (exerciseName) => {
    // Map common exercises to YouTube video IDs
    const videoMap = {
      'bench press': 'rT7DgCr-3pg',
      'barbell bench press': 'rT7DgCr-3pg',
      'squat': 'ultWZbUMPL8',
      'barbell squat': 'ultWZbUMPL8',
      'deadlift': 'op9kVnSso6Q',
      'barbell deadlift': 'op9kVnSso6Q',
      'pull-up': 'eGo4IYlbE5g',
      'weighted pull-up': 'eGo4IYlbE5g',
      'lat pulldown': 'CAwf7n6Luuc',
      'barbell row': 'FWJR5Ve8bnQ',
      'bent over row': 'FWJR5Ve8bnQ',
      'overhead press': '_RlRDWO2jfg',
      'shoulder press': '_RlRDWO2jfg',
      'dumbbell press': 'VmB1G1K7v94',
      'incline press': '8iPEnn-ltC8',
      'incline dumbbell press': '8iPEnn-ltC8',
      'bicep curl': 'ykJmrZ5v0Oo',
      'barbell curl': 'ykJmrZ5v0Oo',
      'tricep pushdown': '2-LAMcpzODU',
      'cable pushdown': '2-LAMcpzODU',
      'leg press': 'IZxyjW7MPJQ',
      'romanian deadlift': 'JCXUYuzwNrM',
      'lunge': 'QOVaHwm-Q6U',
      'walking lunge': 'QOVaHwm-Q6U',
      'calf raise': 'gwLzBJYoWlI',
      'lateral raise': '3VcKaXpzqRo',
      'face pull': 'rep-qVOkqgk',
      'hammer curl': 'zC3nLlEvin4',
      'skull crusher': 'd_KZxkY_0cM',
      'cable row': 'GZbfZ033f74',
      'seated row': 'GZbfZ033f74',
      'dip': '2z8JmcrW-As',
      'chest fly': 'eozdVDA78K0',
      'cable fly': 'Iwe6AmxVf7o',
    };
    
    const nameLower = exerciseName.toLowerCase();
    for (const [key, videoId] of Object.entries(videoMap)) {
      if (nameLower.includes(key)) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    return null;
  };
  
  const addSet = (type = 'working') => {
    const newSet = {
      id: Date.now().toString(),
      set_number: exercise.sets.length + 1,
      set_type: type,
      weight: null,
      reps: null,
      distance: exercise.category === 'Cardio' ? null : undefined,
      duration: exercise.category === 'Cardio' ? null : undefined,
      rpe: null,
      rest_time: 60,
      completed: false,
      notes: null
    };
    
    onUpdateExercise(exercise.id, 'sets', [...exercise.sets, newSet]);
  };

  const updateSet = (setId, field, value) => {
    const updatedSets = exercise.sets.map(set => 
      set.id === setId ? { ...set, [field]: value } : set
    );
    onUpdateExercise(exercise.id, 'sets', updatedSets);
  };

  const deleteSet = (setId) => {
    const updatedSets = exercise.sets.filter(set => set.id !== setId);
    onUpdateExercise(exercise.id, 'sets', updatedSets);
  };

  const copyFromPrevious = () => {
    if (!previousData || !previousData.sets) return;
    
    const copiedSets = previousData.sets.map((prevSet, index) => ({
      id: Date.now().toString() + index,
      set_number: index + 1,
      set_type: prevSet.set_type,
      weight: prevSet.weight,
      reps: prevSet.reps,
      distance: prevSet.distance,
      duration: prevSet.duration,
      rpe: null,
      rest_time: prevSet.rest_time || 60,
      completed: false,
      notes: null
    }));
    
    onUpdateExercise(exercise.id, 'sets', copiedSets);
  };

  return (
    <div className={`rounded-xl shadow-sm border overflow-hidden mb-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Exercise Header */}
      <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Drag Handle */}
            <div className={`cursor-move ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 4L2 6l2-2m0 4l2-2-2-2m8 0v4m0-4l2 2-2-2m0 4L12 6l-2 2m8 0v4m0-4l2 2-2-2m0 4l-2-2 2-2" />
              </svg>
            </div>
            
            {/* Exercise Name */}
            <div className="flex-1">
              <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{exercise.name}</h3>
              {exercise.muscle_groups && exercise.muscle_groups.length > 0 && (
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{exercise.muscle_groups.join(', ')}</p>
              )}
              {exercise.target_muscle && !exercise.muscle_groups && (
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}>{exercise.target_muscle}</p>
              )}
            </div>
            
            {/* Video Guide Button */}
            <button
              onClick={() => setShowVideoGuide(!showVideoGuide)}
              className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                showVideoGuide 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{showVideoGuide ? 'Hide' : 'Video Guide'}</span>
              <svg className={`w-4 h-4 transition-transform ${showVideoGuide ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          <div className="flex items-center space-x-2">
            {/* Superset/Circuit Badge */}
            {exercise.superset_id && (
              <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full font-medium">
                Superset
              </span>
            )}
            {exercise.circuit_id && (
              <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full font-medium">
                Circuit
              </span>
            )}

            {/* Options Menu */}
            <div className="relative group">
              <button className="p-2 text-gray-400 hover:" style={{color: "#2d3748"}}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
              
              {/* Dropdown Menu */}
              <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 invisible group-hover:visible z-10">
                <button
                  onClick={() => setShowNotes(!showNotes)}
                  className="w-full px-4 py-2 text-left text-sm  hover:bg-gray-50" style={{color: "#2d3748"}}
                >
                  {showNotes ? 'Hide' : 'Show'} Notes
                </button>
                <button
                  onClick={() => setShowSubstitutions(!showSubstitutions)}
                  className="w-full px-4 py-2 text-left text-sm  hover:bg-gray-50" style={{color: "#2d3748"}}
                >
                  Substitutions
                </button>
                {previousData && (
                  <button
                    onClick={copyFromPrevious}
                    className="w-full px-4 py-2 text-left text-sm  hover:bg-gray-50" style={{color: "#2d3748"}}
                  >
                    Copy from Previous
                  </button>
                )}
                <button
                  onClick={() => onDeleteExercise(exercise.id)}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50"
                >
                  Delete Exercise
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible Video Guide Section */}
        {showVideoGuide && (
          <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-in slide-in-from-top-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Video Embed */}
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                {getExerciseVideoEmbed(exercise.name) ? (
                  <iframe
                    src={getExerciseVideoEmbed(exercise.name)}
                    title={`${exercise.name} tutorial`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-white">
                    <svg className="w-12 h-12 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <a 
                      href={getYouTubeSearchUrl(exercise.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Search on YouTube
                    </a>
                  </div>
                )}
              </div>
              
              {/* Instructions */}
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">How to Perform</h4>
                {exercise.instructions && Array.isArray(exercise.instructions) ? (
                  <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
                    {exercise.instructions.map((step, idx) => (
                      <li key={idx} className="leading-relaxed">{step}</li>
                    ))}
                  </ol>
                ) : exercise.instructions ? (
                  <p className="text-sm text-gray-700">{exercise.instructions}</p>
                ) : (
                  <p className="text-sm text-gray-500 italic">No instructions available for this exercise.</p>
                )}
                
                {/* Quick Tips */}
                {exercise.notes && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-blue-800">💡 Trainer Tip:</p>
                    <p className="text-sm text-blue-700">{exercise.notes}</p>
                  </div>
                )}
                
                {/* Equipment & Target */}
                <div className="flex flex-wrap gap-2 mt-3">
                  {exercise.equipment && (
                    <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">
                      🏋️ {exercise.equipment}
                    </span>
                  )}
                  {exercise.target_muscle && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      💪 {exercise.target_muscle}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sets Header */}
      <div className={`px-4 py-2 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-100'}`}>
        <div className={`flex items-center space-x-2 text-xs font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-600'}`}>
          <div className="w-8 text-center">SET</div>
          {showPrevious && <div className="min-w-[60px]">PREVIOUS</div>}
          <div className="flex-1 min-w-[80px]">WEIGHT</div>
          <div className="flex-1 min-w-[60px]">REPS</div>
          {exercise.category === 'Cardio' && (
            <>
              <div className="flex-1 min-w-[60px]">DISTANCE</div>
              <div className="flex-1 min-w-[60px]">TIME</div>
            </>
          )}
          <div className="min-w-[50px]">REST</div>
          <div className="min-w-[20px]">✓</div>
          <div className="min-w-[20px]"></div>
        </div>
      </div>

      {/* Sets */}
      <div className={`p-4 space-y-2 ${isDarkMode ? 'bg-gray-800' : ''}`}>
        {exercise.sets.map((set, index) => (
          <SetRow
            key={set.id}
            set={set}
            exerciseId={exercise.id}
            onUpdateSet={updateSet}
            onDeleteSet={deleteSet}
            previousSet={previousData?.sets?.[index]}
            showPrevious={showPrevious}
            isDarkMode={isDarkMode}
            isAIGenerated={exercise.is_ai_generated || false}
            prescribedReps={exercise.prescribed_reps || exercise.reps}
            prescribedRest={exercise.rest_seconds}
          />
        ))}

        {/* Add Set Buttons */}
        <div className="flex items-center space-x-2 pt-2">
          <button
            onClick={() => addSet('working')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors font-medium ${isDarkMode ? 'bg-blue-900 text-blue-100 hover:bg-blue-800' : 'bg-blue-100 hover:bg-blue-200'}`}
            style={{color: isDarkMode ? '#93c5fd' : '#1a1a1a'}}
          >
            + Set
          </button>
          <button
            onClick={() => addSet('warmup')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors font-medium ${isDarkMode ? 'bg-yellow-900 text-yellow-100 hover:bg-yellow-800' : 'bg-yellow-100 hover:bg-yellow-200'}`}
            style={{color: isDarkMode ? '#fde047' : '#1a1a1a'}}
          >
            + Warmup
          </button>
          <button
            onClick={() => addSet('dropset')}
            className={`px-3 py-1 text-sm rounded-lg transition-colors font-medium ${isDarkMode ? 'bg-purple-900 text-purple-100 hover:bg-purple-800' : 'bg-purple-100 hover:bg-purple-200'}`}
            style={{color: isDarkMode ? '#d8b4fe' : '#1a1a1a'}}
          >
            + Drop Set
          </button>
        </div>
      </div>

      {/* Exercise Notes */}
      {showNotes && (
        <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-100 bg-gray-50'}`}>
          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Exercise Notes</label>
          <textarea
            value={exercise.exercise_notes || ''}
            onChange={(e) => onUpdateExercise(exercise.id, 'exercise_notes', e.target.value)}
            placeholder="Add notes about form, modifications, or observations..."
            rows="3"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
          />
        </div>
      )}

      {/* Substitutions */}
      {showSubstitutions && (
        <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-100 bg-gray-50'}`}>
          <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>Alternative Exercises</label>
          <div className="flex flex-wrap gap-2">
            {exercise.substitutions && exercise.substitutions.map((sub, index) => (
              <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                {sub}
              </span>
            ))}
            <button className="px-2 py-1 bg-gray-100  text-sm rounded-full hover:bg-gray-200 transition-colors" style={{color: "#2d3748"}}>
              + Add Alternative
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Exercise Library Modal
const ExerciseLibraryModal = ({ isOpen, onClose, onAddExercise, exerciseLibrary }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [customExercise, setCustomExercise] = useState({
    name: '',
    category: '',
    muscle_groups: [],
    instructions: ''
  });
  const [showCustomForm, setShowCustomForm] = useState(false);

  const categories = ['All', 'Push', 'Pull', 'Legs', 'Cardio', 'Core'];
  const muscleGroups = ['Chest', 'Shoulders', 'Triceps', 'Lats', 'Biceps', 'Rhomboids', 'Quadriceps', 'Glutes', 'Hamstrings', 'Calves', 'Core', 'Abs', 'Obliques'];

  const filteredExercises = exerciseLibrary.filter(exercise => {
    const matchesSearch = exercise.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || exercise.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleAddCustomExercise = () => {
    if (!customExercise.name.trim()) return;
    
    const newExercise = {
      id: Date.now().toString(),
      name: customExercise.name,
      category: customExercise.category || 'Other',
      muscle_groups: customExercise.muscle_groups,
      instructions: customExercise.instructions,
      sets: [],
      is_custom: true,
      order_index: 0
    };
    
    onAddExercise(newExercise);
    setCustomExercise({ name: '', category: '', muscle_groups: [], instructions: '' });
    setShowCustomForm(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold " style={{color: "#1a1a1a"}}>Exercise Library</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:" style={{color: "#2d3748"}}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Search and Filters */}
          <div className="mt-4 flex items-center space-x-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search exercises..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <button
              onClick={() => setShowCustomForm(!showCustomForm)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              + Custom
            </button>
          </div>
        </div>

        {/* Custom Exercise Form */}
        {showCustomForm && (
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <h3 className="text-lg font-medium  mb-4" style={{color: "#1a1a1a"}}>Create Custom Exercise</h3>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Exercise name"
                value={customExercise.name}
                onChange={(e) => setCustomExercise({...customExercise, name: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={customExercise.category}
                onChange={(e) => setCustomExercise({...customExercise, category: e.target.value})}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select category</option>
                {categories.slice(1).map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <textarea
              placeholder="Exercise instructions"
              value={customExercise.instructions}
              onChange={(e) => setCustomExercise({...customExercise, instructions: e.target.value})}
              rows="2"
              className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex items-center space-x-2 mt-2">
              <button
                onClick={handleAddCustomExercise}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Add Exercise
              </button>
              <button
                onClick={() => setShowCustomForm(false)}
                className="px-4 py-2 bg-gray-300  rounded-lg hover:bg-gray-400" style={{color: "#2d3748"}}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Exercise List */}
        <div className="p-6 overflow-y-auto max-h-96">
          <div className="grid grid-cols-1 gap-2">
            {filteredExercises.map(exercise => (
              <div
                key={exercise.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                onClick={() => {
                  const newExercise = {
                    ...exercise,
                    id: Date.now().toString(),
                    sets: [{
                      id: Date.now().toString(),
                      set_number: 1,
                      set_type: 'working',
                      weight: null,
                      reps: null,
                      distance: exercise.category === 'Cardio' ? null : undefined,
                      duration: exercise.category === 'Cardio' ? null : undefined,
                      rpe: null,
                      rest_time: 60,
                      completed: false
                    }]
                  };
                  onAddExercise(newExercise);
                }}
              >
                <div>
                  <h4 className="font-medium " style={{color: "#1a1a1a"}}>{exercise.name}</h4>
                  <p className="text-sm " style={{color: "#2d3748"}}>
                    {exercise.category} • {exercise.muscle_groups?.join(', ')}
                  </p>
                </div>
                <button className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200">
                  Add
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Superset/Circuit Manager
const SupersetManager = ({ exercises, onUpdateSuperset, onUpdateCircuit }) => {
  const [showSuperset, setShowSuperset] = useState(false);
  const [showCircuit, setShowCircuit] = useState(false);
  const [selectedExercises, setSelectedExercises] = useState([]);

  const createSuperset = () => {
    if (selectedExercises.length < 2) return;
    const supersetId = Date.now().toString();
    selectedExercises.forEach(exerciseId => {
      onUpdateSuperset(exerciseId, supersetId);
    });
    setSelectedExercises([]);
    setShowSuperset(false);
  };

  const createCircuit = () => {
    if (selectedExercises.length < 3) return;
    const circuitId = Date.now().toString();
    selectedExercises.forEach(exerciseId => {
      onUpdateCircuit(exerciseId, circuitId);
    });
    setSelectedExercises([]);
    setShowCircuit(false);
  };

  return (
    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
      <h3 className="font-medium  mb-3" style={{color: "#1a1a1a"}}>Group Exercises</h3>
      <div className="flex items-center space-x-2">
        <button
          onClick={() => setShowSuperset(!showSuperset)}
          className="px-3 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200"
        >
          Create Superset
        </button>
        <button
          onClick={() => setShowCircuit(!showCircuit)}
          className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
        >
          Create Circuit
        </button>
      </div>

      {(showSuperset || showCircuit) && (
        <div className="mt-4">
          <p className="text-sm  mb-2" style={{color: "#2d3748"}}>
            Select exercises to group into a {showSuperset ? 'superset' : 'circuit'}:
          </p>
          <div className="space-y-2">
            {exercises.map(exercise => (
              <label key={exercise.id} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedExercises.includes(exercise.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedExercises([...selectedExercises, exercise.id]);
                    } else {
                      setSelectedExercises(selectedExercises.filter(id => id !== exercise.id));
                    }
                  }}
                  className="rounded"
                />
                <span className="text-sm">{exercise.name}</span>
              </label>
            ))}
          </div>
          <div className="mt-3 flex items-center space-x-2">
            <button
              onClick={showSuperset ? createSuperset : createCircuit}
              disabled={selectedExercises.length < (showSuperset ? 2 : 3)}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
            >
              Create {showSuperset ? 'Superset' : 'Circuit'}
            </button>
            <button
              onClick={() => {
                setShowSuperset(false);
                setShowCircuit(false);
                setSelectedExercises([]);
              }}
              className="px-3 py-2 bg-gray-300  rounded-lg hover:bg-gray-400" style={{color: "#2d3748"}}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Everfit Workout Container
const EverfitWorkout = ({ workout, onUpdateWorkout, previousWorkout, backendUrl, isClient = false }) => {
  const { isDarkMode } = useTheme();
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [workoutNotes, setWorkoutNotes] = useState(workout.workout_notes || '');
  const [workoutTimer, setWorkoutTimer] = useState(0);
  const [isWorkoutStarted, setIsWorkoutStarted] = useState(false);

  const fetchExerciseLibrary = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/exercise-library`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setExerciseLibrary(data.exercises || []);
      }
    } catch (error) {
      console.error('Error fetching exercise library:', error);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchExerciseLibrary();
  }, [fetchExerciseLibrary]);

  useEffect(() => {
    let interval;
    if (isWorkoutStarted) {
      interval = setInterval(() => {
        setWorkoutTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isWorkoutStarted]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(workout.exercises);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order_index for all exercises
    const updatedExercises = items.map((exercise, index) => ({
      ...exercise,
      order_index: index
    }));

    onUpdateWorkout({ ...workout, exercises: updatedExercises });
  };

  const addExercise = (exercise) => {
    const updatedExercises = [...workout.exercises, {
      ...exercise,
      order_index: workout.exercises.length
    }];
    onUpdateWorkout({ ...workout, exercises: updatedExercises });
    setShowLibrary(false);
  };

  const updateExercise = (exerciseId, field, value) => {
    const updatedExercises = workout.exercises.map(exercise =>
      exercise.id === exerciseId ? { ...exercise, [field]: value } : exercise
    );
    onUpdateWorkout({ ...workout, exercises: updatedExercises });
  };

  const deleteExercise = (exerciseId) => {
    const updatedExercises = workout.exercises.filter(exercise => exercise.id !== exerciseId);
    onUpdateWorkout({ ...workout, exercises: updatedExercises });
  };

  const updateSuperset = (exerciseId, supersetId) => {
    updateExercise(exerciseId, 'superset_id', supersetId);
  };

  const updateCircuit = (exerciseId, circuitId) => {
    updateExercise(exerciseId, 'circuit_id', circuitId);
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const calculateWorkoutStats = () => {
    let totalSets = 0;
    let completedSets = 0;
    let totalVolume = 0;

    workout.exercises.forEach(exercise => {
      // Handle both array format (manual workouts) and number format (AI-generated)
      const sets = Array.isArray(exercise.sets) ? exercise.sets : [];
      sets.forEach(set => {
        totalSets++;
        if (set.completed) {
          completedSets++;
          if (set.weight && set.reps) {
            totalVolume += set.weight * set.reps;
          }
        }
      });
      
      // If sets is a number (AI-generated), count it as total sets
      if (typeof exercise.sets === 'number' || typeof exercise.sets === 'string') {
        totalSets += parseInt(exercise.sets) || 0;
      }
    });

    return { totalSets, completedSets, totalVolume };
  };

  const stats = calculateWorkoutStats();

  return (
    <div className="max-w-4xl mx-auto">
      {/* Workout Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold " style={{color: "#1a1a1a"}}>{workout.name}</h1>
            <p className=" mt-1" style={{color: "#2d3748"}}>
              {stats.completedSets} of {stats.totalSets} sets completed
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Workout Timer */}
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {formatTime(workoutTimer)}
              </div>
              <div className="text-sm " style={{color: "#2d3748"}}>Duration</div>
            </div>

            {/* Total Volume */}
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {Math.round(stats.totalVolume)}kg
              </div>
              <div className="text-sm " style={{color: "#2d3748"}}>Volume</div>
            </div>

            {/* Start/Finish Workout */}
            {!isClient && (
              <button
                onClick={() => setIsWorkoutStarted(!isWorkoutStarted)}
                className={`px-6 py-3 rounded-lg font-semibold ${
                  isWorkoutStarted 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {isWorkoutStarted ? 'Finish Workout' : 'Start Workout'}
              </button>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${stats.totalSets > 0 ? (stats.completedSets / stats.totalSets) * 100 : 0}%` }}
            ></div>
          </div>
          <p className="text-sm  mt-1" style={{color: "#2d3748"}}>
            {stats.totalSets > 0 ? Math.round((stats.completedSets / stats.totalSets) * 100) : 0}% Complete
          </p>
        </div>
      </div>

      {/* Superset/Circuit Manager */}
      {!isClient && workout.exercises.length > 0 && (
        <SupersetManager
          exercises={workout.exercises}
          onUpdateSuperset={updateSuperset}
          onUpdateCircuit={updateCircuit}
        />
      )}

      {/* Exercise List */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="exercises">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {workout.exercises.map((exercise, index) => (
                <Draggable key={exercise.id} draggableId={exercise.id} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <ExerciseCard
                        exercise={exercise}
                        onUpdateExercise={updateExercise}
                        onDeleteExercise={deleteExercise}
                        previousData={previousWorkout?.exercises?.find(prev => prev.name === exercise.name)}
                        showPrevious={!!previousWorkout}
                        backendUrl={backendUrl}
                        isDarkMode={isDarkMode}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Add Exercise Button */}
      {!isClient && (
        <div className="text-center mb-6">
          <button
            onClick={() => setShowLibrary(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold flex items-center space-x-2 mx-auto"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Add Exercise</span>
          </button>
        </div>
      )}

      {/* Workout Notes */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="font-semibold text-lg text-gray-900 mb-4">Workout Notes</h3>
        <textarea
          value={workoutNotes}
          onChange={(e) => {
            setWorkoutNotes(e.target.value);
            onUpdateWorkout({ ...workout, workout_notes: e.target.value });
          }}
          placeholder="How did the workout feel? Any observations or modifications made?"
          rows="4"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Exercise Library Modal */}
      <ExerciseLibraryModal
        isOpen={showLibrary}
        onClose={() => setShowLibrary(false)}
        onAddExercise={addExercise}
        exerciseLibrary={exerciseLibrary}
      />
    </div>
  );
};

// Body Measurements Tracker Component
const BodyMeasurementsTracker = ({ measurementsData }) => {
  if (!measurementsData || measurementsData.length === 0) {
    return (
      <div className="text-center py-8 " style={{color: "#2d3748"}}>
        <p>No measurements recorded yet</p>
        <p className="text-sm">Start tracking your body measurements!</p>
      </div>
    );
  }

  const latestMeasurements = measurementsData[measurementsData.length - 1];
  const firstMeasurements = measurementsData[0];
  
  const measurements = [
    { label: 'Chest', key: 'chest', unit: 'in', color: '#010eda' },
    { label: 'Waist', key: 'waist', unit: 'in', color: '#000000' },
    { label: 'Hips', key: 'hips', unit: 'in', color: '#010eda' },
    { label: 'Thighs', key: 'thighs', unit: 'in', color: '#000000' },
    { label: 'Arms', key: 'arms', unit: 'in', color: '#010eda' }
  ];

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200">
      <h3 className="text-lg font-semibold text-black mb-4">Body Measurements Progress</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        {measurements.map(({ label, key, unit, color }) => {
          const current = latestMeasurements[key];
          const initial = firstMeasurements[key];
          const change = current - initial;
          
          return (
            <div key={key} className="text-center p-4 bg-gray-50 rounded-lg">
              <div className="text-2xl font-bold mb-1" style={{ color }}>
                {current}{unit}
              </div>
              <div className="text-sm font-medium  mb-1" style={{color: "#1a1a1a"}}>{label}</div>
              <div className={`text-xs ${
                change === 0 ? 'text-gray-500' : 
                change < 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {change === 0 ? 'No change' : 
                 `${change > 0 ? '+' : ''}${change.toFixed(1)}${unit}`}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4 text-center">
        <div>
          <div className="text-lg font-bold text-blue-600">{measurementsData.length}</div>
          <div className="text-sm " style={{color: "#2d3748"}}>Entries</div>
        </div>
        <div>
          <div className="text-lg font-bold text-gray-900">
            {new Date(latestMeasurements.timestamp).toLocaleDateString()}
          </div>
          <div className="text-sm " style={{color: "#2d3748"}}>Latest Entry</div>
        </div>
        <div>
          <div className="text-lg font-bold text-gray-900">
            {new Date(firstMeasurements.timestamp).toLocaleDateString()}
          </div>
          <div className="text-sm " style={{color: "#2d3748"}}>First Entry</div>
        </div>
      </div>
    </div>
  );
};

// Auth Context
const AuthContext = createContext();
const ThemeContext = createContext();

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme Provider Component
const ThemeProvider = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('renzifitness_theme');
    if (saved) {
      return saved === 'dark';
    }
    // Default to system preference if no saved preference
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      const saved = localStorage.getItem('renzifitness_theme');
      if (!saved) {
        setIsDarkMode(e.matches);
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('renzifitness_theme', newTheme ? 'dark' : 'light');
  };

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  const value = {
    isDarkMode,
    toggleTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Components
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    checkAuth();
    handleOAuthCallback();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthCallback = async () => {
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/oauth-callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId })
        });
        
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
          window.location.hash = '';
        }
      } catch (error) {
        console.error('OAuth callback failed:', error);
      }
    }
  };

  const login = async (email, password) => {
    const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    });
    
    if (response.ok) {
      const data = await response.json();
      setUser(data.user);
      
      // Store session token for PWA/standalone mode where cookies may not work
      if (data.session_token) {
        localStorage.setItem('session_token', data.session_token);
        console.log('[Auth] Session token stored for PWA mode');
      }
      
      // Auto-enable push notifications after login
      setTimeout(async () => {
        try {
          if ('Notification' in window && Notification.permission === 'default') {
            const permission = await Notification.requestPermission();
            if (permission === 'granted') {
              await enablePushNotifications();
              console.log('✅ Push notifications enabled automatically');
            }
          } else if (Notification.permission === 'granted') {
            await enablePushNotifications();
          }
        } catch (err) {
          console.log('Push notification setup skipped:', err.message);
        }
      }, 1000);
      
      return data;
    } else {
      const error = await response.json();
      throw new Error(error.detail);
    }
  };

  const register = async (userData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(userData)
      });
      
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        
        // Auto-enable push notifications after registration
        setTimeout(async () => {
          try {
            if ('Notification' in window && Notification.permission === 'default') {
              const permission = await Notification.requestPermission();
              if (permission === 'granted') {
                await enablePushNotifications();
                console.log('✅ Push notifications enabled automatically');
              }
            } else if (Notification.permission === 'granted') {
              await enablePushNotifications();
            }
          } catch (err) {
            console.log('Push notification setup skipped:', err.message);
          }
        }, 1000);
        
        return data;
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }
    } catch (err) {
      console.error('Register fetch error:', err);
      if (err.message) {
        throw err;
      }
      throw new Error('Network error. Please check your connection and try again.');
    }
  };

  const logout = async () => {
    await fetch(`${BACKEND_URL}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    // Clear stored session token
    localStorage.removeItem('session_token');
    setUser(null);
  };

  const loginWithGoogle = () => {
    const currentUrl = window.location.origin;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(currentUrl)}`;
  };

  const value = {
    user,
    login,
    register,
    logout,
    loginWithGoogle,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Role Selection Component
const RoleSelection = ({ onRoleSelect }) => {
  const { isDarkMode } = useTheme();
  
  return (
    <div className={`min-h-screen flex items-center justify-center p-6 relative overflow-hidden ${
      isDarkMode ? 'bg-black' : 'bg-white'
    }`}>
      {/* Floating breathing objects - only show in dark mode */}
      {isDarkMode && (
        <>
          <div className="floating-object-light floating-object-1 opacity-20"></div>
          <div className="floating-object-light floating-object-2 opacity-20"></div>
          <div className="floating-object-light floating-object-3 opacity-20"></div>
          <div className="floating-object-light floating-object-4 opacity-20"></div>
          <div className="floating-object-light floating-object-5 opacity-20"></div>
          <div className="floating-object-light floating-object-6 opacity-20"></div>
        </>
      )}
      
      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          {/* RenziFitness Logo */}
          <div className="mb-8 flex flex-col items-center">
            <div className={`w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 shadow-2xl bg-white mb-4 ${
              isDarkMode ? 'border-white' : 'border-gray-200'
            }`}>
              <img 
                src="https://customer-assets.emergentagent.com/job_renzifitness/artifacts/oeuw8yx5_White%20Background.png"
                alt="RenziFitness Logo"
                className="w-full h-full object-cover scale-125"
              />
            </div>
            <h1 className={`heading-2 mb-2 ${isDarkMode ? 'text-black' : 'text-gray-900'}`}>RenziFitness</h1>
          </div>
          
        <div className="text-center">
          <p className="body-large text-black mb-4">Transform your fitness journey</p>
          <p className="body-medium text-black">Choose your path to excellence</p>
        </div>
        </div>
        
        <div className="space-y-4">
          <button
            onClick={() => onRoleSelect('client')}
            className="btn-primary w-full h-16 text-lg"
          >
            I'm a Client
          </button>
          
          <button
            onClick={() => onRoleSelect('trainer')}
            className="btn-secondary w-full h-12"
          >
            I'm a Trainer
          </button>
        </div>
      </div>
    </div>
  );
};

// Auth Form Component
const AuthForm = ({ role, invitationToken }) => {
  // If there's an invitation token, start in signup mode (not login)
  const [isLogin, setIsLogin] = useState(!invitationToken);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phone: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [invitationData, setInvitationData] = useState(null);
  const [loadingInvitation, setLoadingInvitation] = useState(!!invitationToken);

  const { login, register, loginWithGoogle } = useAuth();
  const { isDarkMode } = useTheme();
  
  // Backend URL for API calls
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch invitation details when token is present
  useEffect(() => {
    const fetchInvitationDetails = async () => {
      if (!invitationToken) return;
      
      try {
        setLoadingInvitation(true);
        const response = await fetch(`${BACKEND_URL}/api/invitations/${invitationToken}`);
        
        if (response.ok) {
          const data = await response.json();
          setInvitationData(data);
          // Pre-fill the form with invitation data
          setFormData(prev => ({
            ...prev,
            email: data.client_email || '',
            name: data.client_name || ''
          }));
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Invalid or expired invitation link');
        }
      } catch (err) {
        console.error('Error fetching invitation:', err);
        setError('Failed to validate invitation. Please try again or contact your trainer.');
      } finally {
        setLoadingInvitation(false);
      }
    };

    fetchInvitationDetails();
  }, [invitationToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        // Validate form data before submission
        if (!formData.email || !formData.password || !formData.name || !formData.phone) {
          throw new Error('Please fill in all fields');
        }
        await register({
          ...formData,
          role: role,
          invitation_token: invitationToken || undefined
        });
      }
    } catch (err) {
      console.error('Registration error:', err);
      // Handle different error types
      if (err.message) {
        setError(err.message);
      } else if (typeof err === 'string') {
        setError(err);
      } else {
        setError('An error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-6 ${
      isDarkMode ? 'bg-black' : 'bg-white'
    }`}>
      <div className="max-w-md w-full space-y-8">
        {invitationToken && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
              </svg>
              <div>
                <p className="font-semibold text-blue-900 text-sm">You've been invited!</p>
                <p className="text-blue-800 text-xs mt-1">Create your client account to get started with RenziFitness.</p>
              </div>
            </div>
          </div>
        )}
        <div className="text-center">
        <div className="text-center">
          <h1 className={`heading-2 mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {invitationToken ? 'Create Your Account' : (isLogin ? 'Welcome Back' : 'Get Started')}
          </h1>
          <p className={`body-medium ${isDarkMode ? 'text-white' : 'text-black'}`}>
            {invitationToken 
              ? 'Join RenziFitness as a client' 
              : (isLogin ? 'Sign in to your account' : `Create your ${role} account`)
            }
          </p>
        </div>
        </div>

        <div className={`rounded-2xl shadow-lg p-8 ${isDarkMode ? 'bg-gray-900' : 'bg-white border border-gray-100'}`}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Email Address
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                disabled={!!invitationToken && !!invitationData}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  invitationToken && invitationData ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                }`} 
                style={{borderColor: '#010eda'}}
                data-testid="auth-email-input"
              />
              {invitationToken && invitationData && (
                <p className="text-xs text-gray-500 mt-1">Email is pre-filled from your invitation and cannot be changed.</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Password
              </label>
              <input
                type="password"
                name="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{borderColor: '#010eda'}}
              />
            </div>

            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-800 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{borderColor: '#010eda'}}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-800 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    inputMode="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="e.g. 555-123-4567"
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{borderColor: '#010eda'}}
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="auth-submit-button"
            >
              {loading ? 'Processing...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-2 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-700'}`}>Or continue with</span>
              </div>
            </div>

            <button
              onClick={loginWithGoogle}
              className="mt-4 w-full btn-secondary"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>
          </div>

          {/* Hide login/signup toggle when using invitation link */}
          {!invitationToken && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setIsLogin(!isLogin)}
                className={`link-text ${isDarkMode ? 'text-white' : ''}`}
              >
                {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Dashboard Components
const ClientDashboard = () => {
  // Client Dashboard States
  const [activeTab, setActiveTab] = useState('overview');
  const [activeProfileTab, setActiveProfileTab] = useState('info');
  const [macros, setMacros] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [workoutSessions, setWorkoutSessions] = useState([]);
  const [currentWorkout, setCurrentWorkout] = useState(null);
  const [previousWorkouts, setPreviousWorkouts] = useState([]);
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [groceryList, setGroceryList] = useState([]);
  const [aiGroceryList, setAiGroceryList] = useState(null);
  const [savedMeals, setSavedMeals] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [nutritionPlan, setNutritionPlan] = useState(null);
  const [activePlan, setActivePlan] = useState(null);
  const [showPlanOverview, setShowPlanOverview] = useState(false);
  
  // Meal plan filter states
  const [mealPlanSearch, setMealPlanSearch] = useState('');
  const [mealPlanFilter, setMealPlanFilter] = useState('all'); // 'all', 'breakfast', 'lunch', 'dinner', 'snack', 'favorites'
  const [favoriteMeals, setFavoriteMeals] = useState([]);
  const [expandedMeals, setExpandedMeals] = useState({});
  const [showMealPlanFilters, setShowMealPlanFilters] = useState(false);
  const [mealPlanExpanded, setMealPlanExpanded] = useState(true);
  
  // Push notification states
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPromptDismissed, setPushPromptDismissed] = useState(() => {
    return localStorage.getItem('pushPromptDismissed') === 'true';
  });
  const [iosHomeScreenDismissed, setIosHomeScreenDismissed] = useState(() => {
    return localStorage.getItem('iosHomeScreenDismissed') === 'true';
  });
  
  // iOS detection helpers
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                             window.navigator.standalone === true;
  const showIOSHomeScreenPrompt = isIOS && !isInStandaloneMode && !iosHomeScreenDismissed;
  
  const handleDismissIOSPrompt = () => {
    setIosHomeScreenDismissed(true);
    localStorage.setItem('iosHomeScreenDismissed', 'true');
  };
  
  // Timer states for workouts
  const [exerciseTimers, setExerciseTimers] = useState({});
  const [restDurations, setRestDurations] = useState({});
  const [progressData, setProgressData] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [latestMessagePreview, setLatestMessagePreview] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [macroInput, setMacroInput] = useState({
    food_name: '',
    protein: '',
    carbs: '',
    fats: '',
    quantity: '1',
    unit: 'serving',
    date: new Date().toISOString().split('T')[0]
  });
  const [recentFoods, setRecentFoods] = useState([]);
  const [filteredFoods, setFilteredFoods] = useState([]);
  const [showFoodSuggestions, setShowFoodSuggestions] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  const [scanningBarcode, setScanningBarcode] = useState(false);
  const [scannedProduct, setScannedProduct] = useState(null);
  const [servingSizeMultiplier, setServingSizeMultiplier] = useState(1);
  const [editingFood, setEditingFood] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [analyzingFood, setAnalyzingFood] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [passwordChange, setPasswordChange] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [newGroceryItem, setNewGroceryItem] = useState('');
  const [mealName, setMealName] = useState('');
  const [weightInput, setWeightInput] = useState('');
  const [weightData, setWeightData] = useState([]);
  const [measurementsData, setMeasurementsData] = useState([]);
  const [measurementsInput, setMeasurementsInput] = useState({
    chest: '',
    waist: '',
    hips: '',
    thighs: '',
    arms: ''
  });
  const [newMessage, setNewMessage] = useState('');
  const [macroTargets, setMacroTargets] = useState({
    daily_protein: 150,
    daily_carbs: 200,
    daily_fats: 65,
    daily_calories: 1300
  });
  const [weightGoal, setWeightGoal] = useState(null);
  const [weightGoalInput, setWeightGoalInput] = useState('');
  const [showWeightGoalForm, setShowWeightGoalForm] = useState(false);
  const [dailyQuote, setDailyQuote] = useState('');
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [photoUploadProgress, setPhotoUploadProgress] = useState(0);
  const [showDeletePhotoModal, setShowDeletePhotoModal] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  
  // Personal Records (PRs) states
  const [personalRecords, setPersonalRecords] = useState([]);
  const [bestPRs, setBestPRs] = useState([]);
  const [presetLifts, setPresetLifts] = useState([]);
  const [showPRForm, setShowPRForm] = useState(false);
  const [prFormData, setPrFormData] = useState({
    lift_name: '',
    custom_lift_name: '',
    weight: '',
    reps: '',
    notes: ''
  });
  const [prViewMode, setPrViewMode] = useState('summary'); // 'summary' or 'history'
  const [editingPR, setEditingPR] = useState(null);
  
  // Workout Streak states
  const [workoutStreak, setWorkoutStreak] = useState({
    current_streak: 0,
    longest_streak: 0,
    total_workouts: 0,
    streak_active: false
  });
  
  // Profile goals state
  const [profileGoals, setProfileGoals] = useState({
    primaryGoal: '',
    currentWeight: '',
    goalWeight: '',
    height: '',
    // New training preference fields
    intensityLevel: 5,
    trainingExperience: '',
    daysPerWeek: '4',
    equipmentAccess: '',
    injuriesLimitations: '',
    additionalNotes: '',
    // Dietary restrictions
    dietaryRestrictions: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState('');
  const [confirmModalAction, setConfirmModalAction] = useState(null);

  // Helper function to convert AI-generated exercises to workout format
  const convertAIExercisesToWorkoutFormat = (exercises) => {
    if (!exercises || !Array.isArray(exercises)) return [];
    
    return exercises.map((exercise, idx) => {
      // If sets is already an array, return as is
      if (Array.isArray(exercise.sets)) {
        return {
          ...exercise,
          is_ai_generated: true
        };
      }
      
      // Convert string sets (e.g., "4") to array of set objects
      const numSets = parseInt(exercise.sets) || 3;
      // Parse rep range (e.g., "8-12" -> use "8" as target)
      const repRange = exercise.reps || '10';
      const targetReps = repRange.toString().split('-')[0];
      
      const setsArray = Array.from({ length: numSets }, (_, i) => ({
        id: `${idx}_${i}_${Date.now()}`,
        set_number: i + 1,
        weight: '',
        reps: targetReps,
        completed: false
      }));
      
      return {
        ...exercise,
        id: exercise.id || `ex_${idx}_${Date.now()}`,
        sets: setsArray,
        rest_seconds: exercise.rest_seconds || 90,
        is_ai_generated: true,
        prescribed_reps: repRange // Store original rep range for display
      };
    });
  };

  // Timer utility functions
  const getTimerKey = (workoutId, exerciseIndex) => `${workoutId}_${exerciseIndex}`;
  
  const startTimer = (workoutId, exerciseIndex, duration) => {
    const timerKey = getTimerKey(workoutId, exerciseIndex);
    
    // Clear any existing timer
    if (exerciseTimers[timerKey]?.intervalId) {
      clearInterval(exerciseTimers[timerKey].intervalId);
    }
    
    const intervalId = setInterval(() => {
      setExerciseTimers(prev => {
        const timer = prev[timerKey];
        if (!timer || timer.timeLeft <= 0) {
          clearInterval(intervalId);
          // Play notification sound or show alert
          if (timer?.timeLeft <= 0) {
            // Timer completed
            return { ...prev, [timerKey]: { ...timer, isRunning: false, timeLeft: 0 } };
          }
          return prev;
        }
        
        return {
          ...prev,
          [timerKey]: { ...timer, timeLeft: timer.timeLeft - 1 }
        };
      });
    }, 1000);
    
    setExerciseTimers(prev => ({
      ...prev,
      [timerKey]: {
        totalDuration: duration,
        timeLeft: duration,
        isRunning: true,
        intervalId
      }
    }));
  };
  
  const pauseTimer = (workoutId, exerciseIndex) => {
    const timerKey = getTimerKey(workoutId, exerciseIndex);
    setExerciseTimers(prev => {
      const timer = prev[timerKey];
      if (timer?.intervalId) {
        clearInterval(timer.intervalId);
      }
      return { ...prev, [timerKey]: { ...timer, isRunning: false, intervalId: null } };
    });
  };
  
  const resetTimer = (workoutId, exerciseIndex) => {
    const timerKey = getTimerKey(workoutId, exerciseIndex);
    const duration = restDurations[timerKey] || 60; // Default 60 seconds
    
    setExerciseTimers(prev => {
      const timer = prev[timerKey];
      if (timer?.intervalId) {
        clearInterval(timer.intervalId);
      }
      return { 
        ...prev, 
        [timerKey]: { 
          totalDuration: duration,
          timeLeft: duration, 
          isRunning: false, 
          intervalId: null 
        } 
      };
    });
  };
  
  const setRestDuration = (workoutId, exerciseIndex, duration) => {
    const timerKey = getTimerKey(workoutId, exerciseIndex);
    setRestDurations(prev => ({ ...prev, [timerKey]: duration }));
    
    // If timer exists but not running, update it
    setExerciseTimers(prev => {
      if (prev[timerKey] && !prev[timerKey].isRunning) {
        return { 
          ...prev, 
          [timerKey]: { 
            ...prev[timerKey], 
            totalDuration: duration,
            timeLeft: duration 
          } 
        };
      }
      return prev;
    });
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (activeTab === 'overview') {
      // Load data for overview
      fetchMacros();
      fetchWorkoutSessions();
      fetchWeightGoal();
      fetchWeightData();
      fetchMacroTargets();
      fetchWorkoutStreak();
      setDailyQuote(generateDailyQuote());
    } else if (activeTab === 'nutrition') {
      fetchMacros();
      fetchGroceryList();
      fetchSavedMeals();
      fetchMacroTargets();
    } else if (activeTab === 'messages') {
      fetchMessages();
      // Mark all unread messages as read
      markMessagesAsRead();
    } else if (activeTab === 'workouts') {
      fetchWorkouts();
      fetchWorkoutSessions();
      fetchExerciseLibrary();
      fetchActivePlan();
    } else if (activeTab === 'progress') {
      fetchProgressData();
      fetchProgressPhotos();
      fetchWeightData();
      fetchMeasurementsData();
      fetchWeightGoal();
      fetchPersonalRecords();
      fetchPresetLifts();
    } else if (activeTab === 'profile') {
      fetchWeightGoal();
    }
  }, [activeTab]);

  // Seed exercise library on initial load
  useEffect(() => {
    seedExerciseLibrary();
  }, []);

  // Check push notification status on mount
  useEffect(() => {
    const checkPushStatus = async () => {
      const supported = isPushSupported();
      setPushSupported(supported);
      
      if (supported) {
        const enabled = await isPushEnabled();
        setPushEnabled(enabled);
      }
    };
    checkPushStatus();
  }, []);

  // WebSocket connection for real-time messages - connect only on user change
  useEffect(() => {
    if (user?.id) {
      // Connect to WebSocket
      wsService.connect(user.id, BACKEND_URL);
      
      return () => {
        // Only disconnect when user logs out
        wsService.disconnect();
      };
    }
  }, [user?.id]);
  
  // WebSocket message handler - updates when dependencies change
  useEffect(() => {
    if (!user?.id) return;
    
    // Handle new messages
    const handleNewMessage = (data) => {
      console.log('🔔 New message received via WebSocket:', data);
      
      // Play notification sound if enabled
      if (soundEnabled) {
        playNotificationSound();
      }
      
      // Update latest message preview for tooltip
      setLatestMessagePreview({
        senderName: data.sender_name,
        preview: data.preview,
        timestamp: new Date().toISOString()
      });
      
      // Clear preview after 5 seconds
      setTimeout(() => setLatestMessagePreview(null), 5000);
      
      // Refresh messages if on messages tab
      if (activeTab === 'messages') {
        fetchMessages();
      } else {
        // Just increment unread count
        setUnreadMessageCount(prev => prev + 1);
      }
    };
    
    wsService.on('new_message', handleNewMessage);
    
    return () => {
      wsService.off('new_message', handleNewMessage);
    };
  }, [user?.id, soundEnabled, activeTab]);

  // Handle push notification toggle
  const handleTogglePushNotifications = async () => {
    setPushLoading(true);
    try {
      // Check current permission state first
      const currentPermission = 'Notification' in window ? Notification.permission : 'unsupported';
      console.log('[Push Toggle] Current permission:', currentPermission);
      console.log('[Push Toggle] Is iOS:', isIOS);
      console.log('[Push Toggle] Is Standalone:', isInStandaloneMode);
      
      if (pushEnabled) {
        await disablePushNotifications();
        setPushEnabled(false);
        alert('Push notifications disabled');
      } else {
        // iOS-specific checks before attempting to enable
        if (isIOS) {
          if (!isInStandaloneMode) {
            alert('📱 iPhone Setup Required\n\nTo receive notifications:\n\n1. Tap Share button (square with arrow)\n2. Tap "Add to Home Screen"\n3. Open the app from your Home Screen\n4. Then enable notifications here');
            setPushLoading(false);
            return;
          }
          
          if (currentPermission === 'denied') {
            alert('🚫 Notifications Blocked\n\nYou previously denied notifications.\n\nTo fix:\n1. Open iPhone Settings\n2. Scroll to Safari (or this app)\n3. Tap Notifications\n4. Enable "Allow Notifications"\n5. Return here and try again');
            setPushLoading(false);
            return;
          }
        } else {
          // Non-iOS: check if denied
          if (currentPermission === 'denied') {
            alert('🚫 Notifications Blocked\n\nTo enable:\n1. Click the lock/info icon in your browser address bar\n2. Find "Notifications"\n3. Change from "Block" to "Allow"\n4. Refresh and try again');
            setPushLoading(false);
            return;
          }
        }
        
        await enablePushNotifications();
        
        // Verify the subscription was saved by checking status
        try {
          const sessionToken = localStorage.getItem('session_token');
          const statusResponse = await fetch(`${BACKEND_URL}/api/push/status`, {
            credentials: 'include',
            headers: sessionToken ? { 'X-Session-Token': sessionToken } : {}
          });
          const statusData = await statusResponse.json();
          console.log('[Push] Verification status:', statusData);
          
          if (!statusData.enabled) {
            throw new Error('Subscription was not saved to server. Please try again.');
          }
        } catch (verifyError) {
          console.error('[Push] Verification failed:', verifyError);
        }
        
        setPushEnabled(true);
        alert('✅ Push notifications enabled!\n\nYou will now receive alerts for messages, reminders, and updates.');
      }
    } catch (error) {
      console.error('Error toggling push notifications:', error);
      
      // More specific error messages
      const errorMsg = error.message || '';
      if (errorMsg.includes('Add to Home Screen') || errorMsg.includes('home screen')) {
        alert(errorMsg);
      } else if (errorMsg.includes('denied') || errorMsg.includes('blocked')) {
        if (isIOS) {
          alert('🚫 Notifications Blocked\n\nTo fix on iPhone:\n1. Open Settings app\n2. Find Safari or this app\n3. Enable Notifications\n4. Come back and try again');
        } else {
          alert('🚫 Notifications Blocked\n\nTo enable:\n1. Click the lock icon in browser address bar\n2. Allow Notifications\n3. Refresh the page');
        }
      } else if (errorMsg.includes('16.4')) {
        alert('📱 iOS Update Required\n\nPush notifications require iOS 16.4 or later.\n\nPlease update your iPhone:\nSettings → General → Software Update');
      } else if (errorMsg.includes('log in')) {
        alert('🔑 Session Expired\n\nPlease log out and log back in, then try enabling notifications again.');
      } else {
        alert(errorMsg || 'Failed to enable push notifications. Please try again.');
      }
    } finally {
      setPushLoading(false);
    }
  };

  // Handle enabling push from the Messages banner
  const handleEnablePushFromBanner = async () => {
    setPushLoading(true);
    try {
      await enablePushNotifications();
      setPushEnabled(true);
      setPushPromptDismissed(true);
      localStorage.setItem('pushPromptDismissed', 'true');
      alert('✅ Push notifications enabled!\n\nYou will now receive notifications when:\n• Your trainer sends you a message\n• You have workout reminders\n• You receive updates');
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      // If permission denied, still dismiss the prompt
      if (error.message?.includes('denied')) {
        setPushPromptDismissed(true);
        localStorage.setItem('pushPromptDismissed', 'true');
        alert('Notifications are blocked for this site.\n\nTo enable:\n1. Tap the lock/info icon in your browser\'s address bar\n2. Find "Notifications" in Site Settings\n3. Change from "Block" to "Allow"\n4. Refresh the page and try again');
      } else {
        alert(error.message || 'Failed to enable push notifications');
      }
    } finally {
      setPushLoading(false);
    }
  };

  // Dismiss push prompt without enabling
  const handleDismissPushPrompt = () => {
    setPushPromptDismissed(true);
    localStorage.setItem('pushPromptDismissed', 'true');
  };

  // Send test push notification
  const handleTestPushNotification = async () => {
    try {
      const result = await sendTestPush();
      console.log('[Push Test] Result:', result);
      
      if (result.success && result.sent > 0) {
        alert('Test notification sent! Check your notifications. (' + result.subscriptions_found + ' subscription(s) found)');
      } else if (result.errors) {
        alert('Notification could not be delivered.\n\nReason: ' + result.errors.map(e => `${e.status || ''} ${e.reason || ''}`.trim()).join('; ') + '\n\n' + result.message);
      } else if (result.debug) {
        // Show debug info
        alert('Push test failed.\n\nDebug info:\n' + JSON.stringify(result.debug, null, 2) + '\n\n' + result.message);
      } else {
        alert('No active push subscriptions found for this account.\n\nTry toggling push OFF then ON again.');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      alert('Failed to send test notification: ' + error.message + '\n\nTry toggling push OFF then ON again.');
    }
  };

  // Cleanup timers when component unmounts or when switching away from workouts
  useEffect(() => {
    if (activeTab !== 'workouts') {
      // Clear all active timers when leaving workouts tab
      Object.values(exerciseTimers).forEach(timer => {
        if (timer.intervalId) {
          clearInterval(timer.intervalId);
        }
      });
      setExerciseTimers({});
    }
    
    // Fetch recent foods and meal plans when nutrition tab is opened
    if (activeTab === 'nutrition') {
      fetchRecentFoods();
      fetchMealPlans();
      fetchNutritionPlan();
    }
  }, [activeTab]); // Removed exerciseTimers from dependency array to prevent infinite loop

  // Initialize barcode scanner when modal opens
  useEffect(() => {
    if (showBarcodeScanner && typeof window !== 'undefined') {
      // Dynamically import html5-qrcode
      import('html5-qrcode').then(({ Html5Qrcode }) => {
        const html5QrCode = new Html5Qrcode("barcode-reader");
        
        html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 }
          },
          (decodedText) => {
            // Barcode detected
            console.log('Barcode detected:', decodedText);
            html5QrCode.stop();
            handleBarcodeDetected(decodedText);
          },
          (errorMessage) => {
            // Scanning errors (ignore)
          }
        ).catch(err => {
          console.error('Error starting scanner:', err);
          alert('Could not access camera. Please check permissions.');
          setShowBarcodeScanner(false);
        });
        
        // Cleanup
        return () => {
          html5QrCode.stop().catch(() => {});
        };
      });
    }
  }, [showBarcodeScanner]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(exerciseTimers).forEach(timer => {
        if (timer.intervalId) {
          clearInterval(timer.intervalId);
        }
      });
    };
  }, []); // Empty dependency array - only run on mount/unmount

  const fetchMacros = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/macros`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMacros(data.macros);
      }
    } catch (error) {
      console.error('Error fetching macros:', error);
    }
  };

  // Get today's macros for Overview tab
  const getTodaysMacros = () => {
    const today = new Date().toISOString().split('T')[0];
    return macros.filter(macro => {
      const macroDate = new Date(macro.date).toISOString().split('T')[0];
      return macroDate === today;
    });
  };

  // Enhanced API Functions
  const fetchExerciseLibrary = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/exercise-library`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setExerciseLibrary(data.exercises || []);
      }
    } catch (error) {
      console.error('Error fetching exercise library:', error);
    }
  };

  const fetchWorkoutSessions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/workout-sessions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWorkoutSessions(data.sessions || []);
      }
    } catch (error) {
      console.error('Error fetching workout sessions:', error);
    }
  };

  const fetchWorkoutStreak = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/workout-streak`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWorkoutStreak(data);
      }
    } catch (error) {
      console.error('Error fetching workout streak:', error);
    }
  };

  const fetchActivePlan = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/client/active-plan`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setActivePlan(data.active_plan);
      }
    } catch (error) {
      console.error('Error fetching active plan:', error);
    }
  };

  const createWorkoutSession = async (sessionData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/workout-sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(sessionData)
      });
      if (response.ok) {
        fetchWorkoutSessions();
        return await response.json();
      }
    } catch (error) {
      console.error('Error creating workout session:', error);
    }
  };

  const updateWorkoutSession = async (sessionId, sessionData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/workout-sessions/${sessionId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(sessionData)
      });
      if (response.ok) {
        fetchWorkoutSessions();
        return await response.json();
      }
    } catch (error) {
      console.error('Error updating workout session:', error);
    }
  };

  const seedExerciseLibrary = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/seed-exercise-library`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        fetchExerciseLibrary();
      }
    } catch (error) {
      console.error('Error seeding exercise library:', error);
    }
  };

  const fetchWorkouts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/workouts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWorkouts(data.workouts);
      }
    } catch (error) {
      console.error('Error fetching workouts:', error);
    }
  };

  // Stub function for template deletion (used by handleConfirmDelete)
  // Clients don't manage workout templates, but this function is needed for the delete confirmation handler
  const fetchWorkoutTemplates = async () => {
    // No-op for clients - templates are managed by trainers
    console.log('fetchWorkoutTemplates called in ClientDashboard - no action needed');
  };

  const fetchSavedMeals = async () => {
    const savedMealsData = localStorage.getItem(`saved_meals_${user?.id}`);
    if (savedMealsData) {
      setSavedMeals(JSON.parse(savedMealsData));
    }
  };

  const fetchGroceryList = async () => {
    // Fetch user's personal grocery list from localStorage
    const savedList = localStorage.getItem(`grocery_list_${user?.id}`);
    if (savedList) {
      setGroceryList(JSON.parse(savedList));
    }
    
    // Fetch AI-generated grocery list from nutrition plan
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${user?.id}/nutrition-plan`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.nutrition_plan?.grocery_list) {
          setAiGroceryList(data.nutrition_plan.grocery_list);
        }
      }
    } catch (error) {
      console.error('Error fetching AI grocery list:', error);
    }
  };

  const fetchMealPlans = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${user?.id}/meal-plans`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMealPlans(data.meal_plans || []);
      }
      // Load favorites from localStorage
      const storedFavorites = localStorage.getItem(`favoriteMeals_${user?.id}`);
      if (storedFavorites) {
        setFavoriteMeals(JSON.parse(storedFavorites));
      }
    } catch (error) {
      console.error('Error fetching meal plans:', error);
    }
  };

  const toggleFavoriteMeal = (mealId) => {
    setFavoriteMeals(prev => {
      const newFavorites = prev.includes(mealId) 
        ? prev.filter(id => id !== mealId)
        : [...prev, mealId];
      localStorage.setItem(`favoriteMeals_${user?.id}`, JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  const toggleMealExpanded = (mealId) => {
    setExpandedMeals(prev => ({
      ...prev,
      [mealId]: !prev[mealId]
    }));
  };

  const getFilteredMealPlans = () => {
    let filtered = mealPlans;
    
    // Search filter
    if (mealPlanSearch.trim()) {
      const search = mealPlanSearch.toLowerCase();
      filtered = filtered.filter(meal => 
        meal.name?.toLowerCase().includes(search) ||
        meal.meal_type?.toLowerCase().includes(search) ||
        meal.ingredients?.some(ing => ing.toLowerCase().includes(search))
      );
    }
    
    // Category/type filter
    if (mealPlanFilter === 'favorites') {
      filtered = filtered.filter(meal => favoriteMeals.includes(meal.id));
    } else if (mealPlanFilter !== 'all') {
      filtered = filtered.filter(meal => meal.meal_type?.toLowerCase() === mealPlanFilter);
    }
    
    return filtered;
  };

  const fetchNutritionPlan = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${user?.id}/nutrition-plan`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNutritionPlan(data.nutrition_plan);
        // Update macro targets if the nutrition plan has them
        if (data.nutrition_plan) {
          setMacroTargets({
            daily_protein: data.nutrition_plan.daily_protein || macroTargets.daily_protein,
            daily_carbs: data.nutrition_plan.daily_carbs || macroTargets.daily_carbs,
            daily_fats: data.nutrition_plan.daily_fats || macroTargets.daily_fats,
            daily_calories: data.nutrition_plan.daily_calories || macroTargets.daily_calories
          });
        }
      }
    } catch (error) {
      console.error('Error fetching nutrition plan:', error);
    }
  };

  const fetchProgressData = async () => {
    const savedProgress = localStorage.getItem(`progress_${user?.id}`);
    if (savedProgress) {
      setProgressData(JSON.parse(savedProgress));
    }
  };

  const fetchProgressPhotos = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/progress-photos`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProgressPhotos(data.progress_photos || []);
      }
    } catch (error) {
      console.error('Error fetching progress photos:', error);
    }
  };

  const fetchMacroTargets = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/macro-targets`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMacroTargets(data.targets);
      }
    } catch (error) {
      console.error('Error fetching macro targets:', error);
    }
  };

  const fetchWeightData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/weight-logs`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWeightData(data.weights);
      }
    } catch (error) {
      console.error('Error fetching weight data:', error);
    }
  };

  const fetchMeasurementsData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/body-measurements`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMeasurementsData(data.measurements || []);
      }
    } catch (error) {
      console.error('Error fetching measurements data:', error);
    }
  };

  // Personal Records (PRs) functions
  const fetchPersonalRecords = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/prs`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPersonalRecords(data.prs || []);
        setBestPRs(data.best_prs || []);
      }
    } catch (error) {
      console.error('Error fetching personal records:', error);
    }
  };

  const fetchPresetLifts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/prs/preset-lifts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPresetLifts(data.preset_lifts || []);
      }
    } catch (error) {
      console.error('Error fetching preset lifts:', error);
    }
  };

  const handlePRSubmit = async (e) => {
    e.preventDefault();
    
    const liftName = prFormData.lift_name === 'custom' 
      ? prFormData.custom_lift_name 
      : prFormData.lift_name;
    
    if (!liftName || !prFormData.weight || !prFormData.reps) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/prs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          lift_name: liftName,
          weight: parseFloat(prFormData.weight),
          reps: parseInt(prFormData.reps),
          notes: prFormData.notes || null
        })
      });

      if (response.ok) {
        await fetchPersonalRecords();
        setPrFormData({ lift_name: '', custom_lift_name: '', weight: '', reps: '', notes: '' });
        setShowPRForm(false);
        alert('🏆 Personal Record logged successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to log PR: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error logging PR:', error);
      alert('Failed to log PR. Please try again.');
    }
  };

  const handlePRUpdate = async (prId, updateData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/prs/${prId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        await fetchPersonalRecords();
        setEditingPR(null);
        alert('✅ PR updated successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to update PR: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating PR:', error);
      alert('Failed to update PR. Please try again.');
    }
  };

  const handlePRDelete = async (prId) => {
    if (!window.confirm('Are you sure you want to delete this personal record?')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/prs/${prId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchPersonalRecords();
        alert('✅ PR deleted successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to delete PR: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting PR:', error);
      alert('Failed to delete PR. Please try again.');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      setPhotoUploadProgress(10);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Image = event.target.result;
        setPhotoUploadProgress(50);
        
        try {
          const response = await fetch(`${BACKEND_URL}/api/progress-photos`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              photo_url: base64Image,
              date_taken: new Date().toISOString()
            })
          });
          
          setPhotoUploadProgress(90);
          
          if (response.ok) {
            const data = await response.json();
            setProgressPhotos([...progressPhotos, data.photo]);
            setPhotoUploadProgress(100);
            setTimeout(() => setPhotoUploadProgress(0), 1000);
            alert('✅ Photo uploaded successfully!');
            
            // Reset file input
            e.target.value = '';
          } else {
            throw new Error('Upload failed');
          }
        } catch (error) {
          console.error('Error uploading photo:', error);
          alert('Failed to upload photo. Please try again.');
          setPhotoUploadProgress(0);
        }
      };
      
      reader.onerror = () => {
        alert('Error reading file');
        setPhotoUploadProgress(0);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing photo:', error);
      alert('Error processing photo');
      setPhotoUploadProgress(0);
    }
  };

  const handleDeletePhoto = async () => {
    if (!photoToDelete) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/progress-photos/${photoToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        setProgressPhotos(progressPhotos.filter(p => p.id !== photoToDelete.id));
        alert('✅ Photo deleted successfully!');
      } else {
        alert('❌ Failed to delete photo. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('❌ Failed to delete photo. Please try again.');
    } finally {
      setShowDeletePhotoModal(false);
      setPhotoToDelete(null);
    }
  };

  // Handle manual food/macro logging form submission
  const handleMacroSubmit = async (e) => {
    e.preventDefault();
    try {
      // Calculate calories: 4 cal/g for protein and carbs, 9 cal/g for fats
      const protein = parseFloat(macroInput.protein) || 0;
      const carbs = parseFloat(macroInput.carbs) || 0;
      const fats = parseFloat(macroInput.fats) || 0;
      const calories = (protein * 4) + (carbs * 4) + (fats * 9);
      
      const response = await fetch(`${BACKEND_URL}/api/macros`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          food_name: macroInput.food_name,
          protein: protein,
          carbs: carbs,
          fats: fats,
          calories: calories,
          quantity: parseFloat(macroInput.quantity) || 1,
          unit: macroInput.unit || 'serving',
          date: new Date().toISOString()
        })
      });

      if (response.ok) {
        // Reset form
        setMacroInput({
          food_name: '',
          protein: '',
          carbs: '',
          fats: '',
          quantity: '1',
          unit: 'serving',
          date: new Date().toISOString().split('T')[0]
        });
        // Refresh macros list
        fetchMacros();
        setShowFoodSuggestions(false);
      } else {
        console.error('Failed to log food');
      }
    } catch (error) {
      console.error('Error logging food:', error);
    }
  };

  const handleAddSavedMeal = async (meal) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/macros`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          protein: meal.protein,
          carbs: meal.carbs,
          fats: meal.fats,
          date: new Date().toISOString()
        })
      });

      if (response.ok) {
        fetchMacros();
        alert(`${meal.name} added to today's macros!`);
      }
    } catch (error) {
      console.error('Error adding saved meal:', error);
    }
  };

  const handleDeleteSavedMeal = (mealId) => {
    const updatedMeals = savedMeals.filter(meal => meal.id !== mealId);
    setSavedMeals(updatedMeals);
    localStorage.setItem(`saved_meals_${user?.id}`, JSON.stringify(updatedMeals));
  };

  const handleGroceryAdd = (e) => {
    e.preventDefault();
    if (newGroceryItem.trim()) {
      const updatedList = [...groceryList, { id: Date.now(), item: newGroceryItem, completed: false }];
      setGroceryList(updatedList);
      localStorage.setItem(`grocery_list_${user?.id}`, JSON.stringify(updatedList));
      setNewGroceryItem('');
    }
  };

  const handleWeightSubmit = async (e) => {
    e.preventDefault();
    if (weightInput) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/weight-logs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            weight: parseFloat(weightInput)
          })
        });

        if (response.ok) {
          setWeightInput('');
          // Refresh weight data to show the new entry
          await fetchWeightData();
          // Also update progress data for backwards compatibility
          const newEntry = {
            id: Date.now(),
            weight: parseFloat(weightInput),
            date: new Date().toISOString(),
            type: 'weight'
          };
          const updatedProgress = [...progressData, newEntry];
          setProgressData(updatedProgress);
          localStorage.setItem(`progress_${user?.id}`, JSON.stringify(updatedProgress));
        } else {
          console.error('Failed to log weight');
        }
      } catch (error) {
        console.error('Error logging weight:', error);
      }
    }
  };

  const handleMeasurementsSubmit = async (e) => {
    e.preventDefault();
    
    // Check if all fields have values
    const hasAllValues = Object.values(measurementsInput).every(value => value && parseFloat(value) > 0);
    if (!hasAllValues) {
      alert('Please fill in all measurements with positive values');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/body-measurements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          chest: parseFloat(measurementsInput.chest),
          waist: parseFloat(measurementsInput.waist),
          hips: parseFloat(measurementsInput.hips),
          thighs: parseFloat(measurementsInput.thighs),
          arms: parseFloat(measurementsInput.arms)
        })
      });

      if (response.ok) {
        // Reset form
        setMeasurementsInput({
          chest: '',
          waist: '',
          hips: '',
          thighs: '',
          arms: ''
        });
        // Refresh measurements data
        await fetchMeasurementsData();
      } else {
        console.error('Failed to log measurements');
      }
    } catch (error) {
      console.error('Error logging measurements:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;
    
    try {
      console.log('📤 Client sending message...');
      console.log('👤 User object:', user);
      
      // Get trainer ID for client
      let recipientId = user?.trainer_id;
      
      if (!recipientId) {
        console.error('❌ Trainer ID not found in user profile');
        alert('Unable to send message: Trainer information not available. Please contact support.');
        return;
      }
      
      console.log('📨 Sending to trainer ID:', recipientId);
      
      // Use the new simpler messages API endpoint
      const response = await fetch(`${BACKEND_URL}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          receiver_id: recipientId,
          message: newMessage.trim()
        })
      });
      
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Message sent successfully:', result);
        // Refresh messages to show the new one
        await fetchMessages();
        setNewMessage('');
        alert('Message sent successfully!');
      } else {
        const error = await response.json();
        console.error('❌ Failed to send message:', error);
        alert(error.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message. Please try again.');
    }
  };
  
  const fetchMessages = async () => {
    try {
      console.log('🔍 Client fetching messages...');
      
      const response = await fetch(`${BACKEND_URL}/api/messages`, {
        credentials: 'include'
      });
      
      console.log('📡 Client messages response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📨 Client messages API response:', data);
        
        // Extract messages array from API response {success, messages, total}
        const messagesArray = data.messages || [];
        
        // Transform messages to match the expected format for client UI
        const transformedMessages = messagesArray.map(msg => {
          const transformed = {
            id: msg.id,
            text: msg.message || msg.content || '',
            sender: msg.sender_id === user.id ? 'client' : 'trainer',
            timestamp: msg.created_at || msg.timestamp,
            senderName: msg.sender_name,
            read: msg.read || false
          };
          return transformed;
        });
        
        setMessages(transformedMessages);
        
        // Calculate unread count (messages from trainer that haven't been read)
        const unreadCount = transformedMessages.filter(msg => 
          msg.sender === 'trainer' && !msg.read
        ).length;
        setUnreadMessageCount(unreadCount);
      } else {
        console.error('Failed to fetch client messages:', response.status);
        setMessages([]);
        setUnreadMessageCount(0);
      }
    } catch (error) {
      console.error('Error fetching client messages:', error);
      setMessages([]);
      setUnreadMessageCount(0);
    }
  };

  // Mark all unread messages as read
  const markMessagesAsRead = async () => {
    try {
      const unreadMessages = messages.filter(msg => msg.sender === 'trainer' && !msg.read);
      for (const msg of unreadMessages) {
        await fetch(`${BACKEND_URL}/api/messages/mark-read/${msg.id}`, {
          method: 'POST',
          credentials: 'include'
        });
      }
      // Refresh to update the count
      if (unreadMessages.length > 0) {
        fetchMessages();
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Note: Removed auto-fetch useEffect to prevent infinite loop
  // Messages are now fetched manually with the refresh button

  // Fetch recent foods for quick add
  const fetchRecentFoods = async () => {
    try {
      console.log('📥 Fetching recent foods...');
      const response = await fetch(`${BACKEND_URL}/api/recent-foods`, {
        credentials: 'include'
      });
      
      console.log('📡 Recent foods response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📦 Recent foods data:', data);
        console.log('🍔 Number of recent foods:', data.foods?.length || 0);
        setRecentFoods(data.foods || []);
      } else {
        console.error('Failed to fetch recent foods:', response.status);
      }
    } catch (error) {
      console.error('Error fetching recent foods:', error);
    }
  };

  // Quick add food from recent foods (MyFitnessPal style)
  const handleQuickAddFood = async (food) => {
    console.log('🍔 Quick adding food:', food);
    try {
      const payload = {
        food_name: food.food_name,
        protein: parseFloat(food.protein) || 0,
        carbs: parseFloat(food.carbs) || 0,
        fats: parseFloat(food.fats) || 0,
        quantity: parseFloat(food.quantity) || 1,
        unit: food.unit || 'serving',
        date: new Date().toISOString()
      };
      
      console.log('📤 Sending payload:', payload);
      
      const response = await fetch(`${BACKEND_URL}/api/macros`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Success:', result);
        alert(`✅ ${food.food_name} added successfully!`);
        fetchMacros();
        fetchRecentFoods(); // Refresh recent foods
      } else {
        const error = await response.json();
        console.error('❌ Error response:', error);
        alert(`Failed to add food: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error quick adding food:', error);
      alert('Error adding food. Please try again.');
    }
  };

  // Filter foods based on search input
  const handleFoodNameChange = (value) => {
    setMacroInput({ ...macroInput, food_name: value });
    
    if (value.trim().length > 0) {
      const filtered = recentFoods.filter(food =>
        food.food_name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredFoods(filtered);
      setShowFoodSuggestions(filtered.length > 0);
    } else {
      setFilteredFoods([]);
      setShowFoodSuggestions(false);
    }
  };

  // Select food from autocomplete
  const handleSelectFood = (food) => {
    setMacroInput({
      ...macroInput,
      food_name: food.food_name,
      protein: food.protein,
      carbs: food.carbs,
      fats: food.fats,
      quantity: food.quantity,
      unit: food.unit
    });
    setShowFoodSuggestions(false);
  };

  // Fetch product info from barcode
  const handleBarcodeDetected = async (barcode) => {
    setScanningBarcode(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/barcode/${barcode}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setScannedProduct(data.product);
        setServingSizeMultiplier(1); // Reset to 100g
        setShowBarcodeScanner(false);
        alert(`✅ Product found: ${data.product.name}`);
      } else {
        alert('❌ Product not found in database. Please enter manually.');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      alert('Error scanning barcode. Please try again.');
    } finally {
      setScanningBarcode(false);
    }
  };

  // Log scanned product
  const handleLogScannedProduct = async () => {
    if (!scannedProduct) return;
    
    // Validate serving size
    const multiplier = parseFloat(servingSizeMultiplier);
    if (!multiplier || multiplier <= 0) {
      alert('Please enter a valid serving size in grams');
      return;
    }
    
    const nutrition = scannedProduct.nutrition_per_100g;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/macros`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          food_name: scannedProduct.name,
          protein: nutrition.protein * multiplier,
          carbs: nutrition.carbs * multiplier,
          fats: nutrition.fats * multiplier,
          quantity: multiplier * 100,
          unit: 'g',
          date: new Date().toISOString()
        })
      });
      
      if (response.ok) {
        alert('✅ Food logged successfully!');
        setScannedProduct(null);
        setServingSizeMultiplier(1); // Reset to default
        fetchMacros();
        fetchRecentFoods();
      }
    } catch (error) {
      console.error('Error logging product:', error);
      alert('Error logging food');
    }
  };

  // Handle food image analysis with AI
  const handleFoodImageAnalysis = async (file) => {
    if (!file) return;
    
    setAnalyzingFood(true);
    setAnalysisResult(null);
    
    try {
      // Convert image to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        
        try {
          const response = await fetch(`${BACKEND_URL}/api/analyze-food-image`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ image: base64Data })
          });
          
          if (response.ok) {
            const result = await response.json();
            setAnalysisResult(result);
            // Pre-fill macro input with analyzed values
            if (result.nutrition) {
              setMacroInput(prev => ({
                ...prev,
                food_name: result.food_name || 'Analyzed Food',
                protein: result.nutrition.protein?.toString() || '',
                carbs: result.nutrition.carbs?.toString() || '',
                fats: result.nutrition.fats?.toString() || ''
              }));
            }
          } else {
            alert('Failed to analyze food image. Please try again or enter manually.');
          }
        } catch (error) {
          console.error('Error analyzing food:', error);
          alert('Error analyzing food. Please try again.');
        } finally {
          setAnalyzingFood(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing image:', error);
      setAnalyzingFood(false);
      alert('Error processing image');
    }
  };

  // Save meal to saved meals
  const handleSaveMeal = async () => {
    if (!mealName.trim()) {
      alert('Please enter a meal name');
      return;
    }
    
    if (!macroInput.protein && !macroInput.carbs && !macroInput.fats) {
      alert('Please enter at least one macro value');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/saved-meals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: mealName,
          food_name: macroInput.food_name || mealName,
          protein: parseFloat(macroInput.protein) || 0,
          carbs: parseFloat(macroInput.carbs) || 0,
          fats: parseFloat(macroInput.fats) || 0,
          quantity: parseFloat(macroInput.quantity) || 1,
          unit: macroInput.unit || 'serving'
        })
      });
      
      if (response.ok) {
        alert('Meal saved successfully!');
        setMealName('');
        // Refresh saved meals if there's a function for it
        if (typeof fetchSavedMeals === 'function') {
          fetchSavedMeals();
        }
      } else {
        const error = await response.json();
        alert(`Failed to save meal: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving meal:', error);
      alert('Error saving meal');
    }
  };

  // Edit food entry (adjust serving size only)
  const handleEditFood = (food) => {
    // Store original values and current quantity
    const originalQuantity = food.quantity || 100; // Default to 100g if not set
    
    setEditingFood({
      id: food.id || food._id, // Handle both id formats
      food_name: food.food_name || '',
      // Store per-unit macros (divide by original quantity to get per gram/unit)
      protein_per_unit: food.protein / originalQuantity,
      carbs_per_unit: food.carbs / originalQuantity,
      fats_per_unit: food.fats / originalQuantity,
      original_quantity: originalQuantity,
      new_quantity: originalQuantity,
      unit: food.unit || 'g'
    });
    setShowEditModal(true);
  };

  // Calculate macros based on new quantity
  const calculateAdjustedMacros = () => {
    if (!editingFood) return { protein: 0, carbs: 0, fats: 0 };
    
    const multiplier = editingFood.new_quantity / editingFood.original_quantity;
    return {
      protein: (editingFood.protein_per_unit * editingFood.new_quantity).toFixed(1),
      carbs: (editingFood.carbs_per_unit * editingFood.new_quantity).toFixed(1),
      fats: (editingFood.fats_per_unit * editingFood.new_quantity).toFixed(1)
    };
  };

  // Save edited food (with adjusted serving size)
  const handleSaveEditedFood = async () => {
    if (!editingFood) return;
    
    // Validate quantity
    const quantity = parseFloat(editingFood.new_quantity);
    if (!quantity || quantity <= 0) {
      alert('Please enter a valid serving size greater than 0');
      return;
    }
    
    const adjustedMacros = calculateAdjustedMacros();
    
    try {
      console.log('💾 Saving edited food with ID:', editingFood.id);
      
      const response = await fetch(`${BACKEND_URL}/api/macros/${editingFood.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          food_name: editingFood.food_name,
          protein: parseFloat(adjustedMacros.protein),
          carbs: parseFloat(adjustedMacros.carbs),
          fats: parseFloat(adjustedMacros.fats),
          quantity: quantity,
          unit: editingFood.unit,
          date: new Date().toISOString()
        })
      });
      
      console.log('📡 Edit response status:', response.status);
      
      if (response.ok) {
        alert('✅ Serving size updated successfully!');
        setShowEditModal(false);
        setEditingFood(null);
        fetchMacros();
      } else {
        const error = await response.json();
        console.error('❌ Edit error:', error);
        alert(`Failed to update: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating food:', error);
      alert('Error updating food');
    }
  };

  // Delete food entry
  const handleDeleteFood = async (food, foodName) => {
    // Check all possible ID field names
    const foodId = food.id || food._id || food.foodId;
    
    console.log('🗑️ Deleting food:', foodName, 'ID:', foodId);
    
    if (!foodId) {
      alert('Error: Food ID not found');
      console.error('Cannot find ID in food object:', food);
      return;
    }
    
    // Remove window.confirm() as it's blocked in sandboxed environments
    // Delete immediately (user can see visual feedback)
    
    try {
      console.log(`📤 Sending DELETE request to: ${BACKEND_URL}/api/macros/${foodId}`);
      
      const response = await fetch(`${BACKEND_URL}/api/macros/${foodId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      console.log('📡 Delete response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Delete successful:', result);
        alert(`✅ "${foodName}" deleted successfully!`);
        fetchMacros();
        // Don't refresh recent foods - keep them cached even if deleted
        // This allows users to quickly re-add foods they just deleted
      } else {
        const errorData = await response.json();
        console.error('❌ Delete error response:', errorData);
        alert(`Failed to delete: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('❌ Error deleting food:', error);
      alert('Error deleting food');
    }
  };
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordChange.newPassword !== passwordChange.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }
    
    if (passwordChange.newPassword.length < 6) {
      alert('Password must be at least 6 characters long');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_password: passwordChange.currentPassword,
          new_password: passwordChange.newPassword
        })
      });
      
      if (response.ok) {
        alert('Password changed successfully!');
        setShowPasswordModal(false);
        setPasswordChange({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error changing password. Please try again.');
    }
  };
  
  const handleExportData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/user/export-data`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Create a blob and download it
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `renzifitness-data-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        alert('Data exported successfully!');
      } else {
        alert('Failed to export data');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Error exporting data. Please try again.');
    }
  };
  
  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    
    console.log('User confirmed deletion');
    try {
      console.log('Making DELETE request to:', `${BACKEND_URL}/api/workout-templates/${templateToDelete.id}`);
      const response = await fetch(`${BACKEND_URL}/api/workout-templates/${templateToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      console.log('DELETE response status:', response.status);
      
      if (response.ok) {
        console.log('Delete successful, refreshing templates');
        await fetchWorkoutTemplates();
        alert('Template deleted successfully');
      } else {
        const error = await response.json();
        console.error('Delete failed:', error);
        alert(`Failed to delete template: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(`Error deleting template: ${error.message}`);
    } finally {
      setShowDeleteConfirmModal(false);
      setTemplateToDelete(null);
    }
  };

  const handleWeightGoalSubmit = async (e) => {
    e.preventDefault();
    if (!weightGoalInput.trim()) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/weight-goal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          target_weight: parseFloat(weightGoalInput),
          set_date: new Date().toISOString()
        })
      });

      if (response.ok) {
        const data = await response.json();
        setWeightGoal(data.weight_goal);
        setWeightGoalInput('');
        setShowWeightGoalForm(false);
        fetchWeightGoal();
      }
    } catch (error) {
      console.error('Error setting weight goal:', error);
    }
  };

  const fetchWeightGoal = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/weight-goal`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWeightGoal(data.weight_goal);
        // Also update profileGoals state so Profile tab shows current values
        if (data.weight_goal) {
          setProfileGoals(prev => ({
            ...prev,
            currentWeight: data.weight_goal.current_weight || '',
            goalWeight: data.weight_goal.target_weight || ''
          }));
        }
        // Load training preferences if available
        if (data.training_preferences) {
          setProfileGoals(prev => ({
            ...prev,
            primaryGoal: data.training_preferences.primaryGoal || prev.primaryGoal,
            height: data.training_preferences.height || prev.height,
            intensityLevel: data.training_preferences.intensityLevel || 5,
            trainingExperience: data.training_preferences.trainingExperience || '',
            daysPerWeek: data.training_preferences.daysPerWeek || '4',
            equipmentAccess: data.training_preferences.equipmentAccess || '',
            injuriesLimitations: data.training_preferences.injuriesLimitations || '',
            additionalNotes: data.training_preferences.additionalNotes || '',
            dietaryRestrictions: data.training_preferences.dietaryRestrictions || ''
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching weight goal:', error);
    }
  };

  const deleteWeightGoal = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/weight-goal`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setWeightGoal(null);
      }
    } catch (error) {
      console.error('Error deleting weight goal:', error);
    }
  };

  // Generate daily motivational quote
  const generateDailyQuote = () => {
    const quotes = [
      "Your body can do it. It's your mind that you have to convince.",
      "The only bad workout is the one that didn't happen.",
      "Don't wish for it, work for it.",
      "Success starts with self-discipline.",
      "Push yourself because no one else is going to do it for you.",
      "Great things never come from comfort zones.",
      "Dream it. Wish it. Do it.",
      "Progress not perfection.",
      "You are stronger than your excuses.",
      "Champions train, losers complain.",
      "Fitness is not about being better than someone else. It's about being better than you used to be.",
      "A healthy outside starts from the inside.",
      "Take care of your body. It's the only place you have to live.",
      "What seems impossible today will one day become your warm-up.",
      "The groundwork for all happiness is good health.",
      "Fitness is like a relationship. You can't cheat and expect it to work.",
      "Your limitation—it's only your imagination.",
      "Sometimes later becomes never. Do it now.",
      "The pain you feel today will be the strength you feel tomorrow.",
      "Believe in yourself and all that you are.",
      "You're only one workout away from a good mood.",
      "Strive for progress, not perfection.",
      "The best time to plant a tree was 20 years ago. The second best time is now.",
      "Your health is an investment, not an expense.",
      "Be stronger than your strongest excuse.",
      "Discipline is choosing between what you want now and what you want most.",
      "The hardest part is showing up.",
      "Every workout is progress.",
      "You don't have to be great to get started, but you have to get started to be great.",
      "Make yourself proud."
    ];

    // Use date as seed for consistent daily quote
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    const quoteIndex = dayOfYear % quotes.length;
    
    return quotes[quoteIndex];
  };

  const toggleGroceryItem = (id) => {
    const updatedList = groceryList.map(item => 
      item.id === id ? { ...item, completed: !item.completed } : item
    );
    setGroceryList(updatedList);
    localStorage.setItem(`grocery_list_${user?.id}`, JSON.stringify(updatedList));
  };

  const removeGroceryItem = (id) => {
    const updatedList = groceryList.filter(item => item.id !== id);
    setGroceryList(updatedList);
    localStorage.setItem(`grocery_list_${user?.id}`, JSON.stringify(updatedList));
  };

  return (
    <div className={`min-h-screen pb-32 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} id="client-dashboard">
      <style>{`
        ${isDarkMode ? `
        /* CLIENT DARK MODE: Override ALL inline color styles */
        #client-dashboard [style*="color"] {
          color: #e5e7eb !important;
        }
        
        /* CLIENT DARK MODE: Make headings and bold text white */
        #client-dashboard h1, #client-dashboard h2, #client-dashboard h3,
        #client-dashboard .font-bold, #client-dashboard .font-semibold {
          color: #ffffff !important;
        }
        
        /* CLIENT DARK MODE: Card backgrounds */
        #client-dashboard .bg-white {
          background-color: #1f2937 !important;
        }
        #client-dashboard .bg-gray-50 {
          background-color: #374151 !important;
        }
        #client-dashboard .bg-gray-100 {
          background-color: #374151 !important;
        }
        #client-dashboard .bg-gray-200 {
          background-color: #4b5563 !important;
        }
        
        /* CLIENT DARK MODE: Borders */
        #client-dashboard .border-gray-200,
        #client-dashboard .border-gray-100 {
          border-color: #374151 !important;
        }
        
        /* CLIENT DARK MODE: Keep colored buttons with white text */
        #client-dashboard .bg-blue-600,
        #client-dashboard .bg-blue-600 *,
        #client-dashboard .bg-green-600,
        #client-dashboard .bg-green-600 *,
        #client-dashboard .btn-primary,
        #client-dashboard .btn-primary * {
          color: #ffffff !important;
        }
        
        /* CLIENT DARK MODE: Daily quote */
        #client-dashboard .daily-quote,
        #client-dashboard .daily-quote * {
          color: #FFFFFF !important;
        }
        
        /* CLIENT DARK MODE: Input fields */
        #client-dashboard input,
        #client-dashboard textarea,
        #client-dashboard select {
          background-color: #374151 !important;
          color: #f3f4f6 !important;
          border-color: #4b5563 !important;
        }
        #client-dashboard input::placeholder,
        #client-dashboard textarea::placeholder {
          color: #9ca3af !important;
        }
        
        /* CLIENT DARK MODE: Table elements */
        #client-dashboard table tbody td {
          color: #e5e7eb !important;
        }
        
        /* CLIENT DARK MODE: EXCEPTIONS - Light gradient backgrounds need BLACK text */
        #client-dashboard [class*="from-green-50"],
        #client-dashboard [class*="from-green-50"] h2,
        #client-dashboard [class*="from-green-50"] p,
        #client-dashboard [class*="from-green-50"] span,
        #client-dashboard [class*="from-green-50"] div,
        #client-dashboard [class*="from-green-50"] *,
        #client-dashboard [class*="from-purple-50"],
        #client-dashboard [class*="from-purple-50"] h2,
        #client-dashboard [class*="from-purple-50"] p,
        #client-dashboard [class*="from-purple-50"] span,
        #client-dashboard [class*="from-purple-50"] div,
        #client-dashboard [class*="from-purple-50"] *,
        #client-dashboard [class*="from-blue-50"][class*="to-indigo-50"],
        #client-dashboard [class*="from-blue-50"][class*="to-indigo-50"] * {
          color: #1a1a1a !important;
        }
        /* Override inline styles in gradient sections */
        #client-dashboard [class*="from-green-50"] [style*="color"],
        #client-dashboard [class*="from-purple-50"] [style*="color"] {
          color: #1a1a1a !important;
        }
        /* Keep buttons inside gradients with white text */
        #client-dashboard [class*="from-green-50"] button,
        #client-dashboard [class*="from-green-50"] button *,
        #client-dashboard [class*="from-purple-50"] button,
        #client-dashboard [class*="from-purple-50"] button * {
          color: #ffffff !important;
        }
        /* Keep badge text colors */
        #client-dashboard .bg-purple-100 span,
        #client-dashboard .bg-green-100 span {
          color: inherit !important;
        }
        ` : `
        /* LIGHT MODE: Daily quote - white text */
        #client-dashboard .daily-quote,
        #client-dashboard .daily-quote * {
          color: #FFFFFF !important;
        }
        /* LIGHT MODE: Selected day in calendar - white text */
        #client-dashboard .my-workouts-area .bg-blue-600,
        #client-dashboard .my-workouts-area .bg-blue-600 * {
          color: #FFFFFF !important;
        }
        `}
      `}</style>
      {/* Header - with safe area padding for iOS notch */}
      <header className={`shadow-sm border-b ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="container mx-auto px-3 sm:px-6 py-2 sm:py-3 flex justify-between items-center">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full overflow-hidden border-2 shadow-lg ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-white'}`}>
              <img 
                src="https://customer-assets.emergentagent.com/job_renzifitness/artifacts/oeuw8yx5_White%20Background.png"
                alt="RenziFitness Logo"
                className="w-full h-full object-cover scale-125"
              />
            </div>
            <h1 className={`text-base sm:text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>RenziFitness</h1>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <span className={`hidden sm:inline text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Hi, {user?.name}</span>
            <button
              onClick={logout}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-lg border ${isDarkMode ? 'border-gray-600 text-white hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Welcome Section */}
            <div className={`rounded-2xl shadow-lg p-4 sm:p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="text-center">
                <h1 className={`text-xl sm:text-2xl font-bold mb-1 sm:mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Welcome back, {user?.name || 'there'}!
                </h1>
                <p className={`text-sm sm:text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {new Date().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
            </div>

            <ProgressReportCard isDarkMode={isDarkMode} />

            {/* Daily Motivational Quote */}
            <div className="daily-quote bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl shadow-lg p-4 sm:p-6 text-white">
              <div className="text-center">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 sm:mb-3 opacity-80" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
                </svg>
                <p className="text-base sm:text-lg font-medium mb-1 sm:mb-2">Daily Motivation</p>
                <p className="text-lg sm:text-xl font-light italic leading-relaxed">
                  "{dailyQuote}"
                </p>
              </div>
            </div>

            {/* Workout Streak Counter */}
            <div className={`rounded-2xl shadow-lg p-4 sm:p-6 ${
              workoutStreak.current_streak > 0 
                ? 'bg-gradient-to-r from-orange-500 to-red-500' 
                : isDarkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 sm:space-x-4">
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center ${
                    workoutStreak.current_streak > 0 
                      ? 'bg-white/20' 
                      : isDarkMode ? 'bg-gray-700' : 'bg-orange-100'
                  }`}>
                    <span className="text-2xl sm:text-3xl">🔥</span>
                  </div>
                  <div>
                    <div className={`text-3xl sm:text-4xl font-bold ${
                      workoutStreak.current_streak > 0 ? 'text-white' : isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {workoutStreak.current_streak}
                    </div>
                    <div className={`text-sm sm:text-base ${
                      workoutStreak.current_streak > 0 ? 'text-white/80' : isDarkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      Day Streak
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-xs sm:text-sm ${
                    workoutStreak.current_streak > 0 ? 'text-white/70' : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Best: {workoutStreak.longest_streak} {workoutStreak.longest_streak === 1 ? 'day' : 'days'}
                  </div>
                  <div className={`text-xs sm:text-sm ${
                    workoutStreak.current_streak > 0 ? 'text-white/70' : isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Total: {workoutStreak.total_workouts} workouts
                  </div>
                </div>
              </div>
              {workoutStreak.current_streak === 0 && (
                <p className={`mt-3 text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Complete a workout today to start your streak! 💪
                </p>
              )}
              {workoutStreak.current_streak >= 7 && (
                <div className="mt-3 text-center">
                  <span className="inline-block px-3 py-1 rounded-full bg-white/20 text-white text-sm font-medium">
                    🏆 {workoutStreak.current_streak >= 30 ? 'Monthly Champion!' : workoutStreak.current_streak >= 14 ? 'Two Week Warrior!' : 'Week Warrior!'}
                  </span>
                </div>
              )}
            </div>

            <div>
            {/* Weight Goal Progress */}
            {weightGoal && (
              <div className={`rounded-2xl shadow-lg p-4 sm:p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <h2 className={`text-lg sm:text-xl font-semibold mb-3 sm:mb-4 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                  Weight Goal Progress
                </h2>
                
                {weightData.length > 0 ? (
                  <div>
                    <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center mb-3 sm:mb-4">
                      <div>
                        <div className={`text-lg sm:text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {weightData[0]?.weight || '--'} lbs
                        </div>
                        <div className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Current</div>
                      </div>
                      <div>
                        <div className="text-lg sm:text-2xl font-bold text-blue-600">
                          {weightGoal.target_weight} lbs
                        </div>
                        <div className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Goal</div>
                      </div>
                      <div>
                        <div className={`text-lg sm:text-2xl font-bold ${
                          weightData[0]?.weight 
                            ? weightData[0].weight > weightGoal.target_weight
                              ? 'text-red-600'
                              : 'text-green-600'
                            : 'text-gray-900'
                        }`}>
                          {weightData[0]?.weight 
                            ? Math.abs(weightData[0].weight - weightGoal.target_weight).toFixed(1)
                            : '--'
                          } lbs
                        </div>
                        <div className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {weightData[0]?.weight 
                            ? weightData[0].weight > weightGoal.target_weight 
                              ? 'To Lose'
                              : 'To Gain'
                            : 'Remaining'
                          }
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mt-3 sm:mt-4">
                      <div className={`flex items-center justify-between text-xs sm:text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <span>Progress</span>
                        <span>
                          {weightData[0]?.weight === weightGoal.target_weight 
                            ? '🎉 Goal Achieved!'
                            : `${Math.abs(weightData[0].weight - weightGoal.target_weight).toFixed(1)} lbs ${
                                weightData[0].weight > weightGoal.target_weight ? 'to lose' : 'to gain'
                              }`
                          }
                        </span>
                      </div>
                      <div className={`w-full rounded-full h-3 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                        <div 
                          className={`h-3 rounded-full transition-all duration-300 ${
                            weightData[0]?.weight === weightGoal.target_weight 
                              ? 'bg-green-500'
                              : 'bg-blue-600'
                          }`}
                          style={{ 
                            width: `${Math.min(
                              Math.abs(
                                ((weightData[0]?.weight - (weightData[1]?.weight || weightData[0].weight)) / 
                                 Math.abs(weightGoal.target_weight - (weightData[1]?.weight || weightData[0].weight))) * 100
                              ), 100
                            )}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className=" mb-3" style={{color: "#2d3748"}}>Start logging your weight to track progress!</p>
                    <button
                      onClick={() => setActiveTab('progress')}
                      className="btn-primary px-6 py-2"
                    >
                      Log Weight
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* Today's Workout Preview */}
            <div className={`todays-workout-section rounded-2xl shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h2 className={`text-xl font-semibold mb-4 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <svg className="w-6 h-6 mr-2 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Today's Workout
              </h2>
              
              {(() => {
                const today = new Date().toISOString().split('T')[0];
                const todayWorkout = workoutSessions.find(session => 
                  session.scheduled_date && 
                  !session.scheduled_date.includes('2099') &&
                  session.scheduled_date.split('T')[0] === today
                );
                
                return todayWorkout ? (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className={`text-lg font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{todayWorkout.name}</h3>
                        <div className={`flex items-center space-x-4 text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <span>{todayWorkout.exercises?.length || 0} exercises</span>
                          <span>~{todayWorkout.estimated_duration || 45} min</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            todayWorkout.status === 'completed' 
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {todayWorkout.status === 'completed' ? 'Completed' : 'Ready'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Exercise Preview */}
                    {todayWorkout.exercises && todayWorkout.exercises.length > 0 && (
                      <div className="space-y-2 mb-4">
                        {todayWorkout.exercises.slice(0, 3).map((exercise, index) => (
                          <div key={index} className={`flex items-center space-x-3 p-2 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-blue-600">{index + 1}</span>
                            </div>
                            <div className="flex-1">
                              <div className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{exercise.name}</div>
                              <div className={`text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {getSetsCount(exercise.sets)} sets × {getRepsDisplay(exercise)} reps
                              </div>
                            </div>
                          </div>
                        ))}
                        {todayWorkout.exercises.length > 3 && (
                          <div className="text-center text-sm  py-1" style={{color: "#2d3748"}}>
                            +{todayWorkout.exercises.length - 3} more exercises
                          </div>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => setActiveTab('workouts')}
                      className={`w-full py-3 rounded-lg font-medium transition-colors ${
                        todayWorkout.status === 'completed'
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      {todayWorkout.status === 'completed' ? 'View Completed Workout' : todayWorkout.status === 'in_progress' ? 'Continue Workout' : 'Start Workout'}
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium  mb-2" style={{color: "#1a1a1a"}}>No Workout Today</h3>
                    <p className=" mb-4" style={{color: "#2d3748"}}>You don't have a workout scheduled for today.</p>
                    <button
                      onClick={() => setActiveTab('workouts')}
                      className="btn-primary px-6 py-2"
                    >
                      View Workouts
                    </button>
                  </div>
                );
              })()}
            </div>

            {/* Nutrition Overview */}
            <div className="todays-nutrition-section bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-semibold  mb-4 flex items-center" style={{color: "#1a1a1a"}}>
                <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Today's Nutrition
              </h2>
              
{(() => {
                const todaysMacros = getTodaysMacros();
                return todaysMacros.length > 0 ? (
                  <div>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      {/* Protein */}
                      <div className="text-center">
                        <div className="text-lg font-bold text-blue-600 mb-1">
                          {Math.round((todaysMacros.reduce((sum, log) => sum + log.protein, 0) / macroTargets.daily_protein) * 100)}%
                        </div>
                        <div className="text-sm font-medium  mb-2" style={{color: "#2d3748"}}>Protein</div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                            style={{ 
                              width: `${Math.min((todaysMacros.reduce((sum, log) => sum + log.protein, 0) / macroTargets.daily_protein) * 100, 100)}%` 
                            }}
                          ></div>
                        </div>
                        <div className="text-xs  mt-1" style={{color: "#2d3748"}}>
                          {todaysMacros.reduce((sum, log) => sum + log.protein, 0).toFixed(1)}g / {macroTargets.daily_protein}g
                        </div>
                      </div>

                      {/* Carbs */}
                      <div className="text-center">
                        <div className="text-lg font-bold text-purple-600 mb-1">
                          {Math.round((todaysMacros.reduce((sum, log) => sum + log.carbs, 0) / macroTargets.daily_carbs) * 100)}%
                        </div>
                        <div className="text-sm font-medium  mb-2" style={{color: "#2d3748"}}>Carbs</div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full transition-all duration-300" 
                            style={{ 
                              width: `${Math.min((todaysMacros.reduce((sum, log) => sum + log.carbs, 0) / macroTargets.daily_carbs) * 100, 100)}%` 
                            }}
                          ></div>
                        </div>
                        <div className="text-xs  mt-1" style={{color: "#2d3748"}}>
                          {todaysMacros.reduce((sum, log) => sum + log.carbs, 0).toFixed(1)}g / {macroTargets.daily_carbs}g
                        </div>
                      </div>

                      {/* Fats */}
                      <div className="text-center">
                        <div className="text-lg font-bold text-yellow-600 mb-1">
                          {Math.round((todaysMacros.reduce((sum, log) => sum + log.fats, 0) / macroTargets.daily_fats) * 100)}%
                        </div>
                        <div className="text-sm font-medium  mb-2" style={{color: "#2d3748"}}>Fats</div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-yellow-600 h-2 rounded-full transition-all duration-300" 
                            style={{ 
                              width: `${Math.min((todaysMacros.reduce((sum, log) => sum + log.fats, 0) / macroTargets.daily_fats) * 100, 100)}%` 
                            }}
                          ></div>
                        </div>
                        <div className="text-xs  mt-1" style={{color: "#2d3748"}}>
                          {todaysMacros.reduce((sum, log) => sum + log.fats, 0).toFixed(1)}g / {macroTargets.daily_fats}g
                        </div>
                      </div>
                    </div>

                    {/* Calories Summary */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold  mb-1" style={{color: "#1a1a1a"}}>
                          {todaysMacros.reduce((sum, log) => sum + log.calories, 0).toFixed(0)} / {macroTargets.daily_calories}
                        </div>
                        <div className="text-sm  mb-2" style={{color: "#2d3748"}}>Calories Today</div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-blue-500 h-3 rounded-full transition-all duration-300" 
                            style={{ 
                              width: `${Math.min((todaysMacros.reduce((sum, log) => sum + log.calories, 0) / macroTargets.daily_calories) * 100, 100)}%` 
                            }}
                          ></div>
                        </div>
                        <div className="text-xs  mt-1" style={{color: "#2d3748"}}>
                          {Math.max(0, macroTargets.daily_calories - todaysMacros.reduce((sum, log) => sum + log.calories, 0)).toFixed(0)} calories remaining
                        </div>
                      </div>
                    </div>

                  <button
                    onClick={() => setActiveTab('nutrition')}
                    className="w-full py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors font-medium"
                  >
                    Log More Food
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium  mb-2" style={{color: "#1a1a1a"}}>No Food Logged Today</h3>
                  <p className=" mb-4" style={{color: "#2d3748"}}>Start tracking your nutrition to stay on target.</p>
                  <button
                    onClick={() => setActiveTab('nutrition')}
                    className="btn-primary px-6 py-2"
                  >
                    Log Food
                  </button>
                </div>
              );
              })()}
            </div>

            {/* Quick Actions */}
            <div className="quick-actions bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-semibold  mb-4" style={{color: "#1a1a1a"}}>Quick Actions</h2>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => setActiveTab('progress')}
                  className="flex flex-col items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <svg className="w-8 h-8 text-blue-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span className="text-sm font-medium text-blue-700">Log Weight</span>
                </button>

                <button
                  onClick={() => setActiveTab('nutrition')}
                  className="flex flex-col items-center p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <svg className="w-8 h-8 text-green-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span className="text-sm font-medium text-green-700">Add Meal</span>
                </button>

                {!weightGoal && (
                  <button
                    onClick={() => setActiveTab('progress')}
                    className="flex flex-col items-center p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                  >
                    <svg className="w-8 h-8 text-purple-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    <span className="text-sm font-medium text-purple-700">Set Goal</span>
                  </button>
                )}

                <button
                  onClick={() => setActiveTab('messages')}
                  className="flex flex-col items-center p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors"
                >
                  <svg className="w-8 h-8 text-orange-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <span className="text-sm font-medium text-orange-700">Message Trainer</span>
                </button>
              </div>
            </div>
          </div>
          </div>
        )}
        
        {activeTab === 'workouts' && (
          <div className="space-y-8">
            {/* Active Plan Overview Banner */}
            {activePlan && (
              <div className={`rounded-xl shadow-lg p-4 ${isDarkMode ? 'bg-gradient-to-r from-blue-900 to-indigo-900' : 'bg-gradient-to-r from-blue-500 to-indigo-600'}`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-white">
                    <h3 className="text-lg font-bold">Your 4-Week Training Plan</h3>
                    <p className="text-sm text-blue-100">
                      {activePlan.weeks_remaining !== null 
                        ? `${activePlan.weeks_remaining} week${activePlan.weeks_remaining !== 1 ? 's' : ''} remaining` 
                        : 'Active plan'}
                      {activePlan.plan_end_date && ` • Ends ${new Date(activePlan.plan_end_date).toLocaleDateString()}`}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowPlanOverview(true)}
                    className="px-4 py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 text-sm"
                    data-testid="view-full-plan-btn"
                  >
                    View Full Plan
                  </button>
                </div>
              </div>
            )}

            {/* Workout Calendar View */}
            <ClientDailyWorkoutView
              workoutSessions={workoutSessions}
              onStartWorkout={(session) => {
                try {
                  console.log('[Dashboard] Starting workout session:', session?.id, session?.name);
                  
                  if (!session || !session.id) {
                    console.error('[Dashboard] Invalid session data');
                    alert('Unable to start this workout. Please refresh and try again.');
                    return;
                  }
                  
                  const exercises = session.exercises || [];
                  console.log('[Dashboard] Session has', exercises.length, 'exercises');
                  
                  const convertedExercises = convertAIExercisesToWorkoutFormat(exercises);
                  console.log('[Dashboard] Converted to', convertedExercises.length, 'exercises');
                  
                  setCurrentWorkout({
                    id: session.id,
                    name: session.name || 'Workout',
                    exercises: convertedExercises,
                    workout_notes: session.workout_notes || ''
                  });
                  updateWorkoutSession(session.id, { status: 'in_progress' });
                } catch (error) {
                  console.error('[Dashboard] Error starting workout:', error);
                  alert('Error starting workout. Please try again.');
                }
              }}
              backendUrl={BACKEND_URL}
              isDarkMode={isDarkMode}
              onRefresh={fetchWorkoutSessions}
              clientEquipment={profileGoals.equipmentAccess}
            />

            {/* Quick Start Section */}
            {!currentWorkout && workouts.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
                <h2 className="heading-4 dark:text-white mb-6" style={{color: "#1a1a1a"}}>Available Templates</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {workouts.slice(0, 4).map(workout => (
                        <div key={workout.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium " style={{color: "#1a1a1a"}}>{workout.name}</h4>
                              <p className="text-sm  mt-1" style={{color: "#2d3748"}}>
                                {workout.exercises.length} exercises
                              </p>
                            </div>
                            <button
                              onClick={() => {
                                const convertedExercises = convertAIExercisesToWorkoutFormat(workout.exercises || []);
                                const sessionData = {
                                  name: workout.name,
                                  exercises: convertedExercises,
                                  scheduled_date: new Date().toISOString(),
                                  template_id: workout.id
                                };
                                createWorkoutSession(sessionData).then(result => {
                                  if (result) {
                                    setCurrentWorkout({
                                      id: result.session_id,
                                      name: workout.name,
                                      exercises: convertedExercises,
                                      workout_notes: ''
                                    });
                                  }
                                });
                              }}
                              className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm"
                            >
                              Start
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                {/* Recent Completed Workouts */}
                {workoutSessions.filter(session => session.status === 'completed').length > 0 && (
                  <div className="mt-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Workouts</h3>
                    <div className="space-y-3">
                      {workoutSessions
                        .filter(session => session.status === 'completed')
                        .slice(0, 3)
                        .map(session => (
                          <div key={session.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium " style={{color: "#1a1a1a"}}>{session.name}</p>
                              <p className="text-sm " style={{color: "#2d3748"}}>
                                {new Date(session.completed_at).toLocaleDateString()} •
                                {session.total_duration && ` ${Math.round(session.total_duration / 60)}min`}
                                {session.total_volume && ` • ${Math.round(session.total_volume)}kg volume`}
                              </p>
                            </div>
                            <button 
                              onClick={() => {
                                const convertedExercises = convertAIExercisesToWorkoutFormat(session.exercises || []);
                                const sessionData = {
                                  name: `${session.name} (Repeat)`,
                                  exercises: convertedExercises,
                                  scheduled_date: new Date().toISOString()
                                };
                                createWorkoutSession(sessionData).then(result => {
                                  if (result) {
                                    setCurrentWorkout({
                                      id: result.session_id,
                                      name: sessionData.name,
                                      exercises: convertedExercises,
                                      workout_notes: ''
                                    });
                                  }
                                });
                              }}
                              className="text-blue-600 hover:text-blue-800 text-sm"
                            >
                              Repeat
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Full Plan Overview Modal */}
            {showPlanOverview && activePlan && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
                <div className={`rounded-2xl shadow-2xl w-full max-w-4xl my-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`} data-testid="plan-overview-modal">
                  {/* Header */}
                  <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Your 4-Week Training Plan
                        </h3>
                        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {activePlan.plan_start_date && `Started ${new Date(activePlan.plan_start_date).toLocaleDateString()}`}
                          {activePlan.plan_end_date && ` • Ends ${new Date(activePlan.plan_end_date).toLocaleDateString()}`}
                        </p>
                      </div>
                      <button
                        onClick={() => setShowPlanOverview(false)}
                        className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 max-h-[70vh] overflow-y-auto">
                    {/* Plan Summary */}
                    <div className={`rounded-xl p-4 mb-6 ${isDarkMode ? 'bg-gradient-to-r from-blue-900 to-indigo-900' : 'bg-gradient-to-r from-blue-500 to-indigo-600'}`}>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-white">
                        <div>
                          <p className="text-blue-200 text-xs">Duration</p>
                          <p className="text-lg font-bold">{activePlan.duration_weeks || 4} Weeks</p>
                        </div>
                        <div>
                          <p className="text-blue-200 text-xs">Weeks Remaining</p>
                          <p className="text-lg font-bold">{activePlan.weeks_remaining ?? 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-blue-200 text-xs">Workouts/Week</p>
                          <p className="text-lg font-bold">{activePlan.workout_plan?.days?.length || 0}</p>
                        </div>
                        <div>
                          <p className="text-blue-200 text-xs">Total Exercises</p>
                          <p className="text-lg font-bold">
                            {activePlan.workout_plan?.days?.reduce((sum, day) => sum + (day.exercises?.length || 0), 0) || 0}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Workout Days */}
                    <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Weekly Workout Schedule
                    </h4>
                    <div className="space-y-4">
                      {activePlan.workout_plan?.days?.map((day, idx) => (
                        <div 
                          key={idx} 
                          className={`border rounded-xl overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
                          data-testid={`workout-day-${idx + 1}`}
                        >
                          {/* Day Header */}
                          <div className={`p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <h5 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                  {day.day_name || `Day ${idx + 1}`}
                                </h5>
                                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {day.target_muscles?.join(', ') || 'Full Body'}
                                </p>
                              </div>
                              <span className={`text-sm px-3 py-1 rounded-full ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                                {day.exercises?.length || 0} exercises
                              </span>
                            </div>
                          </div>

                          {/* Exercises List */}
                          <div className="p-4 space-y-3">
                            {(() => {
                              // Group superset exercises together
                              const exercises = day.exercises || [];
                              const renderedExercises = [];
                              let exerciseNumber = 1;
                              
                              for (let exIdx = 0; exIdx < exercises.length; exIdx++) {
                                const exercise = exercises[exIdx];
                                const exerciseName = exercise.name || '';
                                
                                // Check if this is a superset (contains "+", "SS", or "Superset" in name)
                                const isSuperset = exerciseName.includes('+') || 
                                                   / SS /i.test(exerciseName) ||
                                                   exerciseName.toLowerCase().includes('superset') ||
                                                   exercise.superset_with;
                                
                                if (isSuperset) {
                                  // Parse superset exercises (e.g., "Superset: Exercise A + Exercise B" or "Exercise A SS Exercise B")
                                  let supersetExercises = [];
                                  
                                  // Determine separator: + or SS
                                  const hasSSIndicator = / SS /i.test(exerciseName);
                                  const separator = hasSSIndicator ? / SS /i : '+';
                                  
                                  if (exerciseName.includes('+') || hasSSIndicator) {
                                    // Remove "Superset:" or "Superset X:" prefix and "(Superset)" suffix
                                    let cleanName = exerciseName
                                      .replace(/^Superset\s*\d*:\s*/i, '')
                                      .replace(/\s*\(Superset\)/i, '')
                                      .trim();
                                    const parts = cleanName.split(separator).map(s => s.trim());
                                    supersetExercises = parts.map((name, i) => ({
                                      name: name,
                                      sets: exercise.sets,
                                      reps: exercise.reps?.includes?.(',') ? exercise.reps.split(',')[i]?.trim() : exercise.reps,
                                      notes: exercise.notes
                                    }));
                                  } else {
                                    supersetExercises = [{ 
                                      name: exerciseName.replace(/^Superset:\s*/i, ''),
                                      sets: exercise.sets,
                                      reps: exercise.reps,
                                      notes: exercise.notes
                                    }];
                                  }
                                  
                                  renderedExercises.push(
                                    <div 
                                      key={exIdx} 
                                      className={`rounded-xl overflow-hidden border-2 ${isDarkMode ? 'border-purple-700 bg-purple-900/20' : 'border-purple-300 bg-purple-50'}`}
                                    >
                                      {/* Superset Header */}
                                      <div className={`px-3 py-2 flex items-center gap-2 ${isDarkMode ? 'bg-purple-900/40' : 'bg-purple-100'}`}>
                                        <span className={`w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${isDarkMode ? 'bg-purple-700 text-purple-200' : 'bg-purple-200 text-purple-700'}`}>
                                          {exerciseNumber}
                                        </span>
                                        <span className={`text-xs font-bold uppercase tracking-wide ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                                          ⚡ Superset
                                        </span>
                                        <span className={`text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                          No rest between exercises
                                        </span>
                                      </div>
                                      
                                      {/* Superset Exercises */}
                                      <div className="p-3 space-y-2">
                                        {supersetExercises.map((subEx, subIdx) => (
                                          <div 
                                            key={subIdx}
                                            className={`flex items-start gap-3 p-2 rounded-lg ${isDarkMode ? 'bg-gray-800/50' : 'bg-white'}`}
                                          >
                                            <span className={`w-5 h-5 flex-shrink-0 rounded flex items-center justify-center text-xs font-medium ${isDarkMode ? 'bg-purple-800 text-purple-300' : 'bg-purple-100 text-purple-600'}`}>
                                              {String.fromCharCode(65 + subIdx)}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                              <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                                {subEx.name}
                                              </p>
                                              <div className="flex flex-wrap gap-1.5 mt-1">
                                                {subEx.sets && (
                                                  <span className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                                    {subEx.sets} sets
                                                  </span>
                                                )}
                                                {subEx.reps && (
                                                  <span className={`text-xs px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-green-400' : 'bg-green-50 text-green-600'}`}>
                                                    {subEx.reps} reps
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      
                                      {/* Rest and Notes */}
                                      <div className={`px-3 py-2 border-t ${isDarkMode ? 'border-purple-800 bg-gray-800/30' : 'border-purple-200 bg-gray-50'}`}>
                                        <div className="flex items-center gap-3">
                                          <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-orange-900/50 text-orange-400' : 'bg-orange-50 text-orange-600'}`}>
                                            {exercise.rest_seconds || 90}s rest after superset
                                          </span>
                                        </div>
                                        {exercise.notes && (
                                          <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {exercise.notes}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                } else {
                                  // Regular exercise
                                  renderedExercises.push(
                                    <div 
                                      key={exIdx} 
                                      className={`flex items-start gap-3 p-3 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}
                                    >
                                      <span className={`w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-sm font-bold ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                                        {exerciseNumber}
                                      </span>
                                      <div className="flex-1 min-w-0">
                                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                          {exercise.name}
                                        </p>
                                        <div className="flex flex-wrap gap-2 mt-1">
                                          <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                            {getSetsCount(exercise.sets)} sets
                                          </span>
                                          <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-green-400' : 'bg-green-50 text-green-600'}`}>
                                            {getRepsDisplay(exercise)} reps
                                          </span>
                                          <span className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-orange-400' : 'bg-orange-50 text-orange-600'}`}>
                                            {exercise.rest_seconds}s rest
                                          </span>
                                        </div>
                                        {exercise.notes && (
                                          <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {exercise.notes}
                                          </p>
                                        )}
                                      </div>
                                      {exercise.video_url && (
                                        <a
                                          href={exercise.video_url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-blue-500 hover:text-blue-600 flex-shrink-0"
                                        >
                                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                          </svg>
                                        </a>
                                      )}
                                    </div>
                                  );
                                }
                                exerciseNumber++;
                              }
                              
                              return renderedExercises;
                            })()}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Diet Plan Summary */}
                    {activePlan.diet_plan && (
                      <div className="mt-6">
                        <h4 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Nutrition Targets
                        </h4>
                        <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-green-600'}`}>Daily Protein</p>
                              <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {activePlan.diet_plan.macro_adjustments?.daily_protein || 0}g
                              </p>
                            </div>
                            <div>
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-green-600'}`}>Daily Carbs</p>
                              <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {activePlan.diet_plan.macro_adjustments?.daily_carbs || 0}g
                              </p>
                            </div>
                            <div>
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-green-600'}`}>Daily Fats</p>
                              <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {activePlan.diet_plan.macro_adjustments?.daily_fats || 0}g
                              </p>
                            </div>
                            <div>
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-green-600'}`}>Daily Calories</p>
                              <p className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {activePlan.diet_plan.macro_adjustments?.daily_calories || 0}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className={`p-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <button
                      onClick={() => setShowPlanOverview(false)}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                    >
                      Got It
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Barcode Scanner Modal */}
        {showBarcodeScanner && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold " style={{color: "#1a1a1a"}}>Scan Barcode</h3>
                  <button
                    onClick={() => setShowBarcodeScanner(false)}
                    className="text-gray-400 hover:" style={{color: "#2d3748"}}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div id="barcode-reader" className="w-full"></div>
                {scanningBarcode && (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 " style={{color: "#2d3748"}}>Fetching product info...</p>
                  </div>
                )}
                <p className="text-sm  mt-4 text-center" style={{color: "#2d3748"}}>
                  Position the barcode within the frame to scan
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Edit Food Modal - Adjust Serving Size */}
        {showEditModal && editingFood && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold " style={{color: "#1a1a1a"}}>Adjust Serving Size</h3>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingFood(null);
                    }}
                    className="text-gray-400 hover:" style={{color: "#2d3748"}}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-4">
                {/* Food Name (Read-only) */}
                <div>
                  <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>Food</label>
                  <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 font-medium">
                    {editingFood.food_name}
                  </div>
                </div>

                {/* Serving Size Adjustment */}
                <div>
                  <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>
                    Serving Size ({editingFood.unit})
                  </label>
                  <div className="flex items-center space-x-3">
                    <button
                      type="button"
                      onClick={() => setEditingFood({...editingFood, new_quantity: Math.max(1, editingFood.new_quantity - 10)})}
                      className="px-3 py-2 bg-gray-200  rounded-lg hover:bg-gray-300" style={{color: "#2d3748"}}
                    >
                      -10
                    </button>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={editingFood.new_quantity || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string or valid numbers
                        if (value === '' || !isNaN(value)) {
                          setEditingFood({...editingFood, new_quantity: value === '' ? '' : parseFloat(value)});
                        }
                      }}
                      onBlur={(e) => {
                        // If empty or invalid on blur, set to 1
                        if (e.target.value === '' || parseFloat(e.target.value) <= 0) {
                          setEditingFood({...editingFood, new_quantity: 1});
                        }
                      }}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center text-lg font-semibold"
                      placeholder="Enter grams"
                    />
                    <button
                      type="button"
                      onClick={() => setEditingFood({...editingFood, new_quantity: (editingFood.new_quantity || 0) + 10})}
                      className="px-3 py-2 bg-gray-200  rounded-lg hover:bg-gray-300" style={{color: "#2d3748"}}
                    >
                      +10
                    </button>
                  </div>
                  <p className="text-xs  mt-2 text-center" style={{color: "#2d3748"}}>
                    Original: {editingFood.original_quantity}{editingFood.unit}
                  </p>
                </div>

                {/* Auto-calculated Macros Preview */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <h5 className="font-semibold text-gray-900 mb-3 text-center">Updated Nutrition Facts</h5>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div>
                      <p className="text-xs " style={{color: "#2d3748"}}>Protein</p>
                      <p className="text-lg font-bold text-gray-900">{calculateAdjustedMacros().protein}g</p>
                    </div>
                    <div>
                      <p className="text-xs " style={{color: "#2d3748"}}>Carbs</p>
                      <p className="text-lg font-bold text-gray-900">{calculateAdjustedMacros().carbs}g</p>
                    </div>
                    <div>
                      <p className="text-xs " style={{color: "#2d3748"}}>Fats</p>
                      <p className="text-lg font-bold text-gray-900">{calculateAdjustedMacros().fats}g</p>
                    </div>
                  </div>
                  <p className="text-xs text-center  mt-2" style={{color: "#2d3748"}}>
                    Macros automatically calculated based on serving size
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setEditingFood(null);
                    }}
                    className="px-4 py-2 bg-gray-200  rounded-lg hover:bg-gray-300" style={{color: "#2d3748"}}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEditedFood}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Update Serving Size</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Scanned Product Display Modal */}
        {scannedProduct && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold " style={{color: "#1a1a1a"}}>Product Information</h3>
                  <button
                    onClick={() => setScannedProduct(null)}
                    className="text-gray-400 hover:" style={{color: "#2d3748"}}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {/* Product Image and Name */}
                <div className="flex items-start space-x-4 mb-6">
                  {scannedProduct.image_url && (
                    <img 
                      src={scannedProduct.image_url} 
                      alt={scannedProduct.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900">{scannedProduct.name}</h4>
                    {scannedProduct.brand && (
                      <p className="text-sm " style={{color: "#2d3748"}}>{scannedProduct.brand}</p>
                    )}
                    <p className="text-xs  mt-1" style={{color: "#2d3748"}}>Barcode: {scannedProduct.barcode}</p>
                  </div>
                </div>

                {/* Serving Size Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>
                    Serving Size (grams)
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={servingSizeMultiplier === '' ? '' : (servingSizeMultiplier * 100).toFixed(0)}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string or valid numbers
                        if (value === '') {
                          setServingSizeMultiplier('');
                        } else if (!isNaN(value)) {
                          // Convert grams to multiplier (divide by 100)
                          setServingSizeMultiplier(parseFloat(value) / 100);
                        }
                      }}
                      onBlur={(e) => {
                        // If empty or invalid on blur, set to 100g (multiplier 1)
                        if (e.target.value === '' || parseFloat(e.target.value) <= 0) {
                          setServingSizeMultiplier(1);
                        }
                      }}
                      className="w-32 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center text-lg font-semibold"
                      placeholder="100"
                    />
                    <span className=" font-medium" style={{color: "#2d3748"}}>grams</span>
                  </div>
                  <p className="text-xs  mt-2" style={{color: "#2d3748"}}>Enter total grams (nutrition is per 100g)</p>
                </div>

                {/* Nutrition Facts */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h5 className="font-semibold text-gray-900 mb-3">Nutrition Facts (per {(servingSizeMultiplier * 100).toFixed(0)}g)</h5>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex justify-between">
                      <span className="" style={{color: "#2d3748"}}>Calories:</span>
                      <span className="font-semibold">{(scannedProduct.nutrition_per_100g.calories * servingSizeMultiplier).toFixed(0)} kcal</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="" style={{color: "#2d3748"}}>Protein:</span>
                      <span className="font-semibold">{(scannedProduct.nutrition_per_100g.protein * servingSizeMultiplier).toFixed(1)}g</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="" style={{color: "#2d3748"}}>Carbs:</span>
                      <span className="font-semibold">{(scannedProduct.nutrition_per_100g.carbs * servingSizeMultiplier).toFixed(1)}g</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="" style={{color: "#2d3748"}}>Fats:</span>
                      <span className="font-semibold">{(scannedProduct.nutrition_per_100g.fats * servingSizeMultiplier).toFixed(1)}g</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="" style={{color: "#2d3748"}}>Fiber:</span>
                      <span className="font-semibold">{(scannedProduct.nutrition_per_100g.fiber * servingSizeMultiplier).toFixed(1)}g</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="" style={{color: "#2d3748"}}>Sugar:</span>
                      <span className="font-semibold">{(scannedProduct.nutrition_per_100g.sugar * servingSizeMultiplier).toFixed(1)}g</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-3">
                  <button
                    onClick={() => setScannedProduct(null)}
                    className="px-4 py-2 bg-gray-200  rounded-lg hover:bg-gray-300" style={{color: "#2d3748"}}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLogScannedProduct}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Log Food</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {(() => {
                const todaysMacros = getTodaysMacros();
                return activeTab === 'nutrition' && (
                  <div className="nutrition-tab-content space-y-8">
                    {/* Macro Logging Section */}
                    <div className="bg-white rounded-2xl shadow-lg p-6">
                      <div className="flex justify-between items-center mb-6">
                        <h2 className="heading-4">Daily Targets & Progress</h2>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                          {/* Protein Progress */}
                          <div className="text-center">
                            <div className="text-2xl font-bold  mb-2" style={{color: "#1a1a1a"}}>
                              {Math.round((todaysMacros.reduce((sum, log) => sum + log.protein, 0) / macroTargets.daily_protein) * 100)}%
                            </div>
                            <div className="text-sm font-medium  mb-2" style={{color: "#1a1a1a"}}>Protein</div>
                            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                              <div 
                                className="bg-blue-600 h-3 rounded-full transition-all duration-300" 
                                style={{ 
                                  width: `${Math.min((todaysMacros.reduce((sum, log) => sum + log.protein, 0) / macroTargets.daily_protein) * 100, 100)}%` 
                                }}
                              ></div>
                            </div>
                            <div className={`text-xs ${isDarkMode ? 'text-white' : 'text-black'}`}>
                              {todaysMacros.reduce((sum, log) => sum + log.protein, 0).toFixed(1)}g / {macroTargets.daily_protein}g
                            </div>
                          </div>

                          {/* Carbs Progress */}
                          <div className="text-center">
                            <div className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {Math.round((todaysMacros.reduce((sum, log) => sum + log.carbs, 0) / macroTargets.daily_carbs) * 100)}%
                            </div>
                            <div className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Carbs</div>
                            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                              <div 
                                className="h-3 rounded-full transition-all duration-300" 
                                style={{ 
                                  backgroundColor: '#010eda',
                                  width: `${Math.min((todaysMacros.reduce((sum, log) => sum + log.carbs, 0) / macroTargets.daily_carbs) * 100, 100)}%` 
                                }}
                              ></div>
                            </div>
                            <div className={`text-xs ${isDarkMode ? 'text-white' : 'text-black'}`}>
                              {todaysMacros.reduce((sum, log) => sum + log.carbs, 0).toFixed(1)}g / {macroTargets.daily_carbs}g
                            </div>
                          </div>

                          {/* Fats Progress */}
                          <div className="text-center">
                            <div className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {Math.round((todaysMacros.reduce((sum, log) => sum + log.fats, 0) / macroTargets.daily_fats) * 100)}%
                            </div>
                            <div className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Fats</div>
                            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                              <div 
                                className="h-3 rounded-full transition-all duration-300" 
                                style={{ 
                                  backgroundColor: '#000000',
                                  width: `${Math.min((todaysMacros.reduce((sum, log) => sum + log.fats, 0) / macroTargets.daily_fats) * 100, 100)}%` 
                                }}
                              ></div>
                            </div>
                            <div className={`text-xs ${isDarkMode ? 'text-white' : 'text-black'}`}>
                              {todaysMacros.reduce((sum, log) => sum + log.fats, 0).toFixed(1)}g / {macroTargets.daily_fats}g
                            </div>
                          </div>
                        </div>

                        {/* Daily Summary */}
                        <div className="mt-6 p-4 bg-white rounded-lg" style={{border: '1px solid #010eda'}}>
                          <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-black'}`}>Today's Progress</h3>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className={`text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-black'}`}>Total Calories Consumed</div>
                              <div className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {todaysMacros.reduce((sum, log) => sum + log.calories, 0).toFixed(0)} cal
                              </div>
                            </div>
                            <div>
                              <div className={`text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-black'}`}>Remaining</div>
                              <div className={`text-xl font-bold ${
                                macroTargets.daily_calories - todaysMacros.reduce((sum, log) => sum + log.calories, 0) > 0 
                                  ? (isDarkMode ? 'text-blue-400' : 'text-blue-600')
                                  : (isDarkMode ? 'text-white' : 'text-black')
                              }`}>
                                {Math.max(0, macroTargets.daily_calories - todaysMacros.reduce((sum, log) => sum + log.calories, 0)).toFixed(0)} cal
                              </div>
                            </div>
                          </div>
                          
                          {/* Overall Progress Bar */}
                          <div className="mt-4">
                            <div className="flex justify-between items-center mb-2">
                              <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Daily Goal Progress</span>
                              <span className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-black'}`}>
                                {Math.round((todaysMacros.reduce((sum, log) => sum + log.calories, 0) / macroTargets.daily_calories) * 100)}%
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4">
                              <div 
                                className="h-4 rounded-full transition-all duration-500"
                                style={{ 
                                  backgroundColor: (todaysMacros.reduce((sum, log) => sum + log.calories, 0) / macroTargets.daily_calories) >= 1 
                                    ? '#000000' 
                                    : (todaysMacros.reduce((sum, log) => sum + log.calories, 0) / macroTargets.daily_calories) >= 0.8 
                                    ? '#010eda' 
                                    : '#010eda',
                                  width: `${Math.min((todaysMacros.reduce((sum, log) => sum + log.calories, 0) / macroTargets.daily_calories) * 100, 100)}%` 
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                    </div>

            {/* Today's Logged Foods Section */}
            {getTodaysMacros().length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-semibold  mb-4" style={{color: "#1a1a1a"}}>Today's Logged Foods</h2>
                <div className="space-y-3">
                  {getTodaysMacros().map((food, index) => {
                    console.log('🍔 Food object:', food);
                    console.log('🔑 Food ID:', food.id);
                    console.log('🔑 Food _id:', food._id);
                    return (
                    <div key={food.id || index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900">{food.food_name || 'Food Entry'}</h4>
                        <p className="text-sm  mt-1" style={{color: "#2d3748"}}>
                          <span className="inline-block mr-3">🥩 P: {food.protein}g</span>
                          <span className="inline-block mr-3">🍞 C: {food.carbs}g</span>
                          <span className="inline-block mr-3">🥑 F: {food.fats}g</span>
                          <span className="inline-block">🔥 {food.calories} cal</span>
                        </p>
                        {food.quantity && (
                          <p className="text-xs  mt-1" style={{color: "#2d3748"}}>
                            Quantity: {food.quantity} {food.unit || 'serving'}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
                        <button
                          onClick={() => handleEditFood(food)}
                          className="p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-lg transition-colors"
                          title="Edit"
                          data-testid="edit-food-btn"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteFood(food, food.food_name || 'Food Entry')}
                          className="p-2 bg-red-100 text-red-600 hover:bg-red-200 rounded-lg transition-colors"
                          title="Delete"
                          data-testid="delete-food-btn"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            )}

            {/* Camera Food Scanner Section */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl shadow-lg p-6 border-2 border-green-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold mb-1 flex items-center text-gray-900">
                    <svg className="w-6 h-6 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Smart Food Scanner
                  </h2>
                  <p className="text-sm text-gray-700">Scan your food with camera to auto-calculate macros</p>
                </div>
              </div>

              {analyzingFood ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
                  <p className=" font-medium" style={{color: "#2d3748"}}>Analyzing your food...</p>
                  <p className="text-sm  mt-2" style={{color: "#2d3748"}}>Identifying nutritional information</p>
                </div>
              ) : analysisResult ? (
                <div className="bg-white rounded-xl p-4 border-2 border-green-300">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg text-gray-900">{analysisResult.food_name}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      analysisResult.confidence === 'high' ? 'bg-green-100 text-green-700' :
                      analysisResult.confidence === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-orange-100 text-orange-700'
                    }`}>
                      {analysisResult.confidence} confidence
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-3 mb-3">
                    <div className="text-center bg-blue-50 rounded-lg p-2">
                      <div className="text-xs  mb-1" style={{color: "#2d3748"}}>Calories</div>
                      <div className="text-xl font-bold text-blue-600">{analysisResult.calories}</div>
                    </div>
                    <div className="text-center bg-green-50 rounded-lg p-2">
                      <div className="text-xs  mb-1" style={{color: "#2d3748"}}>Protein</div>
                      <div className="text-xl font-bold text-green-600">{analysisResult.protein}g</div>
                    </div>
                    <div className="text-center bg-yellow-50 rounded-lg p-2">
                      <div className="text-xs  mb-1" style={{color: "#2d3748"}}>Carbs</div>
                      <div className="text-xl font-bold text-yellow-600">{analysisResult.carbs}g</div>
                    </div>
                    <div className="text-center bg-purple-50 rounded-lg p-2">
                      <div className="text-xs  mb-1" style={{color: "#2d3748"}}>Fats</div>
                      <div className="text-xl font-bold text-purple-600">{analysisResult.fats}g</div>
                    </div>
                  </div>

                  <div className="text-sm  bg-gray-50 rounded-lg p-3 mb-3" style={{color: "#2d3748"}}>
                    <span className="font-medium">Serving:</span> {analysisResult.serving_size}
                    {analysisResult.notes && (
                      <><br/><span className="font-medium">Notes:</span> {analysisResult.notes}</>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setAnalysisResult(null);
                        setShowCamera(true);
                      }}
                      className="flex-1 px-4 py-2 bg-gray-200  rounded-lg hover:bg-gray-300 font-medium" style={{color: "#2d3748"}}
                    >
                      Scan Again
                    </button>
                    <button
                      onClick={() => setAnalysisResult(null)}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                    >
                      ✓ Use These Values
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleFoodImageAnalysis(e.target.files[0]);
                        }
                      }}
                      className="hidden"
                    />
                    <div className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 font-semibold transition-colors">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      </svg>
                      📸 Scan Food with Camera
                    </div>
                  </label>
                  <p className="text-xs mt-3 text-gray-700">Or upload a photo from your gallery</p>
                </div>
              )}
            </div>

            {/* Barcode Scanner Section */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold mb-1 flex items-center text-gray-900">
                    <svg className="w-6 h-6 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                    Barcode Scanner
                  </h2>
                  <p className="text-sm text-gray-700">Scan product barcodes for instant nutrition info</p>
                </div>
                <div className="bg-purple-100 px-3 py-1 rounded-full">
                  <span className="text-xs font-semibold text-purple-700">🔎 OpenFoodFacts</span>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={() => setShowBarcodeScanner(true)}
                  className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 font-semibold transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                  📦 Scan Product Barcode
                </button>
                <p className="text-xs mt-3 text-gray-700">Works with most packaged foods worldwide</p>
              </div>
            </div>

            {/* Recent Foods Section - MyFitnessPal Style */}
            {recentFoods.length > 0 && (
              <div className="recent-foods-section bg-white rounded-2xl shadow-lg p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4 text-gray-900">Recent Foods</h2>
                <p className="text-sm mb-4 text-gray-600">Quick add foods you've logged before</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {recentFoods.slice(0, 9).map((food) => (
                    <button
                      key={food.id}
                      onClick={() => handleQuickAddFood(food)}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left bg-white"
                      style={{color: '#1a1a1a'}}
                    >
                      <div className="flex-1">
                        <p style={{color: '#1a1a1a', fontWeight: 500}}>{food.food_name}</p>
                        <p style={{color: '#4a5568', fontSize: '0.75rem'}}>
                          P: {food.protein}g • C: {food.carbs}g • F: {food.fats}g
                        </p>
                      </div>
                      <svg className="w-5 h-5 text-blue-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Macro Logging Section */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="heading-4 mb-6" style={{color: isDarkMode ? '#ffffff' : '#1a1a1a'}}>Log Food Entry</h2>
              <form onSubmit={handleMacroSubmit} className="space-y-4">
                {/* Food Name with Autocomplete */}
                <div className="relative">
                  <label className="block text-sm font-medium mb-2" style={{color: isDarkMode ? '#ffffff' : '#1a1a1a'}}>Food Name</label>
                  <input
                    type="text"
                    value={macroInput.food_name}
                    onChange={(e) => handleFoodNameChange(e.target.value)}
                    onFocus={() => macroInput.food_name && setShowFoodSuggestions(filteredFoods.length > 0)}
                    className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{borderColor: '#010eda', backgroundColor: isDarkMode ? '#374151' : '#ffffff', color: isDarkMode ? '#ffffff' : '#1a1a1a'}}
                    placeholder="e.g., Chicken Breast, Oatmeal..."
                    required
                  />
                  
                  {/* Autocomplete Dropdown */}
                  {showFoodSuggestions && filteredFoods.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredFoods.map((food) => (
                        <button
                          key={food.id}
                          type="button"
                          onClick={() => handleSelectFood(food)}
                          className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-0"
                        >
                          <p className="font-medium " style={{color: "#1a1a1a"}}>{food.food_name}</p>
                          <p className="text-xs " style={{color: "#2d3748"}}>
                            P: {food.protein}g • C: {food.carbs}g • F: {food.fats}g • {food.quantity} {food.unit}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Macros Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{color: isDarkMode ? '#ffffff' : '#1a1a1a'}}>Protein (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={macroInput.protein}
                      onChange={(e) => setMacroInput({...macroInput, protein: e.target.value})}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{borderColor: '#010eda', backgroundColor: isDarkMode ? '#374151' : '#ffffff', color: isDarkMode ? '#ffffff' : '#1a1a1a'}}
                      placeholder="25"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{color: isDarkMode ? '#ffffff' : '#1a1a1a'}}>Carbs (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={macroInput.carbs}
                      onChange={(e) => setMacroInput({...macroInput, carbs: e.target.value})}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{borderColor: '#010eda', backgroundColor: isDarkMode ? '#374151' : '#ffffff', color: isDarkMode ? '#ffffff' : '#1a1a1a'}}
                      placeholder="30"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{color: isDarkMode ? '#ffffff' : '#1a1a1a'}}>Fats (g)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={macroInput.fats}
                      onChange={(e) => setMacroInput({...macroInput, fats: e.target.value})}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{borderColor: '#010eda', backgroundColor: isDarkMode ? '#374151' : '#ffffff', color: isDarkMode ? '#ffffff' : '#1a1a1a'}}
                      placeholder="10"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{color: isDarkMode ? '#ffffff' : '#1a1a1a'}}>Quantity</label>
                    <input
                      type="number"
                      step="0.1"
                      value={macroInput.quantity}
                      onChange={(e) => setMacroInput({...macroInput, quantity: e.target.value})}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{borderColor: '#010eda', backgroundColor: isDarkMode ? '#374151' : '#ffffff', color: isDarkMode ? '#ffffff' : '#1a1a1a'}}
                      placeholder="1"
                      required
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-800 mb-2">Unit</label>
                    <select
                      value={macroInput.unit}
                      onChange={(e) => setMacroInput({...macroInput, unit: e.target.value})}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{borderColor: '#010eda'}}
                    >
                      <option value="serving">Serving</option>
                      <option value="g">Grams</option>
                      <option value="oz">Ounces</option>
                      <option value="cup">Cup</option>
                      <option value="tbsp">Tablespoon</option>
                      <option value="tsp">Teaspoon</option>
                    </select>
                  </div>
                  <div className="pt-8">
                    <button type="submit" className="btn-primary px-8 py-3 whitespace-nowrap">
                      Log Food
                    </button>
                  </div>
                </div>
              </form>

              {/* Save Meal Section */}
              {(macroInput.protein || macroInput.carbs || macroInput.fats) && (
                <div className="border-t border-gray-200 pt-6 pb-6">
                  <h3 className="font-semibold text-lg mb-4">Save This Meal</h3>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={mealName}
                      onChange={(e) => setMealName(e.target.value)}
                      placeholder="Enter meal name (e.g., 'Breakfast Bowl')"
                      className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{borderColor: '#010eda'}}
                    />
                    <button
                      onClick={handleSaveMeal}
                      className="btn-secondary px-6"
                    >
                      Save Meal
                    </button>
                  </div>
                </div>
              )}

              {/* AI-Generated Meal Plan Section */}
              {mealPlans.length > 0 && (
                <div className="border-t border-gray-200 pt-6 pb-6">
                  {/* Header with expand/collapse */}
                  <button
                    onClick={() => setMealPlanExpanded(!mealPlanExpanded)}
                    className="w-full flex items-center justify-between mb-4 group"
                  >
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-lg" style={{color: isDarkMode ? '#ffffff' : '#1a1a1a'}}>
                        🍽️ Your Meal Plan
                      </h3>
                      <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {getFilteredMealPlans().length} of {mealPlans.length} meals
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {mealPlanFilter !== 'all' && (
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300">
                          {mealPlanFilter === 'favorites' ? '❤️' : mealPlanFilter}
                        </span>
                      )}
                      <svg 
                        className={`w-5 h-5 transition-transform ${mealPlanExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>
                  
                  {/* Expandable Content */}
                  {mealPlanExpanded && (
                    <>
                      {/* Search Bar with Filter Icon */}
                      <div className="mb-4">
                        <div className="relative">
                          <svg className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <input
                            type="text"
                            placeholder="Search meals by name or ingredient..."
                            value={mealPlanSearch}
                            onChange={(e) => setMealPlanSearch(e.target.value)}
                            className={`w-full pl-10 pr-20 py-2.5 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                          />
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                            {mealPlanSearch && (
                              <button
                                onClick={() => setMealPlanSearch('')}
                                className={`p-1 rounded ${isDarkMode ? 'text-gray-400 hover:text-gray-300 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                            {/* Filter Button */}
                            <button
                              onClick={() => setShowMealPlanFilters(!showMealPlanFilters)}
                              className={`p-1.5 rounded-lg transition-colors ${
                                showMealPlanFilters || mealPlanFilter !== 'all'
                                  ? 'bg-blue-600 text-white'
                                  : isDarkMode 
                                    ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                              }`}
                              title="Filter meals"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                              </svg>
                            </button>
                          </div>
                        </div>
                        
                        {/* Filter Dropdown */}
                        {showMealPlanFilters && (
                          <div className={`mt-2 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-lg`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Filter by</span>
                              {mealPlanFilter !== 'all' && (
                                <button
                                  onClick={() => setMealPlanFilter('all')}
                                  className="text-xs text-blue-500 hover:text-blue-600"
                                >
                                  Clear filter
                                </button>
                              )}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {[
                                { value: 'all', label: 'All Meals' },
                                { value: 'favorites', label: '❤️ Favorites', count: favoriteMeals.length },
                                { value: 'breakfast', label: 'Breakfast' },
                                { value: 'lunch', label: 'Lunch' },
                                { value: 'dinner', label: 'Dinner' },
                                { value: 'snack', label: 'Snack' }
                              ].map(filter => (
                                <button
                                  key={filter.value}
                                  onClick={() => {
                                    setMealPlanFilter(filter.value);
                                    setShowMealPlanFilters(false);
                                  }}
                                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                                    mealPlanFilter === filter.value
                                      ? 'bg-blue-600 text-white'
                                      : isDarkMode 
                                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {filter.label}
                                  {filter.count !== undefined && filter.count > 0 && (
                                    <span className="ml-1 text-xs">({filter.count})</span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Daily Totals Summary */}
                      {nutritionPlan && (
                    <div className={`mb-6 p-4 rounded-xl ${isDarkMode ? 'bg-gradient-to-r from-green-900/50 to-blue-900/50 border border-green-700' : 'bg-gradient-to-r from-green-50 to-blue-50 border border-green-200'}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-green-500">✓</span>
                        <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Daily Macro Targets</h4>
                      </div>
                      <div className="grid grid-cols-4 gap-2 sm:gap-4 text-center">
                        <div className={`p-2 sm:p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                          <p className={`text-lg sm:text-xl md:text-2xl font-bold ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>{nutritionPlan.daily_protein || 0}g</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Protein</p>
                        </div>
                        <div className={`p-2 sm:p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                          <p className={`text-lg sm:text-xl md:text-2xl font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>{nutritionPlan.daily_carbs || 0}g</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Carbs</p>
                        </div>
                        <div className={`p-2 sm:p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                          <p className={`text-lg sm:text-xl md:text-2xl font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{nutritionPlan.daily_fats || 0}g</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Fats</p>
                        </div>
                        <div className={`p-2 sm:p-3 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                          <p className={`text-base sm:text-lg md:text-xl font-bold ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>{nutritionPlan.daily_calories || 0}</p>
                          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Calories</p>
                        </div>
                      </div>
                      <p className={`text-xs mt-3 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        All meals below add up to these daily targets
                      </p>
                    </div>
                  )}
                  
                  {/* Meal Cards */}
                  {getFilteredMealPlans().length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {getFilteredMealPlans().map((meal) => {
                        const isExpanded = expandedMeals[meal.id] !== false; // Default to expanded
                        const isFavorite = favoriteMeals.includes(meal.id);
                        
                        return (
                          <div key={meal.id} className={`border rounded-lg overflow-hidden hover:shadow-md transition-shadow ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            {/* Card Header - Always Visible */}
                            <div className="p-4">
                              <div className="flex justify-between items-start">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <h4 className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{meal.name}</h4>
                                  </div>
                                  <span className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>{meal.meal_type}</span>
                                </div>
                                <div className="flex items-center gap-1 ml-2">
                                  {/* Favorite Button */}
                                  <button
                                    onClick={() => toggleFavoriteMeal(meal.id)}
                                    className={`p-1.5 rounded-full transition-colors ${
                                      isFavorite 
                                        ? 'text-red-500 hover:text-red-600' 
                                        : isDarkMode ? 'text-gray-500 hover:text-red-400' : 'text-gray-400 hover:text-red-500'
                                    }`}
                                    title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                                  >
                                    <svg className="w-5 h-5" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                  </button>
                                  {/* Expand/Collapse Button */}
                                  <button
                                    onClick={() => toggleMealExpanded(meal.id)}
                                    className={`p-1.5 rounded-full transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
                                    title={isExpanded ? 'Collapse' : 'Expand'}
                                  >
                                    <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                              
                              {/* Quick Macros Summary - Always Visible */}
                              <div className={`flex items-center gap-3 mt-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <span>{meal.macros?.calories || 0} cal</span>
                                <span>•</span>
                                <span>{meal.macros?.protein || 0}g P</span>
                                <span>•</span>
                                <span>{meal.macros?.carbs || 0}g C</span>
                                <span>•</span>
                                <span>{meal.macros?.fats || 0}g F</span>
                              </div>
                            </div>
                            
                            {/* Expandable Content */}
                            {isExpanded && (
                              <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                                {/* Detailed Macros */}
                                <div className={`grid grid-cols-4 gap-2 text-center text-xs mt-3 p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                  <div>
                                    <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{meal.macros?.protein || 0}g</p>
                                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Protein</p>
                                  </div>
                                  <div>
                                    <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{meal.macros?.carbs || 0}g</p>
                                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Carbs</p>
                                  </div>
                                  <div>
                                    <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{meal.macros?.fats || 0}g</p>
                                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Fats</p>
                                  </div>
                                  <div>
                                    <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{meal.macros?.calories || 0}</p>
                                    <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Cal</p>
                                  </div>
                                </div>
                                
                                {/* Ingredients */}
                                {meal.ingredients && meal.ingredients.length > 0 && (
                                  <div className="mt-3">
                                    <p className={`text-xs font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Ingredients:</p>
                                    <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                      {meal.ingredients.join(', ')}
                                    </p>
                                  </div>
                                )}
                                
                                {/* Add to Today button */}
                                <button
                                  onClick={() => handleAddSavedMeal({
                                    name: meal.name,
                                    protein: meal.macros?.protein || 0,
                                    carbs: meal.macros?.carbs || 0,
                                    fats: meal.macros?.fats || 0,
                                    calories: meal.macros?.calories || 0
                                  })}
                                  className="w-full mt-3 bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                                >
                                  + Log This Meal
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {mealPlanSearch || mealPlanFilter !== 'all' ? (
                        <>
                          <p className="mb-2">No meals match your search or filter</p>
                          <button
                            onClick={() => { setMealPlanSearch(''); setMealPlanFilter('all'); }}
                            className="text-blue-500 hover:text-blue-600 text-sm"
                          >
                            Clear filters
                          </button>
                        </>
                      ) : (
                        <p>No meals in your plan yet</p>
                      )}
                    </div>
                  )}
                  
                  {/* Nutrition Plan Details */}
                  {nutritionPlan && (
                    <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-blue-50'}`}>
                      <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>💧 Hydration & Supplements</h4>
                      {nutritionPlan.hydration && (
                        <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          <strong>Hydration:</strong> {nutritionPlan.hydration}
                        </p>
                      )}
                      {nutritionPlan.supplements && nutritionPlan.supplements.length > 0 && (
                        <div>
                          <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Supplements:</p>
                          <ul className={`text-sm list-disc list-inside ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {nutritionPlan.supplements.map((supp, idx) => (
                              <li key={idx}>{supp.name} - {supp.timing}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                    </>
                  )}
                </div>
              )}

              {/* Saved Meals Section */}
              <div className="border-t border-gray-200 pt-6 pb-6">
                <h3 className="font-semibold text-lg mb-4">Saved Meals</h3>
                {savedMeals.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <p className=" text-sm" style={{color: "#2d3748"}}>No saved meals yet</p>
                    <p className="text-gray-400 text-xs">Save macro combinations for quick logging</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {savedMeals.map((meal) => (
                      <div key={meal.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-medium " style={{color: "#1a1a1a"}}>{meal.name}</h4>
                          <button
                            onClick={() => handleDeleteSavedMeal(meal.id)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        <div className="space-y-1 text-sm  mb-3" style={{color: "#2d3748"}}>
                          <div className="flex justify-between">
                            <span>Protein:</span>
                            <span>{meal.protein}g</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Carbs:</span>
                            <span>{meal.carbs}g</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Fats:</span>
                            <span>{meal.fats}g</span>
                          </div>
                          <div className="flex justify-between font-medium  border-t border-gray-200 pt-1" style={{color: "#1a1a1a"}}>
                            <span>Calories:</span>
                            <span>{meal.calories.toFixed(0)}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleAddSavedMeal(meal)}
                          className="w-full bg-gray-100 text-black hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                        >
                          Add to Today
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Recent History</h3>
                {macros.length === 0 ? (
                  <p className=" text-center py-4" style={{color: "#2d3748"}}>No macro data logged yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-black' : 'text-gray-700'}`}>Date</th>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-black' : 'text-gray-700'}`}>Protein</th>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-black' : 'text-gray-700'}`}>Carbs</th>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-black' : 'text-gray-700'}`}>Fats</th>
                          <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-black' : 'text-gray-700'}`}>Calories</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {macros.slice(0, 5).map((macro, index) => (
                          <tr key={index}>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {new Date(macro.date).toLocaleDateString()}
                            </td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{macro.protein}g</td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{macro.carbs}g</td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{macro.fats}g</td>
                            <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{macro.calories.toFixed(0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* Grocery List Section */}
            <div className={`rounded-2xl shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <h2 className={`heading-4 mb-6 ${isDarkMode ? 'text-white' : ''}`}>Grocery List</h2>
              
              {/* AI-Generated Weekly Grocery List */}
              {aiGroceryList && (
                <div className="mb-8">
                  <div className={`flex items-center gap-2 mb-4 pb-2 border-b ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                      Weekly Grocery List
                    </h3>
                  </div>
                  
                  {/* Check if it's the new structured format or old array format */}
                  {typeof aiGroceryList === 'object' && !Array.isArray(aiGroceryList) ? (
                    // New structured format with categories
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(aiGroceryList).map(([category, items]) => (
                        <div key={category} className={`p-4 rounded-xl ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <h4 className={`font-semibold capitalize mb-3 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                            {category === 'proteins' && '🥩'}
                            {category === 'carbohydrates' && '🍚'}
                            {category === 'dairy' && '🥛'}
                            {category === 'fruits_vegetables' && '🥗'}
                            {category === 'fats_oils' && '🫒'}
                            {category === 'supplements' && '💊'}
                            {category.replace(/_/g, ' ')}
                          </h4>
                          <ul className="space-y-2">
                            {Array.isArray(items) && items.map((item, idx) => (
                              <li key={idx} className={`text-sm flex justify-between items-start ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                <span className="flex-1">
                                  {typeof item === 'object' ? (
                                    <>
                                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{item.item}</span>
                                      <span className={`ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>({item.quantity})</span>
                                      {item.notes && <span className={`block text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{item.notes}</span>}
                                    </>
                                  ) : (
                                    item
                                  )}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : Array.isArray(aiGroceryList) ? (
                    // Old array format
                    <ul className={`grid grid-cols-1 md:grid-cols-2 gap-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {aiGroceryList.map((item, idx) => (
                        <li key={idx} className={`flex items-center gap-2 p-2 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <span className="text-green-500">•</span>
                          <span>{typeof item === 'string' ? item : item.item || item.name}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              )}
              
              {/* User's Personal Grocery List */}
              <div className={`${aiGroceryList ? 'pt-4 border-t ' + (isDarkMode ? 'border-gray-600' : 'border-gray-200') : ''}`}>
                <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                  {aiGroceryList ? 'My Additional Items' : 'My Grocery List'}
                </h3>
                
                <form onSubmit={handleGroceryAdd} className="flex gap-3 mb-6">
                  <input
                    type="text"
                    value={newGroceryItem}
                    onChange={(e) => setNewGroceryItem(e.target.value)}
                    placeholder="Add grocery item..."
                    className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : ''}`}
                    style={{borderColor: isDarkMode ? undefined : '#010eda'}}
                  />
                  <button type="submit" className="btn-primary px-6">Add Item</button>
                </form>

                <div className="space-y-3">
                  {groceryList.length === 0 ? (
                    <div className="text-center py-8">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <svg className={`w-8 h-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                        </svg>
                      </div>
                      <p className={`text-lg mb-2 ${isDarkMode ? 'text-gray-300' : ''}`} style={{color: isDarkMode ? undefined : "#2d3748"}}>
                        {aiGroceryList ? 'No additional items added' : 'Your grocery list is empty'}
                      </p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Add items you need to buy</p>
                    </div>
                  ) : (
                    groceryList.map((item) => (
                      <div key={item.id} className={`flex items-center justify-between p-4 rounded-lg border ${
                        item.completed 
                          ? (isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200')
                          : (isDarkMode ? 'bg-gray-700 border-gray-500' : 'bg-white border-gray-300')
                      }`}>
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={item.completed}
                            onChange={() => toggleGroceryItem(item.id)}
                            className="h-5 w-5 text-blue-600 focus:ring-blue-500 rounded"
                          />
                          <span className={`${item.completed ? 'line-through ' + (isDarkMode ? 'text-gray-500' : 'text-gray-500') : (isDarkMode ? 'text-white' : 'text-gray-900')}`}>
                            {item.item}
                          </span>
                        </div>
                        <button
                          onClick={() => removeGroceryItem(item.id)}
                          className="text-red-500 hover:text-red-700 p-1"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
                );
              })()}

        {activeTab === 'progress' && (
          <div className="space-y-8">
            {/* Weight Goal Section */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="heading-4">Weight Goal</h2>
                {!weightGoal && !showWeightGoalForm && (
                  <button
                    onClick={() => setShowWeightGoalForm(true)}
                    className="btn-primary px-4 py-2 text-sm"
                  >
                    Set Goal
                  </button>
                )}
              </div>

              {showWeightGoalForm ? (
                <form onSubmit={handleWeightGoalSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>
                      Target Weight (lbs)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={weightGoalInput}
                      onChange={(e) => setWeightGoalInput(e.target.value)}
                      placeholder="Enter your weight goal"
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{borderColor: '#010eda'}}
                      required
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button type="submit" className="btn-primary px-6 py-2">
                      Set Goal
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowWeightGoalForm(false);
                        setWeightGoalInput('');
                      }}
                      className="btn-secondary px-6 py-2"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : weightGoal ? (
                <div className="text-center">
                  <div className="mb-6">
                    <div className="text-4xl font-bold text-blue-600 mb-2">
                      {weightGoal.target_weight} lbs
                    </div>
                    <div className="text-sm " style={{color: "#2d3748"}}>Your Target Weight</div>
                  </div>

                  {/* Progress to Goal */}
                  {weightData.length > 0 && (
                    <div className="bg-gray-50 rounded-lg p-6 mb-6">
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <div className="text-2xl font-bold " style={{color: "#1a1a1a"}}>
                            {weightData[0]?.weight || '--'} lbs
                          </div>
                          <div className="text-sm " style={{color: "#2d3748"}}>Current Weight</div>
                        </div>
                        <div>
                          <div className={`text-2xl font-bold ${
                            weightData[0]?.weight 
                              ? weightData[0].weight > weightGoal.target_weight
                                ? 'text-red-600'
                                : 'text-green-600'
                              : 'text-gray-900'
                          }`}>
                            {weightData[0]?.weight 
                              ? Math.abs(weightData[0].weight - weightGoal.target_weight).toFixed(1)
                              : '--'
                            } lbs
                          </div>
                          <div className="text-sm " style={{color: "#2d3748"}}>
                            {weightData[0]?.weight 
                              ? weightData[0].weight > weightGoal.target_weight 
                                ? 'To Lose'
                                : 'To Gain'
                              : 'Difference'
                            }
                          </div>
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-blue-600">
                            {weightGoal.target_weight} lbs
                          </div>
                          <div className="text-sm " style={{color: "#2d3748"}}>Goal Weight</div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      {weightData[0]?.weight && (
                        <div className="mt-6">
                          <div className="flex items-center justify-between text-sm  mb-2" style={{color: "#2d3748"}}>
                            <span>Progress to Goal</span>
                            <span>
                              {weightData[0].weight === weightGoal.target_weight 
                                ? '🎉 Goal Reached!'
                                : `${Math.abs(weightData[0].weight - weightGoal.target_weight).toFixed(1)} lbs ${
                                    weightData[0].weight > weightGoal.target_weight ? 'to lose' : 'to gain'
                                  }`
                              }
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3">
                            <div 
                              className={`h-3 rounded-full transition-all duration-300 ${
                                weightData[0].weight === weightGoal.target_weight 
                                  ? 'bg-green-500'
                                  : 'bg-blue-600'
                              }`}
                              style={{ 
                                width: `${Math.min(
                                  Math.abs(
                                    ((weightData[0].weight - (weightData[1]?.weight || weightData[0].weight)) / 
                                     Math.abs(weightGoal.target_weight - (weightData[1]?.weight || weightData[0].weight))) * 100
                                  ), 100
                                )}%` 
                              }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex items-center justify-center space-x-4">
                    <button
                      onClick={() => {
                        setWeightGoalInput(weightGoal.target_weight.toString());
                        setShowWeightGoalForm(true);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      Edit Goal
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Are you sure you want to delete your weight goal?')) {
                          deleteWeightGoal();
                        }
                      }}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Delete Goal
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium  mb-2" style={{color: "#1a1a1a"}}>Set Your Weight Goal</h3>
                  <p className=" mb-4" style={{color: "#2d3748"}}>
                    Track your progress towards your ideal weight
                  </p>
                  <button
                    onClick={() => setShowWeightGoalForm(true)}
                    className="btn-primary px-6 py-2"
                  >
                    Set Weight Goal
                  </button>
                </div>
              )}
            </div>

            {/* Weight Tracking */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="heading-4 mb-6">Weight Tracking</h2>
              <form onSubmit={handleWeightSubmit} className="flex gap-3 mb-6">
                <input
                  type="number"
                  step="0.1"
                  value={weightInput}
                  onChange={(e) => setWeightInput(e.target.value)}
                  placeholder="Enter weight (lbs)"
                  className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{borderColor: '#010eda'}}
                />
                <button type="submit" className="btn-primary px-6">Log Weight</button>
              </form>

              {/* Weight Progress Chart */}
              <WeightChart weightData={weightData} weightGoal={weightGoal} />

              {/* Recent Weight Entries */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-black mb-4">Recent Entries</h3>
                {(weightData.length === 0 && progressData.filter(p => p.type === 'weight').length === 0) ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <p className=" text-lg mb-2" style={{color: "#2d3748"}}>No weight data yet</p>
                    <p className="text-gray-400 text-sm">Start tracking your progress</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {[...weightData, ...progressData.filter(p => p.type === 'weight')]
                      .sort((a, b) => new Date(b.timestamp || b.date) - new Date(a.timestamp || a.date))
                      .slice(0, 10)
                      .map((entry) => (
                        <div key={entry.id} className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                          <span className="font-medium " style={{color: "#1a1a1a"}}>{entry.weight} lbs</span>
                          <span className="" style={{color: "#2d3748"}}>
                            {new Date(entry.timestamp || entry.date).toLocaleDateString()}
                          </span>
                        </div>
                      ))
                    }
                  </div>
                )}
              </div>
            </div>

            {/* Body Measurements */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="heading-4 mb-6">Body Measurements</h2>
              
              {/* Measurements Input Form */}
              <form onSubmit={handleMeasurementsSubmit} className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">Chest (in)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsInput.chest}
                      onChange={(e) => setMeasurementsInput({...measurementsInput, chest: e.target.value})}
                      placeholder="36.0"
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{borderColor: '#010eda'}}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">Waist (in)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsInput.waist}
                      onChange={(e) => setMeasurementsInput({...measurementsInput, waist: e.target.value})}
                      placeholder="32.0"
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{borderColor: '#010eda'}}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">Hips (in)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsInput.hips}
                      onChange={(e) => setMeasurementsInput({...measurementsInput, hips: e.target.value})}
                      placeholder="38.0"
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{borderColor: '#010eda'}}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">Thighs (in)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsInput.thighs}
                      onChange={(e) => setMeasurementsInput({...measurementsInput, thighs: e.target.value})}
                      placeholder="24.0"
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{borderColor: '#010eda'}}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">Arms (in)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={measurementsInput.arms}
                      onChange={(e) => setMeasurementsInput({...measurementsInput, arms: e.target.value})}
                      placeholder="14.0"
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{borderColor: '#010eda'}}
                    />
                  </div>
                </div>
                <div className="text-center">
                  <button type="submit" className="btn-primary px-8">Log Measurements</button>
                </div>
              </form>

              {/* Body Measurements Progress Display */}
              <BodyMeasurementsTracker measurementsData={measurementsData} />

              {/* Recent Measurements Entries */}
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-black mb-4">Recent Measurements</h3>
                {measurementsData.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 4v12l-4-2-4 2V4M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className=" text-lg mb-2" style={{color: "#2d3748"}}>No measurements yet</p>
                    <p className="text-gray-400 text-sm">Start tracking your body measurements</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {measurementsData.slice(-5).reverse().map((entry) => (
                      <div key={entry.id} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium " style={{color: "#1a1a1a"}}>
                            {new Date(entry.timestamp).toLocaleDateString()}
                          </span>
                          <span className="text-sm " style={{color: "#2d3748"}}>
                            {new Date(entry.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="grid grid-cols-5 gap-4 text-sm">
                          <div className="text-center">
                            <div className="font-medium text-blue-600">{entry.chest}"</div>
                            <div className="" style={{color: "#2d3748"}}>Chest</div>
                          </div>
                          <div className="text-center">
                            <div className="font-medium " style={{color: "#1a1a1a"}}>{entry.waist}"</div>
                            <div className="" style={{color: "#2d3748"}}>Waist</div>
                          </div>
                          <div className="text-center">
                            <div className="font-medium text-blue-600">{entry.hips}"</div>
                            <div className="" style={{color: "#2d3748"}}>Hips</div>
                          </div>
                          <div className="text-center">
                            <div className="font-medium " style={{color: "#1a1a1a"}}>{entry.thighs}"</div>
                            <div className="" style={{color: "#2d3748"}}>Thighs</div>
                          </div>
                          <div className="text-center">
                            <div className="font-medium text-blue-600">{entry.arms}"</div>
                            <div className="" style={{color: "#2d3748"}}>Arms</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Progress Photos */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="heading-4 mb-6">Progress Photos</h2>
              
              {/* Photo Upload Progress */}
              {photoUploadProgress > 0 && photoUploadProgress < 100 && (
                <div className="mb-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{width: `${photoUploadProgress}%`}}
                    />
                  </div>
                  <p className="text-sm  text-center mt-2" style={{color: "#2d3748"}}>Uploading... {photoUploadProgress}%</p>
                </div>
              )}
              
              {progressPhotos.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <p className=" text-lg mb-2" style={{color: "#2d3748"}}>No progress photos yet</p>
                  <p className="text-gray-400 text-sm mb-6">Capture your transformation journey</p>
                  
                  {/* Two Options: Take Photo or Upload */}
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    {/* Take Photo with Camera */}
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="progress-photo-camera"
                    />
                    <label htmlFor="progress-photo-camera" className="btn-primary px-6 py-3 inline-flex items-center justify-center cursor-pointer">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      </svg>
                      Take Photo
                    </label>
                    
                    {/* Upload from Gallery */}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="progress-photo-upload"
                    />
                    <label htmlFor="progress-photo-upload" className="bg-white border-2 border-blue-600 text-white bg-blue-600 px-6 py-3 rounded-lg hover:bg-blue-700 font-medium inline-flex items-center justify-center cursor-pointer">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Upload Photo
                    </label>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Photo Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    {progressPhotos.map((photo, idx) => (
                      <div key={photo.id || idx} className="relative group">
                        <img 
                          src={photo.photo_url} 
                          alt={`Progress ${idx + 1}`}
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all rounded-lg flex items-center justify-center">
                          <button
                            onClick={() => {
                              setPhotoToDelete(photo);
                              setShowDeletePhotoModal(true);
                            }}
                            className="opacity-0 group-hover:opacity-100 p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                        <p className="text-xs  mt-1 text-center" style={{color: "#2d3748"}}>
                          {new Date(photo.date_taken || photo.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Add More Buttons - Two Options */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    {/* Take Photo with Camera */}
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="progress-photo-camera-more"
                    />
                    <label htmlFor="progress-photo-camera-more" className="btn-primary flex-1 text-center cursor-pointer py-3 inline-flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      </svg>
                      Take Photo
                    </label>
                    
                    {/* Upload from Gallery */}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                      id="progress-photo-upload-more"
                    />
                    <label htmlFor="progress-photo-upload-more" className="bg-blue-600 border-2 border-blue-600 text-white flex-1 text-center cursor-pointer py-3 rounded-lg hover:bg-blue-700 font-medium inline-flex items-center justify-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Upload Photo
                    </label>
                  </div>
                  
                  <p className="text-sm  text-center mt-3" style={{color: "#2d3748"}}>
                    {progressPhotos.length} photo{progressPhotos.length !== 1 ? 's' : ''} total
                  </p>
                </div>
              )}
            </div>

            {/* Personal Records (PRs) Section */}
            <div className="bg-white rounded-2xl shadow-lg p-6" data-testid="personal-records-section">
              <div className="flex items-center justify-between mb-6">
                <h2 className="heading-4">Personal Records</h2>
                <button
                  onClick={() => setShowPRForm(true)}
                  className="btn-primary px-4 py-2 text-sm"
                  data-testid="add-pr-btn"
                >
                  + Log New PR
                </button>
              </div>

              {/* PR View Toggle */}
              <div className="flex space-x-2 mb-6">
                <button
                  onClick={() => setPrViewMode('summary')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    prViewMode === 'summary'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid="pr-summary-tab"
                >
                  Best PRs
                </button>
                <button
                  onClick={() => setPrViewMode('history')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    prViewMode === 'history'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  data-testid="pr-history-tab"
                >
                  Full History
                </button>
              </div>

              {/* Best PRs Summary View */}
              {prViewMode === 'summary' && (
                <div>
                  {bestPRs.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-lg mb-2" style={{color: "#2d3748"}}>No PRs logged yet</p>
                      <p className="text-gray-400 text-sm mb-4">Start tracking your personal records</p>
                      <button
                        onClick={() => setShowPRForm(true)}
                        className="btn-primary px-6 py-2"
                      >
                        Log Your First PR
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {bestPRs.map((pr) => (
                        <div 
                          key={pr.id} 
                          className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100"
                          data-testid={`best-pr-card-${pr.lift_name.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-bold text-lg" style={{color: "#1a1a1a"}}>{pr.lift_name}</h4>
                              <div className="flex items-baseline space-x-2 mt-1">
                                <span className="text-2xl font-bold text-blue-600">{pr.weight}</span>
                                <span className="text-sm" style={{color: "#2d3748"}}>lbs</span>
                                <span className="text-sm" style={{color: "#2d3748"}}>x</span>
                                <span className="text-xl font-bold text-blue-600">{pr.reps}</span>
                                <span className="text-sm" style={{color: "#2d3748"}}>reps</span>
                              </div>
                              <p className="text-sm mt-2" style={{color: "#2d3748"}}>
                                Est. 1RM: <span className="font-semibold text-blue-700">{pr.estimated_1rm} lbs</span>
                              </p>
                            </div>
                            <div className="text-right">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                🏆 Best
                              </span>
                              <p className="text-xs mt-2" style={{color: "#2d3748"}}>
                                {new Date(pr.date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Full History View */}
              {prViewMode === 'history' && (
                <div>
                  {personalRecords.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-lg mb-2" style={{color: "#2d3748"}}>No PR history yet</p>
                      <p className="text-gray-400 text-sm">Log your first PR to start tracking</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {personalRecords.map((pr) => (
                        <div 
                          key={pr.id} 
                          className="flex justify-between items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                          data-testid={`pr-history-item-${pr.id}`}
                        >
                          <div className="flex-1">
                            <h4 className="font-semibold" style={{color: "#1a1a1a"}}>{pr.lift_name}</h4>
                            <div className="flex items-center space-x-3 mt-1">
                              <span className="text-blue-600 font-bold">{pr.weight} lbs</span>
                              <span style={{color: "#2d3748"}}>x</span>
                              <span className="text-blue-600 font-bold">{pr.reps} reps</span>
                              {pr.notes && (
                                <span className="text-sm italic" style={{color: "#2d3748"}}>- {pr.notes}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className="text-sm" style={{color: "#2d3748"}}>
                              {new Date(pr.date).toLocaleDateString()}
                            </span>
                            <button
                              onClick={() => handlePRDelete(pr.id)}
                              className="text-red-500 hover:text-red-700 p-1"
                              data-testid={`delete-pr-${pr.id}`}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* PR Form Modal */}
            {showPRForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" data-testid="pr-form-modal">
                  <h3 className="text-xl font-bold mb-4" style={{color: "#1a1a1a"}}>Log New Personal Record</h3>
                  
                  <form onSubmit={handlePRSubmit} className="space-y-4">
                    {/* Lift Selection */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{color: "#2d3748"}}>
                        Select Lift *
                      </label>
                      <select
                        value={prFormData.lift_name}
                        onChange={(e) => setPrFormData({...prFormData, lift_name: e.target.value})}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        style={{borderColor: '#010eda'}}
                        required
                        data-testid="pr-lift-select"
                      >
                        <option value="">Choose a lift...</option>
                        {presetLifts.map((lift) => (
                          <option key={lift} value={lift}>{lift}</option>
                        ))}
                        <option value="custom">+ Custom Lift</option>
                      </select>
                    </div>

                    {/* Custom Lift Name (if selected) */}
                    {prFormData.lift_name === 'custom' && (
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{color: "#2d3748"}}>
                          Custom Lift Name *
                        </label>
                        <input
                          type="text"
                          value={prFormData.custom_lift_name}
                          onChange={(e) => setPrFormData({...prFormData, custom_lift_name: e.target.value})}
                          placeholder="e.g., Incline Bench Press"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          style={{borderColor: '#010eda'}}
                          required
                          data-testid="pr-custom-lift-input"
                        />
                      </div>
                    )}

                    {/* Weight and Reps Row */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{color: "#2d3748"}}>
                          Weight (lbs) *
                        </label>
                        <input
                          type="number"
                          step="0.5"
                          min="0"
                          value={prFormData.weight}
                          onChange={(e) => setPrFormData({...prFormData, weight: e.target.value})}
                          placeholder="225"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          style={{borderColor: '#010eda'}}
                          required
                          data-testid="pr-weight-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2" style={{color: "#2d3748"}}>
                          Reps *
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={prFormData.reps}
                          onChange={(e) => setPrFormData({...prFormData, reps: e.target.value})}
                          placeholder="5"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          style={{borderColor: '#010eda'}}
                          required
                          data-testid="pr-reps-input"
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{color: "#2d3748"}}>
                        Notes (optional)
                      </label>
                      <textarea
                        value={prFormData.notes}
                        onChange={(e) => setPrFormData({...prFormData, notes: e.target.value})}
                        placeholder="e.g., Felt strong, good form"
                        rows="2"
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        style={{borderColor: '#010eda'}}
                        data-testid="pr-notes-input"
                      />
                    </div>

                    {/* Form Buttons */}
                    <div className="flex space-x-3 pt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowPRForm(false);
                          setPrFormData({ lift_name: '', custom_lift_name: '', weight: '', reps: '', notes: '' });
                        }}
                        className="flex-1 px-4 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 font-medium"
                        style={{color: "#2d3748"}}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                        data-testid="pr-submit-btn"
                      >
                        🏆 Log PR
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Delete Photo Confirmation Modal */}
            {showDeletePhotoModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                  <h3 className="text-xl font-bold  mb-4" style={{color: "#1a1a1a"}}>Delete Progress Photo?</h3>
                  <p className=" mb-6" style={{color: "#2d3748"}}>
                    Are you sure you want to delete this progress photo? This action cannot be undone.
                  </p>
                  
                  {photoToDelete && photoToDelete.photo_url && (
                    <div className="mb-6">
                      <img 
                        src={photoToDelete.photo_url} 
                        alt="Photo to delete" 
                        className="w-full h-48 object-cover rounded-lg"
                      />
                    </div>
                  )}
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        setShowDeletePhotoModal(false);
                        setPhotoToDelete(null);
                      }}
                      className="flex-1 px-4 py-3 bg-gray-200  rounded-lg hover:bg-gray-300 font-medium" style={{color: "#2d3748"}}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeletePhoto}
                      className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                    >
                      Delete Photo
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {activeTab === 'messages' && (
          <div className="space-y-4">
            {/* iOS Home Screen Prompt */}
            {showIOSHomeScreenPrompt && (
              <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-4 shadow-lg">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="bg-white/10 rounded-full p-2 mt-0.5">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold">Add to Home Screen</p>
                      <p className="text-gray-300 text-sm mt-1">For push notifications on iPhone:</p>
                      <ol className="text-gray-400 text-xs mt-2 space-y-1 list-decimal ml-4">
                        <li>Tap the <strong className="text-gray-300">Share</strong> button at the bottom of Safari</li>
                        <li>Tap "<strong className="text-gray-300">Add to Home Screen</strong>"</li>
                        <li>Tap "<strong className="text-gray-300">Add</strong>" to confirm</li>
                        <li>Open the app from your Home Screen</li>
                      </ol>
                    </div>
                  </div>
                  <button
                    onClick={handleDismissIOSPrompt}
                    className="p-2 text-white/50 hover:text-white transition-colors flex-shrink-0"
                    aria-label="Dismiss"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            
            {/* Push Notification Prompt Banner */}
            {pushSupported && !pushEnabled && !pushPromptDismissed && (
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="bg-white/20 rounded-full p-2">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-semibold">Enable Push Notifications</p>
                      <p className="text-blue-100 text-sm">Get notified instantly when your trainer messages you</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleEnablePushFromBanner}
                      disabled={pushLoading}
                      className="px-4 py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors disabled:opacity-50"
                    >
                      {pushLoading ? 'Enabling...' : 'Enable'}
                    </button>
                    <button
                      onClick={handleDismissPushPrompt}
                      className="p-2 text-white/70 hover:text-white transition-colors"
                      aria-label="Dismiss"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-2xl shadow-lg p-6 h-96 flex flex-col">
              <div className="flex items-center justify-between mb-6">
                <h2 className="heading-4">Messages with Trainer</h2>
                <button
                  onClick={fetchMessages}
                  className="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg flex items-center space-x-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Refresh</span>
                </button>
              </div>
              
              {/* Messages List */}
              <div className="flex-1 overflow-y-auto space-y-4 mb-4 max-h-64">
                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <p className=" text-lg mb-2" style={{color: "#2d3748"}}>No messages yet</p>
                    <p className="text-gray-400 text-sm">Start a conversation with your trainer</p>
                  </div>
                ) : (
                  messages.map((message) => (
                    <div key={message.id} className={`flex ${message.sender === 'client' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`message-content max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${message.sender === 'client' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-900'}`}>
                        <p className="text-sm">{message.text}</p>
                        <p className={`text-xs mt-1 ${message.sender === 'client' ? 'text-blue-200' : 'text-gray-500'}`}>
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="flex gap-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" style={{borderColor: '#010eda'}}
                />
                <button type="submit" className="btn-primary px-6">Send</button>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="space-y-8">
            {/* Profile Navigation */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg">
              <div className="profile-tab-buttons flex border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setActiveProfileTab('info')}
                  className={`px-6 py-4 font-medium text-sm rounded-tl-2xl ${
                    activeProfileTab === 'info'
                      ? 'text-black bg-gray-100 dark:bg-blue-900/30 border-b-2 border-black'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Profile Info
                </button>
                <button
                  onClick={() => setActiveProfileTab('settings')}
                  className={`px-6 py-4 font-medium text-sm rounded-tr-2xl ${
                    activeProfileTab === 'settings'
                      ? 'text-black bg-gray-100 dark:bg-blue-900/30 border-b-2 border-black'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Settings
                </button>
              </div>

              {/* Profile Info Tab */}
              {activeProfileTab === 'info' && (
                <div className="profile-info-section p-6">
                  <h2 className="heading-4 mb-6  dark:text-white" style={{color: "#1a1a1a"}}>Profile Information</h2>
                  <div className="space-y-6">
                    <div className="flex items-center space-x-4">
                      <div className="w-20 h-20 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                        <svg className="w-10 h-10 text-gray-400 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900 dark:text-white font-bold">{user?.name}</h3>
                        <p className="text-black dark:text-white">{user?.email}</p>
                        <button className="text-blue-600 dark:text-blue-400 text-sm hover:underline mt-1">
                          Change Profile Picture
                        </button>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Phone Number</label>
                          <input
                            type="tel"
                            defaultValue={user?.phone}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Emergency Contact</label>
                          <input
                            type="tel"
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="+1 (555) 987-6543"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                      <h3 className="font-semibold text-lg mb-4 text-gray-900 dark:text-white font-bold">Fitness Goals</h3>
                      <div className="space-y-4">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Primary Goal</label>
                          <select 
                            value={profileGoals.primaryGoal}
                            onChange={(e) => setProfileGoals({...profileGoals, primaryGoal: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Select a goal</option>
                            <option value="Weight Loss">Weight Loss</option>
                            <option value="Muscle Gain">Muscle Gain</option>
                            <option value="Strength Training">Strength Training</option>
                            <option value="Endurance">Endurance</option>
                            <option value="General Fitness">General Fitness</option>
                          </select>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Current Weight (lbs)</label>
                            <input
                              type="number"
                              value={profileGoals.currentWeight}
                              onChange={(e) => setProfileGoals({...profileGoals, currentWeight: e.target.value})}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="150"
                            />
                          </div>
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Goal Weight (lbs)</label>
                            <input
                              type="number"
                              value={profileGoals.goalWeight}
                              onChange={(e) => setProfileGoals({...profileGoals, goalWeight: e.target.value})}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="140"
                            />
                          </div>
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Height</label>
                            <input
                              type="text"
                              value={profileGoals.height}
                              onChange={(e) => setProfileGoals({...profileGoals, height: e.target.value})}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="5'6&quot;"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Training Preferences Section */}
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                      <h3 className="font-semibold text-lg mb-4 text-gray-900 dark:text-white font-bold">Training Preferences</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Help your trainer create better personalized plans by providing more details about your preferences.</p>
                      
                      <div className="space-y-4">
                        {/* Intensity Level Slider */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                            Preferred Workout Intensity: <span className="font-bold text-blue-600">{profileGoals.intensityLevel}/10</span>
                          </label>
                          <input
                            type="range"
                            min="1"
                            max="10"
                            value={profileGoals.intensityLevel}
                            onChange={(e) => setProfileGoals({...profileGoals, intensityLevel: parseInt(e.target.value)})}
                            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                          <div className="flex justify-between text-xs text-gray-500 mt-1">
                            <span>Light (Recovery)</span>
                            <span>Moderate</span>
                            <span>Intense (Maximum Effort)</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Training Experience */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Training Experience</label>
                            <select 
                              value={profileGoals.trainingExperience}
                              onChange={(e) => setProfileGoals({...profileGoals, trainingExperience: e.target.value})}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Select experience level</option>
                              <option value="Beginner">Beginner (0-6 months)</option>
                              <option value="Intermediate">Intermediate (6 months - 2 years)</option>
                              <option value="Advanced">Advanced (2-5 years)</option>
                              <option value="Expert">Expert (5+ years)</option>
                            </select>
                          </div>

                          {/* Days Per Week */}
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Days Available Per Week</label>
                            <select 
                              value={profileGoals.daysPerWeek}
                              onChange={(e) => setProfileGoals({...profileGoals, daysPerWeek: e.target.value})}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="2">2 days</option>
                              <option value="3">3 days</option>
                              <option value="4">4 days</option>
                              <option value="5">5 days</option>
                              <option value="6">6 days</option>
                            </select>
                          </div>
                        </div>

                        {/* Equipment Access */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Equipment Access</label>
                          <select 
                            value={profileGoals.equipmentAccess}
                            onChange={(e) => setProfileGoals({...profileGoals, equipmentAccess: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="">Select equipment access</option>
                            <option value="Full Gym">Full Gym (All equipment available)</option>
                            <option value="Home Gym">Home Gym (Basic equipment: dumbbells, bench, etc.)</option>
                            <option value="Minimal">Minimal (Bodyweight, resistance bands)</option>
                            <option value="Commercial Gym">Commercial Gym (Standard gym machines & free weights)</option>
                          </select>
                        </div>

                        {/* Injuries/Limitations */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Injuries or Physical Limitations</label>
                          <input
                            type="text"
                            value={profileGoals.injuriesLimitations}
                            onChange={(e) => setProfileGoals({...profileGoals, injuriesLimitations: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., Lower back issues, shoulder injury, knee problems..."
                          />
                        </div>

                        {/* Dietary Restrictions / Food Allergies */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>
                            Food Allergies / Dietary Restrictions
                          </label>
                          <input
                            type="text"
                            value={profileGoals.dietaryRestrictions}
                            onChange={(e) => setProfileGoals({...profileGoals, dietaryRestrictions: e.target.value})}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="e.g., Gluten-free, Lactose intolerant, Nut allergy, Vegetarian, Vegan..."
                          />
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            This will be used when generating your personalized meal plans to avoid foods you can't eat.
                          </p>
                        </div>

                        {/* Additional Notes */}
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Additional Notes for Your Trainer</label>
                          <textarea
                            value={profileGoals.additionalNotes}
                            onChange={(e) => setProfileGoals({...profileGoals, additionalNotes: e.target.value})}
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Any preferences, schedule constraints, favorite exercises, or things you'd like to focus on..."
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                      <button 
                        onClick={async () => {
                          setSavingProfile(true);
                          try {
                            const response = await fetch(`${BACKEND_URL}/api/profile/goals`, {
                              method: 'PUT',
                              headers: {'Content-Type': 'application/json'},
                              credentials: 'include',
                              body: JSON.stringify(profileGoals)
                            });
                            if (response.ok) {
                              // Refetch weight goal to update other tabs
                              await fetchWeightGoal();
                              await fetchWeightData();
                              setErrorModalMessage('✅ Profile Updated!\n\nYour fitness goals and training preferences have been saved successfully.');
                              setShowErrorModal(true);
                            } else {
                              setErrorModalMessage('❌ Error\n\nFailed to save profile. Please try again.');
                              setShowErrorModal(true);
                            }
                          } catch (error) {
                            console.error('Error saving profile:', error);
                            setErrorModalMessage('❌ Error\n\nFailed to save profile. Please try again.');
                            setShowErrorModal(true);
                          } finally {
                            setSavingProfile(false);
                          }
                        }}
                        disabled={savingProfile}
                        className={`btn-primary ${savingProfile ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {savingProfile ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Settings Tab */}
              {activeProfileTab === 'settings' && (
                <div className="settings-section p-6">
                  <h2 className="heading-4 mb-6" style={{color: '#1a1a1a', fontSize: '24px', fontWeight: 'bold'}}>Settings</h2>
                  
                  {/* Appearance Section */}
                  <div className="space-y-8">
                    <div>
                      <h3 className="font-semibold text-lg mb-4 text-black dark:text-white">Appearance</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-black dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                              </svg>
                            </div>
                            <div>
                              <p className="font-medium" style={{color: '#0000FF', fontSize: '18px'}}>Dark Mode</p>
                              <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>Toggle between light and dark theme</p>
                            </div>
                          </div>
                          <button
                            onClick={toggleTheme}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              isDarkMode ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                isDarkMode ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Notifications Section */}
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                      <h3 className="font-semibold text-lg mb-4 text-black dark:text-white">Push Notifications</h3>
                      
                      {/* Main Push Notification Toggle */}
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                              </svg>
                            </div>
                            <div>
                              <p className="font-semibold" style={{color: '#1a1a1a', fontSize: '16px'}}>Enable Push Notifications</p>
                              <p className="text-sm" style={{color: '#2d3748', fontSize: '13px'}}>
                                {!pushSupported 
                                  ? 'Push notifications are not supported in this browser'
                                  : pushEnabled 
                                    ? 'You will receive alerts for messages, reminders, and updates'
                                    : 'Get notified even when the app is closed'}
                              </p>
                            </div>
                          </div>
                          <button 
                            data-testid="push-notifications-toggle"
                            onClick={handleTogglePushNotifications}
                            disabled={!pushSupported || pushLoading}
                            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                              pushEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-500'
                            } ${(!pushSupported || pushLoading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            {pushLoading ? (
                              <span className="absolute inset-0 flex items-center justify-center">
                                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              </span>
                            ) : (
                              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                                pushEnabled ? 'translate-x-6' : 'translate-x-1'
                              }`} />
                            )}
                          </button>
                        </div>

                        {/* Test Notification Button */}
                        {pushEnabled && (
                          <button
                            data-testid="push-notifications-test-btn"
                            onClick={handleTestPushNotification}
                            className="w-full p-3 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          >
                            Send Test Notification
                          </button>
                        )}

                        <NotificationPrefs />

                        <PushCheckPanel />
                        
                        {/* Debug Button */}
                        <button
                          onClick={async () => {
                            const sessionToken = localStorage.getItem('session_token');
                            const debugInfo = {
                              hasSessionToken: !!sessionToken,
                              tokenPreview: sessionToken ? sessionToken.substring(0, 20) + '...' : 'none',
                              userId: user?.id || 'not logged in',
                              userEmail: user?.email || 'unknown'
                            };
                            
                            try {
                              const response = await fetch(`${BACKEND_URL}/api/push/status`, {
                                credentials: 'include',
                                headers: sessionToken ? { 'X-Session-Token': sessionToken } : {}
                              });
                              const status = await response.json();
                              debugInfo.serverStatus = status;
                            } catch (e) {
                              debugInfo.serverError = e.message;
                            }
                            
                            alert('Debug Info:\n\n' + JSON.stringify(debugInfo, null, 2));
                          }}
                          className="w-full p-2 text-xs bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg"
                        >
                          🔍 Debug Push Status
                        </button>

                        {/* Current Status Diagnostic */}
                        <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                          <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Notification Status:</p>
                          <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
                            <p>• Device: {isIOS ? 'iPhone/iPad' : 'Other'}</p>
                            <p>• App Mode: {isInStandaloneMode ? '✅ Installed (Home Screen)' : '⚠️ Browser Mode'}</p>
                            <p>• Permission: {
                              !('Notification' in window) ? '❌ Not Supported' :
                              Notification.permission === 'granted' ? '✅ Allowed' :
                              Notification.permission === 'denied' ? '❌ Blocked' :
                              '⏳ Not Asked Yet'
                            }</p>
                            <p>• Subscription: {pushEnabled ? '✅ Active' : '❌ Not Active'}</p>
                          </div>
                          {isIOS && Notification.permission === 'default' && isInStandaloneMode && (
                            <p className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                              💡 Tap the toggle above - a permission popup should appear. Tap "Allow" to enable.
                            </p>
                          )}
                        </div>

                        {/* Permission Status Info - Blocked */}
                        {pushSupported && getNotificationPermission() === 'denied' && (
                          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                            <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">
                              🚫 Notifications are Blocked
                            </p>
                            {isIOS ? (
                              <>
                                <p className="text-xs text-red-600 dark:text-red-400 mb-2">
                                  To enable on iPhone:
                                </p>
                                <ol className="text-xs text-red-600 dark:text-red-400 list-decimal ml-4 space-y-1">
                                  <li>Open the <strong>Settings</strong> app on your iPhone</li>
                                  <li>Scroll down and tap <strong>Safari</strong></li>
                                  <li>Tap <strong>Notifications</strong></li>
                                  <li>Find this website and tap <strong>Allow</strong></li>
                                  <li>Return here and tap the toggle again</li>
                                </ol>
                              </>
                            ) : (
                              <>
                                <p className="text-xs text-red-600 dark:text-red-400 mb-2">
                                  To enable notifications:
                                </p>
                                <ol className="text-xs text-red-600 dark:text-red-400 list-decimal ml-4 space-y-1">
                                  <li>Click the lock/info icon in the address bar</li>
                                  <li>Find "Notifications" in Site Settings</li>
                                  <li>Change from "Block" to "Allow"</li>
                                  <li>Refresh this page</li>
                                </ol>
                              </>
                            )}
                          </div>
                        )}
                        
                        {/* iOS Home Screen Info - Not Installed */}
                        {isIOS && !isInStandaloneMode && (
                          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <svg className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                              </svg>
                              <div>
                                <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                                  iPhone Users - Add to Home Screen
                                </p>
                                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                  For push notifications, add this app to your Home Screen:
                                </p>
                                <ol className="text-xs text-amber-600 dark:text-amber-400 list-decimal ml-4 mt-1 space-y-0.5">
                                  <li>Tap Share button in Safari</li>
                                  <li>Tap "Add to Home Screen"</li>
                                  <li>Open app from Home Screen</li>
                                </ol>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* iOS Installed - Troubleshooting */}
                        {isIOS && isInStandaloneMode && !pushEnabled && (
                          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <div className="flex items-start space-x-2">
                              <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div>
                                <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                  Enable Notifications on iPhone
                                </p>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                  1. Tap the toggle above to enable<br/>
                                  2. When prompted, tap "Allow"<br/>
                                  3. If no prompt appears, check Settings → Notifications → RenziFitness
                                </p>
                                <p className="text-xs text-blue-500 dark:text-blue-400 mt-2">
                                  <strong>Note:</strong> Requires iOS 16.4 or later
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Notification Types Info */}
                        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <p className="font-medium text-gray-900 dark:text-white mb-2">You'll be notified about:</p>
                          <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                            <li className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>New messages from your trainer</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Workout reminders</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Food logging reminders</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Weekly check-ins</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Notification Sound Settings */}
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                      <h3 className="font-semibold text-lg mb-4 text-black dark:text-white">Sound Settings</h3>
                      <div className="space-y-4">
                        {/* Sound Selection */}
                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">Notification Sound</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">Choose your preferred notification tone</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {getSoundPresets().map(preset => (
                              <button
                                key={preset.id}
                                onClick={() => {
                                  const prefs = getStoredPreferences() || { enabled: true, soundId: 'chime', volume: 1, mutedTypes: [] };
                                  prefs.soundId = preset.id;
                                  savePreferences(prefs);
                                  previewSound(preset.id);
                                }}
                                className={`p-3 rounded-lg border-2 transition-all flex items-center justify-between ${
                                  (getStoredPreferences()?.soundId || 'chime') === preset.id
                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                                    : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'
                                }`}
                              >
                                <span className="text-sm font-medium text-gray-900 dark:text-white">{preset.name}</span>
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                </svg>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Volume Control */}
                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">Volume</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">Adjust notification volume</p>
                            </div>
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                              {Math.round((getStoredPreferences()?.volume || 1) * 100)}%
                            </span>
                          </div>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={(getStoredPreferences()?.volume || 1) * 100}
                            onChange={(e) => {
                              const prefs = getStoredPreferences() || { enabled: true, soundId: 'chime', volume: 1, mutedTypes: [] };
                              prefs.volume = parseInt(e.target.value) / 100;
                              savePreferences(prefs);
                            }}
                            onMouseUp={() => {
                              const prefs = getStoredPreferences();
                              if (prefs) previewSound(prefs.soundId || 'chime');
                            }}
                            onTouchEnd={() => {
                              const prefs = getStoredPreferences();
                              if (prefs) previewSound(prefs.soundId || 'chime');
                            }}
                            className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600"
                          />
                        </div>

                        {/* Mute Specific Types */}
                        <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="mb-3">
                            <p className="font-medium text-gray-900 dark:text-white">Mute Notification Types</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Silence sounds for specific notifications</p>
                          </div>
                          <div className="space-y-2">
                            {NOTIFICATION_TYPES.map(type => {
                              const prefs = getStoredPreferences() || { enabled: true, soundId: 'chime', volume: 1, mutedTypes: [] };
                              const isMuted = prefs.mutedTypes?.includes(type.id);
                              return (
                                <div key={type.id} className="flex items-center justify-between py-2">
                                  <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{type.name}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{type.description}</p>
                                  </div>
                                  <button
                                    onClick={() => {
                                      const currentPrefs = getStoredPreferences() || { enabled: true, soundId: 'chime', volume: 1, mutedTypes: [] };
                                      if (!currentPrefs.mutedTypes) currentPrefs.mutedTypes = [];
                                      if (isMuted) {
                                        currentPrefs.mutedTypes = currentPrefs.mutedTypes.filter(t => t !== type.id);
                                      } else {
                                        currentPrefs.mutedTypes.push(type.id);
                                      }
                                      savePreferences(currentPrefs);
                                      // Force re-render
                                      window.dispatchEvent(new Event('storage'));
                                    }}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                      !isMuted ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-500'
                                    }`}
                                  >
                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${
                                      !isMuted ? 'translate-x-6' : 'translate-x-1'
                                    }`} />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Master Sound Toggle */}
                        <div className="flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                              </svg>
                            </div>
                            <div>
                              <p className="font-medium text-red-700 dark:text-red-300">Mute All Sounds</p>
                              <p className="text-sm text-red-600 dark:text-red-400">Disable all in-app notification sounds</p>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const prefs = getStoredPreferences() || { enabled: true, soundId: 'chime', volume: 1, mutedTypes: [] };
                              prefs.enabled = !prefs.enabled;
                              savePreferences(prefs);
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              !(getStoredPreferences()?.enabled ?? true) ? 'bg-red-600' : 'bg-gray-300 dark:bg-gray-500'
                            }`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-md transition-transform ${
                              !(getStoredPreferences()?.enabled ?? true) ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Privacy Section */}
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                      <h3 className="font-semibold text-lg mb-4 text-black dark:text-white">Privacy</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-black dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                            </div>
                            <div>
                              <p className="font-medium" style={{color: '#1a1a1a', fontSize: '16px', fontWeight: '600'}}>Profile Visibility</p>
                              <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>Control who can see your profile</p>
                            </div>
                          </div>
                          <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                            <option>Trainer Only</option>
                            <option>Private</option>
                            <option>Public</option>
                          </select>
                        </div>
                        
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-black dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                            </div>
                            <div>
                              <p className="font-medium" style={{color: '#1a1a1a', fontSize: '16px', fontWeight: '600'}}>Data Analytics</p>
                              <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>Help improve the app with anonymous data</p>
                            </div>
                          </div>
                          <button className="relative inline-flex h-6 w-11 items-centers rounded-full bg-blue-600 dark:bg-gray-400">
                            <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6" />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Preferences Section */}
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                      <h3 className="font-semibold text-lg mb-4 text-black dark:text-white">Preferences</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-black dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                              </svg>
                            </div>
                            <div>
                              <p className="font-medium" style={{color: '#1a1a1a', fontSize: '16px', fontWeight: '600'}}>Units</p>
                              <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>Weight and measurement units</p>
                            </div>
                          </div>
                          <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                            <option>Imperial (lbs, ft)</option>
                            <option>Metric (kg, cm)</option>
                          </select>
                        </div>
                        
                        <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-black dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                              </svg>
                            </div>
                            <div>
                              <p className="font-medium" style={{color: '#1a1a1a', fontSize: '16px', fontWeight: '600'}}>Language</p>
                              <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>App display language</p>
                            </div>
                          </div>
                          <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                            <option>English</option>
                            <option>Spanish</option>
                            <option>French</option>
                            <option>German</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Account Section */}
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-6">
                      <h3 className="font-semibold text-lg mb-4 text-black dark:text-white">Account</h3>
                      <div className="space-y-4">
                        <button 
                          onClick={() => setShowPasswordModal(true)}
                          className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-black dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                              </svg>
                            </div>
                            <div className="text-left">
                              <p className="font-medium" style={{color: '#1a1a1a', fontSize: '16px', fontWeight: '600'}}>Change Password</p>
                              <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>Update your account password</p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        
                        <button 
                          onClick={handleExportData}
                          className="w-full flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-black dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                            </div>
                            <div className="text-left">
                              <p className="font-medium" style={{color: '#1a1a1a', fontSize: '16px', fontWeight: '600'}}>Export Data</p>
                              <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>Download your fitness data</p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        
                        <button className="w-full flex items-center justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-red-200 dark:bg-red-800 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-red-600 dark:text-red-400">Delete Account</p>
                              <p className="text-sm text-red-500 dark:text-red-400">Permanently delete your account</p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold " style={{color: "#1a1a1a"}}>Change Password</h3>
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordChange({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="text-gray-400 hover:" style={{color: "#2d3748"}}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleChangePassword} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>
                  Current Password
                </label>
                <input
                  type="password"
                  value={passwordChange.currentPassword}
                  onChange={(e) => setPasswordChange({ ...passwordChange, currentPassword: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter your current password"
                />
              </div>

              <div>
                <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordChange.newPassword}
                  onChange={(e) => setPasswordChange({ ...passwordChange, newPassword: e.target.value })}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter new password (min 6 characters)"
                />
                <p className="mt-1 text-xs " style={{color: "#2d3748"}}>Password must be at least 6 characters long</p>
              </div>

              <div>
                <label className="block text-sm font-medium  mb-2" style={{color: "#2d3748"}}>
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordChange.confirmPassword}
                  onChange={(e) => setPasswordChange({ ...passwordChange, confirmPassword: e.target.value })}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Confirm your new password"
                />
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordChange({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="px-4 py-2 bg-gray-200  rounded-lg hover:bg-gray-300 transition-colors" style={{color: "#2d3748"}}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Change Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bottom Navigation - Full Everfit Style */}
      <nav className={`fixed bottom-0 left-0 right-0 border-t px-2 sm:px-4 z-50 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))', paddingTop: '0.5rem' }}>
        <div className="flex justify-around items-center max-w-lg mx-auto">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex flex-col items-center py-1.5 sm:py-2 px-1.5 sm:px-3 rounded-lg transition-colors ${
              activeTab === 'overview'
                ? isDarkMode ? 'text-white bg-gray-700' : 'text-black bg-gray-100'
                : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg className="w-5 h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'overview' ? 2.5 : 2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span className={`text-[10px] sm:text-xs ${activeTab === 'overview' ? 'font-semibold' : 'font-medium'}`}>Overview</span>
          </button>

          <button
            onClick={() => setActiveTab('workouts')}
            className={`flex flex-col items-center py-1.5 sm:py-2 px-1.5 sm:px-3 rounded-lg transition-colors ${
              activeTab === 'workouts'
                ? isDarkMode ? 'text-white bg-gray-700' : 'text-black bg-gray-100'
                : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg className="w-5 h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'workouts' ? 2.5 : 2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
            <span className={`text-[10px] sm:text-xs ${activeTab === 'workouts' ? 'font-semibold' : 'font-medium'}`}>Workouts</span>
          </button>

          <button
            onClick={() => setActiveTab('nutrition')}
            className={`flex flex-col items-center py-1.5 sm:py-2 px-1.5 sm:px-3 rounded-lg transition-colors ${
              activeTab === 'nutrition'
                ? isDarkMode ? 'text-white bg-gray-700' : 'text-black bg-gray-100'
                : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg className="w-5 h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'nutrition' ? 2.5 : 2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className={`text-[10px] sm:text-xs ${activeTab === 'nutrition' ? 'font-semibold' : 'font-medium'}`}>Nutrition</span>
          </button>

          <button
            onClick={() => setActiveTab('progress')}
            className={`flex flex-col items-center py-1.5 sm:py-2 px-1.5 sm:px-3 rounded-lg transition-colors ${
              activeTab === 'progress'
                ? isDarkMode ? 'text-white bg-gray-700' : 'text-black bg-gray-100'
                : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg className="w-5 h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'progress' ? 2.5 : 2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span className={`text-[10px] sm:text-xs ${activeTab === 'progress' ? 'font-semibold' : 'font-medium'}`}>Progress</span>
          </button>

          <button
            onClick={() => setActiveTab('messages')}
            className={`flex flex-col items-center py-1.5 sm:py-2 px-1.5 sm:px-3 rounded-lg transition-colors relative ${
              activeTab === 'messages'
                ? isDarkMode ? 'text-white bg-gray-700' : 'text-black bg-gray-100'
                : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="relative">
              <svg className="w-5 h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'messages' ? 2.5 : 2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {unreadMessageCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">
                  {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                </span>
              )}
              {/* Message Preview Tooltip */}
              {latestMessagePreview && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 animate-pulse">
                  <div className={`px-3 py-2 rounded-lg shadow-lg text-xs whitespace-nowrap ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900 border border-gray-200'}`}>
                    <p className="font-semibold">{latestMessagePreview.senderName}</p>
                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>{latestMessagePreview.preview}</p>
                  </div>
                  <div className={`w-2 h-2 rotate-45 absolute left-1/2 -translate-x-1/2 -bottom-1 ${isDarkMode ? 'bg-gray-700' : 'bg-white border-r border-b border-gray-200'}`}></div>
                </div>
              )}
            </div>
            <span className={`text-[10px] sm:text-xs ${activeTab === 'messages' ? 'font-semibold' : 'font-medium'}`}>Messages</span>
          </button>

          <button
            onClick={() => setActiveTab('profile')}
            className={`flex flex-col items-center py-1.5 sm:py-2 px-1.5 sm:px-3 rounded-lg transition-colors ${
              activeTab === 'profile'
                ? isDarkMode ? 'text-white bg-gray-700' : 'text-black bg-gray-100'
                : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <svg className="w-5 h-5 mb-0.5 sm:mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === 'profile' ? 2.5 : 2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span className={`text-[10px] sm:text-xs ${activeTab === 'profile' ? 'font-semibold' : 'font-medium'}`}>Profile</span>
          </button>
        </div>
      </nav>

      {/* Error/Message Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-xl w-full max-w-md ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {errorModalMessage.includes('✅') ? 'Success' : errorModalMessage.includes('🔄') ? 'Please Wait' : 'Notice'}
              </h3>
            </div>
            <div className="p-6">
              <p className={`whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {errorModalMessage}
              </p>
            </div>
            <div className={`p-6 border-t flex justify-end ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => {
                  setShowErrorModal(false);
                  setErrorModalMessage('');
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-xl w-full max-w-md ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Confirm Action
              </h3>
            </div>
            <div className="p-6">
              <p className={`whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {confirmModalMessage}
              </p>
            </div>
            <div className={`p-6 border-t flex justify-end space-x-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmModalMessage('');
                  setConfirmModalAction(null);
                }}
                className={`px-6 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmModalAction) confirmModalAction();
                  setShowConfirmModal(false);
                  setConfirmModalMessage('');
                  setConfirmModalAction(null);
                }}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TrainerDashboard = () => {
  const [activeTab, setActiveTab] = useState('clients');
  const [activeProfileTab, setActiveProfileTab] = useState('info');
  const [activeWorkoutTab, setActiveWorkoutTab] = useState('templates');
  const [clients, setClients] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [workoutTemplates, setWorkoutTemplates] = useState([]);
  const [exerciseLibrary, setExerciseLibrary] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDetail, setShowClientDetail] = useState(false);
  const [showClientProfile, setShowClientProfile] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [showDeleteClientModal, setShowDeleteClientModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [showArchiveClientModal, setShowArchiveClientModal] = useState(false);
  const [clientToArchive, setClientToArchive] = useState(null);
  const [showArchivedClients, setShowArchivedClients] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitationLink, setInvitationLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [deletingClient, setDeletingClient] = useState(false);
  const [newWorkout, setNewWorkout] = useState({
    name: '',
    exercises: [],
    is_template: false
  });
  const [clientTargets, setClientTargets] = useState({
    protein: '150',
    carbs: '200',
    fats: '65'
  });
  const [invitations, setInvitations] = useState([]);
  const [invitationForm, setInvitationForm] = useState({
    client_name: '',
    client_email: ''
  });
  // Video upload states
  const [uploadingVideos, setUploadingVideos] = useState({});
  const [videoPreviewUrls, setVideoPreviewUrls] = useState({});

  // Push notification states
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [trainerVideoExercise, setTrainerVideoExercise] = useState(null);

  const [pushPromptDismissed, setPushPromptDismissed] = useState(() => {
    return localStorage.getItem('pushPromptDismissed') === 'true';
  });
  const [iosHomeScreenDismissed, setIosHomeScreenDismissed] = useState(() => {
    return localStorage.getItem('iosHomeScreenDismissed') === 'true';
  });
  
  // iOS detection helpers
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                             window.navigator.standalone === true;
  const showIOSHomeScreenPrompt = isIOS && !isInStandaloneMode && !iosHomeScreenDismissed;
  
  const handleDismissIOSPrompt = () => {
    setIosHomeScreenDismissed(true);
    localStorage.setItem('iosHomeScreenDismissed', 'true');
  };

  // Automation states
  const [automationSettings, setAutomationSettings] = useState({
    daily_reminders_enabled: true,
    weekly_checkins_enabled: true,
    daily_reminder_time: '09:00',
    weekly_checkin_day: 'sunday'
  });
  const [messageHistory, setMessageHistory] = useState([]);
  const [loadingAutomation, setLoadingAutomation] = useState(false);
  
  // Message modal states
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  const [messages, setMessages] = useState([]);
  const [selectedConversationClient, setSelectedConversationClient] = useState(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [latestMessagePreview, setLatestMessagePreview] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // AI Plans states
  const [pendingPlans, setPendingPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  // PR Notification states
  const [prNotifications, setPrNotifications] = useState([]);
  const [showPrNotifications, setShowPrNotifications] = useState(false);
  const [unreadPrCount, setUnreadPrCount] = useState(0);
  const [showPlanDetailModal, setShowPlanDetailModal] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [editingPlan, setEditingPlan] = useState(false);
  const [showGenerateConfirmModal, setShowGenerateConfirmModal] = useState(false);
  const [isGeneratingPlans, setIsGeneratingPlans] = useState(false);
  const [selectedClientForPlan, setSelectedClientForPlan] = useState('');
  const [trainerPlanNotes, setTrainerPlanNotes] = useState('');
  const [savedPlanNotesAt, setSavedPlanNotesAt] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalMessage, setConfirmModalMessage] = useState('');
  const [confirmModalAction, setConfirmModalAction] = useState(null);

  // Pre-fill the last instructions used for this client's plan
  useEffect(() => {
    if (!showGenerateConfirmModal || !selectedClientForPlan) return;
    setSavedPlanNotesAt(null);
    const token = localStorage.getItem('session_token');
    fetch(`${BACKEND_URL}/api/trainer/clients/${selectedClientForPlan}/plan-instructions`, {
      credentials: 'include', headers: token ? { 'X-Session-Token': token } : {}
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        setTrainerPlanNotes(d?.instructions || '');
        setSavedPlanNotesAt(d?.instructions ? d.saved_at : null);
      })
      .catch(() => {});
  }, [showGenerateConfirmModal, selectedClientForPlan]);

  // Debug log
  console.log('TrainerDashboard loaded - showGenerateConfirmModal state exists:', typeof setShowGenerateConfirmModal);

  // Restore authentication hooks
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch messages when Messages tab is active or on initial load for badge
  React.useEffect(() => {
    // Fetch on initial load to get unread count for badge
    fetchTrainerMessages();
  }, []);

  React.useEffect(() => {
    if (activeTab === 'messages') {
      fetchTrainerMessages();
    }
  }, [activeTab]);

  // WebSocket connection for real-time messages (Trainer) - connect only on user change
  React.useEffect(() => {
    if (user?.id) {
      // Connect to WebSocket
      wsService.connect(user.id, BACKEND_URL);
      
      return () => {
        // Only disconnect when user logs out
        wsService.disconnect();
      };
    }
  }, [user?.id]);
  
  // WebSocket message handler (Trainer) - updates when dependencies change
  React.useEffect(() => {
    if (!user?.id) return;
    
    // Handle new messages
    const handleNewMessage = (data) => {
      console.log('🔔 Trainer received new message via WebSocket:', data);
      
      // Play notification sound if enabled
      if (soundEnabled) {
        playNotificationSound();
      }
      
      // Update latest message preview for tooltip
      setLatestMessagePreview({
        senderName: data.sender_name,
        preview: data.preview,
        timestamp: new Date().toISOString()
      });
      
      // Clear preview after 5 seconds
      setTimeout(() => setLatestMessagePreview(null), 5000);
      
      // Refresh messages if on messages tab
      if (activeTab === 'messages') {
        fetchTrainerMessages();
      } else {
        // Just increment unread count
        setUnreadMessageCount(prev => prev + 1);
      }
    };
    
    wsService.on('new_message', handleNewMessage);
    
    return () => {
      wsService.off('new_message', handleNewMessage);
    };
  }, [user?.id, soundEnabled, activeTab]);

  const fetchWorkoutTemplates = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/workout-templates`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWorkoutTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching workout templates:', error);
    }
  };

  const createWorkoutTemplate = async (templateData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/workout-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(templateData)
      });
      if (response.ok) {
        fetchWorkoutTemplates();
        return await response.json();
      }
    } catch (error) {
      console.error('Error creating workout template:', error);
    }
  };

  const fetchTrainerExerciseLibrary = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/exercise-library`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setExerciseLibrary(data.exercises || []);
      }
    } catch (error) {
      console.error('Error fetching exercise library:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'clients') {
      fetchClients();
    } else if (activeTab === 'workouts') {
      fetchWorkouts();
      fetchWorkoutTemplates();
      fetchTrainerExerciseLibrary();
    } else if (activeTab === 'plans') {
      fetchPendingPlans();
      fetchClients(); // Need clients for the dropdown
    } else if (activeTab === 'macro-targets') {
      fetchClients();
    } else if (activeTab === 'invitations') {
      fetchInvitations();
    } else if (activeTab === 'automation') {
      fetchClients();
      fetchWorkoutTemplates();
      fetchInvitations();
      fetchAutomationSettings();
      fetchMessageHistory();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'clients') {
      fetchClients();
    }
  }, [showArchivedClients]);

  // Fetch PR notifications on mount and periodically
  useEffect(() => {
    const fetchPrNotifications = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/notifications/pr-alerts`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setPrNotifications(data.notifications || []);
          setUnreadPrCount((data.notifications || []).filter(n => !n.read).length);
        }
      } catch (error) {
        console.error('Error fetching PR notifications:', error);
      }
    };
    
    fetchPrNotifications();
    // Refresh every 30 seconds
    const interval = setInterval(fetchPrNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Handle WebSocket PR notifications
  useEffect(() => {
    const handleWsMessage = (event) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.type === 'pr_alert') {
          // Add new PR notification to the top
          setPrNotifications(prev => [data, ...prev]);
          setUnreadPrCount(prev => prev + 1);
          // Play notification sound
          if (soundEnabled && window.playNotificationSound) {
            window.playNotificationSound();
          }
        }
      } catch (e) {
        console.error('Error parsing WebSocket message:', e);
      }
    };
    
    // Subscribe to WebSocket events (if not already done elsewhere)
    window.addEventListener('ws_message', handleWsMessage);
    return () => window.removeEventListener('ws_message', handleWsMessage);
  }, [soundEnabled]);

  // Check push notification status on mount
  useEffect(() => {
    const checkPushStatus = async () => {
      const supported = isPushSupported();
      setPushSupported(supported);
      
      if (supported) {
        const enabled = await isPushEnabled();
        setPushEnabled(enabled);
      }
    };
    checkPushStatus();
  }, []);

  // Handle push notification toggle for trainers
  const handleTogglePushNotifications = async () => {
    setPushLoading(true);
    try {
      if (pushEnabled) {
        await disablePushNotifications();
        setPushEnabled(false);
        alert('Push notifications disabled');
      } else {
        await enablePushNotifications();
        setPushEnabled(true);
        alert('Push notifications enabled! You will now receive alerts for client messages and updates.');
      }
    } catch (error) {
      console.error('Error toggling push notifications:', error);
      if (error.message?.includes('denied')) {
        alert('Notifications are blocked for this site.\n\nTo enable:\n1. Tap the lock/info icon in your browser\'s address bar\n2. Find "Notifications" in Site Settings\n3. Change from "Block" to "Allow"\n4. Refresh the page and try again');
      } else {
        alert(error.message || 'Failed to toggle push notifications');
      }
    } finally {
      setPushLoading(false);
    }
  };

  // Handle enabling push from the Messages banner
  const handleEnablePushFromBanner = async () => {
    setPushLoading(true);
    try {
      await enablePushNotifications();
      setPushEnabled(true);
      setPushPromptDismissed(true);
      localStorage.setItem('pushPromptDismissed', 'true');
      alert('✅ Push notifications enabled!\n\nYou will now receive notifications when:\n• Your trainer sends you a message\n• You have workout reminders\n• You receive updates');
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      // If permission denied, still dismiss the prompt
      if (error.message?.includes('denied')) {
        setPushPromptDismissed(true);
        localStorage.setItem('pushPromptDismissed', 'true');
        alert('Notifications are blocked for this site.\n\nTo enable:\n1. Tap the lock/info icon in your browser\'s address bar\n2. Find "Notifications" in Site Settings\n3. Change from "Block" to "Allow"\n4. Refresh the page and try again');
      } else {
        alert(error.message || 'Failed to enable push notifications');
      }
    } finally {
      setPushLoading(false);
    }
  };

  // Dismiss push prompt without enabling
  const handleDismissPushPrompt = () => {
    setPushPromptDismissed(true);
    localStorage.setItem('pushPromptDismissed', 'true');
  };

  // Send test push notification
  const handleTestPushNotification = async () => {
    try {
      const result = await sendTestPush();
      if (result.sent > 0) {
        alert('Test notification sent! Check your notifications. (' + result.subscriptions_found + ' subscription(s) found)');
      } else if (result.errors) {
        alert('Notification could not be delivered.\n\nReason: ' + result.errors.map(e => `${e.status || ''} ${e.reason || ''}`.trim()).join('; ') + '\n\n' + result.message);
      } else {
        alert('No active push subscriptions found for this account.\n\n' + (result.debug ? 'Account: ' + result.debug.user_email + '\n\n' : '') + 'Try toggling push OFF then ON again, or make sure you are logged into the correct account.');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      alert('Failed to send test notification. Try toggling push OFF then ON again.');
    }
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) {
      console.log('No client to delete');
      return;
    }
    
    console.log('Starting delete for client:', clientToDelete);
    setDeletingClient(true);
    
    try {
      console.log('Making DELETE request to:', `${BACKEND_URL}/api/clients/${clientToDelete.id}`);
      const response = await fetch(`${BACKEND_URL}/api/clients/${clientToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      console.log('Delete response status:', response.status);
      
      if (response.ok) {
        console.log('Delete successful, refreshing clients list');
        await fetchClients();
        alert('Client deleted successfully');
      } else {
        const error = await response.json();
        console.error('Delete error:', error);
        alert(`Failed to delete client: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      alert(`Error deleting client: ${error.message}`);
    } finally {
      console.log('Cleaning up delete modal');
      setDeletingClient(false);
      setShowDeleteClientModal(false);
      setClientToDelete(null);
    }
  };

  const handleArchiveClient = async () => {
    if (!clientToArchive) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${clientToArchive.id}/archive`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        await fetchClients();
        alert('Client archived successfully');
      } else {
        const error = await response.json();
        alert(`Failed to archive client: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error archiving client:', error);
      alert(`Error archiving client: ${error.message}`);
    } finally {
      setShowArchiveClientModal(false);
      setClientToArchive(null);
    }
  };

  const handleUnarchiveClient = async (clientId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients/${clientId}/unarchive`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        await fetchClients();
        alert('Client reactivated successfully');
      } else {
        const error = await response.json();
        alert(`Failed to reactivate client: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error reactivating client:', error);
      alert(`Error reactivating client: ${error.message}`);
    }
  };

  // Fetch messages for trainer
  const fetchTrainerMessages = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/messages`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        const messagesArray = data.messages || [];
        setMessages(messagesArray);
        
        // Calculate unread count (messages from clients that haven't been read)
        const unreadCount = messagesArray.filter(msg => 
          msg.sender_id !== user?.id && !msg.read
        ).length;
        setUnreadMessageCount(unreadCount);
      } else if (response.status === 401) {
        console.error('Session expired, please re-login');
        alert('Your session has expired. Please log in again.');
        logout();
      } else {
        console.error('Failed to fetch messages:', response.status);
        setMessages([]);
        setUnreadMessageCount(0);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setMessages([]);
      setUnreadMessageCount(0);
    }
  };

  // Mark PR notification as read
  const markPrNotificationRead = async (notificationId) => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        credentials: 'include'
      });
      setPrNotifications(prev => 
        prev.map(n => n.id === notificationId || n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadPrCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all PR notifications as read
  const markAllPrNotificationsRead = async () => {
    for (const notification of prNotifications.filter(n => !n.read)) {
      await markPrNotificationRead(notification.id || notification.notification_id);
    }
  };

  // Mark trainer messages as read when viewing a client's messages
  const markTrainerMessagesAsRead = async (clientId) => {
    try {
      const unreadFromClient = messages.filter(msg => 
        msg.sender_id === clientId && !msg.read
      );
      
      for (const msg of unreadFromClient) {
        await fetch(`${BACKEND_URL}/api/messages/mark-read/${msg.id}`, {
          method: 'POST',
          credentials: 'include'
        });
      }
      
      // Update local state
      if (unreadFromClient.length > 0) {
        setMessages(prev => prev.map(msg => 
          msg.sender_id === clientId ? { ...msg, read: true } : msg
        ));
        setUnreadMessageCount(prev => Math.max(0, prev - unreadFromClient.length));
      }
    } catch (error) {
      console.error('Error marking trainer messages as read:', error);
    }
  };

  // Send message to client
  const handleSendMessageToClient = async (e) => {
    e.preventDefault();
    
    if (!messageContent.trim() || !messageRecipient) return;
    
    try {
      console.log('📤 Sending message to client:', {
        receiver_id: messageRecipient.id,
        receiver_name: messageRecipient.name,
        message: messageContent.trim()
      });
      
      const response = await fetch(`${BACKEND_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          receiver_id: messageRecipient.id,
          message: messageContent.trim()
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('✅ Message sent successfully:', result);
        setMessageContent('');
        setShowMessageModal(false);
        setMessageRecipient(null);
        await fetchTrainerMessages();
        alert('Message sent successfully!');
      } else {
        const error = await response.json();
        console.error('❌ Failed to send message:', error);
        alert(`Failed to send message: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message. Please try again.');
    }
  };

  const handleSendInvitation = async (e) => {
    e.preventDefault();
    
    if (!invitationForm.client_name || !invitationForm.client_email) {
      alert('Please fill in all fields');
      return;
    }

    try {
      console.log('Sending invitation request:', invitationForm);
      const response = await fetch(`${BACKEND_URL}/api/invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_name: invitationForm.client_name,
          client_email: invitationForm.client_email
        })
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Invitation response:', data);
        setInvitationLink(data.invitation_link);
        setInvitationForm({ client_name: '', client_email: '' });
        alert('Invitation created successfully! Copy the link to send to your client.');
      } else {
        const error = await response.json();
        console.error('Error response:', error);
        alert(`Failed to create invitation: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating invitation:', error);
      alert('Error creating invitation. Please try again.');
    }
  };

  const handleCopyLink = () => {
    // Try modern clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(invitationLink)
        .then(() => {
          setLinkCopied(true);
          setTimeout(() => setLinkCopied(false), 2000);
        })
        .catch((err) => {
          console.error('Clipboard API failed:', err);
          // Fallback to older method
          fallbackCopyTextToClipboard(invitationLink);
        });
    } else {
      // Use fallback for older browsers
      fallbackCopyTextToClipboard(invitationLink);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.width = "2em";
    textArea.style.height = "2em";
    textArea.style.padding = "0";
    textArea.style.border = "none";
    textArea.style.outline = "none";
    textArea.style.boxShadow = "none";
    textArea.style.background = "transparent";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } else {
        alert('Copy failed. Please manually select and copy the link.');
      }
    } catch (err) {
      console.error('Fallback copy failed:', err);
      alert('Copy failed. Please manually select and copy the link.');
    }
    
    document.body.removeChild(textArea);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;
    
    console.log('User confirmed deletion');
    try {
      console.log('Making DELETE request to:', `${BACKEND_URL}/api/workout-templates/${templateToDelete.id}`);
      const response = await fetch(`${BACKEND_URL}/api/workout-templates/${templateToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      console.log('DELETE response status:', response.status);
      
      if (response.ok) {
        console.log('Delete successful, refreshing templates');
        await fetchWorkoutTemplates();
        alert('Template deleted successfully');
      } else {
        const error = await response.json();
        console.error('Delete failed:', error);
        alert(`Failed to delete template: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      alert(`Error deleting template: ${error.message}`);
    } finally {
      setShowDeleteConfirmModal(false);
      setTemplateToDelete(null);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clients?archived=${showArchivedClients}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchWorkouts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/workouts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWorkouts(data.workouts);
      }
    } catch (error) {
      console.error('Error fetching workouts:', error);
    }
  };


  const fetchPendingPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await fetch(`${BACKEND_URL}/api/trainer/pending-plans`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPendingPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Error fetching pending plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleApprovePlan = async (planId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/trainer/plan/${planId}/approve`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        setErrorModalMessage('✅ Plan Approved!\n\nThe workout and diet plan has been approved and assigned to the client.');
        setShowErrorModal(true);
        fetchPendingPlans();
        setShowPlanDetailModal(false);
      } else {
        const data = await response.json();
        setErrorModalMessage(`❌ Error\n\n${data.detail || 'Failed to approve plan'}`);
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error approving plan:', error);
      setErrorModalMessage('❌ Error\n\nFailed to approve plan. Please try again.');
      setShowErrorModal(true);
    }
  };

  const handleRegeneratePlan = async (planId) => {
    try {
      setErrorModalMessage('🔄 Regenerating Plan...\n\nThis may take a moment while the AI generates new workout and diet plans.');
      setShowErrorModal(true);
      
      const response = await fetch(`${BACKEND_URL}/api/trainer/plan/${planId}/regenerate`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        // Reload the plan details
        const detailResponse = await fetch(`${BACKEND_URL}/api/trainer/plan/${planId}`, {
          credentials: 'include'
        });
        if (detailResponse.ok) {
          const data = await detailResponse.json();
          setSelectedPlan(data.plan);
          setShowErrorModal(false);
          setErrorModalMessage('✅ Plan Regenerated!\n\nThe workout and diet plans have been regenerated with new AI recommendations.');
          setShowErrorModal(true);
        }
      } else {
        const data = await response.json();
        setErrorModalMessage(`❌ Error\n\n${data.detail || 'Failed to regenerate plan'}`);
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error('Error regenerating plan:', error);
      setErrorModalMessage('❌ Error\n\nFailed to regenerate plan. Please try again.');
      setShowErrorModal(true);
    }
  };

  const handleRejectPlan = async (planId) => {
    // Use the custom confirmation modal instead of window.confirm
    setConfirmModalMessage('Are you sure you want to reject this plan? This action cannot be undone.');
    setConfirmModalAction(() => async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/trainer/plan/${planId}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        if (response.ok) {
          setErrorModalMessage('Plan has been rejected and removed.');
          setShowErrorModal(true);
          fetchPendingPlans();
          setShowPlanDetailModal(false);
        } else {
          const data = await response.json();
          setErrorModalMessage(`❌ Error\n\n${data.detail || 'Failed to reject plan'}`);
          setShowErrorModal(true);
        }
      } catch (error) {
        console.error('Error rejecting plan:', error);
        setErrorModalMessage('❌ Error\n\nFailed to reject plan. Please try again.');
        setShowErrorModal(true);
      }
    });
    setShowConfirmModal(true);
  };


  const handleCreateWorkout = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/workouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newWorkout)
      });

      if (response.ok) {
        fetchWorkouts();
        setNewWorkout({ 
          name: '', 
          exercises: [{ name: '', sets: '', reps: '', video_url: '', external_video_url: '', instructions: '' }] 
        });
        // Clear video preview URLs
        setVideoPreviewUrls({});
      }
    } catch (error) {
      console.error('Error creating workout:', error);
    }
  };

  const addExercise = () => {
    setNewWorkout({
      ...newWorkout,
      exercises: [...newWorkout.exercises, { name: '', sets: '', reps: '', video_url: '', external_video_url: '', instructions: '' }]
    });
  };

  const updateExercise = (index, field, value) => {
    const updatedExercises = [...newWorkout.exercises];
    updatedExercises[index][field] = value;
    setNewWorkout({ ...newWorkout, exercises: updatedExercises });
  };

  // Video upload functionality
  const handleVideoUpload = async (exerciseIndex, file) => {
    if (!file) return;
    
    // Check file type
    const allowedTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      alert('Please upload a valid video file (MP4, AVI, MOV, WMV)');
      return;
    }
    
    // Check file size (100MB limit)
    if (file.size > 100 * 1024 * 1024) {
      alert('Video file size must be less than 100MB');
      return;
    }
    
    setUploadingVideos(prev => ({ ...prev, [exerciseIndex]: true }));
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/videos/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        updateExercise(exerciseIndex, 'video_url', data.video_url);
        
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setVideoPreviewUrls(prev => ({ ...prev, [exerciseIndex]: previewUrl }));
        
        alert('Video uploaded successfully!');
      } else {
        const error = await response.json();
        alert('Upload failed: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploadingVideos(prev => ({ ...prev, [exerciseIndex]: false }));
    }
  };
  
  const removeVideo = async (exerciseIndex) => {
    const exercise = newWorkout.exercises[exerciseIndex];
    
    // If there's an uploaded video, try to delete it
    if (exercise.video_url) {
      const filename = exercise.video_url.split('/').pop();
      try {
        await fetch(`${BACKEND_URL}/api/videos/${filename}`, {
          method: 'DELETE',
          credentials: 'include'
        });
      } catch (error) {
        console.error('Error deleting video:', error);
      }
    }
    
    // Clear video URLs and preview
    updateExercise(exerciseIndex, 'video_url', '');
    updateExercise(exerciseIndex, 'external_video_url', '');
    
    // Clean up preview URL
    if (videoPreviewUrls[exerciseIndex]) {
      URL.revokeObjectURL(videoPreviewUrls[exerciseIndex]);
      setVideoPreviewUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[exerciseIndex];
        return newUrls;
      });
    }
  };
  
  const removeExercise = (index) => {
    // Clean up video if exists
    removeVideo(index);
    
    // Remove exercise
    const updatedExercises = newWorkout.exercises.filter((_, i) => i !== index);
    setNewWorkout({ ...newWorkout, exercises: updatedExercises });
  };

  const handleSetClientTargets = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/macro-targets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          protein: parseFloat(clientTargets.protein),
          carbs: parseFloat(clientTargets.carbs),
          fats: parseFloat(clientTargets.fats),
          client_id: selectedClient.id
        })
      });

      if (response.ok) {
        alert('Macro targets set successfully for ' + selectedClient.name);
        setSelectedClient(null);
        setClientTargets({ protein: '150', carbs: '200', fats: '65' });
      } else {
        console.error('Failed to set targets');
      }
    } catch (error) {
      console.error('Error setting client targets:', error);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setInvitations(data.invitations);
      }
    } catch (error) {
      console.error('Error fetching invitations:', error);
    }
  };

  const handleCreateInvitation = async (e) => {
    e.preventDefault();
    if (!invitationForm.client_name || !invitationForm.client_email) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/invitations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(invitationForm)
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Invitation created successfully! Share this link: ${data.invitation_link}`);
        setInvitationForm({ client_name: '', client_email: '' });
        fetchInvitations();
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error creating invitation:', error);
      alert('Error creating invitation');
    }
  };

  const copyInvitationLink = async (token) => {
    const link = `${window.location.origin}/signup?invitation=${token}`;
    try {
      await navigator.clipboard.writeText(link);
      alert('Invitation link copied to clipboard!');
    } catch (error) {
      alert(`Copy this link: ${link}`);
    }
  };

  // Automated Messaging Functions
  const triggerAutomatedMessages = async (messageType = null) => {
    setLoadingAutomation(true);
    try {
      const endpoint = messageType 
        ? `${BACKEND_URL}/api/automated-messages/trigger`
        : `${BACKEND_URL}/api/automated-messages/send`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: messageType ? JSON.stringify({ message_type: messageType }) : JSON.stringify({})
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.message}`);
        fetchMessageHistory();
      } else {
        alert('❌ Failed to send automated messages');
      }
    } catch (error) {
      console.error('Error triggering automated messages:', error);
      alert('❌ Error sending messages');
    } finally {
      setLoadingAutomation(false);
    }
  };

  const fetchAutomationSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/automated-messages/settings`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAutomationSettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching automation settings:', error);
    }
  };

  const updateAutomationSettings = async (newSettings) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/automated-messages/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newSettings)
      });

      if (response.ok) {
        const data = await response.json();
        setAutomationSettings(data.settings);
        alert('✅ Settings updated successfully!');
      } else {
        alert('❌ Failed to update settings');
      }
    } catch (error) {
      console.error('Error updating automation settings:', error);
      alert('❌ Error updating settings');
    }
  };

  const fetchMessageHistory = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/automated-messages/history`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMessageHistory(data.message_history);
      }
    } catch (error) {
      console.error('Error fetching message history:', error);
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`} id="trainer-dashboard">
      <style>{`
        ${isDarkMode ? `
        /* TRAINER DARK MODE: Override ALL text to be light by default */
        #trainer-dashboard * {
          color: #e5e7eb;
        }
        
        /* TRAINER DARK MODE: Ensure inline styles are overridden */
        #trainer-dashboard [style*="color"] {
          color: #e5e7eb !important;
        }
        
        /* TRAINER DARK MODE: Table rows */
        #trainer-dashboard table tbody tr {
          background-color: #1f2937 !important;
          border-color: #374151 !important;
        }
        #trainer-dashboard table tbody tr:hover {
          background-color: #374151 !important;
        }
        #trainer-dashboard table tbody td {
          color: #f3f4f6 !important;
          border-color: #374151 !important;
        }
        #trainer-dashboard table tbody td * {
          color: #f3f4f6 !important;
        }
        
        /* TRAINER DARK MODE: Card backgrounds */
        #trainer-dashboard .bg-white {
          background-color: #1f2937 !important;
        }
        #trainer-dashboard .bg-gray-50 {
          background-color: #374151 !important;
        }
        #trainer-dashboard .bg-gray-100 {
          background-color: #374151 !important;
        }
        
        /* TRAINER DARK MODE: Borders */
        #trainer-dashboard .border-gray-200,
        #trainer-dashboard .border-gray-100 {
          border-color: #374151 !important;
        }
        
        /* TRAINER DARK MODE: Make headings and important text bright white */
        #trainer-dashboard h1, #trainer-dashboard h2, #trainer-dashboard h3,
        #trainer-dashboard .font-semibold, #trainer-dashboard .font-bold,
        #trainer-dashboard .font-medium {
          color: #ffffff !important;
        }
        
        /* TRAINER DARK MODE: Secondary text slightly dimmer */
        #trainer-dashboard .text-sm, #trainer-dashboard .text-xs {
          color: #d1d5db !important;
        }
        
        /* TRAINER DARK MODE: Buttons with colored backgrounds - white text */
        #trainer-dashboard .bg-blue-600,
        #trainer-dashboard .bg-blue-600 *,
        #trainer-dashboard .bg-green-600,
        #trainer-dashboard .bg-green-600 *,
        #trainer-dashboard .bg-red-600,
        #trainer-dashboard .bg-red-600 * {
          color: #ffffff !important;
        }
        
        /* TRAINER DARK MODE: Gray buttons */
        #trainer-dashboard button.bg-gray-100,
        #trainer-dashboard button.bg-gray-200 {
          background-color: #374151 !important;
          color: #e5e7eb !important;
        }
        
        /* TRAINER DARK MODE: Placeholder text in inputs */
        #trainer-dashboard input::placeholder,
        #trainer-dashboard textarea::placeholder {
          color: #9ca3af !important;
        }
        
        /* TRAINER DARK MODE: Input fields */
        #trainer-dashboard input,
        #trainer-dashboard textarea,
        #trainer-dashboard select {
          background-color: #374151 !important;
          color: #f3f4f6 !important;
          border-color: #4b5563 !important;
        }
        ` : ''}
      `}</style>
      {/* Header - with safe area padding for iOS notch */}
      <header className={`shadow-sm border-b ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="container mx-auto px-3 sm:px-6 py-2 sm:py-3 flex justify-between items-center">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-full overflow-hidden border-2 shadow-lg ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-white'}`}>
              <img 
                src="https://customer-assets.emergentagent.com/job_renzifitness/artifacts/oeuw8yx5_White%20Background.png"
                alt="RenziFitness Logo"
                className="w-full h-full object-cover scale-125"
              />
            </div>
            <h1 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>RenziFitness</h1>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* PR Notifications Bell */}
            <div className="relative">
              <button
                onClick={() => setShowPrNotifications(!showPrNotifications)}
                className={`p-2 rounded-lg relative ${isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
                title="PR Alerts"
              >
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadPrCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadPrCount > 9 ? '9+' : unreadPrCount}
                  </span>
                )}
              </button>
              
              {/* PR Notifications Dropdown */}
              {showPrNotifications && (
                <div className={`absolute right-0 mt-2 w-80 sm:w-96 rounded-xl shadow-xl border z-50 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                  <div className={`p-3 border-b flex items-center justify-between ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <h3 className={`font-semibold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      <span className="text-yellow-500 mr-2">🏆</span>
                      PR Alerts
                    </h3>
                    {unreadPrCount > 0 && (
                      <button
                        onClick={markAllPrNotificationsRead}
                        className="text-xs text-blue-500 hover:text-blue-600"
                      >
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {prNotifications.length === 0 ? (
                      <div className={`p-4 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span className="text-2xl block mb-2">🏋️</span>
                        No PR alerts yet
                      </div>
                    ) : (
                      prNotifications.slice(0, 10).map((notification, index) => (
                        <div
                          key={notification.id || notification.notification_id || index}
                          onClick={() => markPrNotificationRead(notification.id || notification.notification_id)}
                          className={`p-3 border-b cursor-pointer transition-colors ${
                            notification.read 
                              ? isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
                              : isDarkMode ? 'bg-yellow-900/20 border-gray-700' : 'bg-yellow-50 border-yellow-100'
                          } ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                        >
                          <div className="flex items-start space-x-3">
                            <span className="text-2xl">
                              {notification.pr_type === 'first' ? '🎉' : notification.pr_type === 'weight' ? '🏆' : '💪'}
                            </span>
                            <div className="flex-1">
                              <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                {notification.client_name} hit a new PR!
                              </p>
                              <p className={`text-sm ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                                {notification.exercise_name}: {notification.weight} lbs × {notification.reps}
                              </p>
                              {notification.previous_weight && notification.pr_type === 'weight' && (
                                <p className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                  +{(notification.weight - notification.previous_weight).toFixed(0)} lbs improvement!
                                </p>
                              )}
                              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                {notification.created_at || notification.timestamp 
                                  ? new Date(notification.created_at || notification.timestamp).toLocaleString()
                                  : 'Just now'
                                }
                              </p>
                            </div>
                            {!notification.read && (
                              <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></span>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <span className={`hidden sm:inline text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Hi, {user?.name}</span>
            <button
              onClick={logout}
              className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm rounded-lg border ${isDarkMode ? 'border-gray-600 text-white hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className={`border-b sticky top-0 z-10 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="container mx-auto px-3 sm:px-6">
          <div className="flex space-x-4 sm:space-x-8 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveTab('clients')}
              className={`py-3 sm:py-4 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                activeTab === 'clients'
                  ? 'border-blue-600 text-blue-600'
                  : isDarkMode ? 'border-transparent text-gray-300 hover:text-gray-100' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Clients
            </button>
            <button
              onClick={() => setActiveTab('plans')}
              className={`py-3 sm:py-4 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                activeTab === 'plans'
                  ? 'border-blue-600 text-blue-600'
                  : isDarkMode ? 'border-transparent text-gray-300 hover:text-gray-100' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              AI Plans
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`py-3 sm:py-4 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 relative ${
                activeTab === 'messages'
                  ? 'border-blue-600 text-blue-600'
                  : isDarkMode ? 'border-transparent text-gray-300 hover:text-gray-100' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="relative">
                Messages
                {unreadMessageCount > 0 && (
                  <span className="absolute -top-2 -right-4 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {unreadMessageCount > 99 ? '99+' : unreadMessageCount}
                  </span>
                )}
              </span>
              {/* Message Preview Tooltip */}
              {latestMessagePreview && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 animate-pulse">
                  <div className={`px-3 py-2 rounded-lg shadow-lg text-xs whitespace-nowrap ${isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900 border border-gray-200'}`}>
                    <p className="font-semibold text-left">{latestMessagePreview.senderName}</p>
                    <p className={`text-left ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{latestMessagePreview.preview}</p>
                  </div>
                  <div className={`w-2 h-2 rotate-45 absolute left-1/2 -translate-x-1/2 -top-1 ${isDarkMode ? 'bg-gray-700' : 'bg-white border-l border-t border-gray-200'}`}></div>
                </div>
              )}
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              className={`py-3 sm:py-4 px-2 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap flex-shrink-0 ${
                activeTab === 'profile'
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-100'
              }`}
            >
              Profile
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="container mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {activeTab === 'clients' && !showClientProfile && (
          <div className={`rounded-xl sm:rounded-2xl shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 sm:p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} gap-3`}>
              <h2 className={`text-lg sm:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {showArchivedClients ? 'Archived Clients' : 'My Clients'}
              </h2>
              <div className="flex items-center justify-between sm:justify-end space-x-2 sm:space-x-4">
                {!showArchivedClients && (
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs sm:text-sm font-medium transition-colors flex items-center space-x-1 sm:space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="hidden xs:inline">Invite</span>
                    <span className="xs:hidden sm:inline">Invite Client</span>
                  </button>
                )}
                <button
                  onClick={() => {
                    console.log('Toggle clicked, current state:', showArchivedClients);
                    setShowArchivedClients(!showArchivedClients);
                    console.log('New state will be:', !showArchivedClients);
                  }}
                  className={`px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded-lg font-medium transition-colors ${
                    showArchivedClients 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {showArchivedClients ? '← Active' : '📦 Archived'}
                </button>
                <div className={`text-xs sm:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {clients.length}
                </div>
              </div>
            </div>
            
            {clients.length === 0 ? (
              <div className="text-center py-12">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2m5-8a3 3 0 110-6 3 3 0 010 6z" />
                  </svg>
                </div>
                <p className={`text-lg mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>No clients yet</p>
                <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Generate invitation links to invite your first clients</p>
              </div>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="block md:hidden p-3 space-y-3">
                  {clients.map((client, index) => (
                    <div 
                      key={client.id}
                      onClick={() => {
                        setSelectedClient(client);
                        setShowClientDetail(true);
                      }}
                      className={`p-4 rounded-xl border cursor-pointer transition-all ${
                        isDarkMode 
                          ? 'bg-gray-700 border-gray-600 hover:bg-gray-650' 
                          : 'bg-white border-gray-200 hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg ${
                            ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-yellow-500'][index % 5]
                          }`}>
                            {client.name?.charAt(0)?.toUpperCase() || 'C'}
                          </div>
                          <div>
                            <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{client.name}</div>
                            <div className="flex items-center space-x-2 mt-0.5">
                              <span className="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                                Connected
                              </span>
                            </div>
                          </div>
                        </div>
                        <svg className={`w-5 h-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}`}>
                          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Last Activity</div>
                          <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {Math.floor(Math.random() * 7) === 0 ? 'Today' : `${Math.floor(Math.random() * 7) + 1}d ago`}
                          </div>
                        </div>
                        <div className={`p-2 rounded-lg ${isDarkMode ? 'bg-gray-600' : 'bg-gray-50'}`}>
                          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>7d Training</div>
                          <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{Math.floor(Math.random() * 100)}%</div>
                        </div>
                      </div>
                      
                      {/* Quick Actions */}
                      <div className="flex items-center justify-end space-x-2 mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                        {showArchivedClients ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnarchiveClient(client.id);
                            }}
                            className="flex items-center space-x-1 text-green-600 hover:text-green-800 px-3 py-1.5 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 text-sm font-medium"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            <span>Reactivate</span>
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setMessageRecipient(client);
                                setShowMessageModal(true);
                              }}
                              className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 px-3 py-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-sm font-medium"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                              <span>Message</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setClientToArchive(client);
                                setShowArchiveClientModal(true);
                              }}
                              className="p-2 text-orange-600 hover:text-orange-800 rounded-lg hover:bg-orange-50 dark:hover:bg-orange-900/20"
                              title="Archive"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className={`border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                    <tr>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <div className="flex items-center space-x-1 cursor-pointer">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>Name</span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <div className="flex items-center space-x-1 cursor-pointer">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Last Activity</span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <div className="flex items-center space-x-1 cursor-pointer">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <span>Last 7d Training</span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </th>
                      <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        <div className="flex items-center space-x-1 cursor-pointer">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          <span>Last 30d Training</span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium  uppercase tracking-wider dark:text-gray-300" style={{color: "#2d3748"}}>
                        <div className="flex items-center space-x-1 cursor-pointer hover: dark:text-gray-200" style={{color: "#2d3748"}}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <span>Last 7d Tasks</span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium  uppercase tracking-wider dark:text-gray-300" style={{color: "#2d3748"}}>
                        <div className="flex items-center space-x-1 cursor-pointer hover: dark:text-gray-200" style={{color: "#2d3748"}}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                          </svg>
                          <span>Category</span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium  uppercase tracking-wider dark:text-gray-300" style={{color: "#2d3748"}}>
                        <div className="flex items-center space-x-1 cursor-pointer hover: dark:text-gray-200" style={{color: "#2d3748"}}>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Status</span>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium  uppercase tracking-wider dark:text-gray-300" style={{color: "#2d3748"}}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800">
                    {clients.map((client, index) => (
                      <tr 
                        key={client.id} 
                        onClick={() => {
                          setSelectedClient(client);
                          setShowClientDetail(true);
                        }}
                        className="hover:bg-gray-50 cursor-pointer transition-colors dark:bg-gray-700"
                      >
                        {/* Name Column */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                              ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-yellow-500'][index % 5]
                            }`}>
                              {client.name?.charAt(0)?.toUpperCase() || 'C'}
                            </div>
                            <div>
                              <div className="text-sm font-medium  dark:text-white" style={{color: "#1a1a1a"}}>{client.name}</div>
                            </div>
                          </div>
                        </td>
                        
                        {/* Last Activity */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            <span className="text-sm  dark:text-gray-300" style={{color: "#2d3748"}}>
                              {Math.floor(Math.random() * 7) === 0 ? 'Today' : `${Math.floor(Math.random() * 7) + 1}d`}
                            </span>
                          </div>
                        </td>
                        
                        {/* Last 7d Training */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {Math.floor(Math.random() * 100)}%
                          </div>
                        </td>
                        
                        {/* Last 30d Training */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {Math.floor(Math.random() * 100)}%
                          </div>
                        </td>
                        
                        {/* Last 7d Tasks */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm  dark:text-gray-300" style={{color: "#2d3748"}}>--</div>
                        </td>
                        
                        {/* Category */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm  dark:text-gray-300" style={{color: "#2d3748"}}>--</div>
                        </td>
                        
                        {/* Status */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-900 dark:text-white">Online</span>
                            <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                              Connected
                            </span>
                          </div>
                        </td>
                        
                        {/* Actions */}
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            {(() => {
                              console.log('Rendering actions for client:', client.name, 'showArchivedClients:', showArchivedClients);
                              return showArchivedClients ? (
                                /* Reactivate button for archived clients */
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('Reactivate button clicked for:', client.name);
                                    handleUnarchiveClient(client.id);
                                  }}
                                  className="text-green-600 hover:text-green-800 hover:bg-green-50 p-2 rounded transition-colors"
                                  title="Reactivate client"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                  </svg>
                                </button>
                              ) : (
                                <>
                                  {/* Message button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMessageRecipient(client);
                                      setShowMessageModal(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded transition-colors"
                                    title="Send message"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                  </button>
                                  {/* Archive button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setClientToArchive(client);
                                      setShowArchiveClientModal(true);
                                    }}
                                    className="text-orange-600 hover:text-orange-800 hover:bg-orange-50 p-2 rounded transition-colors"
                                    title="Archive client"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                                    </svg>
                                  </button>
                                  {/* Delete button */}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      alert('Trash icon clicked! Opening delete modal...');
                                      setClientToDelete(client);
                                      setShowDeleteClientModal(true);
                                    }}
                                    className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded transition-colors"
                                    title="Delete client"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </>
            )}

            {/* Client Detail Modal */}
            <ClientDetailModal
              isOpen={showClientDetail}
              onClose={() => {
                setShowClientDetail(false);
                setSelectedClient(null);
              }}
              client={selectedClient}
              workoutTemplates={workoutTemplates}
              workouts={workouts}
              backendUrl={BACKEND_URL}
            />
          </div>
        )}

        {/* Individual Client Profile */}
        {activeTab === 'clients' && showClientProfile && selectedClient && (
          <EverfitClientProfile 
            client={selectedClient}
            onClose={() => {
              setShowClientProfile(false);
              setSelectedClient(null);
            }}
            backendUrl={BACKEND_URL}
          />
        )}

        {activeTab === 'workouts' && (
          <div className="space-y-8">
            {/* Workout Templates Tab */}
            <div className="bg-white rounded-2xl shadow-lg dark:bg-gray-800">
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex">
                  <button 
                    onClick={() => setActiveWorkoutTab('templates')}
                    className={`w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                      activeWorkoutTab === 'templates'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Templates
                  </button>
                  <button 
                    onClick={() => setActiveWorkoutTab('create-new')}
                    className={`w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                      activeWorkoutTab === 'create-new'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Create New
                  </button>
                </nav>
              </div>

              <div className="p-6">
                <>
                {activeWorkoutTab === 'templates' && (
                  <div>
                    {/* Existing Templates */}
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-white">Workout Templates</h3>
                  
                  {workoutTemplates.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-gray-700">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 011-1h1a1 1 0 011 1v1m-4 0h4m-4 4v1m0-1h4m-4 1v1h4m-2-3h.01" />
                        </svg>
                      </div>
                      <p className=" text-lg mb-2 dark:text-gray-300" style={{color: "#2d3748"}}>No templates created yet</p>
                      <p className="text-gray-400 text-sm">Create your first workout template to get started</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {workoutTemplates.map(template => (
                        <div key={template.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow dark:border-gray-700">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-semibold text-lg text-gray-900 dark:text-white">{template.name}</h4>
                              {template.description && (
                                <p className="text-sm  mt-1 dark:text-gray-400" style={{color: "#2d3748"}}>{template.description}</p>
                              )}
                            </div>
                            {template.difficulty_level && (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                template.difficulty_level === 'Beginner' ? 'bg-green-100 text-green-800' :
                                template.difficulty_level === 'Intermediate' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {template.difficulty_level}
                              </span>
                            )}
                          </div>
                          
                          <div className="text-sm  mb-4 dark:text-gray-400" style={{color: "#2d3748"}}>
                            <p>{template.exercises.length} exercises</p>
                            {template.estimated_duration && (
                              <p>{template.estimated_duration} minutes</p>
                            )}
                          </div>

                          {/* Exercise Preview */}
                          <div className="space-y-1 mb-4">
                            {template.exercises.slice(0, 3).map((exercise, idx) => (
                              <div key={idx} className="text-sm  dark:text-gray-200" style={{color: "#2d3748"}}>
                                {exercise.name}
                              </div>
                            ))}
                            {template.exercises.length > 3 && (
                              <div className="text-sm  dark:text-gray-300" style={{color: "#2d3748"}}>
                                +{template.exercises.length - 3} more exercises
                              </div>
                            )}
                          </div>

                          <div className="flex flex-col space-y-2">
                            <button 
                              onClick={() => {
                                setNewWorkout({
                                  name: `${template.name} - ${new Date().toLocaleDateString()}`,
                                  exercises: template.exercises,
                                  is_template: false
                                });
                              }}
                              className="w-full px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-medium"
                            >
                              Use Template
                            </button>
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => {
                                  // Load template into the editor
                                  setNewWorkout({
                                    ...template,
                                    is_template: true
                                  });
                                  setActiveWorkoutTab('create-new');
                                }}
                                className="flex-1 px-3 py-2 bg-gray-100  rounded-lg hover:bg-gray-200 text-sm dark:text-gray-400" style={{color: "#2d3748"}}
                              >
                                Edit
                              </button>
                              <button 
                                onClick={() => {
                                  console.log('Delete button clicked for template:', template.id);
                                  setTemplateToDelete(template);
                                  setShowDeleteConfirmModal(true);
                                }}
                                className="flex-1 px-3 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 text-sm font-medium flex items-center justify-center"
                                title="Delete template"
                              >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  </div>
                </div>
                )}

                {activeWorkoutTab === 'create-new' && (
                  <div>
                    {/* Create New Workout */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-white">Create New Workout</h3>
                  
                  {newWorkout.exercises.length > 0 ? (
                    /* Everfit-style Workout Builder */
                    <div className="bg-gray-50 rounded-xl p-6 dark:bg-gray-700">
                      <EverfitWorkout
                        workout={newWorkout}
                        onUpdateWorkout={setNewWorkout}
                        backendUrl={BACKEND_URL}
                        isClient={false}
                      />
                      
                      <div className="mt-6 flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={newWorkout.is_template}
                              onChange={(e) => setNewWorkout({ ...newWorkout, is_template: e.target.checked })}
                              className="rounded"
                            />
                            <span className="text-sm  dark:text-gray-200" style={{color: "#2d3748"}}>Save as template</span>
                          </label>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              setNewWorkout({ name: '', exercises: [], is_template: false });
                              setVideoPreviewUrls({});
                            }}
                            className="px-4 py-2 bg-gray-300  rounded-lg hover:bg-gray-400 dark:text-gray-200" style={{color: "#2d3748"}}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={async () => {
                              if (newWorkout.is_template) {
                                // Check if this is an update (has an id) or create new
                                if (newWorkout.id) {
                                  // Update existing template
                                  try {
                                    const response = await fetch(`${BACKEND_URL}/api/workout-templates/${newWorkout.id}`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      credentials: 'include',
                                      body: JSON.stringify({
                                        name: newWorkout.name,
                                        exercises: newWorkout.exercises,
                                        description: newWorkout.description || '',
                                        estimated_duration: newWorkout.estimated_duration || null,
                                        difficulty_level: newWorkout.difficulty_level || null,
                                      })
                                    });
                                    
                                    if (response.ok) {
                                      alert('Template updated successfully!');
                                      fetchWorkoutTemplates();
                                      setActiveWorkoutTab('templates');
                                    } else {
                                      alert('Failed to update template');
                                    }
                                  } catch (error) {
                                    console.error('Error updating template:', error);
                                    alert('Error updating template');
                                  }
                                } else {
                                  // Create new template
                                  await createWorkoutTemplate({
                                    name: newWorkout.name,
                                    exercises: newWorkout.exercises,
                                    description: '',
                                    estimated_duration: null,
                                    difficulty_level: null,
                                    tags: []
                                  });
                                }
                              } else {
                                const fakeEvent = { preventDefault: () => {} };
                                await handleCreateWorkout(fakeEvent);
                              }
                              setNewWorkout({ name: '', exercises: [], is_template: false });
                              setVideoPreviewUrls({});
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            {newWorkout.is_template ? (newWorkout.id ? 'Update Template' : 'Save Template') : 'Create Workout'}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Workout Creation Form */
                    <form onSubmit={handleCreateWorkout} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium  mb-2 dark:text-gray-200" style={{color: "#2d3748"}}>
                          Workout Name
                        </label>
                        <input
                          type="text"
                          value={newWorkout.name}
                          onChange={(e) => setNewWorkout({ ...newWorkout, name: e.target.value })}
                          placeholder="Enter workout name"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                          style={{borderColor: '#010eda'}}
                          required
                        />
                      </div>

                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => {
                            if (!newWorkout.name.trim()) {
                              alert('Please enter a workout name first');
                              return;
                            }
                            setNewWorkout({
                              ...newWorkout,
                              exercises: [{
                                id: Date.now().toString(),
                                name: '',
                                category: '',
                                muscle_groups: [],
                                sets: [],
                                video_url: '',
                                external_video_url: '',
                                instructions: '',
                                order_index: 0
                              }]
                            });
                          }}
                          className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold flex items-center space-x-2 mx-auto"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <span>Start Building Workout</span>
                        </button>
                      </div>
                    </form>
                  )}
                </div>

                {/* Existing Workouts */}
                {workouts.length > 0 && (
                  <div className="border-t border-gray-200 pt-6 mt-8 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-white">Recent Workouts</h3>
                    <div className="space-y-4">
                      {workouts.slice(0, 5).map((workout) => (
                        <div key={workout.id} className="border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow dark:border-gray-700">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-lg text-gray-900 dark:text-white">{workout.name}</h4>
                              <p className="text-sm  mt-1 dark:text-gray-400" style={{color: "#2d3748"}}>
                                {workout.exercises.length} exercises • Created {new Date(workout.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            
                            <div className="flex items-center space-x-2">
                              <button 
                                onClick={() => {
                                  setNewWorkout({
                                    name: `${workout.name} (Copy)`,
                                    exercises: workout.exercises,
                                    is_template: false
                                  });
                                }}
                                className="px-3 py-1 bg-gray-100  rounded-lg hover:bg-gray-200 text-sm dark:text-gray-400" style={{color: "#2d3748"}}
                              >
                                Duplicate
                              </button>
                            </div>
                          </div>

                          {/* Exercise Preview */}
                          <div className="mt-3 space-y-2">
                            {workout.exercises.slice(0, 2).map((exercise, idx) => (
                              <div key={idx} className="bg-gray-50 rounded p-3 dark:bg-gray-700">
                                <span className="font-medium">{exercise.name}</span>
                                <span className=" ml-2 dark:text-gray-400" style={{color: "#2d3748"}}>{exercise.sets?.length || 0} sets</span>
                                
                                {/* Exercise Instructions */}
                                {exercise.instructions && (
                                  <p className="text-sm  mt-1 italic dark:text-gray-400" style={{color: "#2d3748"}}>{exercise.instructions}</p>
                                )}
                                
                                {/* Video Display for Trainer */}
                                <ExerciseVideo 
                                  video_url={exercise.video_url}
                                  external_video_url={exercise.external_video_url}
                                  exerciseName={exercise.name}
                                  backendUrl={BACKEND_URL}
                                />
                              </div>
                            ))}
                            {workout.exercises.length > 2 && (
                              <div className="text-sm  text-center py-2 dark:text-gray-300" style={{color: "#2d3748"}}>
                                +{workout.exercises.length - 2} more exercises
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                  </div>
                )}
                </>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'targets' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 dark:bg-gray-800">
            <h2 className="heading-4 mb-6">Set Client Macro Targets</h2>
            
            {/* Client Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-800 mb-2">Select Client</label>
              <select 
                value={selectedClient ? selectedClient.id : ''} 
                onChange={(e) => {
                  const client = clients.find(c => c.id === e.target.value);
                  setSelectedClient(client);
                }}
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                style={{borderColor: '#010eda'}}
              >
                <option value="">Choose a client...</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>

            {selectedClient && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Setting targets for: {selectedClient.name}
                </h3>
                
                <form onSubmit={handleSetClientTargets} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-2">Protein Target (g)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={clientTargets.protein}
                        onChange={(e) => setClientTargets({...clientTargets, protein: e.target.value})}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                        style={{borderColor: '#010eda'}}
                        placeholder="150"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-2">Carbs Target (g)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={clientTargets.carbs}
                        onChange={(e) => setClientTargets({...clientTargets, carbs: e.target.value})}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                        style={{borderColor: '#010eda'}}
                        placeholder="200"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-2">Fats Target (g)</label>
                      <input
                        type="number"
                        step="0.1"
                        value={clientTargets.fats}
                        onChange={(e) => setClientTargets({...clientTargets, fats: e.target.value})}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                        style={{borderColor: '#010eda'}}
                        placeholder="65"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <button type="submit" className="btn-primary px-8">
                      Set Macro Targets
                    </button>
                  </div>
                </form>

                <div className="mt-6 p-4 bg-gray-50 rounded-lg dark:bg-gray-700">
                  <h4 className="font-medium text-gray-800 mb-2">Calculated Daily Calories:</h4>
                  <p className="text-2xl font-bold text-blue-600">
                    {((parseFloat(clientTargets.protein) || 0) * 4) + 
                     ((parseFloat(clientTargets.carbs) || 0) * 4) + 
                     ((parseFloat(clientTargets.fats) || 0) * 10)} calories
                  </p>
                  <p className="text-sm  mt-1 dark:text-gray-400" style={{color: "#2d3748"}}>
                    Protein: {((parseFloat(clientTargets.protein) || 0) * 4)} cal • 
                    Carbs: {((parseFloat(clientTargets.carbs) || 0) * 4)} cal • 
                    Fats: {((parseFloat(clientTargets.fats) || 0) * 10)} cal
                  </p>
                </div>
              </div>
            )}
          </div>
        )}



        {activeTab === 'plans' && (
          <div className="space-y-6">
            <div className={`rounded-2xl shadow-lg p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className="mb-6">
                <div>
                  <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    🤖 AI-Generated Plans
                  </h2>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Generate personalized workout and diet plans for individual clients
                  </p>
                </div>
              </div>

              {/* Client Selector */}
              <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-blue-50 border border-blue-200'}`}>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Select Client to Generate Plan
                </label>
                <div className="flex space-x-3">
                  <select
                    data-testid="ai-plan-client-select"
                    value={selectedClientForPlan || ''}
                    onChange={(e) => setSelectedClientForPlan(e.target.value)}
                    className={`flex-1 px-4 py-3 border rounded-lg ${
                      isDarkMode 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">-- Select a client --</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name} ({client.email})
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => {
                      if (selectedClientForPlan) {
                        setShowGenerateConfirmModal(true);
                      } else {
                        alert('Please select a client first');
                      }
                    }}
                    data-testid="ai-plan-generate-btn"
                    disabled={!selectedClientForPlan || isGeneratingPlans}
                    className={`px-6 py-3 rounded-lg text-sm font-medium whitespace-nowrap ${
                      !selectedClientForPlan || isGeneratingPlans
                        ? 'bg-gray-400 cursor-not-allowed text-white'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {isGeneratingPlans ? '⏳ Generating...' : '🤖 Generate Plan'}
                  </button>
                </div>
                {selectedClientForPlan && (
                  <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    💡 This will analyze {clients.find(c => c.id === selectedClientForPlan)?.name}'s progress and create a unique plan
                  </p>
                )}
              </div>

              <PlanAdjustmentsPanel isDarkMode={isDarkMode} />

              {loadingPlans ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className={`mt-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading plans...</p>
                </div>
              ) : pendingPlans.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    No Pending Plans
                  </h3>
                  <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Plans are generated automatically on the 1st of each month
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingPlans.map((plan) => (
                    <div 
                      key={plan.id}
                      className={`border rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer ${
                        isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-white'
                      }`}
                      onClick={async () => {
                        try {
                          const response = await fetch(`${BACKEND_URL}/api/trainer/plan/${plan.id}`, {
                            credentials: 'include'
                          });
                          if (response.ok) {
                            const data = await response.json();
                            setSelectedPlan(data.plan);
                            setShowPlanDetailModal(true);
                          }
                        } catch (error) {
                          console.error('Error fetching plan details:', error);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {plan.client_name}
                            </h3>
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full">
                              Pending Review
                            </span>
                          </div>
                          <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            {plan.month} - Generated {new Date(plan.generated_at).toLocaleDateString()}
                          </p>
                          <div className="flex space-x-4 text-sm">
                            <div className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                              <span className="font-medium">Workout Days:</span> {plan.workout_plan?.days?.length || 0}
                            </div>
                            <div className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                              <span className="font-medium">Meals:</span> {plan.diet_plan?.meal_suggestions?.length || 0}
                            </div>
                          </div>
                        </div>
                        <svg className={`w-6 h-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="space-y-8">
            {/* Profile Navigation */}
            <div className={`rounded-2xl shadow-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <div className={`flex border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <button
                  onClick={() => setActiveProfileTab('info')}
                  className={`px-6 py-4 font-medium text-sm rounded-tl-2xl ${
                    activeProfileTab === 'info'
                      ? `${isDarkMode ? 'text-white bg-blue-900/30 border-b-2 border-white' : 'text-black bg-gray-100 border-b-2 border-black'}`
                      : `${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
                  }`}
                >
                  Profile Info
                </button>
                <button
                  onClick={() => setActiveProfileTab('settings')}
                  className={`px-6 py-4 font-medium text-sm rounded-tr-2xl ${
                    activeProfileTab === 'settings'
                      ? `${isDarkMode ? 'text-white bg-blue-900/30 border-b-2 border-white' : 'text-black bg-gray-100 border-b-2 border-black'}`
                      : `${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`
                  }`}
                >
                  Settings
                </button>
              </div>

              {/* Profile Info Tab */}
              {activeProfileTab === 'info' && (
                <div className="p-6">
                  <h2 className={`heading-4 mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Trainer Profile Information</h2>
                  <div className="space-y-6">
                    <div className="flex items-center space-x-4">
                      <div className={`w-20 h-20 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                        <svg className={`w-10 h-10 ${isDarkMode ? 'text-gray-300' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{user?.name}</h3>
                        <p className={`${isDarkMode ? 'text-white' : 'text-black'}`}>{user?.email}</p>
                        <p className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>Personal Trainer</p>
                        <button className={`text-sm hover:underline mt-1 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          Change Profile Picture
                        </button>
                      </div>
                    </div>

                    <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Phone Number</label>
                          <input
                            type="tel"
                            defaultValue={user?.phone}
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              isDarkMode 
                                ? 'border-gray-600 bg-gray-700 text-white' 
                                : 'border-gray-300 bg-white text-gray-900'
                            }`}
                            placeholder="+1 (555) 123-4567"
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Emergency Contact</label>
                          <input
                            type="tel"
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              isDarkMode 
                                ? 'border-gray-600 bg-gray-700 text-white' 
                                : 'border-gray-300 bg-white text-gray-900'
                            }`}
                            placeholder="+1 (555) 987-6543"
                          />
                        </div>
                      </div>
                    </div>

                    <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Professional Information</h3>
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Years of Experience</label>
                            <input
                              type="number"
                              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                isDarkMode 
                                  ? 'border-gray-600 bg-gray-700 text-white' 
                                  : 'border-gray-300 bg-white text-gray-900'
                              }`}
                              placeholder="5"
                            />
                          </div>
                          <div>
                            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Specialization</label>
                            <select className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              isDarkMode 
                                ? 'border-gray-600 bg-gray-700 text-white' 
                                : 'border-gray-300 bg-white text-gray-900'
                            }`}>
                              <option>Weight Loss</option>
                              <option>Muscle Building</option>
                              <option>Strength Training</option>
                              <option>Endurance</option>
                              <option>Sports Performance</option>
                              <option>Rehabilitation</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Certifications</label>
                          <textarea
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              isDarkMode 
                                ? 'border-gray-600 bg-gray-700 text-white' 
                                : 'border-gray-300 bg-white text-gray-900'
                            }`}
                            rows="3"
                            placeholder="NASM-CPT, ACSM-CPT, etc."
                          />
                        </div>
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Bio</label>
                          <textarea
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                              isDarkMode 
                                ? 'border-gray-600 bg-gray-700 text-white' 
                                : 'border-gray-300 bg-white text-gray-900'
                            }`}
                            rows="4"
                            placeholder="Tell your clients about your training philosophy and experience..."
                          />
                        </div>
                      </div>
                    </div>

                    <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <button className={`btn-primary ${isDarkMode ? 'bg-white text-black' : ''}`}>Save Changes</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Settings Tab */}
              {activeProfileTab === 'settings' && (
                <div className="p-6">
                  <h2 className={`heading-4 mb-6 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Trainer Settings</h2>
                  
                  {/* Appearance Section */}
                  <div className="space-y-8">
                    <div>
                      <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Appearance</h3>
                      <div className="space-y-4">
                        <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                              <svg className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                              </svg>
                            </div>
                            <div>
                              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Dark Mode</p>
                              <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>Toggle between light and dark theme</p>
                            </div>
                          </div>
                          <button
                            onClick={toggleTheme}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              isDarkMode ? 'bg-blue-600' : 'bg-gray-300'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                isDarkMode ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Push Notifications Section */}
                    <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Push Notifications</h3>
                      <div className="space-y-4">
                        {/* Main Push Toggle */}
                        <div className={`flex items-center justify-between p-4 rounded-lg bg-gradient-to-r ${isDarkMode ? 'from-blue-900/30 to-indigo-900/30 border border-blue-800' : 'from-blue-50 to-indigo-50 border border-blue-200'}`}>
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                              </svg>
                            </div>
                            <div>
                              <p className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Enable Push Notifications</p>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                {!pushSupported 
                                  ? 'Push notifications are not supported in this browser'
                                  : pushEnabled 
                                    ? 'You will receive alerts for client messages and updates'
                                    : 'Get notified even when the app is closed'}
                              </p>
                            </div>
                          </div>
                          <button 
                            data-testid="trainer-push-notifications-toggle"
                            onClick={handleTogglePushNotifications}
                            disabled={!pushSupported || pushLoading}
                            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                              pushEnabled ? 'bg-blue-600' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                            } ${(!pushSupported || pushLoading) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            {pushLoading ? (
                              <span className="absolute inset-0 flex items-center justify-center">
                                <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              </span>
                            ) : (
                              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                                pushEnabled ? 'translate-x-6' : 'translate-x-1'
                              }`} />
                            )}
                          </button>
                        </div>

                        {/* Test Notification Button */}
                        {pushEnabled && (
                          <button
                            data-testid="trainer-push-notifications-test-btn"
                            onClick={handleTestPushNotification}
                            className={`w-full p-3 text-sm rounded-lg transition-colors ${
                              isDarkMode 
                                ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            Send Test Notification
                          </button>
                        )}

                        <PushCheckPanel />

                        {/* Permission Status Info */}
                        {pushSupported && getNotificationPermission() === 'denied' && (
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
                            <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-red-300' : 'text-red-700'}`}>
                              Notifications are blocked for this site
                            </p>
                            <p className={`text-xs ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                              To enable notifications:
                            </p>
                            <ol className={`text-xs list-decimal ml-4 mt-1 space-y-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                              <li>Tap the lock/info icon in your browser's address bar</li>
                              <li>Find "Notifications" in Site Settings</li>
                              <li>Change from "Block" to "Allow"</li>
                              <li>Refresh this page</li>
                            </ol>
                          </div>
                        )}
                        
                        {/* iOS Home Screen Info */}
                        {isIOS && !isInStandaloneMode && (
                          <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-amber-900/20 border border-amber-800' : 'bg-amber-50 border border-amber-200'}`}>
                            <div className="flex items-start space-x-2">
                              <svg className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} fill="currentColor" viewBox="0 0 24 24">
                                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                              </svg>
                              <div>
                                <p className={`text-sm font-medium ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                                  iPhone Users
                                </p>
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                  For push notifications, add this app to your Home Screen:
                                </p>
                                <ol className={`text-xs list-decimal ml-4 mt-1 space-y-0.5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                  <li>Tap Share button in Safari</li>
                                  <li>Tap "Add to Home Screen"</li>
                                  <li>Open app from Home Screen</li>
                                </ol>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Notification Types Info */}
                        <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <p className={`font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>You'll be notified about:</p>
                          <ul className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            <li className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>New messages from clients</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Clients starting and finishing workouts</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Monday 9 AM weekly client summary</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>New client registrations</span>
                            </li>
                            <li className="flex items-center space-x-2">
                              <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                              <span>Client progress updates</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    {/* Business Settings Section */}
                    <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Business Settings</h3>
                      <div className="space-y-4">
                        <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                              <svg className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                              </svg>
                            </div>
                            <div>
                              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Session Rates</p>
                              <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>Manage your training session rates</p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                        
                        <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                              <svg className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4h3a1 1 0 011 1v6a1 1 0 01-1 1h-3v4a1 1 0 01-1 1H9a1 1 0 01-1-1v-4H5a1 1 0 01-1-1V8a1 1 0 011-1h3z" />
                              </svg>
                            </div>
                            <div>
                              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Availability</p>
                              <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>Set your available training hours</p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Preferences Section */}
                    <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Preferences</h3>
                      <div className="space-y-4">
                        <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                              <svg className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                              </svg>
                            </div>
                            <div>
                              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Units</p>
                              <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>Weight and measurement units</p>
                            </div>
                          </div>
                          <select className={`px-3 py-2 border rounded-lg text-sm ${
                            isDarkMode 
                              ? 'border-gray-600 bg-gray-400 text-white' 
                              : 'border-gray-300 bg-white text-black'
                          }`}>
                            <option>Imperial (lbs, ft)</option>
                            <option>Metric (kg, cm)</option>
                          </select>
                        </div>
                        
                        <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                              <svg className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                              </svg>
                            </div>
                            <div>
                              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Language</p>
                              <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>App display language</p>
                            </div>
                          </div>
                          <select className={`px-3 py-2 border rounded-lg text-sm ${
                            isDarkMode 
                              ? 'border-gray-600 bg-gray-400 text-white' 
                              : 'border-gray-300 bg-white text-black'
                          }`}>
                            <option>English</option>
                            <option>Spanish</option>
                            <option>French</option>
                            <option>German</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Account Section */}
                    <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                      <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Account</h3>
                      <div className="space-y-4">
                        <button className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors ${
                          isDarkMode 
                            ? 'bg-gray-400 hover:bg-gray-600' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                              <svg className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                              </svg>
                            </div>
                            <div className="text-left">
                              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Change Password</p>
                              <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>Update your account password</p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        
                        <button className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors ${
                          isDarkMode 
                            ? 'bg-gray-400 hover:bg-gray-600' 
                            : 'bg-gray-50 hover:bg-gray-100'
                        }`}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                              <svg className={`w-5 h-5 ${isDarkMode ? 'text-white' : 'text-black'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                            </div>
                            <div className="text-left">
                              <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Export Data</p>
                              <p className={`text-sm ${isDarkMode ? 'text-white' : 'text-black'}`}>Download your training data</p>
                            </div>
                          </div>
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                        
                        <button onClick={logout} className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors ${
                          isDarkMode 
                            ? 'bg-red-900/20 hover:bg-red-900/30' 
                            : 'bg-red-50 hover:bg-red-100'
                        }`}>
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-red-800' : 'bg-red-200'}`}>
                              <svg className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                            </div>
                            <div className="text-left">
                              <p className={`font-medium ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>Logout</p>
                              <p className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-500'}`}>Sign out of your account</p>
                            </div>
                          </div>
                          <svg className={`w-5 h-5 ${isDarkMode ? 'text-red-400' : 'text-red-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="space-y-4">
            {/* iOS Home Screen Prompt */}
            {showIOSHomeScreenPrompt && (
              <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl p-4 shadow-lg">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="bg-white/10 rounded-full p-2 mt-0.5">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold">Add to Home Screen</p>
                      <p className="text-gray-300 text-sm mt-1">For push notifications on iPhone:</p>
                      <ol className="text-gray-400 text-xs mt-2 space-y-1 list-decimal ml-4">
                        <li>Tap the <strong className="text-gray-300">Share</strong> button at the bottom of Safari</li>
                        <li>Tap "<strong className="text-gray-300">Add to Home Screen</strong>"</li>
                        <li>Tap "<strong className="text-gray-300">Add</strong>" to confirm</li>
                        <li>Open the app from your Home Screen</li>
                      </ol>
                    </div>
                  </div>
                  <button
                    onClick={handleDismissIOSPrompt}
                    className="p-2 text-white/50 hover:text-white transition-colors flex-shrink-0"
                    aria-label="Dismiss"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            
            {/* Push Notification Prompt Banner */}
            {pushSupported && !pushEnabled && !pushPromptDismissed && (
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 shadow-lg">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-white/20 rounded-full p-2">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-semibold">Enable Push Notifications</p>
                      <p className="text-blue-100 text-sm">Get notified instantly when your clients message you</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleEnablePushFromBanner}
                      disabled={pushLoading}
                      className="px-4 py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors disabled:opacity-50"
                    >
                      {pushLoading ? 'Enabling...' : 'Enable'}
                    </button>
                    <button
                      onClick={handleDismissPushPrompt}
                      className="p-2 text-white/70 hover:text-white transition-colors"
                      aria-label="Dismiss"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

          <div className="bg-white rounded-xl shadow-lg overflow-hidden dark:bg-gray-800" style={{height: 'calc(100vh - 200px)'}}>
            <div className="flex flex-col md:flex-row h-full">
              {/* Client List Sidebar - Full width on mobile, sidebar on desktop */}
              <div className={`${selectedClient ? 'hidden md:flex' : 'flex'} w-full md:w-80 border-r border-gray-200 dark:border-gray-700 flex-col ${selectedClient ? '' : 'h-full'}`}>
                <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-base sm:text-lg font-bold dark:text-white" style={{color: "#1a1a1a"}}>Conversations</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{clients.length} clients</p>
                </div>
                
                <div className="flex-1 overflow-y-auto">
                  {(() => {
                    // Group messages by client to show in sidebar
                    const clientConversations = {};
                    
                    if (messages && Array.isArray(messages)) {
                      messages.forEach(msg => {
                        if (!msg) return;
                        const clientId = msg.sender_id === user?.id ? msg.receiver_id : msg.sender_id;
                        const clientName = msg.sender_id === user?.id ? msg.receiver_name : msg.sender_name;
                        
                        if (!clientId) return;
                        
                        if (!clientConversations[clientId]) {
                          clientConversations[clientId] = {
                            clientId,
                            clientName: clientName || 'Unknown',
                            lastMessage: msg,
                            unreadCount: 0,
                            messageCount: 0
                          };
                        }
                        clientConversations[clientId].messageCount++;
                        if (!msg.read && msg.sender_id !== user?.id) {
                          clientConversations[clientId].unreadCount++;
                        }
                      });
                    }
                    
                    // Also add clients without messages
                    clients.forEach(client => {
                      if (!clientConversations[client.id]) {
                        clientConversations[client.id] = {
                          clientId: client.id,
                          clientName: client.name,
                          lastMessage: null,
                          unreadCount: 0,
                          messageCount: 0
                        };
                      }
                    });
                    
                    const conversationList = Object.values(clientConversations).sort((a, b) => {
                      if (a.lastMessage && b.lastMessage) {
                        return new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at);
                      }
                      return a.lastMessage ? -1 : 1;
                    });
                    
                    if (conversationList.length === 0) {
                      return (
                        <div className="text-center py-8 px-4">
                          <p className="text-gray-500 dark:text-gray-400">No clients yet</p>
                        </div>
                      );
                    }
                    
                    return conversationList.map(conv => (
                      <button
                        key={conv.clientId}
                        onClick={() => {
                          const client = clients.find(c => c.id === conv.clientId) || { id: conv.clientId, name: conv.clientName };
                          setSelectedClient(client);
                          // Mark messages from this client as read
                          markTrainerMessagesAsRead(conv.clientId);
                        }}
                        className={`w-full p-3 sm:p-4 text-left border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          selectedClient?.id === conv.clientId ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-l-blue-600' : ''
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-semibold text-sm sm:text-base ${
                            selectedClient?.id === conv.clientId ? 'bg-blue-600' : 'bg-gray-400'
                          }`}>
                            {conv.clientName?.[0]?.toUpperCase() || 'C'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className={`font-semibold truncate ${selectedClient?.id === conv.clientId ? 'text-blue-600' : 'text-gray-900 dark:text-white'}`}>
                                {conv.clientName}
                              </p>
                              {conv.unreadCount > 0 && (
                                <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                                  {conv.unreadCount}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {conv.lastMessage ? conv.lastMessage.message?.substring(0, 30) + '...' : 'No messages yet'}
                            </p>
                            {conv.lastMessage && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                {new Date(conv.lastMessage.created_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    ));
                  })()}
                </div>
              </div>
              
              {/* Chat Area */}
              <div className={`${selectedClient ? 'flex' : 'hidden md:flex'} flex-1 flex-col h-full`}>
                {selectedClient ? (
                  <>
                    {/* Chat Header */}
                    <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 sm:space-x-3">
                          {/* Back button for mobile */}
                          <button
                            onClick={() => setSelectedClient(null)}
                            className="md:hidden p-1 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm sm:text-base">
                            {selectedClient.name?.[0]?.toUpperCase() || 'C'}
                          </div>
                          <div>
                            <p className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white">{selectedClient.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">{selectedClient.email}</p>
                          </div>
                        </div>
                        <button
                          onClick={fetchTrainerMessages}
                          className="p-2 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
                          title="Refresh"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50 dark:bg-gray-900">
                      {(() => {
                        const clientMessages = messages.filter(msg => 
                          msg.sender_id === selectedClient.id || msg.receiver_id === selectedClient.id
                        ).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                        
                        if (clientMessages.length === 0) {
                          return (
                            <div className="text-center py-12">
                              <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                <span className="text-2xl">💬</span>
                              </div>
                              <p className="text-gray-500 dark:text-gray-400">No messages with {selectedClient.name} yet</p>
                              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Send the first message below</p>
                            </div>
                          );
                        }
                        
                        return clientMessages.map(msg => {
                          const isFromTrainer = msg.sender_id === user?.id;
                          return (
                            <div key={msg.id} className={`flex ${isFromTrainer ? 'justify-end' : 'justify-start'}`}>
                              <div className={`max-w-md px-4 py-3 rounded-2xl ${
                                isFromTrainer 
                                  ? 'bg-blue-600 text-white rounded-br-md' 
                                  : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700 rounded-bl-md'
                              }`}>
                                <p className="text-sm">{msg.message}</p>
                                <p className={`text-xs mt-1 ${isFromTrainer ? 'text-blue-200' : 'text-gray-500 dark:text-gray-400'}`}>
                                  {new Date(msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                                </p>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>
                    
                    {/* Message Input */}
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        if (!messageContent.trim()) return;
                        
                        try {
                          const response = await fetch(`${BACKEND_URL}/api/messages`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify({
                              receiver_id: selectedClient.id,
                              message: messageContent
                            })
                          });
                          
                          if (response.ok) {
                            setMessageContent('');
                            fetchTrainerMessages();
                          }
                        } catch (error) {
                          console.error('Error sending message:', error);
                        }
                      }} className="flex items-center space-x-3">
                        <input
                          type="text"
                          value={messageContent}
                          onChange={(e) => setMessageContent(e.target.value)}
                          placeholder={`Message ${selectedClient.name}...`}
                          className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-full bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <button
                          type="submit"
                          disabled={!messageContent.trim()}
                          className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                        </button>
                      </form>
                    </div>
                  </>
                ) : (
                  <div className="hidden md:flex flex-1 items-center justify-center bg-gray-50 dark:bg-gray-900">
                    <div className="text-center">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-2">Select a conversation</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Choose a client from the left to start messaging</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        )}
      </main>
      
      {/* Message Modal */}
      {showMessageModal && messageRecipient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md dark:bg-gray-800">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold  dark:text-white" style={{color: "#1a1a1a"}}>
                  Send Message to {messageRecipient.name}
                </h3>
                <button
                  onClick={() => {
                    setShowMessageModal(false);
                    setMessageRecipient(null);
                    setMessageContent('');
                  }}
                  className="text-gray-400 hover: dark:text-gray-400" style={{color: "#2d3748"}}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSendMessageToClient} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium  mb-2 dark:text-gray-200" style={{color: "#2d3748"}}>
                  Message
                </label>
                <textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  required
                  rows={5}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none dark:border-gray-600"
                  placeholder="Type your message here..."
                />
              </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowMessageModal(false);
                    setMessageRecipient(null);
                    setMessageContent('');
                  }}
                  className="px-4 py-2 bg-gray-200  rounded-lg hover:bg-gray-300 transition-colors dark:text-gray-200" style={{color: "#2d3748"}}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  <span>Send Message</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


      {/* Plan Detail Modal */}
      {showPlanDetailModal && selectedPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className={`rounded-xl shadow-xl w-full max-w-6xl my-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {selectedPlan.client_name}'s Plan - {selectedPlan.month}
                  </h3>
                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Generated {new Date(selectedPlan.generated_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => setShowPlanDetailModal(false)}
                  className={`${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Client Analysis */}
              <div className={`rounded-xl p-4 mb-6 ${isDarkMode ? 'bg-gray-750' : 'bg-blue-50'}`}>
                <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>📊 Client Analysis</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Current Weight</p>
                    <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedPlan.analysis_data?.weight_progress?.current || 'N/A'} lbs
                    </p>
                  </div>
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Goal Weight</p>
                    <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedPlan.analysis_data?.weight_progress?.goal || 'N/A'} lbs
                    </p>
                  </div>
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Workout Adherence</p>
                    <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedPlan.analysis_data?.workout_adherence?.completion_rate?.toFixed(0) || 0}%
                    </p>
                  </div>
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Nutrition Adherence</p>
                    <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {selectedPlan.analysis_data?.nutrition_adherence?.avg_adherence?.toFixed(0) || 0}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Workout Plan */}
              <div className="mb-6">
                <h4 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  🏋️ Workout Plan ({selectedPlan.workout_plan?.days?.length || 0} Days)
                </h4>
                <div className="space-y-4">
                  {selectedPlan.workout_plan?.days?.map((day, idx) => (
                    <div key={idx} className={`border rounded-lg p-4 ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <h5 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{day.day_name}</h5>
                        <span className={`text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                          {day.exercises?.length || 0} exercises
                        </span>
                      </div>
                      <p className={`text-sm mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Target: {day.target_muscles?.join(', ')}
                      </p>
                      <div className="space-y-2">
                        {(() => {
                          // Group superset exercises together
                          const exercises = day.exercises || [];
                          const renderedExercises = [];
                          let exerciseNumber = 1;
                          
                          for (let exIdx = 0; exIdx < exercises.length; exIdx++) {
                            const exercise = exercises[exIdx];
                            const exerciseName = exercise.name || '';
                            
                            // Check if this is a superset (contains "+", "SS", or "Superset")
                            const hasSSIndicator = / SS /i.test(exerciseName);
                            const isSuperset = exerciseName.includes('+') || 
                                               hasSSIndicator ||
                                               exerciseName.toLowerCase().includes('superset');
                            
                            if (isSuperset) {
                              // Parse superset exercises
                              let supersetExercises = [];
                              let cleanName = exerciseName
                                .replace(/^Superset\s*\d*:\s*/i, '')
                                .replace(/\s*\(Superset\)/i, '')
                                .trim();
                              
                              // Determine separator
                              const separator = hasSSIndicator ? / SS /i : '+';
                              
                              if (cleanName.includes('+') || hasSSIndicator) {
                                const parts = cleanName.split(separator).map(s => s.trim());
                                supersetExercises = parts.map((name, i) => ({
                                  name: name,
                                  sets: exercise.sets,
                                  reps: exercise.reps?.includes?.(',') ? exercise.reps.split(',')[i]?.trim() : exercise.reps
                                }));
                              } else {
                                supersetExercises = [{ name: cleanName, sets: exercise.sets, reps: exercise.reps }];
                              }
                              
                              renderedExercises.push(
                                <div 
                                  key={exIdx} 
                                  className={`rounded-xl overflow-hidden border-2 ${isDarkMode ? 'border-purple-600 bg-purple-900/20' : 'border-purple-300 bg-purple-50'}`}
                                >
                                  {/* Superset Header */}
                                  <div className={`px-3 py-2 flex items-center gap-2 ${isDarkMode ? 'bg-purple-900/50' : 'bg-purple-100'}`}>
                                    <span className={`w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold ${isDarkMode ? 'bg-purple-700 text-purple-200' : 'bg-purple-200 text-purple-700'}`}>
                                      {exerciseNumber}
                                    </span>
                                    <span className={`text-xs font-bold uppercase tracking-wide ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                                      ⚡ Superset
                                    </span>
                                    <span className={`text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-500'}`}>
                                      No rest between exercises
                                    </span>
                                  </div>
                                  
                                  {/* Superset Exercises */}
                                  <div className="p-3 space-y-2">
                                    {supersetExercises.map((subEx, subIdx) => (
                                      <div 
                                        key={subIdx}
                                        className={`flex items-start gap-3 p-2 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}
                                      >
                                        <span className={`w-5 h-5 flex-shrink-0 rounded flex items-center justify-center text-xs font-medium ${isDarkMode ? 'bg-purple-800 text-purple-300' : 'bg-purple-100 text-purple-600'}`}>
                                          {String.fromCharCode(65 + subIdx)}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                          <p className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                            {subEx.name}
                                          </p>
                                          <button
                                            data-testid={`trainer-superset-video-btn-${exIdx}-${subIdx}`}
                                            onClick={() => setTrainerVideoExercise({ name: subEx.name, attached: subEx.external_video_url || subEx.video_url || '' })}
                                            className="text-xs text-blue-600 hover:underline"
                                          >
                                            View / Fix Video
                                          </button>
                                          <div className="flex flex-wrap gap-2 mt-1">
                                            <span className={`text-xs ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                              {subEx.sets} sets
                                            </span>
                                            <span className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                              {subEx.reps} reps
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  
                                  {/* Rest and Notes */}
                                  <div className={`px-3 py-2 border-t ${isDarkMode ? 'border-purple-800' : 'border-purple-200'}`}>
                                    <span className={`text-xs ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                      {exercise.rest_seconds || 90}s rest after completing superset
                                    </span>
                                    {exercise.notes && (
                                      <p className={`text-xs mt-1 italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                        💡 {exercise.notes}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              );
                            } else {
                              // Regular exercise
                              renderedExercises.push(
                                <div key={exIdx} className={`p-3 rounded ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                                          {exerciseNumber}
                                        </span>
                                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{exercise.name}</p>
                                      </div>
                                      <div className="flex flex-wrap gap-3 mt-2">
                                        <span className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                          {getSetsCount(exercise.sets)} sets
                                        </span>
                                        <span className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                          {getRepsDisplay(exercise)} reps
                                        </span>
                                        <span className={`text-sm ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                                          {exercise.rest_seconds}s rest
                                        </span>
                                        {exercise.weight && (
                                          <span className={`text-sm ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                                            {exercise.weight}
                                          </span>
                                        )}
                                      </div>
                                      {exercise.notes && (
                                        <p className={`text-xs mt-1 italic ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                          💡 {exercise.notes}
                                        </p>
                                      )}
                                      <button
                                        data-testid={`trainer-exercise-video-btn-${exIdx}`}
                                        onClick={() => setTrainerVideoExercise({ name: exercise.name, attached: exercise.external_video_url || exercise.video_url || '' })}
                                        className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                                      >
                                        View / Fix Video
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            exerciseNumber++;
                          }
                          return renderedExercises;
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Diet Plan */}
              <div>
                <h4 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>🍽️ Diet Plan</h4>
                <div className={`rounded-lg p-4 mb-4 ${isDarkMode ? 'border border-gray-700 bg-gray-750' : 'bg-green-50'}`}>
                  <h5 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Macro Targets</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Protein</p>
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedPlan.diet_plan?.macro_adjustments?.daily_protein || 0}g
                      </p>
                    </div>
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Carbs</p>
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedPlan.diet_plan?.macro_adjustments?.daily_carbs || 0}g
                      </p>
                    </div>
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Fats</p>
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedPlan.diet_plan?.macro_adjustments?.daily_fats || 0}g
                      </p>
                    </div>
                    <div>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Calories</p>
                      <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {selectedPlan.diet_plan?.macro_adjustments?.daily_calories || 0}
                      </p>
                    </div>
                  </div>
                </div>

                <h5 className={`font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Meal Suggestions</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedPlan.diet_plan?.meal_suggestions?.slice(0, 4).map((meal, idx) => (
                    <div key={idx} className={`border rounded-lg p-4 ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200'}`}>
                      <p className={`font-medium mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{meal.name}</p>
                      <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{meal.meal_type}</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        P: {meal.macros?.protein}g • C: {meal.macros?.carbs}g • F: {meal.macros?.fats}g
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className={`p-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <button
                  onClick={() => handleRejectPlan(selectedPlan.id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
                >
                  ❌ Reject
                </button>
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleRegeneratePlan(selectedPlan.id)}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      isDarkMode 
                        ? 'bg-gray-700 text-white hover:bg-gray-600' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    🔄 Regenerate
                  </button>
                  <button
                    onClick={() => handleApprovePlan(selectedPlan.id)}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
                  >
                    ✅ Approve & Assign
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 dark:bg-gray-800">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2 dark:text-white">
              Delete Template?
            </h3>
            
            <p className="text-sm  text-center mb-6 dark:text-gray-400" style={{color: "#2d3748"}}>
              Are you sure you want to delete "<span className="font-semibold">{templateToDelete?.name}</span>"? This action cannot be undone.
            </p>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  console.log('User cancelled deletion');
                  setShowDeleteConfirmModal(false);
                  setTemplateToDelete(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200  rounded-lg hover:bg-gray-300 font-medium dark:text-gray-200" style={{color: "#2d3748"}}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Archive Client Confirmation Modal */}
      {showArchiveClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 dark:bg-gray-800">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-orange-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2 dark:text-white">
              Archive Client?
            </h3>
            
            <p className="text-sm  text-center mb-4 dark:text-gray-400" style={{color: "#2d3748"}}>
              Archive <span className="font-semibold">{clientToArchive?.name}</span>?
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
              <p className="text-xs text-blue-800 font-medium mb-2">📦 Archiving will:</p>
              <ul className="text-xs text-blue-700 space-y-1 ml-4">
                <li>• Hide client from your active clients list</li>
                <li>• Keep ALL their data safe (workouts, macros, progress)</li>
                <li>• Disable their login access temporarily</li>
                <li>• Allow you to reactivate them anytime</li>
              </ul>
              <p className="text-xs text-blue-800 font-semibold mt-2">💡 This is reversible - you can reactivate later!</p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowArchiveClientModal(false);
                  setClientToArchive(null);
                }}
                className="flex-1 px-4 py-2 bg-gray-200  rounded-lg hover:bg-gray-300 font-medium dark:text-gray-200" style={{color: "#2d3748"}}
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveClient}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
              >
                Archive Client
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Client Confirmation Modal */}
      {showDeleteClientModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 dark:bg-gray-800">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2 dark:text-white">
              Delete Client?
            </h3>
            
            <p className="text-sm  text-center mb-4 dark:text-gray-400" style={{color: "#2d3748"}}>
              Are you sure you want to delete <span className="font-semibold">{clientToDelete?.name}</span>?
            </p>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
              <p className="text-xs text-red-800 font-medium mb-2">⚠️ This will permanently delete:</p>
              <ul className="text-xs text-red-700 space-y-1 ml-4">
                <li>• All workout sessions and progress</li>
                <li>• All macro logs and nutrition data</li>
                <li>• All weight tracking and body measurements</li>
                <li>• All messages and goals</li>
                <li>• Client account and login access</li>
              </ul>
              <p className="text-xs text-red-800 font-semibold mt-2">This action cannot be undone!</p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteClientModal(false);
                  setClientToDelete(null);
                }}
                disabled={deletingClient}
                className="flex-1 px-4 py-2 bg-gray-200  rounded-lg hover:bg-gray-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-200" style={{color: "#2d3748"}}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  alert('Delete button was clicked! Check console for details.');
                  console.log('Delete button clicked!');
                  handleDeleteClient();
                }}
                disabled={deletingClient}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingClient ? 'Deleting...' : 'Delete Client'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Invite Client Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full dark:bg-gray-800">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold  dark:text-white" style={{color: "#1a1a1a"}}>Invite New Client</h3>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInvitationLink('');
                  setInvitationForm({ client_name: '', client_email: '' });
                  setLinkCopied(false);
                }}
                className="text-gray-400 hover: dark:text-gray-400" style={{color: "#2d3748"}}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6">
              {!invitationLink ? (
                <form onSubmit={handleSendInvitation}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium  mb-2 dark:text-gray-200" style={{color: "#2d3748"}}>
                        Client Name *
                      </label>
                      <input
                        type="text"
                        value={invitationForm.client_name}
                        onChange={(e) => setInvitationForm({ ...invitationForm, client_name: e.target.value })}
                        placeholder="e.g., John Smith"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:border-gray-600"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium  mb-2 dark:text-gray-200" style={{color: "#2d3748"}}>
                        Client Email *
                      </label>
                      <input
                        type="email"
                        value={invitationForm.client_email}
                        onChange={(e) => setInvitationForm({ ...invitationForm, client_email: e.target.value })}
                        placeholder="client@example.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:border-gray-600"
                        required
                      />
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div>
                          <p className="text-sm font-medium text-blue-900 mb-1">How it works:</p>
                          <ul className="text-xs text-blue-800 space-y-1">
                            <li>• A unique invitation link will be generated</li>
                            <li>• Copy and send the link to your client via email</li>
                            <li>• Link expires in 7 days</li>
                            <li>• Client creates their account using the link</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex space-x-3 mt-6">
                    <button
                      type="button"
                      onClick={() => {
                        setShowInviteModal(false);
                        setInvitationForm({ client_name: '', client_email: '' });
                      }}
                      className="flex-1 px-4 py-2 bg-gray-200  rounded-lg hover:bg-gray-300 font-medium dark:text-gray-200" style={{color: "#2d3748"}}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                    >
                      Generate Link
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="font-semibold text-green-900">Invitation Link Generated!</p>
                    </div>
                    <p className="text-sm text-green-800">Copy the link below and send it to your client.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium  mb-2 dark:text-gray-200" style={{color: "#2d3748"}}>
                      Invitation Link
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={invitationLink}
                        readOnly
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm dark:bg-gray-700"
                      />
                      <button
                        onClick={handleCopyLink}
                        className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                          linkCopied
                            ? 'bg-green-600 text-white'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {linkCopied ? (
                          <div className="flex items-center space-x-1">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>Copied!</span>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-1">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                            <span>Copy</span>
                          </div>
                        )}
                      </button>
                    </div>
                    <p className="text-xs  mt-2 dark:text-gray-300" style={{color: "#2d3748"}}>
                      ⏰ This link expires in 7 days
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      setShowInviteModal(false);
                      setInvitationLink('');
                      setInvitationForm({ client_name: '', client_email: '' });
                      setLinkCopied(false);
                    }}
                    className="w-full px-4 py-2 bg-gray-200  rounded-lg hover:bg-gray-300 font-medium dark:text-gray-200" style={{color: "#2d3748"}}
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Generate Plans Confirmation Modal */}
      {showGenerateConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-xl w-full max-w-lg ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Generate AI Plan
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Generate a personalized workout and diet plan for{' '}
                <span className="font-bold">
                  {clients.find(c => c.id === selectedClientForPlan)?.name}
                </span>
                {' '}based on their progress data.
              </p>
              
              {/* Trainer Customization Notes */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Customization Instructions (Optional)
                </label>
                <textarea
                  value={trainerPlanNotes}
                  onChange={(e) => setTrainerPlanNotes(e.target.value)}
                  placeholder="Add specific instructions for the AI to incorporate into the plan, e.g.:
• Add supersets for chest exercises
• Include 15 min HIIT cardio at the end of each workout
• Focus on higher volume for legs
• Add dropsets on the last set of isolation exercises
• Include a weekly cheat meal"
                  rows="5"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${
                    isDarkMode 
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  data-testid="trainer-plan-notes-input"
                />
                {savedPlanNotesAt && (
                  <p data-testid="trainer-plan-notes-prefilled" className="text-xs mt-1 text-blue-600">
                    Pre-filled from this client's last plan ({new Date(savedPlanNotesAt + 'Z').toLocaleDateString()}). Edit as needed.
                  </p>
                )}
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  These instructions will be directly incorporated into the generated workout and diet plan.
                </p>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Saved for this client and reused for the automatic plan on the 1st of each month.
                </p>
              </div>
              
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                ⏱️ This may take 30-60 seconds to analyze and generate.
              </p>
            </div>
            <div className={`p-6 border-t flex justify-end space-x-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => {
                  setShowGenerateConfirmModal(false);
                  setTrainerPlanNotes('');
                }}
                disabled={isGeneratingPlans}
                className={`px-4 py-2 rounded-lg ${
                  isDarkMode 
                    ? 'bg-gray-700 text-white hover:bg-gray-600' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setIsGeneratingPlans(true);
                  try {
                    console.log('Making API request for client:', selectedClientForPlan);
                    console.log('Trainer notes:', trainerPlanNotes);
                    
                    const response = await fetch(`${BACKEND_URL}/api/trainer/generate-plan-for-client/${selectedClientForPlan}`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify({ trainer_notes: trainerPlanNotes })
                    });
                    
                    console.log('Response status:', response.status);
                    
                    if (response.ok) {
                      const data = await response.json();
                      console.log('Response data:', data);
                      
                      // Show "generating" status
                      const clientName = clients.find(c => c.id === selectedClientForPlan)?.name;
                      setShowGenerateConfirmModal(false);
                      setErrorModalMessage(`🔄 Generating Plan...\n\nYour plan for ${clientName} is being generated.\n\nStatus: Initializing...`);
                      setShowErrorModal(true);
                      
                      // Poll for completion using the new status endpoint
                      const planId = data.plan_id;
                      let attempts = 0;
                      const maxAttempts = 60; // 60 * 2 seconds = 120 seconds max
                      
                      const pollInterval = setInterval(async () => {
                        attempts++;
                        try {
                          // Use the new status endpoint for real-time updates
                          const statusResponse = await fetch(`${BACKEND_URL}/api/trainer/plan-status/${planId}`, {
                            credentials: 'include'
                          });
                          
                          if (statusResponse.ok) {
                            const statusData = await statusResponse.json();
                            
                            // Update the modal with current status
                            const statusMessages = {
                              'generating': '🔄 Generating...',
                              'analyzing': '📊 Analyzing client data...',
                              'generating_workout': '🏋️ Creating workout plan...',
                              'generating_diet': '🍽️ Creating diet plan...',
                              'generating_retry': '🔄 Retrying generation...',
                              'pending_review': '✅ Complete!',
                              'failed': '❌ Failed'
                            };
                            
                            const displayStatus = statusMessages[statusData.status] || statusData.message || 'Processing...';
                            
                            if (statusData.status === 'pending_review') {
                              // Plan is ready!
                              clearInterval(pollInterval);
                              setErrorModalMessage(`✅ Success!\n\nPlan generated for ${clientName}!`);
                              setShowErrorModal(true);
                              setTimeout(() => {
                                fetchPendingPlans();
                                setIsGeneratingPlans(false);
                                setSelectedClientForPlan('');
                                setTrainerPlanNotes('');
                                setShowErrorModal(false);
                              }, 2000);
                            } else if (statusData.status === 'failed') {
                              // Plan generation failed
                              clearInterval(pollInterval);
                              setErrorModalMessage(`❌ Error\n\nFailed to generate plan for ${clientName}.\n\n${statusData.error || 'Unknown error'}`);
                              setShowErrorModal(true);
                              setIsGeneratingPlans(false);
                            } else {
                              // Update status message
                              setErrorModalMessage(`🔄 Generating Plan...\n\nYour plan for ${clientName} is being generated.\n\nStatus: ${displayStatus}\n\nTime elapsed: ${attempts * 2}s`);
                            }
                          }
                          
                          if (attempts >= maxAttempts) {
                            clearInterval(pollInterval);
                            setErrorModalMessage(`⏱️ Still Processing\n\nPlan generation is taking longer than expected. The AI is working on your detailed plan.\n\nPlease check the AI Plans page in a minute.`);
                            setShowErrorModal(true);
                            setIsGeneratingPlans(false);
                            fetchPendingPlans();
                          }
                        } catch (pollError) {
                          console.error('Error polling for plan status:', pollError);
                        }
                      }, 2000); // Poll every 2 seconds for more responsive updates
                      
                    } else {
                      const error = await response.json();
                      console.error('API Error:', error);
                      
                      // Provide helpful error messages
                      let errorMessage = error.detail || 'Failed to generate plan';
                      
                      if (errorMessage.includes('Please set weight goals')) {
                        const clientName = clients.find(c => c.id === selectedClientForPlan)?.name;
                        errorMessage = `Cannot generate plan for ${clientName}\n\n` +
                          `Weight goals are required. Please:\n` +
                          `1. Go to "Clients" tab\n` +
                          `2. Click on ${clientName}\n` +
                          `3. Set their Current Weight and Goal Weight\n` +
                          `4. Then come back to generate the plan`;
                      } else if (errorMessage.includes('Not enough client data')) {
                        const clientName = clients.find(c => c.id === selectedClientForPlan)?.name;
                        errorMessage = `Cannot generate plan for ${clientName}\n\n` +
                          `The client needs more activity data:\n` +
                          `• At least 2-3 completed workouts\n` +
                          `• At least 5-7 days of nutrition logs\n` +
                          `• Weight tracking entries\n\n` +
                          `Please have the client log their workouts and nutrition first.`;
                      }
                      
                      setErrorModalMessage(errorMessage);
                      setShowErrorModal(true);
                      setIsGeneratingPlans(false);
                      setShowGenerateConfirmModal(false);
                    }
                  } catch (error) {
                    console.error('Error generating plan:', error);
                    setErrorModalMessage(`❌ Error\n\n${error.message}\n\nPlease try again or contact support if the issue persists.`);
                    setShowErrorModal(true);
                    setIsGeneratingPlans(false);
                    setShowGenerateConfirmModal(false);
                  }
                }}
                data-testid="ai-plan-generate-now-btn"
                disabled={isGeneratingPlans}
                className={`px-6 py-2 rounded-lg font-medium ${
                  isGeneratingPlans
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isGeneratingPlans ? '⏳ Generating...' : '✅ Generate Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error/Message Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-xl w-full max-w-md ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {errorModalMessage.includes('✅') ? 'Success' : errorModalMessage.includes('🔄') ? 'Please Wait' : 'Notice'}
              </h3>
            </div>
            <div className="p-6">
              <p className={`whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {errorModalMessage}
              </p>
            </div>
            <div className={`p-6 border-t flex justify-end ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => {
                  setShowErrorModal(false);
                  setErrorModalMessage('');
                }}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal for Reject/Delete */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-xl w-full max-w-md ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Confirm Action
              </h3>
            </div>
            <div className="p-6">
              <p className={`whitespace-pre-line ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {confirmModalMessage}
              </p>
            </div>
            <div className={`p-6 border-t flex justify-end space-x-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmModalMessage('');
                  setConfirmModalAction(null);
                }}
                className={`px-6 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmModalAction) confirmModalAction();
                  setShowConfirmModal(false);
                  setConfirmModalMessage('');
                  setConfirmModalAction(null);
                }}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {trainerVideoExercise && (
        <ExerciseVideoModal
          exerciseName={trainerVideoExercise.name}
          attachedUrl={trainerVideoExercise.attached}
          backendUrl={BACKEND_URL}
          isDarkMode={isDarkMode}
          canReplace
          onClose={() => setTrainerVideoExercise(null)}
        />
      )}

    </div>
  );
};

// Client Detail Modal with Assignment Interface
const ClientDetailModal = ({ isOpen, onClose, client, workoutTemplates, workouts, backendUrl, onViewFullProfile }) => {
  const [weightData, setWeightData] = useState([]);
  const [workoutStats, setWorkoutStats] = useState({ last7Days: 0, last30Days: 0, nextWeek: 0 });
  const [workoutResults, setWorkoutResults] = useState([]);
  const [showWorkoutResults, setShowWorkoutResults] = useState(false);
  const [selectedWorkoutResult, setSelectedWorkoutResult] = useState(null);
  const [notes, setNotes] = useState('');
  const [limitations, setLimitations] = useState('');
  const [goals, setGoals] = useState([]);
  const [progressPhotos, setProgressPhotos] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [showScheduleManager, setShowScheduleManager] = useState(false);
  const [clientAssignments, setClientAssignments] = useState([]);
  const [selectedDateForAssign, setSelectedDateForAssign] = useState(null);
  const [scheduleWeekOffset, setScheduleWeekOffset] = useState(0);
  const [workoutToMove, setWorkoutToMove] = useState(null);
  const [movingWorkout, setMovingWorkout] = useState(false);
  
  // Modal states for adding goals/countdowns/photos
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [showAddCountdown, setShowAddCountdown] = useState(false);
  const [showAddPhoto, setShowAddPhoto] = useState(false);
  const [targetWeight, setTargetWeight] = useState('');
  const [countdownForm, setCountdownForm] = useState({ title: '', target_date: '', description: '' });
  const [photoUploadProgress, setPhotoUploadProgress] = useState(0);

  // Helper function to normalize date string for comparison
  const normalizeDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      const date = new Date(dateValue);
      return date.toISOString().split('T')[0];
    } catch {
      return null;
    }
  };

  // Find assignment for a specific date
  const getAssignmentForDate = (dateStr) => {
    return clientAssignments.find(a => normalizeDate(a.scheduled_date) === dateStr);
  };

  useEffect(() => {
    if (isOpen && client) {
      fetchClientData();
      fetchWorkoutResults();
      fetchClientAssignments();
    }
  }, [isOpen, client]);

  const fetchWorkoutResults = async () => {
    if (!client) return;
    try {
      const response = await fetch(`${backendUrl}/api/trainer/client/${client.id}/workout-results`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWorkoutResults(data.workout_results || []);
        // Update workout stats from real data
        const completed = data.workout_results?.filter(w => w.status === 'completed') || [];
        const last7 = completed.filter(w => {
          const dateStr = w.completed_at || w.scheduled_date;
          if (!dateStr) return false;
          const date = new Date(dateStr);
          const daysDiff = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
          return daysDiff <= 7;
        }).length;
        const last30 = completed.filter(w => {
          const dateStr = w.completed_at || w.scheduled_date;
          if (!dateStr) return false;
          const date = new Date(dateStr);
          const daysDiff = (Date.now() - date.getTime()) / (1000 * 60 * 60 * 24);
          return daysDiff <= 30;
        }).length;
        setWorkoutStats({ last7Days: last7, last30Days: last30, nextWeek: workoutStats.nextWeek });
      }
    } catch (error) {
      console.error('Error fetching workout results:', error);
    }
  };

  const fetchClientData = async () => {
    if (!client) return;
    try {
      // Fetch weight data
      const weightRes = await fetch(`${backendUrl}/api/clients/${client.id}/weight-logs`, {
        credentials: 'include'
      });
      if (weightRes.ok) {
        const data = await weightRes.json();
        setWeightData(data.logs || []);
      }

      // Fetch workout stats (placeholder for now)
      setWorkoutStats({
        last7Days: Math.floor(Math.random() * 7),
        last30Days: Math.floor(Math.random() * 30),
        nextWeek: Math.floor(Math.random() * 5)
      });

      // Fetch notes from client profile
      const profileRes = await fetch(`${backendUrl}/api/clients/${client.id}/profile`, {
        credentials: 'include'
      });
      if (profileRes.ok) {
        const data = await profileRes.json();
        setNotes(data.notes || '');
        setLimitations(data.limitations || '');
      }

      // Fetch progress photos
      const photosRes = await fetch(`${backendUrl}/api/clients/${client.id}/progress-photos`, {
        credentials: 'include'
      });
      if (photosRes.ok) {
        const data = await photosRes.json();
        setProgressPhotos(data.progress_photos || []);
      }

      // Fetch goals
      const goalsRes = await fetch(`${backendUrl}/api/clients/${client.id}/goals`, {
        credentials: 'include'
      });
      if (goalsRes.ok) {
        const data = await goalsRes.json();
        setGoals(data.goals || []);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
    }
  };

  const fetchClientAssignments = async () => {
    if (!client) return;
    try {
      // Fetch from workout-assignments endpoint (where new assignments are stored)
      const response = await fetch(`${backendUrl}/api/clients/${client.id}/workout-assignments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const assignments = data.workout_assignments || [];
        setClientAssignments(assignments);
        // Update next week count
        const nextWeekCount = assignments.filter(s => {
          if (!s.scheduled_date) return false;
          const date = new Date(s.scheduled_date);
          const now = new Date();
          const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
          return date >= now && date <= weekFromNow;
        }).length;
        setWorkoutStats(prev => ({ ...prev, nextWeek: nextWeekCount }));
      }
    } catch (error) {
      console.error('Error fetching client assignments:', error);
    }
  };

  const handleAssignWorkout = async (workoutId, date) => {
    if (!client || !workoutId || !date) return;
    
    try {
      const workout = workoutTemplates?.find(w => w.id === workoutId) || workouts?.find(w => w.id === workoutId);
      if (!workout) {
        alert('Workout not found');
        return;
      }

      const response = await fetch(`${backendUrl}/api/workout-assignments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: client.id,
          workout_id: workoutId,
          name: workout.name,
          scheduled_date: date.toISOString().split('T')[0],
          exercises: workout.exercises || []
        })
      });

      if (response.ok) {
        alert('Workout assigned successfully!');
        fetchClientAssignments();
        setSelectedDateForAssign(null);
      } else {
        const error = await response.json();
        alert(`Failed to assign workout: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error assigning workout:', error);
      alert('Failed to assign workout');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!confirm('Are you sure you want to remove this workout assignment?')) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/workout-assignments/${assignmentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchClientAssignments();
      } else {
        alert('Failed to delete assignment');
      }
    } catch (error) {
      console.error('Error deleting assignment:', error);
    }
  };

  const handleMoveAssignment = async (assignment, newDate) => {
    if (!assignment || !newDate) return;
    
    setMovingWorkout(true);
    try {
      const newDateStr = newDate.toISOString().split('T')[0];
      
      const response = await fetch(`${backendUrl}/api/workout-assignments/${assignment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ scheduled_date: newDateStr })
      });
      
      if (response.ok) {
        setWorkoutToMove(null);
        setSelectedDateForAssign(null);
        fetchClientAssignments();
        alert('Workout moved successfully!');
      } else {
        const error = await response.json();
        alert(`Failed to move workout: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error moving workout:', error);
      alert('Failed to move workout');
    } finally {
      setMovingWorkout(false);
    }
  };

  const handleAddGoal = async () => {
    if (!client || !targetWeight) {
      alert('Please enter a target weight');
      return;
    }

    const weight = parseFloat(targetWeight);
    if (isNaN(weight) || weight <= 0) {
      alert('Please enter a valid weight');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/clients/${client.id}/goals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          goal_type: 'weight',
          title: `Target Weight: ${weight} lbs`,
          description: `Weight goal set by trainer`,
          target_value: weight,
          unit: 'lbs',
          target_date: null
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Refetch goals to get the properly formatted data
        const goalsRes = await fetch(`${backendUrl}/api/clients/${client.id}/goals`, {
          credentials: 'include'
        });
        if (goalsRes.ok) {
          const data = await goalsRes.json();
          setGoals(data.goals || []);
        }
        setTargetWeight('');
        setShowAddGoal(false);
        alert('Weight goal added successfully!');
      } else {
        const error = await response.json();
        alert('Failed to add weight goal: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error adding weight goal:', error);
      alert('Failed to add weight goal. Please try again.');
    }
  };

  const handleAddCountdown = async () => {
    if (!client || !countdownForm.title || !countdownForm.target_date) {
      alert('Please enter countdown title and target date');
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/clients/${client.id}/goals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          goal_type: 'countdown',
          title: countdownForm.title,
          description: countdownForm.description,
          target_date: countdownForm.target_date,
          target_value: null,
          unit: null
        })
      });

      if (response.ok) {
        const result = await response.json();
        // Refetch goals to get the properly formatted data
        const goalsRes = await fetch(`${backendUrl}/api/clients/${client.id}/goals`, {
          credentials: 'include'
        });
        if (goalsRes.ok) {
          const data = await goalsRes.json();
          setGoals(data.goals || []);
        }
        setCountdownForm({ title: '', target_date: '', description: '' });
        setShowAddCountdown(false);
        alert('Countdown added successfully!');
      } else {
        const error = await response.json();
        alert('Failed to add countdown: ' + (error.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error adding countdown:', error);
      alert('Failed to add countdown. Please try again.');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !client) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    try {
      setPhotoUploadProgress(20);

      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Image = event.target.result;
        
        setPhotoUploadProgress(50);

        try {
          const response = await fetch(`${backendUrl}/api/clients/${client.id}/progress-photos`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              photo_url: base64Image,
              photo_type: 'progress',
              taken_at: new Date().toISOString()
            })
          });

          setPhotoUploadProgress(100);

          if (response.ok) {
            // Refetch photos
            const photosRes = await fetch(`${backendUrl}/api/clients/${client.id}/progress-photos`, {
              credentials: 'include'
            });
            if (photosRes.ok) {
              const data = await photosRes.json();
              setProgressPhotos(data.progress_photos || []);
            }
            setPhotoUploadProgress(0);
            alert('Photo uploaded successfully!');
          } else {
            const error = await response.json();
            alert('Failed to upload photo: ' + (error.detail || 'Unknown error'));
            setPhotoUploadProgress(0);
          }
        } catch (error) {
          console.error('Error uploading photo:', error);
          alert('Failed to upload photo. Please try again.');
          setPhotoUploadProgress(0);
        }
      };

      reader.onerror = () => {
        alert('Error reading file');
        setPhotoUploadProgress(0);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing photo:', error);
      alert('Failed to process photo. Please try again.');
      setPhotoUploadProgress(0);
    }
  };

  const handleSaveNotes = async () => {
    if (!client) return;

    try {
      const response = await fetch(`${backendUrl}/api/clients/${client.id}/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes, limitations })
      });

      if (response.ok) {
        alert('Notes saved successfully!');
      } else {
        alert('Failed to save notes');
      }
    } catch (error) {
      console.error('Error saving notes:', error);
      alert('Failed to save notes. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-8">
        <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-xl dark:bg-gray-800">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-xl">
                {client?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <h2 className="text-2xl font-bold  dark:text-white" style={{color: "#1a1a1a"}}>{client?.name}</h2>
                <p className="text-sm  dark:text-gray-400" style={{color: "#2d3748"}}>{client?.email}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover: p-2 dark:text-gray-400" style={{color: "#2d3748"}}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* 3-Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
            
            {/* LEFT COLUMN - Training Overview */}
            <div className="space-y-6">
              {/* Training Stats */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 dark:bg-gray-800">
                <h3 className="font-semibold text-gray-900 mb-4 dark:text-white">Training</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm  dark:text-gray-400" style={{color: "#2d3748"}}>Last 7 Days</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {workoutStats.last7Days}/7 Tracked
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm  dark:text-gray-400" style={{color: "#2d3748"}}>Last 30 Days</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {workoutStats.last30Days}/30 Tracked
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm  dark:text-gray-400" style={{color: "#2d3748"}}>Next Week</span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {workoutStats.nextWeek} Assigned
                    </span>
                  </div>
                </div>
                
                {/* Manage Schedule Button */}
                <button
                  onClick={() => setShowScheduleManager(true)}
                  className="w-full mt-4 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium flex items-center justify-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Manage Workout Schedule</span>
                </button>
              </div>

              {/* Workout Schedule Manager Modal */}
              {showScheduleManager && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                          {client?.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Workout Schedule</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{client?.name}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setShowScheduleManager(false);
                          setSelectedDateForAssign(null);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Modal Body */}
                    <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                      {/* Navigation */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setScheduleWeekOffset(Math.max(0, scheduleWeekOffset - 4))}
                            disabled={scheduleWeekOffset === 0}
                            className={`p-2 rounded-lg ${scheduleWeekOffset === 0 ? 'text-gray-300 dark:text-gray-600' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'}`}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                          </button>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[140px] text-center">
                            {(() => {
                              const today = new Date();
                              const startOfWeek = new Date(today);
                              startOfWeek.setDate(today.getDate() - today.getDay() + (scheduleWeekOffset * 7));
                              const endDate = new Date(startOfWeek);
                              endDate.setDate(startOfWeek.getDate() + 27);
                              return `${startOfWeek.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
                            })()}
                          </span>
                          <button
                            onClick={() => setScheduleWeekOffset(scheduleWeekOffset + 4)}
                            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                        {scheduleWeekOffset > 0 && (
                          <button
                            onClick={() => setScheduleWeekOffset(0)}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Back to Today
                          </button>
                        )}
                      </div>

                      {/* 4-Week Calendar */}
                      <div className="mb-6">
                        {/* Day Headers */}
                        <div className="grid grid-cols-7 gap-2 mb-2">
                          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(day => (
                            <div key={day} className="text-center text-xs font-semibold text-gray-500 dark:text-gray-400 py-2">{day}</div>
                          ))}
                        </div>
                        
                        {/* 4 Weeks */}
                        <div className="space-y-2">
                          {[0, 1, 2, 3].map(weekNum => {
                            const today = new Date();
                            const startOfCurrentWeek = new Date(today);
                            startOfCurrentWeek.setDate(today.getDate() - today.getDay());
                            const weekStart = new Date(startOfCurrentWeek);
                            weekStart.setDate(startOfCurrentWeek.getDate() + (scheduleWeekOffset * 7) + (weekNum * 7));
                            
                            return (
                              <div key={weekNum} className="grid grid-cols-7 gap-2">
                                {Array.from({ length: 7 }, (_, dayNum) => {
                                  const date = new Date(weekStart);
                                  date.setDate(weekStart.getDate() + dayNum);
                                  const dateStr = date.toISOString().split('T')[0];
                                  const assignment = getAssignmentForDate(dateStr);
                                  const isToday = date.toDateString() === today.toDateString();
                                  const isPast = date < today && !isToday;
                                  const isSelected = selectedDateForAssign?.toDateString() === date.toDateString();
                                  const isMovingTarget = workoutToMove && !isPast;
                                  const isCurrentWorkoutDate = workoutToMove && normalizeDate(workoutToMove.scheduled_date) === dateStr;
                                  
                                  return (
                                    <button
                                      key={dateStr}
                                      onClick={() => {
                                        if (workoutToMove && !isPast && !isCurrentWorkoutDate) {
                                          handleMoveAssignment(workoutToMove, date);
                                        } else if (!isPast) {
                                          setSelectedDateForAssign(date);
                                        }
                                      }}
                                      className={`p-2 rounded-lg text-left transition-all min-h-[70px] flex flex-col ${
                                        isCurrentWorkoutDate
                                          ? 'bg-yellow-500 text-white ring-2 ring-yellow-300'
                                          : isMovingTarget && !isPast
                                          ? 'ring-2 ring-dashed ring-blue-400 ' + (assignment ? 'bg-green-50 dark:bg-green-900/30' : 'bg-blue-50 dark:bg-blue-900/20')
                                          : isSelected
                                          ? 'bg-blue-600 text-white ring-2 ring-blue-300'
                                          : isToday
                                          ? 'bg-blue-50 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200 ring-2 ring-blue-400'
                                          : assignment
                                          ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900/50'
                                          : isPast
                                          ? 'bg-gray-50 text-gray-300 dark:bg-gray-800 dark:text-gray-600 cursor-not-allowed'
                                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100 dark:bg-gray-700/50 dark:text-gray-300 dark:hover:bg-gray-700'
                                      } ${movingWorkout ? 'opacity-50 pointer-events-none' : ''}`}
                                      disabled={isPast && !workoutToMove}
                                    >
                                      <div className="flex items-center justify-between mb-1">
                                        <span className={`text-xs font-semibold ${isSelected || isCurrentWorkoutDate ? 'text-white' : ''}`}>
                                          {date.getDate()}
                                        </span>
                                        {date.getDate() === 1 && (
                                          <span className={`text-[10px] ${isSelected || isCurrentWorkoutDate ? 'text-blue-200' : 'text-gray-400 dark:text-gray-500'}`}>
                                            {date.toLocaleDateString('en-US', { month: 'short' })}
                                          </span>
                                        )}
                                      </div>
                                      {assignment && (
                                        <div className={`text-[10px] leading-tight truncate ${isSelected || isCurrentWorkoutDate ? 'text-white/90' : 'text-green-700 dark:text-green-300'}`}>
                                          {assignment.name}
                                        </div>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Selected Date Panel */}
                      {selectedDateForAssign && !workoutToMove && (
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 mb-4">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900 dark:text-white">
                              {selectedDateForAssign.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                            </h4>
                            <button
                              onClick={() => setSelectedDateForAssign(null)}
                              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
                            >
                              Cancel
                            </button>
                          </div>
                          
                          {/* Check if there's already an assignment */}
                          {(() => {
                            const dateStr = selectedDateForAssign.toISOString().split('T')[0];
                            const existingAssignment = getAssignmentForDate(dateStr);
                            
                            if (existingAssignment) {
                              return (
                                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-200 dark:border-green-800">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <p className="font-medium text-gray-900 dark:text-white">{existingAssignment.name}</p>
                                      <p className="text-sm text-gray-500">{existingAssignment.exercises?.length || 0} exercises</p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => {
                                          setWorkoutToMove(existingAssignment);
                                          setSelectedDateForAssign(null);
                                        }}
                                        className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg dark:hover:bg-blue-900/20"
                                      >
                                        Move
                                      </button>
                                      <button
                                        onClick={() => handleDeleteAssignment(existingAssignment.id)}
                                        className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg dark:hover:bg-red-900/20"
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            }
                            
                            return (
                              <div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Select a workout to assign:</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                                  {(workoutTemplates || []).length > 0 ? (
                                    (workoutTemplates || []).map(template => (
                                      <button
                                        key={template.id}
                                        onClick={() => handleAssignWorkout(template.id, selectedDateForAssign)}
                                        className="text-left p-3 bg-white hover:bg-blue-50 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-colors border border-gray-200 dark:border-gray-600"
                                      >
                                        <p className="font-medium text-gray-900 dark:text-white text-sm">{template.name}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{template.exercises?.length || 0} exercises</p>
                                      </button>
                                    ))
                                  ) : (
                                    <p className="text-sm text-gray-500 dark:text-gray-400 col-span-2 text-center py-4">
                                      No workout templates available. Create templates in the AI Plans tab first.
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Moving Workout Banner */}
                      {workoutToMove && (
                        <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-xl p-4 mb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-yellow-500 rounded-lg flex items-center justify-center">
                                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                </svg>
                              </div>
                              <div>
                                <p className="font-medium text-yellow-800 dark:text-yellow-200">Moving: {workoutToMove.name}</p>
                                <p className="text-sm text-yellow-600 dark:text-yellow-400">Click on a new date to move this workout</p>
                              </div>
                            </div>
                            <button
                              onClick={() => setWorkoutToMove(null)}
                              className="px-3 py-1.5 text-sm text-yellow-700 hover:text-yellow-800 hover:bg-yellow-100 rounded-lg dark:text-yellow-300 dark:hover:bg-yellow-800/30"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Upcoming Assignments Summary */}
                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">Upcoming Workouts</h4>
                        {clientAssignments.filter(a => a.scheduled_date && new Date(a.scheduled_date) >= new Date()).length > 0 ? (
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {clientAssignments
                              .filter(a => a.scheduled_date && new Date(a.scheduled_date) >= new Date())
                              .sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date))
                              .slice(0, 6)
                              .map(assignment => (
                                <div key={assignment.id} className={`flex items-center justify-between p-3 rounded-lg ${workoutToMove?.id === assignment.id ? 'bg-yellow-100 dark:bg-yellow-900/30 ring-2 ring-yellow-400' : 'bg-gray-50 dark:bg-gray-700/50'}`}>
                                  <div className="min-w-0 flex-1 mr-2">
                                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{assignment.name}</p>
                                    <p className="text-xs text-gray-500">
                                      {new Date(assignment.scheduled_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <button
                                      onClick={() => setWorkoutToMove(workoutToMove?.id === assignment.id ? null : assignment)}
                                      className={`p-1.5 rounded ${workoutToMove?.id === assignment.id ? 'text-yellow-700 bg-yellow-200 dark:text-yellow-300 dark:bg-yellow-800' : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}
                                      title="Move workout"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteAssignment(assignment.id)}
                                      className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded dark:hover:bg-red-900/20"
                                      title="Delete workout"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No upcoming workouts scheduled</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Last Workout */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 dark:bg-gray-800">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Workout History</h3>
                  <button 
                    onClick={() => setShowWorkoutResults(true)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    View All Results →
                  </button>
                </div>
                
                {workoutResults.length > 0 ? (
                  <div className="space-y-2">
                    {workoutResults.slice(0, 3).map((workout, idx) => (
                      <div 
                        key={workout.id || idx} 
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer dark:bg-gray-700"
                        onClick={() => setSelectedWorkoutResult(workout)}
                      >
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${workout.status === 'completed' ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{workout.name || 'Workout'}</p>
                            <p className="text-xs text-gray-500">
                              {workout.exercises?.length || 0} exercises
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-gray-500">
                          {workout.completed_at ? new Date(workout.completed_at).toLocaleDateString() : workout.scheduled_date}
                        </span>
                      </div>
                    ))}
                    {workoutResults.length > 3 && (
                      <p className="text-xs text-center text-gray-500 pt-2">
                        +{workoutResults.length - 3} more workouts
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400">No completed workouts yet</p>
                  </div>
                )}
              </div>

              {/* Monthly Progress Reports */}
              <div data-testid="client-detail-reports-section" className="bg-white border border-gray-200 rounded-lg p-4 dark:bg-gray-800">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Progress Reports</h3>
                <TrainerReportsTab clientId={client.id} />
              </div>

              {/* Body Metrics Overview */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 dark:bg-gray-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Body Metrics Overview</h3>
                  <select className="text-xs border border-gray-300 rounded px-2 py-1 dark:border-gray-600">
                    <option>Last 4 weeks</option>
                    <option>Last 8 weeks</option>
                    <option>Last 12 weeks</option>
                  </select>
                </div>

                {/* Weight Chart Placeholder */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium  dark:text-gray-200" style={{color: "#2d3748"}}>Weight</span>
                    <span className="text-sm  dark:text-gray-400" style={{color: "#2d3748"}}>
                      {weightData.length > 0 ? `${weightData[weightData.length - 1].weight} lbs` : '--'}
                    </span>
                  </div>
                  <div className="h-24 bg-gray-50 rounded flex items-center justify-center border border-gray-200 dark:bg-gray-700">
                    <div className="text-center">
                      <svg className="w-8 h-8 text-gray-400 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <p className="text-xs  dark:text-gray-300" style={{color: "#2d3748"}}>Weight Trend Chart</p>
                    </div>
                  </div>
                </div>

                {/* Body Fat Chart Placeholder */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium  dark:text-gray-200" style={{color: "#2d3748"}}>Body Fat</span>
                    <span className="text-sm  dark:text-gray-400" style={{color: "#2d3748"}}>--</span>
                  </div>
                  <div className="h-24 bg-gray-50 rounded flex items-center justify-center border border-gray-200 dark:bg-gray-700">
                    <div className="text-center">
                      <svg className="w-8 h-8 text-gray-400 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      <p className="text-xs  dark:text-gray-300" style={{color: "#2d3748"}}>Body Fat Trend Chart</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CENTER COLUMN - Goals & Notes */}
            <div className="space-y-6">
              {/* Goal & Countdown */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 dark:bg-gray-800">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Goal & Countdown</h3>
                  <span className="text-xs  dark:text-gray-300" style={{color: "#2d3748"}}>Shared with client</span>
                </div>
                
                <div className="flex space-x-2">
                  <button 
                    onClick={() => setShowAddGoal(true)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium  dark:text-gray-200" style={{color: "#2d3748"}}
                  >
                    + Goal
                  </button>
                  <button 
                    onClick={() => setShowAddCountdown(true)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium  dark:text-gray-200" style={{color: "#2d3748"}}
                  >
                    + Countdown
                  </button>
                </div>

                {goals.length === 0 && (
                  <p className="text-center text-sm  mt-4 dark:text-gray-300" style={{color: "#2d3748"}}>No goals set yet</p>
                )}
                
                {goals.length > 0 && (
                  <div className="mt-4 space-y-3">
                    {goals.map((goal, idx) => {
                      // Calculate days remaining for countdown
                      const daysRemaining = goal.target_date 
                        ? Math.ceil((new Date(goal.target_date) - new Date()) / (1000 * 60 * 60 * 24))
                        : null;
                      
                      return (
                        <div 
                          key={idx} 
                          className={`relative rounded-lg border overflow-hidden ${
                            goal.goal_type === 'weight' 
                              ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' 
                              : goal.goal_type === 'countdown'
                              ? 'bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200'
                              : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200'
                          }`}
                        >
                          {/* Delete button */}
                          <button 
                            onClick={async () => {
                              if (window.confirm(`Remove this ${goal.goal_type === 'countdown' ? 'countdown' : 'goal'}?`)) {
                                try {
                                  await fetch(`${backendUrl}/api/goals/${goal.id}`, {
                                    method: 'DELETE',
                                    credentials: 'include'
                                  });
                                  setGoals(goals.filter(g => g.id !== goal.id));
                                } catch (error) {
                                  console.error('Error deleting goal:', error);
                                }
                              }
                            }}
                            className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/50 text-gray-400 hover:text-red-600 transition-colors z-10 dark:bg-gray-800"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>

                          <div className="p-4">
                            {goal.goal_type === 'weight' ? (
                              // Weight Goal Display
                              <div className="flex items-center space-x-4">
                                <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-1">Target Weight</p>
                                  <p className="text-3xl font-bold text-green-800">{goal.target_value} <span className="text-xl text-green-600">{goal.unit || 'lbs'}</span></p>
                                </div>
                              </div>
                            ) : goal.goal_type === 'countdown' ? (
                              // Countdown Display
                              <div>
                                <div className="flex items-start space-x-3">
                                  <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-semibold text-purple-900 text-sm mb-1">{goal.title}</p>
                                    {daysRemaining !== null && (
                                      <div className="flex items-baseline space-x-2 mb-2">
                                        <span className="text-3xl font-bold text-purple-700">{Math.abs(daysRemaining)}</span>
                                        <span className="text-sm font-medium text-purple-600">
                                          {daysRemaining > 0 ? 'days remaining' : daysRemaining === 0 ? 'TODAY!' : 'days ago'}
                                        </span>
                                      </div>
                                    )}
                                    {goal.target_date && (
                                      <p className="text-xs text-purple-600 font-medium">
                                        📅 {new Date(goal.target_date).toLocaleDateString('en-US', { 
                                          weekday: 'short', 
                                          month: 'short', 
                                          day: 'numeric',
                                          year: 'numeric'
                                        })}
                                      </p>
                                    )}
                                    {goal.description && (
                                      <p className="text-xs text-purple-700 mt-2 italic">{goal.description}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              // Generic Goal Display
                              <div className="flex items-start space-x-3">
                                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-blue-900 text-sm">{goal.title}</p>
                                  {goal.description && (
                                    <p className="text-xs text-blue-700 mt-1">{goal.description}</p>
                                  )}
                                  {goal.target_date && (
                                    <p className="text-xs text-blue-600 font-medium mt-2">
                                      Target: {new Date(goal.target_date).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 dark:bg-gray-800">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Notes</h3>
                  <button 
                    onClick={handleSaveNotes}
                    className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                  >
                    Save
                  </button>
                </div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes about this client..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none dark:border-gray-600"
                  rows={4}
                />
              </div>

              {/* Limitations/Injuries */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 dark:bg-gray-800">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Limitations/Injuries</h3>
                  <button 
                    onClick={handleSaveNotes}
                    className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                  >
                    Save
                  </button>
                </div>
                <textarea
                  value={limitations}
                  onChange={(e) => setLimitations(e.target.value)}
                  placeholder="Note any injuries or limitations..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none dark:border-gray-600"
                  rows={3}
                />
              </div>

              {/* Progress Photo */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 dark:bg-gray-800">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Progress Photo</h3>
                  <label className="text-xs text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
                    + Add Photo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </label>
                </div>
                
                {photoUploadProgress > 0 && photoUploadProgress < 100 && (
                  <div className="mb-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${photoUploadProgress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs  mt-1 dark:text-gray-300" style={{color: "#2d3748"}}>Uploading...</p>
                  </div>
                )}
                
                {progressPhotos.length === 0 ? (
                  <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center dark:bg-gray-700">
                    <svg className="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-sm  mb-2 dark:text-gray-300" style={{color: "#2d3748"}}>No photos yet</p>
                    <label className="text-xs text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
                      Click to upload your first photo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {progressPhotos.slice(0, 6).map((photo, idx) => (
                        <div key={idx} className="aspect-square bg-gray-200 rounded-lg overflow-hidden relative group">
                          {photo.photo_url ? (
                            <>
                              <img src={photo.photo_url} alt={`Progress ${idx + 1}`} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center">
                                <button
                                  onClick={async () => {
                                    if (window.confirm('Delete this photo?')) {
                                      try {
                                        await fetch(`${backendUrl}/api/progress-photos/${photo.id}`, {
                                          method: 'DELETE',
                                          credentials: 'include'
                                        });
                                        setProgressPhotos(progressPhotos.filter(p => p.id !== photo.id));
                                      } catch (error) {
                                        console.error('Error deleting photo:', error);
                                      }
                                    }
                                  }}
                                  className="hidden group-hover:block text-white bg-red-600 rounded-full p-2 hover:bg-red-700"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </>
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-blue-100 to-purple-100"></div>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs  text-center mb-2 dark:text-gray-300" style={{color: "#2d3748"}}>
                      {progressPhotos.length} photo{progressPhotos.length !== 1 ? 's' : ''} total
                    </p>
                  </>
                )}
                
                <div className="flex space-x-2 mt-3">
                  <label className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium text-center cursor-pointer">
                    Add Photo
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </label>
                  {progressPhotos.length > 6 && (
                    <button 
                      onClick={() => alert(`View all ${progressPhotos.length} photos - Full gallery coming soon!`)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-xs font-medium  dark:text-gray-200" style={{color: "#2d3748"}}
                    >
                      View All ({progressPhotos.length})
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - Profile & Updates */}
            <div className="space-y-6">
              {/* Profile */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 dark:bg-gray-800">
                <h3 className="font-semibold text-gray-900 mb-4 dark:text-white">Profile</h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs  mb-1 dark:text-gray-300" style={{color: "#2d3748"}}>Email</p>
                    <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>{client?.email}</p>
                  </div>
                  <div>
                    <p className="text-xs  mb-1 dark:text-gray-300" style={{color: "#2d3748"}}>Timezone</p>
                    <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>America/Los_Angeles (PST)</p>
                  </div>
                  <div>
                    <p className="text-xs  mb-1 dark:text-gray-300" style={{color: "#2d3748"}}>Local Time</p>
                    <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>{new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</p>
                  </div>
                </div>
              </div>

              {/* Updates Feed */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 dark:bg-gray-800">
                <h3 className="font-semibold text-gray-900 mb-4 dark:text-white">Updates</h3>
                
                <div className="space-y-4">
                  {/* Sample activity items */}
                  <div className="flex space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>Completed workout</p>
                      <p className="text-xs  dark:text-gray-300" style={{color: "#2d3748"}}>Upper Body Strength</p>
                      <p className="text-xs text-gray-400 mt-1">6d</p>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>Weight logged</p>
                      <p className="text-xs  dark:text-gray-300" style={{color: "#2d3748"}}>175 lbs</p>
                      <p className="text-xs text-gray-400 mt-1">3w</p>
                    </div>
                  </div>

                  <div className="flex space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm" style={{color: '#2d3748', fontSize: '14px'}}>Started program</p>
                      <p className="text-xs  dark:text-gray-300" style={{color: "#2d3748"}}>12-Week Transformation</p>
                      <p className="text-xs text-gray-400 mt-1">4w</p>
                    </div>
                  </div>
                </div>

                {activityFeed.length === 0 && (
                  <p className="text-center text-sm  mt-4 dark:text-gray-300" style={{color: "#2d3748"}}>No recent activity</p>
                )}
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50 dark:bg-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg  hover:bg-gray-100 font-medium dark:text-gray-200" style={{color: "#2d3748"}}
            >
              Close
            </button>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
              Assign Workout
            </button>
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium">
              Set Macros
            </button>
          </div>
        </div>
      </div>

      {/* Add Goal Modal */}
      {showAddGoal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-white">Set Weight Goal</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium  mb-2 dark:text-gray-200" style={{color: "#2d3748"}}>
                  Target Weight *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={targetWeight}
                    onChange={(e) => setTargetWeight(e.target.value)}
                    placeholder="Enter target weight"
                    step="0.1"
                    min="0"
                    className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:border-gray-600"
                  />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <span className=" text-sm dark:text-gray-300" style={{color: "#2d3748"}}>lbs</span>
                  </div>
                </div>
                <p className="text-xs  mt-1 dark:text-gray-300" style={{color: "#2d3748"}}>Enter the target weight for this client</p>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddGoal(false);
                  setTargetWeight('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg  hover:bg-gray-50 font-medium dark:text-gray-200" style={{color: "#2d3748"}}
              >
                Cancel
              </button>
              <button
                onClick={handleAddGoal}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                Set Goal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Countdown Modal */}
      {showAddCountdown && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 dark:text-white">Add Countdown</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium  mb-1 dark:text-gray-200" style={{color: "#2d3748"}}>
                  Event Title *
                </label>
                <input
                  type="text"
                  value={countdownForm.title}
                  onChange={(e) => setCountdownForm({ ...countdownForm, title: e.target.value })}
                  placeholder="e.g., Competition Day"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:border-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium  mb-1 dark:text-gray-200" style={{color: "#2d3748"}}>
                  Target Date *
                </label>
                <input
                  type="date"
                  value={countdownForm.target_date}
                  onChange={(e) => setCountdownForm({ ...countdownForm, target_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:border-gray-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium  mb-1 dark:text-gray-200" style={{color: "#2d3748"}}>
                  Description
                </label>
                <textarea
                  value={countdownForm.description}
                  onChange={(e) => setCountdownForm({ ...countdownForm, description: e.target.value })}
                  placeholder="Add details about this event..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600"
                  rows={3}
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddCountdown(false);
                  setCountdownForm({ title: '', target_date: '', description: '' });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg  hover:bg-gray-50 font-medium dark:text-gray-200" style={{color: "#2d3748"}}
              >
                Cancel
              </button>
              <button
                onClick={handleAddCountdown}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                Add Countdown
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Workout Results Modal */}
      {showWorkoutResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8 max-h-[90vh] overflow-hidden dark:bg-gray-800">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {client?.name}'s Workout Results
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {workoutResults.filter(w => w.status === 'completed').length} completed workouts
                  </p>
                </div>
                <button
                  onClick={() => setShowWorkoutResults(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full dark:hover:bg-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {workoutResults.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-gray-700">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2 dark:text-white">No Workout Results Yet</h4>
                  <p className="text-gray-500">Once {client?.name} completes workouts, the results will appear here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {workoutResults.map((workout, idx) => (
                    <div 
                      key={workout.id || idx}
                      className="border border-gray-200 rounded-xl overflow-hidden dark:border-gray-700"
                    >
                      {/* Workout Header */}
                      <div 
                        className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600"
                        onClick={() => setSelectedWorkoutResult(selectedWorkoutResult?.id === workout.id ? null : workout)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-3 h-3 rounded-full ${
                              workout.status === 'completed' ? 'bg-green-500' 
                              : workout.status === 'in_progress' ? 'bg-yellow-500'
                              : workout.status === 'scheduled' || workout.status === 'pending' || workout.status === 'assigned' ? 'bg-blue-500'
                              : workout.status === 'skipped' ? 'bg-gray-400'
                              : 'bg-gray-500'
                            }`}></div>
                            <div>
                              <h5 className="font-semibold text-gray-900 dark:text-white">{workout.name || 'Workout'}</h5>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className="text-sm text-gray-500">
                                  {workout.completed_at 
                                    ? new Date(workout.completed_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                                    : workout.scheduled_date
                                  }
                                </span>
                                {workout.duration && (
                                  <span className="text-sm text-gray-500">• {Math.round(workout.duration / 60)} min</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              workout.status === 'completed' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                                : workout.status === 'in_progress'
                                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
                                : workout.status === 'pending' || workout.status === 'assigned' || workout.status === 'scheduled'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                                : workout.status === 'skipped'
                                ? 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {workout.status === 'completed' ? '✓ Completed' 
                                : workout.status === 'in_progress' ? 'In Progress' 
                                : workout.status === 'scheduled' ? 'Scheduled'
                                : workout.status === 'pending' ? 'Pending'
                                : workout.status === 'assigned' ? 'Assigned'
                                : workout.status === 'skipped' ? 'Skipped'
                                : workout.status || 'Logged'}
                            </span>
                            <span className="text-sm text-gray-500">{workout.exercises?.length || 0} exercises</span>
                            <svg 
                              className={`w-5 h-5 text-gray-400 transition-transform ${selectedWorkoutResult?.id === workout.id ? 'rotate-180' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </div>

                        {/* Target Muscles */}
                        {workout.target_muscles && workout.target_muscles.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-3">
                            {workout.target_muscles.map((muscle, i) => (
                              <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs dark:bg-blue-900 dark:text-blue-300">
                                {muscle}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Expanded Exercise Details */}
                      {selectedWorkoutResult?.id === workout.id && (
                        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                          <h6 className="font-medium text-gray-900 mb-3 dark:text-white">Exercise Results</h6>
                          <div className="space-y-3">
                            {workout.exercises?.map((exercise, exIdx) => (
                              <div key={exIdx} className="bg-gray-50 rounded-lg p-3 dark:bg-gray-700">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold dark:bg-blue-900 dark:text-blue-300">
                                        {exIdx + 1}
                                      </span>
                                      <span className="font-medium text-gray-900 dark:text-white">{exercise.name}</span>
                                    </div>

                                    {/* Prescribed vs Actual */}
                                    <div className="mt-2 grid grid-cols-2 gap-4">
                                      <div>
                                        <p className="text-xs text-gray-500 mb-1">Prescribed</p>
                                        <p className="text-sm text-gray-700 dark:text-gray-300">
                                          {getSetsCount(exercise.sets)} sets × {getRepsDisplay(exercise)} reps
                                          {exercise.weight && ` @ ${exercise.weight}`}
                                        </p>
                                      </div>

                                      {/* Actual Sets Completed */}
                                      {exercise.sets_completed && exercise.sets_completed.length > 0 && (
                                        <div>
                                          <p className="text-xs text-gray-500 mb-1">Actual</p>
                                          <div className="space-y-1">
                                            {exercise.sets_completed.map((set, setIdx) => (
                                              <p key={setIdx} className="text-sm font-medium text-green-700 dark:text-green-400">
                                                Set {setIdx + 1}: {set.weight || 0} lbs × {set.reps || 0} reps
                                              </p>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>

                                    {exercise.notes && (
                                      <p className="text-xs text-gray-500 mt-2 italic">📝 {exercise.notes}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Workout Notes */}
                          {workout.workout_notes && (
                            <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200 dark:bg-yellow-900 dark:border-yellow-700">
                              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">Client Notes:</p>
                              <p className="text-sm text-yellow-700 mt-1 dark:text-yellow-300">{workout.workout_notes}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowWorkoutResults(false)}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Everfit-Style Client Management Dashboard
const EverfitClientManager = ({ client, onClose, backendUrl }) => {
  const [activeSection, setActiveSection] = useState('overview');
  const [clientData, setClientData] = useState({
    goals: [],
    tasks: [],
    nutritionPlan: null,
    mealPlans: [],
    progressPhotos: [],
    performanceMetrics: [],
    assessments: [],
    habits: []
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState('');

  useEffect(() => {
    if (client) {
      fetchAllClientData();
    }
  }, [client]);

  const fetchAllClientData = async () => {
    if (!client) return;
    
    try {
      // Fetch all client data in parallel
      const [
        goalsRes,
        tasksRes,
        nutritionRes,
        mealPlansRes,
        photosRes,
        metricsRes,
        assessmentsRes,
        habitsRes
      ] = await Promise.all([
        fetch(`${backendUrl}/api/clients/${client.id}/goals`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/tasks`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/nutrition-plan`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/meal-plans`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/progress-photos`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/performance-metrics`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/assessments`, { credentials: 'include' }),
        fetch(`${backendUrl}/api/clients/${client.id}/habits`, { credentials: 'include' })
      ]);

      const data = {
        goals: goalsRes.ok ? (await goalsRes.json()).goals : [],
        tasks: tasksRes.ok ? (await tasksRes.json()).tasks : [],
        nutritionPlan: nutritionRes.ok ? (await nutritionRes.json()).nutrition_plan : null,
        mealPlans: mealPlansRes.ok ? (await mealPlansRes.json()).meal_plans : [],
        progressPhotos: photosRes.ok ? (await photosRes.json()).progress_photos : [],
        performanceMetrics: metricsRes.ok ? (await metricsRes.json()).performance_metrics : [],
        assessments: assessmentsRes.ok ? (await assessmentsRes.json()).assessments : [],
        habits: habitsRes.ok ? (await habitsRes.json()).habits : []
      };

      setClientData(data);
    } catch (error) {
      console.error('Error fetching client data:', error);
    }
  };

  const handleCreateItem = async (type, itemData) => {
    try {
      const endpoint = {
        'goal': `/api/clients/${client.id}/goals`,
        'task': `/api/clients/${client.id}/tasks`,
        'nutrition': `/api/clients/${client.id}/nutrition-plan`,
        'assessment': `/api/clients/${client.id}/assessment`,
        'habit': `/api/clients/${client.id}/habits`
      }[type];

      const response = await fetch(`${backendUrl}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(itemData)
      });

      if (response.ok) {
        fetchAllClientData(); // Refresh data
        setShowCreateModal(false);
        setCreateType('');
      }
    } catch (error) {
      console.error(`Error creating ${type}:`, error);
    }
  };

  if (!client) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden dark:bg-gray-800">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-500 to-purple-600 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center dark:bg-gray-800">
                <span className="text-white font-bold text-xl">
                  {client.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{client.name}</h2>
                <p className="text-blue-100">{client.email}</p>
                <p className="text-blue-100 text-sm">Client since {new Date(client.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg dark:bg-gray-800"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <div className="mt-6 flex space-x-1 bg-white bg-opacity-20 rounded-lg p-1 dark:bg-gray-800">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'goals', label: 'Goals', icon: '🎯' },
              { id: 'tasks', label: 'Tasks', icon: '✅' },
              { id: 'nutrition', label: 'Nutrition', icon: '🥗' },
              { id: 'workouts', label: 'Workouts', icon: '🏋️' },
              { id: 'progress', label: 'Progress', icon: '📈' },
              { id: 'habits', label: 'Habits', icon: '📅' }
            ].map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeSection === section.id
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-white hover:bg-white hover:bg-opacity-20'
                }`}
              >
                <span>{section.icon}</span>
                <span>{section.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content Area */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Overview Section */}
          {activeSection === 'overview' && (
            <EverfitOverviewSection client={client} clientData={clientData} />
          )}

          {/* Goals Section */}
          {activeSection === 'goals' && (
            <EverfitGoalsSection 
              client={client} 
              goals={clientData.goals}
              onCreateGoal={() => {
                setCreateType('goal');
                setShowCreateModal(true);
              }}
              onRefresh={fetchAllClientData}
              backendUrl={backendUrl}
            />
          )}

          {/* Tasks Section */}
          {activeSection === 'tasks' && (
            <EverfitTasksSection 
              client={client} 
              tasks={clientData.tasks}
              onCreateTask={() => {
                setCreateType('task');
                setShowCreateModal(true);
              }}
              onRefresh={fetchAllClientData}
              backendUrl={backendUrl}
            />
          )}

          {/* Nutrition Section */}
          {activeSection === 'nutrition' && (
            <EverfitNutritionSection 
              client={client} 
              nutritionPlan={clientData.nutritionPlan}
              mealPlans={clientData.mealPlans}
              onCreatePlan={() => {
                setCreateType('nutrition');
                setShowCreateModal(true);
              }}
              onRefresh={fetchAllClientData}
              backendUrl={backendUrl}
            />
          )}

          {/* Workouts Section */}
          {activeSection === 'workouts' && (
            <EverfitWorkoutsSection 
              client={client} 
              backendUrl={backendUrl}
            />
          )}

          {/* Progress Section */}
          {activeSection === 'progress' && (
            <EverfitProgressSection 
              client={client} 
              progressPhotos={clientData.progressPhotos}
              performanceMetrics={clientData.performanceMetrics}
              backendUrl={backendUrl}
            />
          )}

          {/* Habits Section */}
          {activeSection === 'habits' && (
            <EverfitHabitsSection 
              client={client} 
              habits={clientData.habits}
              onCreateHabit={() => {
                setCreateType('habit');
                setShowCreateModal(true);
              }}
              onRefresh={fetchAllClientData}
              backendUrl={backendUrl}
            />
          )}
        </div>

        {/* Create Modals */}
        {showCreateModal && (
          <EverfitCreateModal
            type={createType}
            client={client}
            onClose={() => {
              setShowCreateModal(false);
              setCreateType('');
            }}
            onCreate={handleCreateItem}
          />
        )}
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  // Check for invitation token in URL FIRST (before anything else)
  const urlParams = new URLSearchParams(window.location.search);
  const invitationToken = urlParams.get('invitation');
  
  // Fix: Add localStorage persistence for selectedRole
  const [selectedRole, setSelectedRole] = useState(() => {
    // If there's an invitation token, force client role
    if (invitationToken) {
      return 'client';
    }
    return localStorage.getItem('selectedRole') || null;
  });
  
  // Restore authentication
  const { user, loading, logout } = useAuth();

  // Fix: Handle role selection with persistence
  const handleRoleSelect = (role) => {
    localStorage.setItem('selectedRole', role);
    setSelectedRole(role);
  };

  // Handle invitation token and user authentication
  React.useEffect(() => {
    // If there's an invitation token and a user just registered successfully (client role)
    if (invitationToken && user && user.role === 'client') {
      // Clear the invitation token from URL to allow redirect to dashboard
      const newUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    // If there's an invitation token and a different user is logged in (not the one who should sign up)
    else if (invitationToken && user && user.role !== 'client') {
      // Logout the current user to show the signup page
      logout();
    }
  }, [invitationToken, user, logout]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4  dark:text-gray-400" style={{color: "#2d3748"}}>Loading...</p>
        </div>
      </div>
    );
  }

  // If user is authenticated, show appropriate dashboard (takes priority over invitation token)
  if (user) {
    return (
      <>
        <NotificationReenablePrompt />
        {user.role === 'trainer' ? <TrainerDashboard /> : <ClientDashboard />}
      </>
    );
  }

  // If there's an invitation token and no user, show signup page
  if (invitationToken) {
    return <AuthForm role="client" invitationToken={invitationToken} />;
  }

  // If no role selected, show role selection
  if (!selectedRole) {
    return <RoleSelection onRoleSelect={handleRoleSelect} />;
  }

  // Show auth form for selected role
  return <AuthForm role={selectedRole} />;
};

// Wrap App with AuthProvider and ThemeProvider
const AppWrapper = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <App />
      </AuthProvider>
    </ThemeProvider>
  );
};

export default AppWrapper;